import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { createClient } from '@supabase/supabase-js';
import { F, ACCENTS, T, haptic, Icon, StatusIcon } from './theme.jsx';
import {
  useModalViewport, modalShellStyle, bottomSheetShellStyle,
  ModalCloseButton, BottomSheetHeader, ModalHeader,
} from './modal-utils.jsx';
import {
  usePullToRefresh, PullToRefreshIndicator,
  Skeleton, SkeletonOrderRow, SkeletonWidget,
  SkeletonText, SkeletonListItem, useDelayedSkeleton,
  BottomSheet,
  WidgetCard, WidgetHeader,
  TodaySummaryWidget, BereitCountWidget, ReorderAlarmsWidget, GoalProgressWidget,
  ChannelBreakdownWidget, QuickActionsWidget, RecentActivityWidget, StaleOrdersWidget,
  LagerSummaryWidget, FinanceSummaryWidget,
  computeStaleOrders, formatHoursAgo,
  computeLagerSummary, computeFinanceSummary,
  WIDGETS, DashboardArea, defaultLayoutForRole,
  useValidation,
} from './widgets.jsx';
import { syncManager, SyncIndicator, usePWAInstall } from './sync.jsx';
import {
  _SettingsRowFrame, SettingsLinkRow, SettingsToggleRow,
  SettingsSectionLabel, SettingsCard, SettingsRow, SettingsToggle,
  ConfirmDeleteStoreSheet, ModeMembersSheet, InviteCodeSheet, ConfirmDialog,
  exportSingleStoreCsv,
} from './store-sheets.jsx';
import { FleaHub } from './flea.jsx';
import { MarketHub } from './market.jsx';

const API_URL = "/api/";

// ── Supabase client ───────────────────────────────────────────
const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL      || "https://mkfmlsgxiafbyehftxol.supabase.co";
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || "sb_publishable_QcKeNDbD_GVZbqOlNxzDnw_9vAHv1EZ";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ── Role-permission map ───────────────────────────────────────
const ROLE_PERMS = {
  owner:       ["read","write","delete","finance","manage_members","manage_roles"],
  admin:       ["read","write","delete","finance","manage_members"],
  fulfillment: ["read","write"],
  finance:     ["read","finance"],
  viewer:      ["read"],
};

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error(`ErrorBoundary [${this.props.fallbackLabel || "App"}] caught:`, error, info);
  }
  render() {
    if (this.state.error) {
      const label = this.props.fallbackLabel || "UI";
      const onReset = this.props.onReset;
      return React.createElement("div", {
        style: { padding: 24, fontFamily: "system-ui", color: "#fff", background: "#1a1a1a", minHeight: "100vh" }
      },
        React.createElement("h2", { style: { color: "#FF453A" } }, `Fehler im ${label}`),
        React.createElement("pre", { style: { fontSize: 11, whiteSpace: "pre-wrap", color: "#FF9F0A" } }, String(this.state.error?.stack || this.state.error)),
        React.createElement("button", {
          onClick: () => { this.setState({ error: null }); if (onReset) onReset(); },
          style: { marginTop: 16, padding: "10px 20px", borderRadius: 8, border: "none", background: "#0A84FF", color: "#fff", fontSize: 14, cursor: "pointer" }
        }, "Zurück zur Übersicht")
      );
    }
    return this.props.children;
  }
}

const STORAGE_KEY  = "memorica-orders";
const DARKMODE_KEY = "memorica-darkmode";

// E-Mails die automatisch in den Helfer-Modus gezwungen werden
// (egal was im persistenten State steht)
const HELPER_EMAILS = ["md@recout.de"];

const STATUS = {
  "Offen":     { color: "#0A84FF", bg: "rgba(10,132,255,0.15)" },
  "Bereit":    { color: "#BF5AF2", bg: "rgba(191,90,242,0.15)" },
  "Versendet": { color: "#30D158", bg: "rgba(48,209,88,0.15)"  },
  "Retoure":   { color: "#FF453A", bg: "rgba(255,69,58,0.15)"  },
};
const STATUS_ORDER = ["Offen", "Bereit", "Versendet", "Retoure"];
const TABS    = ["Alle", "Bereit", "Versendet", "Retoure"];
// KANAELE constant removed — channels are now per-store via getAllChannels() / getActiveChannels()

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = (k = "etsy") => ({ bestellnummer: "", kanal: k, datum: today(), status: "Offen", notiz: "", trackingNumber: "", retoureErhalten: false, adresse: "", labelKey: "", labelMime: "" });
const fmt = (d) => d ? new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—";
const fmt2 = (n) => (n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ageInDays = (d) => { if (!d) return 0; const then = new Date(d); const now = new Date(); return Math.floor((now - then) / 86400000); };
const ageLabel = (d) => {
  const n = ageInDays(d);
  if (n <= 0) return "heute";
  if (n === 1) return "gestern";
  return `vor ${n} Tagen`;
};

// Builds the label base URL: store-scoped when storeId+token given, legacy fallback otherwise.
function labelBaseUrl(storeId, token) {
  if (storeId && token) return `/api/stores/${storeId}/labels/`;
  return "/api/labels/"; // legacy (migration period)
}

function labelHeaders(token) {
  if (token) return { "Authorization": `Bearer ${token}` };
  return {}; // legacy: Cloudflare Access cookie
}

// Lädt ein File (PDF/Bild) zum Worker → R2 hoch.
// Gibt { key, mime } zurück; key ist der R2-Object-Key, der pro Order gespeichert wird.
//
// iOS Safari kann "Load failed" werfen wenn die fetch-Verbindung abbricht.
// Wir lesen die Datei vorher in einen ArrayBuffer (statt File-Stream) — das
// ist auf iOS deutlich stabiler bei größeren Dateien.
async function uploadLabel(file, orderId, storeId, token) {
  if (!file) throw new Error("Keine Datei");
  const sizeBytes = file.size || 0;
  if (sizeBytes > 10 * 1024 * 1024) {
    throw new Error(`Datei zu groß (${(sizeBytes / 1024 / 1024).toFixed(1)} MB, max 10 MB)`);
  }
  const ext = (file.name?.split?.(".")?.pop?.() || "bin").toLowerCase();
  const safeExt = ["pdf", "png", "jpg", "jpeg", "webp", "heic"].includes(ext) ? ext : "bin";
  const key = `order-${orderId}.${safeExt}`;
  const url = labelBaseUrl(storeId, token) + key;
  const mime = file.type || "application/octet-stream";

  // Read full file into memory first — much more reliable on iOS Safari
  // than passing the File object directly (which streams).
  let buffer;
  try {
    buffer = await file.arrayBuffer();
  } catch (e) {
    throw new Error(`Datei konnte nicht gelesen werden: ${e.message}`);
  }

  // XMLHttpRequest: deutlich stabiler als fetch auf iOS Safari für PUT mit Binärdaten.
  // Eigene Promise-Wrapper-Implementierung mit echtem Status + Body-Reading bei Errors.
  function xhrPut(url, body, mime, token) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      else xhr.withCredentials = true;
      xhr.setRequestHeader("Content-Type", mime);
      xhr.timeout = 60_000; // 60s
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ status: xhr.status, body: xhr.responseText });
        } else {
          let detail = xhr.responseText;
          try { const d = JSON.parse(detail); detail = d?.reason || d?.error || detail; } catch {}
          reject(new Error(`HTTP ${xhr.status} — ${detail || "no body"}`));
        }
      };
      xhr.onerror = () => reject(new Error("network error"));
      xhr.ontimeout = () => reject(new Error("timeout"));
      xhr.onabort = () => reject(new Error("aborted"));
      xhr.send(body);
    });
  }

  // Try up to 3x — real iOS hiccups often resolve on retry
  let lastError = null;
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const blob = new Blob([buffer], { type: mime });
      await xhrPut(url, blob, mime, token);
      return { key, mime };
    } catch (e) {
      lastError = e;
      const msg = String(e?.message || "");
      const isNetwork = /network|timeout|aborted|load failed/i.test(msg);
      if (!isNetwork || attempt === 3) {
        const url_short = url.replace(/^https?:\/\/[^/]+/, "");
        throw new Error(`${msg} (PUT ${url_short}, ${(buffer.byteLength / 1024).toFixed(0)} KB, ${attempt}× XHR-versucht)`);
      }
      await new Promise(r => setTimeout(r, 400 * attempt));
    }
  }
  throw lastError || new Error("Upload fehlgeschlagen");
}

// Holt eine kurzlebige Signed-URL für ein Label (für Anzeige/Druck im neuen Tab).
async function getLabelUrl(key, storeId, token) {
  const res = await fetch(labelBaseUrl(storeId, token) + key + "?signed=1", {
    credentials: token ? "omit" : "include",
    headers: labelHeaders(token),
  });
  if (!res.ok) throw new Error(`Label-URL fehlgeschlagen: HTTP ${res.status}`);
  const data = await res.json();
  return data.url;
}

// Löscht ein Label aus R2 (bei Bestell-Storno).
async function deleteLabel(key, storeId, token) {
  if (!key) return;
  try {
    await fetch(labelBaseUrl(storeId, token) + key, {
      method: "DELETE",
      credentials: token ? "omit" : "include",
      headers: labelHeaders(token),
    });
  } catch (e) { console.warn("Label-Delete fehlgeschlagen:", e); }
}

// ─────────────────────────────────────────────────────────────
// Tracking number extraction from shipping labels (client-side)
//
// Pipeline:
//   1. PDF → render first page to canvas via pdf.js
//      Image (PNG/JPEG/WEBP) → decode to canvas
//   2. Canvas → ZXing barcode decoder (Code128, QR, DataMatrix, …)
//   3. If no barcode → lazy-load Tesseract.js → OCR canvas → regex match
//   4. Sanitize result (uppercase, alphanumeric, max 50 chars)
//
// Everything runs in the browser. No data sent to extraction servers.
// ─────────────────────────────────────────────────────────────

// Tracking-Pattern-Stack: hochspezifische Carrier-Formate ZUERST,
// generische Ziffern-Patterns nur als allerletzter Fallback. Jeder
// Treffer wird gegen Phone- und sonstige False-Positive-Heuristiken geprüft.
const TRACKING_PATTERNS = [
  // DHL international ("RR123456789DE", "LX012345678CN") — sehr spezifisch
  { re: /\b[A-Z]{2}\d{9,11}[A-Z]{2}\b/, weight: 100, name: "intl" },
  // DHL Paket DE (20 Ziffern)
  { re: /\b\d{20}\b/, weight: 95, name: "dhl20" },
  // DHL Express (10-12 Ziffern) — zwischen Paket und kurz
  { re: /\b\d{12}\b/, weight: 50, name: "dhl12" },
  // Hermes ("H" + 14-20 Ziffern)
  { re: /\bH\d{14,20}\b/, weight: 90, name: "hermes" },
  // UPS ("1Z" + 16 alphanumeric)
  { re: /\b1Z[A-Z0-9]{16}\b/, weight: 100, name: "ups" },
  // DPD (14 Ziffern)
  { re: /\b\d{14}\b/, weight: 70, name: "dpd14" },
  // GLS (11 Ziffern)
  { re: /\b\d{11}\b/, weight: 35, name: "gls11" },
  // FedEx (15 Ziffern)
  { re: /\b\d{15}\b/, weight: 70, name: "fedex" },
  // Alphanumeric mit min. 1 Buchstabe (kein Phone)
  { re: /\b(?=[A-Z0-9]*[A-Z])[A-Z0-9]{12,30}\b/, weight: 60, name: "alpha" },
];

// Tracking-Keyword-Match: NUR spezifische Keywords ("Sendungsnr", "Tracking", "AWB").
// "Paket" allein ist zu generisch — eingeschränkt auf "Paketnr" / "Paketnummer".
// Capture-Group nimmt NUR alphanumeric + `-` (KEINE Spaces) damit "Max Mustermann"
// nicht fälschlich als Tracking durchrutscht.
const TRACKING_KEYWORD_RE = /(?:tracking[- ]?(?:nr|number)|sendung(?:sverfolgung|snr|snummer|s-?nr)|paket(?:nr|nummer)|versand(?:nr|nummer)|shipment[- ]?(?:nr|number)|consignment[- ]?(?:nr|number)|awb)\s*[:#-]?\s*([A-Z0-9][A-Z0-9-]{7,40})/i;

// Phone-Number-Patterns — Phone-Nummern haben IMMER einen Separator
// (Leerzeichen, Bindestrich, Slash). Lange Ziffernketten ohne Separator
// sind KEINE Telefon-Nummern (z.B. DHL 003404342...).
const PHONE_PATTERNS = [
  /\+\s*\d{1,3}[\s\-/]\d/,            // International prefix "+49 ..."
  /\b00\d{2,3}[\s\-/]\d/,             // Deutscher Auslandspräfix "0049 ..." nur MIT Separator
  /\b0\d{2,4}[\s\-/]\d/,              // Deutsche Vorwahl "030 ..." / "0151-..."
  /\(\s*0?\d+\s*\)\s*\d/,             // "(030) ..."
];
const PHONE_KEYWORD_RE = /(?:^|\s)(?:tel(?:efon)?|phone|fon|mobil|mobile|fax|hotline)\s*[:.]?\s*$/i;

// Invoice/Order/Customer-Keywords — wenn unmittelbar VOR einem numerischen
// Match auf der gleichen Zeile, ist die Nummer höchstwahrscheinlich eine
// Rechnungs-/Bestell-/Kundennummer und KEINE Trackingnummer.
const INVOICE_KEYWORD_RE = /(?:^|\s)(?:rechnung(?:s)?(?:nr|nummer)?|bestell(?:nr|nummer|ung)?|order(?:[- ]?(?:id|nr|number))?|invoice(?:[- ]?(?:nr|number))?|kunden(?:[- ]?(?:nr|nummer))?|customer(?:[- ]?(?:id|nr))?|reference|referenz|auftrag(?:s)?(?:nr|nummer)?|art(?:ikel)?[- ]?nr|sku|ust[- ]?id|steuer)\s*[:.#-]?\s*$/i;

function sanitizeTrackingNumber(raw) {
  if (!raw) return null;
  const cleaned = String(raw).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 50);
  if (cleaned.length < 8) return null;
  return cleaned;
}

// False-Positive-Kontext-Check: ist der Match
//   (a) Teil einer Telefonnummer auf gleicher Zeile, oder
//   (b) direkt nach einem Phone-Keyword ("Tel:", "Fax:"), oder
//   (c) direkt nach einem Invoice-/Order-/Kunden-Keyword ("Rechnungsnr:", "Bestellnr:", "Kunden-Nr:"...)?
// Wenn ja → Match wird NICHT als Trackingnummer akzeptiert.
function looksLikeFalsePositiveInContext(text, matchIdx, matchLen) {
  // Zeilen-Grenzen
  const lineStart = text.lastIndexOf("\n", matchIdx - 1) + 1;
  const lineEndN  = text.indexOf("\n", matchIdx + matchLen);
  const lineEnd   = lineEndN === -1 ? text.length : lineEndN;
  const line = text.slice(lineStart, lineEnd);
  const matchPosInLine = matchIdx - lineStart;
  const before = line.slice(0, matchPosInLine);

  // 1. Phone-Pattern überlappt mit dem Match-Bereich auf gleicher Zeile?
  for (const re of PHONE_PATTERNS) {
    const lineRe = new RegExp(re.source, "g");
    let pm;
    while ((pm = lineRe.exec(line)) !== null) {
      const phoneStart = pm.index;
      const phoneEnd   = pm.index + pm[0].length;
      if (phoneStart <= matchPosInLine + matchLen && phoneEnd >= matchPosInLine) return true;
    }
  }

  // 2. Direkt davor "Tel:" / "Phone:" / "Fax:" etc.?
  if (PHONE_KEYWORD_RE.test(before)) return true;

  // 3. Direkt davor "Rechnungsnr:" / "Bestellung:" / "Kunden-Nr:" etc.?
  if (INVOICE_KEYWORD_RE.test(before)) return true;

  return false;
}

// Returns the top-1 candidate or null. Backwards-compat wrapper.
function matchTrackingPattern(text) {
  const cs = matchTrackingPatternCandidates(text, 1);
  return cs[0]?.value || null;
}

// Returns up to maxCount distinct candidates sorted by score desc.
// Each candidate: { value, score, name, sourceText }
function matchTrackingPatternCandidates(text, maxCount = 5) {
  if (!text) return [];
  const upper = text.toUpperCase();
  const seen = new Set();
  const out = [];

  function push(value, score, name, sourceText) {
    if (!value || seen.has(value)) return;
    seen.add(value);
    out.push({ value, score, name, sourceText });
  }

  // 1. Höchste Priorität: spezifischer Tracking-Keyword + sanitized check
  const kw = text.match(TRACKING_KEYWORD_RE);
  if (kw && kw[1]) {
    const sanitized = sanitizeTrackingNumber(kw[1]);
    if (sanitized && sanitized.length >= 8 && /\d/.test(sanitized)) {
      push(sanitized, 200, "keyword", kw[0]);
    }
  }

  // 2. Pattern-Treffer sammeln und scoren
  for (const { re, weight, name } of TRACKING_PATTERNS) {
    const globalRe = new RegExp(re.source, "g");
    let m;
    while ((m = globalRe.exec(upper)) !== null) {
      const matched = m[0];
      const idx = m.index;
      if (looksLikeFalsePositiveInContext(text, idx, matched.length)) continue;
      const sanitized = sanitizeTrackingNumber(matched);
      if (!sanitized || !/\d/.test(sanitized)) continue;
      const score = weight + Math.min(20, sanitized.length);
      push(sanitized, score, name, matched);
    }
  }

  out.sort((a, b) => b.score - a.score);
  return out.slice(0, maxCount);
}

// Strenger Validator: prüft ob ein sanitized String EXAKT einem bekannten
// Carrier-Format entspricht. Nutzt nur die spezifischen Carrier-Patterns
// (NICHT das generische "alpha"-Catchall) — das schützt vor False-Positives
// bei Kunden-Referenzen, URLs, beliebigen IDs.
//
// Wird benutzt von:
//   - extractTrackingNumber Stage-1 (akzeptiert Barcode nur wenn Tracking-Format)
//   - handleScanResult (silent-skip bei Nicht-Tracking-Codes)
const STRICT_TRACKING_PATTERNS = TRACKING_PATTERNS.filter(p => p.name !== "alpha");

function looksLikeValidTrackingNumber(sanitized) {
  if (!sanitized || sanitized.length < 8) return false;
  // Tracking braucht IMMER mindestens eine Ziffer
  if (!/\d/.test(sanitized)) return false;
  for (const { re } of STRICT_TRACKING_PATTERNS) {
    // \b…\b in der Pattern-Source entfernen, dann ^…$ wrappen für vollständige Match-Prüfung
    const inner = re.source.replace(/^\\b/, "").replace(/\\b$/, "");
    const fullMatch = new RegExp("^(?:" + inner + ")$");
    if (fullMatch.test(sanitized)) return true;
  }
  return false;
}

// Render a File (PDF or image) to an offscreen canvas. Returns canvas + raster size.
async function fileToCanvas(file) {
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  const isPdf = type.includes("pdf") || name.endsWith(".pdf");

  if (isPdf) {
    // Lazy-load pdf.js ESM module
    const pdfjs = await import("https://cdn.jsdelivr.net/npm/pdfjs-dist@4.10.38/build/pdf.min.mjs");
    if (window.__PDFJS_WORKER_SRC__) {
      pdfjs.GlobalWorkerOptions.workerSrc = window.__PDFJS_WORKER_SRC__;
    }
    const buf = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: buf }).promise;
    const page = await pdf.getPage(1);
    // Render at 2x scale for better OCR/barcode legibility
    const viewport = page.getViewport({ scale: 2.0 });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    await page.render({ canvasContext: ctx, viewport }).promise;
    return canvas;
  }

  // Image path
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);
    return canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Try to read any barcode on the canvas using ZXing.
// Returns the decoded text, or null if nothing found.
async function scanBarcodeFromCanvas(canvas) {
  if (typeof ZXingBrowser === "undefined") return null;
  try {
    // Multi-format reader for shipping labels: Code128, QR, DataMatrix, EAN, ITF
    const hints = new Map();
    if (typeof ZXing !== "undefined" && ZXing.DecodeHintType && ZXing.BarcodeFormat) {
      hints.set(ZXing.DecodeHintType.POSSIBLE_FORMATS, [
        ZXing.BarcodeFormat.CODE_128,
        ZXing.BarcodeFormat.CODE_39,
        ZXing.BarcodeFormat.QR_CODE,
        ZXing.BarcodeFormat.DATA_MATRIX,
        ZXing.BarcodeFormat.EAN_13,
        ZXing.BarcodeFormat.ITF,
        ZXing.BarcodeFormat.PDF_417,
      ]);
      hints.set(ZXing.DecodeHintType.TRY_HARDER, true);
    }
    const reader = new ZXingBrowser.BrowserMultiFormatReader(hints);
    const result = await reader.decodeFromCanvas(canvas);
    return result?.getText?.() || null;
  } catch (e) {
    // ZXing throws NotFoundException when nothing recognized — that's fine
    return null;
  }
}

// Lazy-load Tesseract.js and run OCR. Returns extracted text or null.
let _tesseractPromise = null;
async function loadTesseract() {
  if (_tesseractPromise) return _tesseractPromise;
  _tesseractPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdn.jsdelivr.net/npm/tesseract.js@5.1.1/dist/tesseract.min.js";
    s.onload = () => resolve(window.Tesseract);
    s.onerror = () => { _tesseractPromise = null; reject(new Error("Tesseract load failed")); };
    document.head.appendChild(s);
  });
  return _tesseractPromise;
}

async function ocrCanvas(canvas) {
  try {
    const Tesseract = await loadTesseract();
    if (!Tesseract) return null;
    // Quick OCR (~3-6s for a label). German + English language.
    const { data } = await Tesseract.recognize(canvas, "eng+deu");
    return data?.text || null;
  } catch (e) {
    console.warn("OCR failed:", e?.message);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────
// Audio feedback (Web Audio API, no external assets)
// ─────────────────────────────────────────────────────────────
let _audioCtx = null;
function getAudioCtx() {
  if (_audioCtx) return _audioCtx;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    _audioCtx = new Ctor();
    return _audioCtx;
  } catch { return null; }
}
function playBeep(type = "success") {
  const ctx = getAudioCtx();
  if (!ctx) return;
  // Some browsers require resume() after a user gesture
  if (ctx.state === "suspended") ctx.resume();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain); gain.connect(ctx.destination);
  if (type === "success") {
    osc.frequency.value = 880;
    osc.type = "sine";
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.10);
    osc.start();
    osc.stop(ctx.currentTime + 0.12);
  } else {
    // error: lower buzzy tone
    osc.frequency.value = 220;
    osc.type = "square";
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.14, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.22);
    osc.start();
    osc.stop(ctx.currentTime + 0.24);
  }
}

// ─────────────────────────────────────────────────────────────
// Order matching for scanned barcodes
// Restricts to "Bereit" orders (workflow constraint).
// Match strategies (in order): exact → substring beidseitig.
// Returns array of matching orders (0, 1, or many).
// ─────────────────────────────────────────────────────────────
function findOrdersForCode(code, allOrders) {
  if (!code || !Array.isArray(allOrders)) return [];
  const sanitized = sanitizeTrackingNumber(code);
  if (!sanitized) return [];

  const candidates = allOrders.filter(o => o.status === "Bereit");

  // Stage 1: exact tracking number match
  const exact = candidates.filter(o => {
    const t = sanitizeTrackingNumber(o.trackingNumber || "");
    return t && t === sanitized;
  });
  if (exact.length > 0) return exact;

  // Stage 2: substring (label barcodes often embed extra data around the tracking)
  const substr = candidates.filter(o => {
    const t = sanitizeTrackingNumber(o.trackingNumber || "");
    if (!t || t.length < 8) return false;
    return sanitized.includes(t) || t.includes(sanitized);
  });
  return substr;
}

// Top-level: extract tracking number from a shipping label file.
// Returns { value, source } or null.
// Extrahiert eine Tracking-Nummer aus einem Versand-Label.
// Returns: { primary, candidates, ocrText, source } | null
//   - primary: best-guess sanitized value (string) or null
//   - candidates: Array von { value, score, name, sourceText } (max 5, sorted by score)
//   - ocrText: roher OCR-Text (für Debug + manuelle Korrektur durch User)
//   - source: "barcode" (auto-akzeptabel) | "ocr" (User-Bestätigung empfohlen) | null
async function extractTrackingNumber(file) {
  let canvas;
  try {
    canvas = await fileToCanvas(file);
  } catch (e) {
    console.warn("Could not render file:", e?.message);
    return null;
  }

  // Stage 1 — barcode (fast, ~99% of modern labels)
  // Wir akzeptieren den Barcode-Wert nur wenn er einem bekannten
  // Carrier-Format entspricht. Sonst (z.B. Kunden-Referenz im Barcode) →
  // fall through zur OCR-Stage, die ggf. die echte Tracking-Nr in der
  // Klartext-Schicht des Labels findet.
  const barcode = await scanBarcodeFromCanvas(canvas);
  if (barcode) {
    const sanitized = sanitizeTrackingNumber(barcode);
    if (sanitized && looksLikeValidTrackingNumber(sanitized)) {
      return {
        primary: sanitized,
        candidates: [{ value: sanitized, score: 999, name: "barcode", sourceText: barcode }],
        ocrText: "",
        source: "barcode",
      };
    }
    // Barcode dekodiert, aber nicht Tracking-Format — weiter zu OCR-Stage
  }

  // Stage 2 — OCR fallback (mit Phone- + Invoice-Filter)
  const text = await ocrCanvas(canvas);

  const candidates = matchTrackingPatternCandidates(text || "", 5);

  if (candidates.length === 0) {
    return { primary: null, candidates: [], ocrText: text || "", source: "ocr" };
  }
  return {
    primary: candidates[0].value,
    candidates,
    ocrText: text || "",
    source: "ocr",
  };
}


// Responsive layout breakpoints
function useViewport() {
  const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 430);
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  const isMobile  = vw < 768;
  const isTablet  = vw >= 768 && vw < 1024;
  const isDesktop = vw >= 1024;
  // Width of the list column (left side on desktop, full width on mobile/tablet)
  const listW = isMobile ? 430 : isTablet ? 560 : 440;
  // Total container max width
  const containerMaxW = isDesktop ? 1280 : listW;
  return { vw, isMobile, isTablet, isDesktop, listW, containerMaxW };
}

// ─────────────────────────────────────────────────────────────
// Onboarding screens (shown before the main app)
// ─────────────────────────────────────────────────────────────

function AuthScreen({ onAuth }) {
  const [mode, setMode] = useState("login"); // "login" | "register" | "magic"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [magicSent, setMagicSent] = useState(false);

  const isDark = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
  const bg   = isDark ? "#0d1117" : "#dde8f8";
  // iOS-26 Liquid-Glass Card — gleicher Look wie modalShellStyle.
  const card = isDark ? "rgba(28,28,30,0.78)" : "rgba(250,250,252,0.82)";
  const text = isDark ? "#fff" : "#1a1a1a";
  const sub  = isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)";
  const inp  = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
  const border = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)";
  const F = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', sans-serif";

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true); setError("");
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (err) { setError(err.message); return; }
    onAuth(data.session);
  }

  async function handleRegister(e) {
    e.preventDefault();
    setLoading(true); setError("");
    const { data, error: err } = await supabase.auth.signUp({ email, password });
    setLoading(false);
    if (err) { setError(err.message); return; }
    if (data.session) { onAuth(data.session); return; }
    setError("Konto erstellt! Bitte E-Mail bestätigen, dann einloggen.");
    setMode("login");
  }

  async function handleMagicLink(e) {
    e.preventDefault();
    setLoading(true); setError("");
    const { error: err } = await supabase.auth.signInWithOtp({ email });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setMagicSent(true);
  }

  const inputStyle = {
    width: "100%", boxSizing: "border-box", padding: "13px 14px",
    background: inp, border: `1px solid ${border}`, borderRadius: 12,
    color: text, fontSize: 16, fontFamily: F, outline: "none",
  };
  const btnPrimary = {
    width: "100%", padding: "14px", background: "#0A84FF",
    border: "none", borderRadius: 14, color: "#fff", fontSize: 16,
    fontWeight: 600, fontFamily: F, cursor: "pointer",
    boxShadow: "0 4px 20px rgba(10,132,255,0.35)",
    opacity: loading ? 0.7 : 1,
  };
  const btnGhost = {
    background: "none", border: "none", color: "#0A84FF",
    fontFamily: F, fontSize: 14, fontWeight: 500, cursor: "pointer", padding: "6px 0",
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: F, overflowY: "auto" }}>
      <div style={{
        width: "100%", maxWidth: 380, background: card, borderRadius: 24, padding: "36px 28px",
        backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
        border: `1px solid ${border}`,
        boxShadow: isDark
          ? "0 24px 60px rgba(0,0,0,0.55), 0 0 0 0.5px rgba(255,255,255,0.05) inset"
          : "0 24px 60px rgba(0,0,0,0.18), 0 0 0 0.5px rgba(255,255,255,0.6) inset",
      }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          {/* SVG-Logo statt Emoji — konsistent mit App-weiten Icons */}
          <div style={{
            width: 64, height: 64, borderRadius: 18, margin: "0 auto 12px",
            background: "linear-gradient(180deg, #0A84FF 0%, #0066D6 100%)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 6px 24px rgba(10,132,255,0.4)",
          }}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M5 11L16 5L27 11V21L16 27L5 21V11Z" stroke="#fff" strokeWidth="2" strokeLinejoin="round"/>
              <path d="M5 11L16 17M16 17L27 11M16 17V27" stroke="#fff" strokeWidth="2" strokeLinejoin="round"/>
            </svg>
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: text, letterSpacing: "-0.5px" }}>Memorica</div>
          <div style={{ fontSize: 14, color: sub, marginTop: 4 }}>
            {mode === "login" ? "Anmelden" : mode === "register" ? "Konto erstellen" : "Magic Link"}
          </div>
        </div>

        {magicSent ? (
          <div style={{ textAlign: "center", color: text, lineHeight: 1.5 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📬</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Link gesendet</div>
            <div style={{ fontSize: 14, color: sub }}>Klicke auf den Link in deiner E-Mail, um dich anzumelden.</div>
            <button style={{ ...btnGhost, marginTop: 20 }} onClick={() => setMagicSent(false)}>Zurück</button>
          </div>
        ) : (
          <form onSubmit={mode === "login" ? handleLogin : mode === "register" ? handleRegister : handleMagicLink}>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <input type="email" placeholder="E-Mail" value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle} />
              {mode !== "magic" && (
                <input type="password" placeholder="Passwort" value={password} onChange={e => setPassword(e.target.value)} required style={inputStyle} />
              )}
              {error && <div style={{ fontSize: 13, color: "#FF453A", padding: "8px 12px", background: "rgba(255,69,58,0.1)", borderRadius: 10 }}>{error}</div>}
              <button type="submit" style={btnPrimary} disabled={loading}>
                {loading ? "..." : mode === "login" ? "Anmelden" : mode === "register" ? "Konto erstellen" : "Magic Link senden"}
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, marginTop: 16 }}>
              {mode === "login" && (<>
                <button type="button" style={btnGhost} onClick={() => { setMode("register"); setError(""); }}>Noch kein Konto? Registrieren</button>
                <button type="button" style={btnGhost} onClick={() => { setMode("magic"); setError(""); }}>Mit Magic Link anmelden</button>
              </>)}
              {mode !== "login" && (
                <button type="button" style={btnGhost} onClick={() => { setMode("login"); setError(""); }}>Zurück zum Login</button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaHubStub / MarketHubStub — Platzhalter-Hubs für die zukünftigen
// Trödel/Standverkauf-Modi (Migration 008 — Funktionalität kommt später).
// Beide nutzen denselben Liquid-Glass-Card-Stil wie Auth-Pages.
// ─────────────────────────────────────────────────────────────
function ModeHubStub({ title, subtitle, accentColor, onBack }) {
  const isDark = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
  const bg     = isDark ? "#0d1117" : "#dde8f8";
  const card   = isDark ? "rgba(28,28,30,0.78)" : "rgba(250,250,252,0.82)";
  const text   = isDark ? "#fff" : "#1a1a1a";
  const sub    = isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)";
  const border = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)";
  const F      = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', sans-serif";

  return (
    <div style={{ minHeight: "100vh", background: bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: F }}>
      <div style={{
        width: "100%", maxWidth: 380, background: card, borderRadius: 24, padding: "28px 26px",
        backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
        border: `1px solid ${border}`,
        boxShadow: isDark
          ? "0 24px 60px rgba(0,0,0,0.55), 0 0 0 0.5px rgba(255,255,255,0.05) inset"
          : "0 24px 60px rgba(0,0,0,0.18), 0 0 0 0.5px rgba(255,255,255,0.6) inset",
      }}>
        <button onClick={onBack} aria-label="Zurück" style={{
          background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
          border: "none", borderRadius: 14, width: 30, height: 30,
          cursor: "pointer", padding: 0, marginBottom: 18,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: isDark ? "rgba(235,235,245,0.65)" : "rgba(60,60,67,0.6)",
        }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div style={{
          width: 56, height: 56, borderRadius: 16, marginBottom: 16,
          background: accentColor.weak, color: accentColor.solid,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L2 7v10l10 5l10-5V7L12 2z"/><path d="M2 7l10 5"/><path d="M22 7l-10 5"/><path d="M12 12V22"/>
          </svg>
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: text, letterSpacing: "-0.4px", marginBottom: 6 }}>{title}</div>
        <div style={{ fontSize: 14, color: sub, lineHeight: 1.5, marginBottom: 22 }}>{subtitle}</div>
        <div style={{
          padding: "12px 14px",
          background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)",
          border: `1px solid ${border}`, borderRadius: 12,
          fontSize: 13, color: sub, lineHeight: 1.45,
        }}>
          Dieser Bereich wird gerade gebaut. Hier kannst du bald deine Markt-Tage tracken.
        </div>
      </div>
    </div>
  );
}

function CreateStoreScreen({ token, onCreated, onBack }) {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isDark = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
  const bg     = isDark ? "#0d1117" : "#dde8f8";
  const card   = isDark ? "rgba(28,28,30,0.78)" : "rgba(250,250,252,0.82)";
  const text   = isDark ? "#fff" : "#1a1a1a";
  const sub    = isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)";
  const inp    = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
  const border = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)";
  const F = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', sans-serif";

  async function handleCreate(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true); setError("");
    try {
      const res = await fetch("/api/stores", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || `HTTP ${res.status}`); }
      const store = await res.json();
      onCreated(store);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  return (
    <div style={{ minHeight: "100vh", background: bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: F }}>
      <div style={{
        width: "100%", maxWidth: 380, background: card, borderRadius: 24, padding: "32px 28px",
        backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
        border: `1px solid ${border}`,
        boxShadow: isDark
          ? "0 24px 60px rgba(0,0,0,0.55), 0 0 0 0.5px rgba(255,255,255,0.05) inset"
          : "0 24px 60px rgba(0,0,0,0.18), 0 0 0 0.5px rgba(255,255,255,0.6) inset",
      }}>
        <button onClick={onBack} aria-label="Zurück" style={{
          background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
          border: "none", borderRadius: 14, width: 30, height: 30,
          cursor: "pointer", padding: 0, marginBottom: 16,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: isDark ? "rgba(235,235,245,0.65)" : "rgba(60,60,67,0.6)",
        }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div style={{ fontSize: 22, fontWeight: 700, color: text, marginBottom: 6, letterSpacing: "-0.4px" }}>Neuen Store erstellen</div>
        <div style={{ fontSize: 14, color: sub, marginBottom: 24 }}>Du wirst automatisch Inhaber dieses Stores.</div>
        <form onSubmit={handleCreate}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input
              type="text" placeholder="Store-Name" value={name}
              onChange={e => setName(e.target.value)} required autoFocus
              style={{ width: "100%", boxSizing: "border-box", padding: "13px 14px", background: inp, border: `1px solid ${border}`, borderRadius: 12, color: text, fontSize: 16, fontFamily: F, outline: "none" }}
            />
            {error && <div style={{ fontSize: 13, color: "#FF453A", padding: "8px 12px", background: "rgba(255,69,58,0.1)", borderRadius: 10 }}>{error}</div>}
            <button type="submit" disabled={loading || !name.trim()} style={{
              width: "100%", padding: "14px", background: "#0A84FF", border: "none",
              borderRadius: 14, color: "#fff", fontSize: 16, fontWeight: 600, fontFamily: F,
              cursor: "pointer", opacity: (loading || !name.trim()) ? 0.6 : 1,
              boxShadow: "0 4px 20px rgba(10,132,255,0.35)",
            }}>{loading ? "..." : "Store erstellen"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function JoinStoreScreen({ token, onJoined, onBack }) {
  const [code, setCode] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isDark = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
  const bg     = isDark ? "#0d1117" : "#dde8f8";
  const card   = isDark ? "rgba(28,28,30,0.78)" : "rgba(250,250,252,0.82)";
  const text   = isDark ? "#fff" : "#1a1a1a";
  const sub    = isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)";
  const inp    = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
  const border = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";
  const F = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', sans-serif";

  async function lookupCode(c) {
    const clean = c.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setCode(clean);
    setPreview(null); setError("");
    if (clean.length < 6) return;
    try {
      const res = await fetch(`/api/invitations/${clean}`);
      if (res.status === 404) { setError("Ungültiger Code"); return; }
      if (res.status === 410) { setError("Dieser Code ist abgelaufen"); return; }
      if (!res.ok) return;
      setPreview(await res.json());
    } catch {}
  }

  async function handleJoin(e) {
    e.preventDefault();
    if (!code) return;
    setLoading(true); setError("");
    try {
      const res = await fetch(`/api/invitations/${code}/redeem`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || `HTTP ${res.status}`); }
      const data = await res.json();
      onJoined({ ...data.store, role: data.role });
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  const ROLE_LABELS = { admin: "Admin", fulfillment: "Fulfillment", finance: "Finanzen", viewer: "Betrachter" };

  return (
    <div style={{ minHeight: "100vh", background: bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: F }}>
      <div style={{
        width: "100%", maxWidth: 380, background: card, borderRadius: 24, padding: "32px 28px",
        backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"}`,
        boxShadow: isDark
          ? "0 24px 60px rgba(0,0,0,0.55), 0 0 0 0.5px rgba(255,255,255,0.05) inset"
          : "0 24px 60px rgba(0,0,0,0.18), 0 0 0 0.5px rgba(255,255,255,0.6) inset",
      }}>
        <button onClick={onBack} aria-label="Zurück" style={{
          background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
          border: "none", borderRadius: 14, width: 30, height: 30,
          cursor: "pointer", padding: 0, marginBottom: 16,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: isDark ? "rgba(235,235,245,0.65)" : "rgba(60,60,67,0.6)",
        }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div style={{ fontSize: 22, fontWeight: 700, color: text, marginBottom: 6, letterSpacing: "-0.4px" }}>Store beitreten</div>
        <div style={{ fontSize: 14, color: sub, marginBottom: 24 }}>Gib den Einladungscode ein, den du erhalten hast.</div>
        <form onSubmit={handleJoin}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input
              type="text" placeholder="Einladungscode (z.B. A1B2C3D4)"
              value={code} onChange={e => lookupCode(e.target.value)}
              maxLength={10} autoFocus
              style={{ width: "100%", boxSizing: "border-box", padding: "13px 14px", background: inp, border: `1px solid ${border}`, borderRadius: 12, color: text, fontSize: 17, fontFamily: "monospace, monospace", textTransform: "uppercase", letterSpacing: "0.12em", outline: "none" }}
            />
            {preview && (
              <div style={{ padding: "12px 14px", background: "rgba(48,209,88,0.1)", borderRadius: 12, border: "1px solid rgba(48,209,88,0.25)" }}>
                <div style={{ fontSize: 13, color: "#30D158", fontWeight: 600 }}>Einladung gefunden</div>
                <div style={{ fontSize: 14, color: text, marginTop: 2 }}>{preview.store_name}</div>
                <div style={{ fontSize: 12, color: sub, marginTop: 1 }}>Rolle: {ROLE_LABELS[preview.role] || preview.role}</div>
              </div>
            )}
            {error && <div style={{ fontSize: 13, color: "#FF453A", padding: "8px 12px", background: "rgba(255,69,58,0.1)", borderRadius: 10 }}>{error}</div>}
            <button type="submit" disabled={loading || !code} style={{
              width: "100%", padding: "14px", background: "#0A84FF", border: "none",
              borderRadius: 14, color: "#fff", fontSize: 16, fontWeight: 600, fontFamily: F,
              cursor: "pointer", opacity: (loading || !code) ? 0.6 : 1,
              boxShadow: "0 4px 20px rgba(10,132,255,0.35)",
            }}>{loading ? "..." : "Beitreten"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ScannerModal — full-screen mobile-first barcode scanner
// Uses ZXing-browser to continuously decode from the rear camera.
// On successful decode, calls onScanResult(text); the parent decides
// whether it matches an order and what to do (match overlay, error, etc.)
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// Dashboard Widget Architecture (MVP)
//
// • Registry: WIDGETS module-level object, each widget has metadata + component
// • Layout: per (user, store) JSONB on store_memberships.dashboard_layout
// • Default: role-based fallback if layout is null
// • Sizes: small (1 col) / medium (2 cols) / large (4 cols)
// • Grid: 1 / 2 / 4 columns at mobile / tablet / desktop
// • Drag-and-drop: NOT in MVP — only visibility toggles in Customize-Modal
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// BatchSlotEditModal — fokussiert nur auf per-Batch-Werte (Pool, Threshold).
// Globale Channel-Settings (Name, Finanzen, Löschen) sind nicht hier — dafür
// gibt es einen Link zum Channels-Manager. Klare Trennung der Scopes:
//   • Hier: "wie viel eBay-Pool im Batch 1?"
//   • Channels-Manager: "wie heisst eBay, was kostet es, was sind die Gebühren?"
// ─────────────────────────────────────────────────────────────
function BatchSlotEditModal({
  t, dark, F, glass,
  batch, channel, isSalesChannel,
  currentSales,
  onClose,
  onSavePool, onSaveThreshold,
  onOpenChannelsManager,
  haptic,
}) {
  const initPool      = batch.reservierungen?.[channel.id] ?? 0;
  const initThreshold = parseInt(batch.minThresholds?.[channel.id], 10) || 0;
  const initVisiblePool = isSalesChannel ? Math.max(0, initPool - currentSales) : initPool;
  const [poolStr,      setPoolStr]      = useState(String(initVisiblePool));
  const [thresholdStr, setThresholdStr] = useState(String(initThreshold));
  const [submitting,   setSubmitting]   = useState(false);
  const { height: vh, kbOpen, offsetTop } = useModalViewport();

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, []);

  const parseInt0 = (s) => {
    const n = parseInt(s, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  const save = async () => {
    if (submitting) return;
    setSubmitting(true);
    const promises = [];
    const newRest = parseInt0(poolStr);
    const newPool = isSalesChannel ? newRest + currentSales : newRest;
    if (newPool !== initPool) promises.push(onSavePool(batch.id, channel.id, newPool));
    if (isSalesChannel) {
      const newThreshold = parseInt0(thresholdStr);
      if (newThreshold !== initThreshold) promises.push(onSaveThreshold(batch.id, channel.id, newThreshold));
    }
    await Promise.all(promises);
    haptic(15);
    onClose();
  };

  // Row WURDE entfernt — war als inline-Component innerhalb des Functions-Bodys
  // definiert. Jedes Keystroke triggerte Re-Render → React sah Row als neue
  // Komponente → Input wurde unmounted+remounted → iOS blurte → Tastatur ging zu.
  // Stattdessen JSX direkt inlinen unten (siehe Verteilung-Section).

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
        touchAction: "none",
        willChange: "opacity",
      }}
        onTouchMove={e => e.preventDefault()}
      />
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, glass, F, dark, width: 380, radius: 22, padding: "20px 0 16px", zIndex: 93 })}>
        {/* Header */}
        <div style={{ padding: "0 20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                padding: "2px 8px", borderRadius: 6,
                background: isSalesChannel ? t.accent.green.weak : t.accent.purple.weak,
                color:      isSalesChannel ? t.accent.green.solid : t.accent.purple.solid,
              }}>{isSalesChannel ? "Verkauf" : "Reservierung"}</span>
            </div>
            <div style={{
              fontSize: 22, fontWeight: 700, color: t.text,
              letterSpacing: "-0.5px", lineHeight: 1.15,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>{channel.name}</div>
            <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>
              {batch.name}
              {isSalesChannel && currentSales > 0 && ` · ${currentSales} verkauft`}
            </div>
          </div>
          <div style={{ flexShrink: 0, marginTop: -4 }}>
            <ModalCloseButton onClose={onClose} dark={dark} />
          </div>
        </div>

        {/* Section: per-batch values */}
        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", padding: "0 20px", marginBottom: 8 }}>
          In diesem Batch
        </div>
        <div style={{ margin: "0 16px", ...glass({ borderRadius: 14 }), overflow: "hidden" }}>
          {/* Row 1: Pool/Reserviert — direkt inline, KEIN inneres Row-Component (sonst keyboard-blur). */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 16px" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, color: t.text, fontWeight: 500, letterSpacing: "-0.1px" }}>
                {isSalesChannel ? "Verfügbar" : "Reserviert"}
              </div>
            </div>
            <input
              type="number" step="1" min="0"
              inputMode="numeric"
              value={poolStr}
              onChange={e => setPoolStr(e.target.value)}
              style={{
                width: 70, border: "none", background: "transparent",
                fontFamily: F, fontSize: 16, fontWeight: 600, color: t.text,
                outline: "none", padding: 0, textAlign: "right",
                fontVariantNumeric: "tabular-nums", flexShrink: 0,
              }}
            />
          </div>
          {isSalesChannel && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 16px", borderTop: `0.5px solid ${t.divider}` }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, color: t.text, fontWeight: 500, letterSpacing: "-0.1px" }}>Mindestbestand-Alarm</div>
                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>0 = kein Alarm</div>
              </div>
              <input
                type="number" step="1" min="0"
                inputMode="numeric"
                value={thresholdStr}
                onChange={e => setThresholdStr(e.target.value)}
                style={{
                  width: 70, border: "none", background: "transparent",
                  fontFamily: F, fontSize: 16, fontWeight: 600, color: t.text,
                  outline: "none", padding: 0, textAlign: "right",
                  fontVariantNumeric: "tabular-nums", flexShrink: 0,
                }}
              />
            </div>
          )}
        </div>

        {/* Primary CTA */}
        <div style={{ padding: "20px 16px 0" }}>
          <button onClick={save} disabled={submitting} style={{
            width: "100%",
            background: submitting ? t.btnGhost : t.accent.blue.solid,
            color: submitting ? t.textMuted : "#fff",
            border: "none", borderRadius: 14, padding: "14px",
            fontFamily: F, fontWeight: 700, fontSize: 15, letterSpacing: "-0.1px",
            cursor: submitting ? "wait" : "pointer",
            boxShadow: submitting ? "none" : `0 4px 18px ${t.accent.blue.weak}`,
            transition: "transform 0.12s ease",
          }}
          onMouseDown={e => { if (!submitting) e.currentTarget.style.transform = "scale(0.98)"; }}
          onMouseUp={e => e.currentTarget.style.transform = ""}
          onMouseLeave={e => e.currentTarget.style.transform = ""}
          onPointerDown={e => { if (!submitting) e.currentTarget.style.transform = "scale(0.98)"; }}
          onPointerUp={e => e.currentTarget.style.transform = ""}
          onPointerLeave={e => e.currentTarget.style.transform = ""}
          >Speichern</button>
        </div>

        {/* Link to Channels-Manager — alle globalen Settings dort */}
        <div style={{ padding: "12px 16px 0", textAlign: "center" }}>
          <button onClick={() => onOpenChannelsManager(channel.id)} style={{
            background: "none", border: "none",
            color: t.accent.blue.solid,
            fontFamily: F, fontWeight: 500, fontSize: 13,
            cursor: "pointer", padding: "6px 12px",
            display: "inline-flex", alignItems: "center", gap: 4,
          }}>
            <span>Kanal-Einstellungen</span>
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
              <path d="M3 2L7 5.5L3 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// BatchEditPopup — Batch umbenennen, Stückzahl & Investment editieren, löschen.
// Lokaler String-State pro Input damit User leeren / mehrstellig tippen können
// (vorher war's an batch.* gebunden mit Math.max(1,...) was leeres Feld blockierte).
// ─────────────────────────────────────────────────────────────
function BatchEditPopup({ t, dark, F, glass, inp, batch, canDelete, fmt2, onClose, onSaveName, onSaveNumbers, onDelete }) {
  const [nameStr,        setNameStr]        = useState(batch.name || "");
  const [stueckStr,      setStueckStr]      = useState(String(batch.stueckzahl || ""));
  const [investStr,      setInvestStr]      = useState(String(batch.investment || ""));
  const { height: vh, kbOpen, offsetTop } = useModalViewport();

  // Live-Preview von COGS (rechnen direkt aus den String-States — bei leerem Wert: 0)
  const stueckPreview = Math.max(1, parseInt(stueckStr, 10) || 1);
  const investPreview = Math.max(0, parseFloat(investStr.replace(",", ".")) || 0);
  const cogsPreview   = investPreview / stueckPreview;

  const commitName = () => {
    const v = (nameStr || "").trim();
    if (v && v !== batch.name) onSaveName(v);
  };
  const commitNumbers = () => {
    const s = parseInt(stueckStr, 10);
    const i = parseFloat(investStr.replace(",", "."));
    const safeS = Number.isFinite(s) && s >= 1 ? s : 1;
    const safeI = Number.isFinite(i) && i >= 0 ? i : 0;
    if (safeS !== batch.stueckzahl || safeI !== batch.investment) {
      onSaveNumbers(safeS, safeI);
    }
    // Eingabe-Felder auf normalisierten Wert resetten falls User Müll getippt hat
    setStueckStr(String(safeS));
    setInvestStr(String(safeI));
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 80,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
        willChange: "opacity",
      }} />
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, glass, F, dark, width: 360, radius: 24, zIndex: 81 })}>
        <ModalHeader t={t} dark={dark} F={F} title="Batch bearbeiten" onClose={onClose} />

        <div style={{ ...glass({ borderRadius: 14 }), overflow: "hidden", marginBottom: 10 }}>
          <input value={nameStr}
            onChange={e => setNameStr(e.target.value)}
            onBlur={commitName}
            placeholder="Name" style={inp({ fontSize: 15, padding: "14px 16px" })} />
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
          <div style={{ flex: 1, ...glass({ borderRadius: 14 }), overflow: "hidden", padding: "10px 14px" }}>
            <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Stück</div>
            <input type="number" inputMode="numeric"
              value={stueckStr}
              onChange={e => setStueckStr(e.target.value)}
              onBlur={commitNumbers}
              style={{ width: "100%", border: "none", background: "transparent", fontFamily: F, fontSize: 17, fontWeight: 600, color: t.text, outline: "none", padding: 0 }} />
          </div>
          <div style={{ flex: 1.2, ...glass({ borderRadius: 14 }), overflow: "hidden", padding: "10px 14px" }}>
            <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Investment €</div>
            <input type="number" step="0.01" inputMode="decimal"
              value={investStr}
              onChange={e => setInvestStr(e.target.value)}
              onBlur={commitNumbers}
              style={{ width: "100%", border: "none", background: "transparent", fontFamily: F, fontSize: 17, fontWeight: 600, color: t.text, outline: "none", padding: 0 }} />
          </div>
        </div>

        <div style={{ ...glass({ borderRadius: 14 }), padding: "10px 14px", marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>COGS pro Stück</div>
          <div style={{ fontSize: 17, fontWeight: 600, color: t.text, letterSpacing: "-0.3px" }}>{fmt2(cogsPreview)} €</div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          {canDelete && (
            <button onClick={onDelete} style={{ flex: 1, background: "rgba(255,69,58,0.15)", border: "none", borderRadius: 12, padding: "13px", fontFamily: F, fontWeight: 600, fontSize: 14, cursor: "pointer", color: "#FF453A" }}>Löschen</button>
          )}
          <button onClick={() => { commitName(); commitNumbers(); onClose(); }} style={{ flex: 1, background: t.accent.blue.solid, border: "none", borderRadius: 12, padding: "13px", fontFamily: F, fontWeight: 700, fontSize: 14, cursor: "pointer", color: "#fff", boxShadow: `0 4px 20px ${t.accent.blue.weak}` }}>Fertig</button>
        </div>
      </div>
    </>
  );
}


// ─────────────────────────────────────────────────────────────
// NewChannelModal — Anlegen eines neuen Kanals (Verkauf oder Reservierung).
// Ersetzt das alte prompt() + confirm()-Inferno mit einem cleanen iOS-Modal.
// Nimmt Name + Typ entgegen, default-Finanzwerte werden gesetzt — User kann
// danach im Channels-Manager über das €-Panel feintunen.
// ─────────────────────────────────────────────────────────────
function NewChannelModal({ t, dark, F, glass, onClose, onCreate }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("sales"); // "sales" | "reserved"
  const [submitting, setSubmitting] = useState(false);
  const trimmed = name.trim();
  const { height: vh, kbOpen, offsetTop } = useModalViewport();

  // Body-Scroll-Lock während Modal offen
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, []);

  const submit = async () => {
    if (!trimmed || submitting) return;
    setSubmitting(true);
    await onCreate(trimmed, type);
    // Modal schliesst sich nach erfolgreichem Anlegen via onCreate-Caller
  };

  const isSales = type === "sales";

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
        touchAction: "none",
        willChange: "opacity",
      }}
        onTouchMove={e => e.preventDefault()}
      />
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, glass, F, dark, width: 400, radius: 22, zIndex: 93 })}>
        <ModalHeader t={t} dark={dark} F={F} title="Neuer Kanal" onClose={onClose} />

        {/* Section: Name */}
        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
          Name
        </div>
        <div style={{ ...glass({ borderRadius: 12 }), padding: "0 14px", display: "flex", alignItems: "center" }}>
          <input
            autoFocus
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && trimmed) submit(); }}
            placeholder="z.B. eBay, Familie, Demo …"
            style={{
              flex: 1, background: "transparent", border: "none",
              fontFamily: F, fontSize: 15, color: t.text, outline: "none",
              padding: "12px 0", letterSpacing: "-0.2px",
            }}
          />
        </div>

        {/* Section: Type */}
        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 18, marginBottom: 6, paddingLeft: 4 }}>
          Typ
        </div>
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8,
        }}>
          {/* Verkaufskanal-Option */}
          <button
            onClick={() => setType("sales")}
            style={{
              padding: "12px 10px",
              background: isSales ? t.accent.green.weak : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"),
              border: `1.5px solid ${isSales ? t.accent.green.solid : t.glassBorder}`,
              borderRadius: 12, cursor: "pointer", fontFamily: F,
              color: isSales ? t.accent.green.solid : t.text,
              transition: "all 0.18s ease",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "-0.1px" }}>Verkaufskanal</span>
          </button>
          {/* Reservierung-Option */}
          <button
            onClick={() => setType("reserved")}
            style={{
              padding: "12px 10px",
              background: !isSales ? t.accent.purple.weak : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"),
              border: `1.5px solid ${!isSales ? t.accent.purple.solid : t.glassBorder}`,
              borderRadius: 12, cursor: "pointer", fontFamily: F,
              color: !isSales ? t.accent.purple.solid : t.text,
              transition: "all 0.18s ease",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "-0.1px" }}>Reservierung</span>
          </button>
        </div>

        {/* Description je nach Typ */}
        <div style={{
          fontSize: 11, color: t.textSub, marginTop: 8, padding: "0 4px",
          lineHeight: 1.4, fontFamily: F,
        }}>
          {isSales
            ? "Bestellungen werden über diesen Kanal erfasst. Preise und Gebühren stellst du danach im €-Bereich ein."
            : "Interner Reservierungs-Pool — z.B. Familie, Demo, Geschenk. Keine Bestellungen."}
        </div>

        {/* Submit */}
        <button
          onClick={submit}
          disabled={!trimmed || submitting}
          // preventDefault auf MouseDown → Klick blurt Input nicht (für iOS-Tastatur-Persistenz)
          onMouseDown={e => e.preventDefault()}
          onTouchStart={e => e.preventDefault()}
          style={{
            width: "100%", marginTop: 22,
            background: trimmed ? t.accent.blue.solid : t.btnGhost,
            color: trimmed ? "#fff" : t.textMuted,
            border: "none", borderRadius: 14, padding: "14px",
            fontFamily: F, fontWeight: 700, fontSize: 15, letterSpacing: "-0.1px",
            cursor: trimmed && !submitting ? "pointer" : "not-allowed",
            boxShadow: trimmed && !submitting ? `0 4px 18px ${t.accent.blue.weak}` : "none",
            transition: "all 0.18s ease",
          }}
        >Anlegen</button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// ManualShipConfirmModal — Doppelte Bestätigung bei manuellem
// Bereit→Versendet (Bypass des Scanner-Workflows).
//
// Drei Hürden, die ALLE überwunden werden müssen, damit der
// Confirm-Button überhaupt klickbar wird:
//   1. Grund auswählen (Pflicht)
//   2. Bei "Anderer Grund": Freitext-Notiz (Pflicht)
//   3. Checkbox "Ich bestätige …" abhaken (Pflicht)
// Das ist die "doppelte Bestätigung" — kein versehentliches Bypassen
// des Scanners möglich. Grund + Datum landen in der Order-Notiz für
// einen sauberen Audit-Trail.
// ─────────────────────────────────────────────────────────────
function ManualShipConfirmModal({ order, reasons, t, dark, F, glass, vh, kbOpen, offsetTop, onClose, onConfirm }) {
  const [reasonId, setReasonId] = useState(null);
  const [otherText, setOtherText] = useState("");
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  const reason = reasons.find(r => r.id === reasonId);
  const needsText = reasonId === "other";
  const ready = !!reason && checked && (!needsText || otherText.trim().length >= 3);

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.4)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
        willChange: "opacity",
      }} />
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, glass, F, dark, width: 400, radius: 22, zIndex: 93 })}>
        <ModalHeader
          t={t} dark={dark} F={F}
          title="Manuell bestätigen"
          subtitle={<>#{order.bestellnummer}</>}
          accent={t.accent.orange}
          accentIcon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4"/><path d="M12 17h.01"/>
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            </svg>
          }
          onClose={onClose}
        />

        {/* Warning copy */}
        <div style={{ fontSize: 13, color: t.textSub, lineHeight: 1.5, marginBottom: 16, padding: "10px 12px", background: t.accent.orange.weak, borderRadius: 10, border: `1px solid ${t.accent.orange.solid}33` }}>
          Standardmässig sollte das <strong style={{ color: t.text }}>Versandlabel gescannt</strong> werden — das verifiziert die Trackingnummer automatisch. Manuell bestätigen nur in Ausnahmen.
        </div>

        {/* Reason picker */}
        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
          Grund <span style={{ color: t.accent.red.solid }}>*</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
          {reasons.map(r => {
            const sel = r.id === reasonId;
            return (
              <button key={r.id} onClick={() => setReasonId(r.id)} style={{
                width: "100%", padding: "12px 14px",
                background: sel ? t.accent.blue.weak : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"),
                border: `1.5px solid ${sel ? t.accent.blue.solid : t.glassBorder}`,
                borderRadius: 11, cursor: "pointer", fontFamily: F,
                color: sel ? t.accent.blue.solid : t.text,
                fontSize: 14, fontWeight: sel ? 600 : 500, textAlign: "left",
                display: "flex", alignItems: "center", gap: 10,
                transition: "all 0.16s ease",
              }}>
                <div style={{
                  width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                  border: `1.5px solid ${sel ? t.accent.blue.solid : t.glassBorder}`,
                  background: sel ? t.accent.blue.solid : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {sel && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </div>
                {r.label}
              </button>
            );
          })}
        </div>

        {/* Optional Freitext nur bei "Anderer Grund" */}
        {needsText && (
          <>
            <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
              Begründung <span style={{ color: t.accent.red.solid }}>*</span>
            </div>
            <div style={{ ...glass({ borderRadius: 11 }), padding: "0 14px", display: "flex", alignItems: "center", marginBottom: 14 }}>
              <input
                autoFocus type="text" value={otherText}
                onChange={e => setOtherText(e.target.value)}
                placeholder="Was ist passiert?"
                style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "11px 0", fontFamily: F, fontSize: 14, color: t.text, minWidth: 0 }}
              />
            </div>
          </>
        )}

        {/* Checkbox confirm */}
        <button onClick={() => setChecked(c => !c)} style={{
          width: "100%", padding: "12px 14px", marginBottom: 14,
          background: checked ? t.accent.green.weak : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"),
          border: `1.5px solid ${checked ? t.accent.green.solid : t.glassBorder}`,
          borderRadius: 11, cursor: "pointer", fontFamily: F,
          display: "flex", alignItems: "flex-start", gap: 10,
          textAlign: "left",
          transition: "all 0.16s ease",
        }}>
          <div style={{
            width: 20, height: 20, borderRadius: 5, flexShrink: 0, marginTop: 1,
            border: `1.5px solid ${checked ? t.accent.green.solid : t.glassBorder}`,
            background: checked ? t.accent.green.solid : "transparent",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {checked && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            )}
          </div>
          <div style={{ flex: 1, fontSize: 13, color: checked ? t.accent.green.solid : t.text, lineHeight: 1.4, fontWeight: checked ? 600 : 500 }}>
            Ich bestätige, dass diese Bestellung tatsächlich versendet bzw. übergeben wurde.
          </div>
        </button>

        {/* Confirm button */}
        <button
          onClick={() => ready && onConfirm(reason.label, otherText.trim())}
          disabled={!ready}
          style={{
            width: "100%", padding: "14px",
            background: ready ? t.accent.green.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
            color: ready ? "#fff" : t.textMuted,
            border: "none", borderRadius: 14,
            fontFamily: F, fontWeight: 700, fontSize: 15, letterSpacing: "-0.1px",
            cursor: ready ? "pointer" : "not-allowed",
            boxShadow: ready ? "0 6px 20px rgba(48,209,88,0.35)" : "none",
            transition: "all 0.18s ease",
          }}
        >Versendet markieren</button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// DashboardCustomizeModal — Widgets ein/aus + Reihenfolge ändern.
//
// Layout: Sticky Header (ModalHeader) oben, scrollbare Liste in der Mitte,
// Reset-Button unten. Outer-Modal hat overflow:hidden, Liste hat eigenen
// overflowY:auto — so kann der User die Liste scrollen, Header + Footer
// bleiben aber immer sichtbar (User kann das Modal nie "verlieren").
//
// Drag-to-Reorder: Long-press (250ms) auf eine Row aktiviert Drag-Mode.
// Während Drag folgt die Row dem Finger Y; andere Rows shiften per
// translateY um Platz zu machen. Auf Release wird die neue Reihenfolge
// gespeichert. Touch-events (pointerdown/move/up) damit's auf iOS smooth läuft.
// ─────────────────────────────────────────────────────────────
function DashboardCustomizeModal({ t, dark, F, glass, vh, kbOpen, offsetTop, currentRole, layout, saveDashboardLayout, haptic, onClose }) {
  const allowedIds = Object.keys(WIDGETS).filter(id => WIDGETS[id].visibleFor.includes(currentRole));
  const orderedIds = [
    ...layout.widgets.map(w => w.id).filter(id => allowedIds.includes(id)),
    ...allowedIds.filter(id => !layout.widgets.some(w => w.id === id)),
  ];
  const isVisible = (id) => {
    const w = layout.widgets.find(w => w.id === id);
    if (!w) return true;
    return w.visible !== false;
  };
  const toggle = (id) => {
    const existing = layout.widgets.find(w => w.id === id);
    let nextWidgets;
    if (existing) {
      nextWidgets = layout.widgets.map(w => w.id === id ? { ...w, visible: !w.visible } : w);
    } else {
      nextWidgets = [...layout.widgets, { id, size: WIDGETS[id].defaultSize, visible: true }];
    }
    saveDashboardLayout({ ...layout, widgets: nextWidgets });
    haptic(8);
  };
  const resetDefaults = () => {
    saveDashboardLayout(defaultLayoutForRole(currentRole));
    haptic(15);
  };
  const reorderTo = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const newOrder = orderedIds.slice();
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    const nextWidgets = newOrder.map(id => {
      const existing = layout.widgets.find(w => w.id === id);
      return existing || { id, size: WIDGETS[id].defaultSize, visible: true };
    });
    saveDashboardLayout({ ...layout, widgets: nextWidgets });
    haptic(15);
  };

  // Drag-State (refs + state für Visuals)
  const [dragId, setDragId] = useState(null);
  const [dragDy, setDragDy] = useState(0);
  const [hoverIdx, setHoverIdx] = useState(null);
  const dragRef = useRef(null);
  const ROW_STRIDE = 68; // Row ~60px (no desc) + gap 8px

  const onHandlePointerDown = (e, id, idx) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { id, startY: e.clientY, startIdx: idx, lastIdx: idx };
    setDragId(id);
    setHoverIdx(idx);
    setDragDy(0);
    haptic(12);
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerUp);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dy = e.clientY - dragRef.current.startY;
    setDragDy(dy);
    const newIdx = Math.max(0, Math.min(orderedIds.length - 1, dragRef.current.startIdx + Math.round(dy / ROW_STRIDE)));
    if (newIdx !== dragRef.current.lastIdx) {
      dragRef.current.lastIdx = newIdx;
      setHoverIdx(newIdx);
      haptic(6);
    }
  };
  const onPointerUp = () => {
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    document.removeEventListener("pointercancel", onPointerUp);
    if (dragRef.current) {
      const { startIdx, lastIdx } = dragRef.current;
      reorderTo(startIdx, lastIdx);
    }
    dragRef.current = null;
    setDragId(null);
    setDragDy(0);
    setHoverIdx(null);
  };

  // Visuelle Y-Shift für jede Row während Drag (Live-Reordering-Feedback)
  const rowOffset = (id, idx) => {
    if (!dragId) return 0;
    if (id === dragId) return dragDy;
    const dragIdx = orderedIds.indexOf(dragId);
    if (hoverIdx === null) return 0;
    if (dragIdx < hoverIdx) {
      if (idx > dragIdx && idx <= hoverIdx) return -ROW_STRIDE;
    } else if (dragIdx > hoverIdx) {
      if (idx >= hoverIdx && idx < dragIdx) return ROW_STRIDE;
    }
    return 0;
  };

  const widgetMeta = {
    today_summary:     { icon: Icon.sun,     accent: t.accent.orange },
    quick_actions:     { icon: Icon.bolt,    accent: t.accent.blue   },
    bereit_count:      { icon: Icon.box,     accent: t.accent.purple },
    reorder_alarms:    { icon: Icon.warning, accent: t.accent.orange },
    goal_progress:     { icon: Icon.target,  accent: t.accent.green  },
    inventory_summary: { icon: Icon.box,     accent: t.accent.orange },
    finance_summary:   { icon: Icon.bars,    accent: t.accent.green  },
    channel_breakdown: { icon: Icon.bars,    accent: t.accent.blue   },
    recent_activity:   { icon: Icon.clock,   accent: t.accent.blue   },
    stale_orders:      { icon: Icon.warning, accent: t.accent.red    },
  };

  const resetFooter = (
    <button onClick={resetDefaults} style={{
      width: "100%", padding: "12px",
      background: "transparent", border: `1px solid ${t.glassBorder}`,
      borderRadius: 12, color: t.textSub,
      fontSize: 13, fontWeight: 500, fontFamily: F, cursor: "pointer",
    }}>Auf Standard zurücksetzen</button>
  );

  return (
    <BottomSheet
      isOpen={true} onClose={onClose}
      size="large" title="Dashboard anpassen"
      subtitle="Drücke und ziehe ≡ um zu sortieren."
      dark={dark} t={t} F={F}
      stickyFooter={resetFooter}
    >
      <div style={{
        padding: "0 22px",
        touchAction: "pan-y",
        overscrollBehavior: "contain",
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8, paddingTop: 8 }}>
          {orderedIds.map((id, idx) => {
            const def = WIDGETS[id];
            const meta = widgetMeta[id] || { icon: Icon.box, accent: t.accent.blue };
            const visible = isVisible(id);
            const isDragging = dragId === id;
            const offset = rowOffset(id, idx);
            return (
              <div key={id} style={{
                width: "100%",
                background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)",
                border: `1px solid ${isDragging ? t.accent.blue.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)")}`,
                borderRadius: 14,
                padding: "10px 12px 10px 4px",
                fontFamily: F, textAlign: "left",
                display: "flex", alignItems: "center", gap: 6,
                transform: `translate3d(0, ${offset}px, 0) ${isDragging ? "scale(1.025)" : ""}`,
                transition: isDragging ? "none" : "transform 0.22s cubic-bezier(0.32,0.72,0,1), border-color 0.16s ease",
                zIndex: isDragging ? 2 : 1,
                boxShadow: isDragging ? "0 12px 32px rgba(0,0,0,0.25)" : "none",
                position: "relative",
                willChange: isDragging ? "transform" : "auto",
              }}>
                {/* Drag-Handle ≡ */}
                <button
                  onPointerDown={(e) => onHandlePointerDown(e, id, idx)}
                  aria-label="Sortieren"
                  style={{
                    flexShrink: 0,
                    width: 32, height: 40,
                    background: "transparent", border: "none",
                    cursor: "grab",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: t.textMuted,
                    touchAction: "none",
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  </svg>
                </button>

                {/* Icon-Badge */}
                <span style={{
                  width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                  background: meta.accent.weak,
                  color: meta.accent.solid,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{typeof meta.icon === "function" ? meta.icon(17) : meta.icon}</span>

                {/* Title — tap toggles visibility */}
                <button onClick={() => toggle(id)} style={{
                  flex: 1, minWidth: 0,
                  background: "transparent", border: "none", padding: 0,
                  cursor: "pointer", fontFamily: F, textAlign: "left",
                }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: t.text, letterSpacing: "-0.2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>{def.title}</span>
                </button>

                {/* Toggle */}
                <button onClick={() => toggle(id)} aria-label={visible ? "Ausblenden" : "Einblenden"} style={{
                  width: 51, height: 31, borderRadius: 16, position: "relative", flexShrink: 0,
                  background: visible ? "#30D158" : (dark ? "rgba(120,120,128,0.32)" : "rgba(120,120,128,0.18)"),
                  boxShadow: visible ? "inset 0 1px 2px rgba(0,0,0,0.18)" : "inset 0 1px 2px rgba(0,0,0,0.06)",
                  transition: "background 0.2s ease",
                  border: "none", cursor: "pointer", padding: 0,
                }}>
                  <span style={{
                    position: "absolute", top: 2, left: visible ? 22 : 2,
                    width: 27, height: 27, borderRadius: "50%",
                    background: "#fff",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.25), 0 0 0 0.5px rgba(0,0,0,0.04)",
                    transition: "left 0.24s cubic-bezier(0.34,1.2,0.64,1)",
                  }} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </BottomSheet>
  );
}

// ─────────────────────────────────────────────────────────────
// GoalEditModal — Persönliches Verkaufsziel ändern.
// Top-anchored, dvh, body-scroll-lock, native iOS-Animation.
// ─────────────────────────────────────────────────────────────
function GoalEditModal({ t, dark, F, glass, goal, setGoal, onClose }) {
  // Lokaler String-State für Eingabe — erlaubt leeren Input während des Tippens.
  // Sync zum echten goal-Number erst onBlur oder bei +/- Buttons.
  const [draft, setDraft] = useState(String(goal));
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  // Wenn goal von aussen ändert (z.B. +/-), sync zur Anzeige
  useEffect(() => { setDraft(String(goal)); }, [goal]);

  const commitDraft = () => {
    const n = parseInt(draft, 10);
    if (Number.isFinite(n) && n >= 1) setGoal(n);
    else setDraft(String(goal)); // bei Müll-Eingabe: zurück auf alten Wert
  };

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, []);
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 70,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
        touchAction: "none",
        willChange: "opacity",
      }}
        onTouchMove={e => e.preventDefault()}
      />
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, glass, F, dark, width: 340, radius: 22, zIndex: 71 })}>
        <ModalHeader
          t={t} dark={dark} F={F}
          title="Verkaufsziel"
          subtitle="Persönliches Ziel — unabhängig vom Lagerbestand."
          onClose={onClose}
        />
        <div style={{ ...glass({ borderRadius: 16 }), overflow: "hidden", marginBottom: 14, display: "flex", alignItems: "center" }}>
          <button onClick={() => setGoal(g => Math.max(1, g - 10))} style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "14px 18px", fontFamily: F, fontSize: 20, fontWeight: 400, color: t.accent.blue.solid,
          }}>−</button>
          <input type="number" value={draft} inputMode="numeric"
            onChange={e => setDraft(e.target.value)}
            onBlur={commitDraft}
            onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
            style={{ flex: 1, minWidth: 0, border: "none", background: "transparent", fontFamily: F, fontSize: 28, fontWeight: 700, color: t.text, outline: "none", padding: "12px 0", textAlign: "center", letterSpacing: "-0.6px" }} />
          <button onClick={() => setGoal(g => g + 10)} style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "14px 18px", fontFamily: F, fontSize: 20, fontWeight: 400, color: t.accent.blue.solid,
          }}>+</button>
        </div>
        <button onClick={() => { commitDraft(); onClose(); }} style={{
          width: "100%", background: t.accent.blue.solid, border: "none", borderRadius: 14,
          padding: "14px", fontFamily: F, fontWeight: 700, fontSize: 15, cursor: "pointer", color: "#fff",
          boxShadow: `0 4px 18px ${t.accent.blue.weak}`,
        }}>Fertig</button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Settings-Panel Helper-Komponenten — iOS-Settings-Style.
// SettingsSection: gruppiert Rows mit Header-Label.
// Row-Varianten: Toggle, Link (chevron), Segmented (3 options inline).
// Alle Rows haben kleines Icon links, Label, optional sub-text + right-side.
// ─────────────────────────────────────────────────────────────
function SettingsSection({ title, t, children }) {
  return (
    <div style={{ marginTop: 22 }}>
      <div style={{
        fontSize: 11, fontWeight: 600, color: t.textMuted,
        letterSpacing: "0.04em", textTransform: "uppercase",
        marginBottom: 8, paddingLeft: 32,
      }}>{title}</div>
      <div style={{
        background: t.glass,
        backdropFilter: "blur(40px) saturate(200%)",
        WebkitBackdropFilter: "blur(40px) saturate(200%)",
        border: `1px solid ${t.glassBorder}`,
        boxShadow: t.glassShadow,
        borderRadius: 14, overflow: "hidden",
        marginLeft: 16, marginRight: 16,
      }}>{children}</div>
    </div>
  );
}


function SettingsSegmentedRow({ icon, accent, label, sub, options, value, onChange, t, dark, F, first }) {
  return (
    <div style={{
      padding: "12px 16px",
      display: "flex", alignItems: "center", gap: 10,
      borderTop: first ? "none" : `0.5px solid ${t.divider}`,
      fontFamily: F,
    }}>
      {icon && (
        <span style={{
          color: accent || t.textMuted,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>{typeof icon === "function" ? icon(16) : icon}</span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: t.text, fontWeight: 500, letterSpacing: "-0.1px" }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2, lineHeight: 1.3 }}>{sub}</div>}
      </div>
      <div style={{
        background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
        borderRadius: 8, padding: 2,
        display: "flex", flexShrink: 0,
      }}>
        {options.map(opt => (
          <button key={opt.v} onClick={(e) => { e.stopPropagation(); onChange(opt.v); }} style={{
            background: value === opt.v ? t.pillBg : "transparent",
            border: "none", borderRadius: 6,
            padding: "5px 10px", cursor: "pointer", fontFamily: F,
            fontSize: 12, fontWeight: value === opt.v ? 600 : 500,
            color: value === opt.v ? t.text : t.textSub,
            boxShadow: value === opt.v ? `0 1px 4px rgba(0,0,0,${dark?0.3:0.08})` : "none",
            transition: "all 0.15s ease",
          }}>{opt.l}</button>
        ))}
      </div>
    </div>
  );
}

// Kanal-Picker — kleines Modal-Sheet mit Channel-Liste + Checkmarks.
function KanalPickerModal({ t, dark, F, glass, channels, value, onPick, onClose }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 60,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
        willChange: "opacity",
      }} />
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, glass, F, dark, width: 360, radius: 24, padding: "20px 22px 12px", zIndex: 61 })}>
        <ModalHeader t={t} dark={dark} F={F} title="Kanal-Filter" onClose={onClose} marginBottom={12} />
        <div style={{ margin: "0 -10px" }}>
          {channels.map((ch, i) => (
            <button key={ch.id} onClick={() => { onPick(ch.id); onClose(); }} style={{
              width: "100%", background: "none", border: "none", cursor: "pointer",
              padding: "13px 12px",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              fontFamily: F, textAlign: "left",
              borderTop: i > 0 ? `0.5px solid ${t.divider}` : "none",
            }}>
              <span style={{ fontSize: 15, color: t.text, fontWeight: 400 }}>{ch.name}</span>
              {value === ch.id && (
                <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
                  <path d="M1 6L5.5 10.5L15 1" stroke={t.accent.blue.solid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function ScannerModal({
  t, dark, F,
  bereitOrders,
  scannerState, scannerError,
  lastMatchedOrder, lastUnmatchedCode,
  onClose, onScanResult, onSetState, onError,
  scannerControlsRef,
  getStatusLabel, getChannelLabel,
  extractTrackingNumber,
}) {
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  // Start the ZXing reader once the modal mounts and the <video> ref is ready
  useEffect(() => {
    let cancelled = false;
    async function start() {
      // No camera API at all → fallback only
      if (!navigator.mediaDevices?.getUserMedia) {
        onError("Kamera in diesem Browser nicht verfügbar");
        onSetState("error");
        return;
      }
      if (typeof ZXingBrowser === "undefined") {
        onError("Scanner-Bibliothek nicht geladen — bitte Seite neu laden");
        onSetState("error");
        return;
      }
      try {
        onSetState("scanning");
        // Configure decoder with shipping-label barcode formats (focused = faster)
        const hints = new Map();
        if (typeof ZXing !== "undefined" && ZXing.DecodeHintType && ZXing.BarcodeFormat) {
          hints.set(ZXing.DecodeHintType.POSSIBLE_FORMATS, [
            ZXing.BarcodeFormat.CODE_128,
            ZXing.BarcodeFormat.QR_CODE,
            ZXing.BarcodeFormat.DATA_MATRIX,
            ZXing.BarcodeFormat.PDF_417,
            ZXing.BarcodeFormat.ITF,
          ]);
        }
        const reader = new ZXingBrowser.BrowserMultiFormatReader(hints);
        // Prefer the rear camera
        const constraints = {
          video: {
            facingMode: { ideal: "environment" },
            width:  { ideal: 1280 },
            height: { ideal: 720 },
          },
        };
        const controls = await reader.decodeFromConstraints(
          constraints,
          videoRef.current,
          (result, err) => {
            if (cancelled) return;
            if (result) onScanResult(result.getText());
            // Errors during scanning are normal (NotFoundException between frames) — ignore
          }
        );
        if (cancelled) {
          try { controls.stop(); } catch {}
          return;
        }
        scannerControlsRef.current = controls;
      } catch (e) {
        if (cancelled) return;
        const msg = String(e?.message || e || "");
        if (/permission|NotAllowed|denied/i.test(msg)) {
          onError("Kamera-Zugriff verweigert");
        } else if (/NotFound|no .* device/i.test(msg)) {
          onError("Keine Kamera gefunden");
        } else {
          onError("Kamera konnte nicht gestartet werden");
        }
        onSetState("error");
      }
    }
    start();
    return () => {
      cancelled = true;
      try { scannerControlsRef.current?.stop?.(); } catch {}
      scannerControlsRef.current = null;
    };
  }, []);

  // Manual photo fallback (no camera permission) — runs the same extraction pipeline
  async function handleManualPhoto(file) {
    if (!file) return;
    onSetState("scanning");
    try {
      const result = await extractTrackingNumber(file);
      if (result?.primary) {
        onScanResult(result.primary);
      } else {
        // Trigger no_match overlay manually with the failure
        onScanResult("");
      }
    } catch {
      onScanResult("");
    }
  }

  return (
    <>
      {/* Fullscreen black backdrop */}
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "#000", zIndex: 100,
      }} />

      {/* Modal content */}
      <div onClick={e => e.stopPropagation()} style={{
        position: "fixed", inset: 0, zIndex: 101,
        display: "flex", flexDirection: "column",
        fontFamily: F, color: "#fff",
      }}>
        {/* Header */}
        <div style={{
          padding: "calc(env(safe-area-inset-top, 0px) + 14px) 16px 14px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "rgba(0,0,0,0.7)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
        }}>
          <ModalCloseButton onClose={onClose} dark={true} variant="onDark" />
          <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.2px" }}>Scannen</div>
          <div style={{ width: 30 }}/>
        </div>

        {/* Camera viewport */}
        <div style={{ flex: 1, position: "relative", overflow: "hidden", background: "#000" }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />

          {/* Scan box overlay (visual guide) */}
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            pointerEvents: "none",
          }}>
            <div style={{
              width: "min(80%, 360px)", aspectRatio: "1.6 / 1",
              border: "2px solid rgba(255,255,255,0.6)",
              borderRadius: 18,
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)",
            }} />
          </div>

          {/* Match overlay (success) */}
          {scannerState === "match" && lastMatchedOrder && (
            <div style={{
              position: "absolute", left: 16, right: 16, top: 24,
              padding: "16px 18px", borderRadius: 18,
              background: "rgba(48,209,88,0.95)",
              boxShadow: "0 6px 24px rgba(48,209,88,0.4)",
              animation: "modalIn 0.18s cubic-bezier(0.34,1.4,0.64,1)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 22,
                  background: "rgba(255,255,255,0.25)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                    <path d="M3 11.5L8.5 17L19 5" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "#fff", letterSpacing: "-0.2px" }}>
                    #{lastMatchedOrder.bestellnummer || "—"}
                  </div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.9)", marginTop: 2 }}>
                    {getChannelLabel(lastMatchedOrder.kanal)} · als {getStatusLabel("Versendet")} markiert
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* No-match overlay (error) */}
          {scannerState === "no_match" && (
            <div style={{
              position: "absolute", left: 16, right: 16, top: 24,
              padding: "16px 18px", borderRadius: 18,
              background: "rgba(255,69,58,0.95)",
              boxShadow: "0 6px 24px rgba(255,69,58,0.4)",
              animation: "modalIn 0.18s cubic-bezier(0.34,1.4,0.64,1)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 22,
                  background: "rgba(255,255,255,0.25)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                    <path d="M4 4L18 18M18 4L4 18" stroke="#fff" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", letterSpacing: "-0.2px" }}>
                    Kein Treffer
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.9)", marginTop: 2, fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {lastUnmatchedCode || "Code nicht erkannt"}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error state — camera permission denied / unavailable */}
          {scannerState === "error" && (
            <div style={{
              position: "absolute", inset: 0, background: "rgba(0,0,0,0.85)",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              padding: 28, textAlign: "center",
            }}>
              <div style={{ fontSize: 40, marginBottom: 14 }}>📷</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#fff", marginBottom: 8 }}>
                {scannerError || "Kamera nicht verfügbar"}
              </div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 22, lineHeight: 1.5, maxWidth: 320 }}>
                Du kannst auch ein Foto vom Versandlabel aufnehmen — die Trackingnummer wird automatisch erkannt.
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                onChange={e => { const f = e.target.files?.[0]; if (f) handleManualPhoto(f); e.target.value = ""; }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  background: "#0A84FF", border: "none", borderRadius: 14,
                  padding: "13px 22px", color: "#fff", fontFamily: F,
                  fontSize: 15, fontWeight: 600, cursor: "pointer",
                  boxShadow: "0 4px 18px rgba(10,132,255,0.35)",
                }}
              >📸 Foto aufnehmen</button>
            </div>
          )}
        </div>

        {/* Footer info */}
        <div style={{
          padding: "14px 16px calc(env(safe-area-inset-bottom, 0px) + 16px)",
          background: "rgba(0,0,0,0.7)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          textAlign: "center",
        }}>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", marginBottom: 4 }}>
            Halte den Barcode in den Rahmen
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }}>
            {bereitOrders.length === 0
              ? "Keine offenen Bestellungen"
              : bereitOrders.length === 1
                ? "1 Bestellung scannbar"
                : `${bereitOrders.length} Bestellungen scannbar`}
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// CSV-Export-Helpers (Block 2 — Daten-Export)
// CSV-Konvertierung läuft komplett client-seitig. Bei großen Datenmengen
// (>10k Rows) lieber später paginiert.
// ─────────────────────────────────────────────────────────────
function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = typeof v === "object" ? JSON.stringify(v) : String(v);
  // RFC 4180: Felder mit ,;"\n quoten und " verdoppeln
  if (/[",;\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
function rowsToCsv(rows, columns) {
  if (!rows || rows.length === 0) return columns.join(",") + "\n";
  const header = columns.join(",");
  const body = rows.map(r => columns.map(c => csvEscape(r[c])).join(",")).join("\n");
  return header + "\n" + body + "\n";
}
// Trigger Browser-Download eines Blobs
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
// Holt alle Daten eines Stores via existierende Endpoints und bündelt sie zu
// CSV-Files. Returns: { [filename]: csvString }
async function fetchStoreCsvFiles(store, authToken) {
  const headers = { "Authorization": `Bearer ${authToken}` };
  const apiBase = `/api/stores/${store.id}`;
  const files = {};
  const safeName = (store.name || "store").replace(/[^a-z0-9]+/gi, "_");
  const prefix = `${safeName}__`;

  // Online-Stores haben Orders + Batches
  if ((store.type || "online") === "online") {
    try {
      const r = await fetch(`${apiBase}/orders?include_archived=1`, { headers });
      if (r.ok) {
        const orders = await r.json();
        files[`${prefix}orders.csv`] = rowsToCsv(orders, [
          "id", "order_number", "channel_id", "buyer_name", "status", "price", "shipping_cost", "fees",
          "tracking_number", "carrier", "label_key", "is_archived", "archived_at", "created_at", "updated_at",
        ]);
      }
    } catch (e) { /* skip */ }
    try {
      const r = await fetch(`${apiBase}/batches`, { headers });
      if (r.ok) {
        const batches = await r.json();
        files[`${prefix}batches.csv`] = rowsToCsv(batches, [
          "id", "label", "status", "starts_at", "ends_at", "total_revenue", "total_fees", "created_at",
        ]);
      }
    } catch (e) { /* skip */ }
  }

  // Flea + Market: Items, Sessions, Sales, Sale-Items
  if (store.type === "flea" || store.type === "market") {
    try {
      const r = await fetch(`${apiBase}/items?include_inactive=1`, { headers });
      if (r.ok) {
        const items = await r.json();
        files[`${prefix}items.csv`] = rowsToCsv(items, [
          "id", "name", "description", "price", "wish_price", "quantity", "active", "created_at", "updated_at",
        ]);
      }
    } catch (e) { /* skip */ }
    try {
      const r = await fetch(`${apiBase}/sessions?limit=2000`, { headers });
      if (r.ok) {
        const raw = await r.json();
        const sessions = Array.isArray(raw) ? raw : (raw.sessions || []);
        files[`${prefix}sessions.csv`] = rowsToCsv(sessions, [
          "id", "location", "starts_at", "ends_at", "notes", "created_at",
        ]);
        // Sales je Session laden (CSV pro Session wäre viel — wir collecten in eine Datei)
        const allSales = []; const allSaleItems = [];
        await Promise.all(sessions.map(async (s) => {
          try {
            const sr = await fetch(`${apiBase}/sessions/${s.id}`, { headers });
            if (!sr.ok) return;
            const detail = await sr.json();
            for (const sale of (detail.sales || [])) {
              allSales.push({ ...sale, _location: s.location });
              for (const si of (sale.sale_items || [])) {
                allSaleItems.push({ ...si, sale_id: sale.id, session_id: s.id });
              }
            }
          } catch (e) { /* skip */ }
        }));
        if (allSales.length > 0) {
          files[`${prefix}sales.csv`] = rowsToCsv(allSales, [
            "id", "session_id", "_location", "total", "payment", "is_bundle", "is_voided",
            "voided_at", "sold_at", "notes",
          ]);
        }
        if (allSaleItems.length > 0) {
          files[`${prefix}sale_items.csv`] = rowsToCsv(allSaleItems, [
            "id", "sale_id", "session_id", "item_id", "item_name", "quantity", "unit_price", "wish_price",
          ]);
        }
      }
    } catch (e) { /* skip */ }
  }

  return files;
}

// Globaler Export: ALLE Stores zu einem ZIP. Lädt JSZip via CDN nach.
// Wirft Error bei Fehler — Caller fängt ab.
async function buildGlobalExportZip(stores, authToken) {
  // JSZip dynamisch laden (CDN); cached im window-Namespace.
  if (!window.JSZip) {
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js";
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  const zip = new window.JSZip();
  // Stores-Übersicht
  zip.file("stores.csv", rowsToCsv(stores, ["id", "name", "type", "owner_id", "created_at", "role"]));
  // Pro Store alle Daten
  for (const store of stores) {
    const files = await fetchStoreCsvFiles(store, authToken);
    for (const [name, content] of Object.entries(files)) {
      zip.file(name, content);
    }
  }
  const blob = await zip.generateAsync({ type: "blob" });
  const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  downloadBlob(blob, `memorica-export-${ts}.zip`);
}


function GlobalSettingsSheet({ isOpen, dark, authUser, authToken, userStores, userPreferences, onPreferencesChange, onDarkChange, onLogout, onClose, showToast }) {
  const t = T[dark ? "dark" : "light"];
  const [pwOpen, setPwOpen]               = useState(false);
  const [privacyOpen, setPrivacyOpen]     = useState(false);
  const [helpOpen, setHelpOpen]           = useState(false);
  const [confirmDeleteAccountOpen, setConfirmDeleteAccountOpen] = useState(false);
  const [exporting, setExporting]         = useState(false);

  const alwaysShowLanding = userPreferences?.always_show_landing === true;

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await buildGlobalExportZip(userStores, authToken);
      showToast("Export heruntergeladen");
    } catch (e) {
      showToast(e.message || "Export fehlgeschlagen", "error");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <BottomSheet isOpen={isOpen} onClose={onClose} title="Einstellungen" dark={dark} t={t} F={F}>
        <SettingsSectionLabel t={t}>Account</SettingsSectionLabel>
        <SettingsCard dark={dark} t={t}>
          <SettingsRow isFirst label="Angemeldet als" sub={authUser?.email || "—"} t={t} dark={dark} F={F} />
          <SettingsRow label="Passwort ändern" onClick={() => setPwOpen(true)} t={t} dark={dark} F={F} />
          <SettingsRow label="Abmelden" onClick={onLogout} t={t} dark={dark} F={F} />
          <SettingsRow label="Konto löschen" destructive onClick={() => setConfirmDeleteAccountOpen(true)} t={t} dark={dark} F={F} />
        </SettingsCard>

        <SettingsSectionLabel t={t}>Anzeige</SettingsSectionLabel>
        <SettingsCard dark={dark} t={t}>
          <SettingsToggle isFirst label="Dark Mode" checked={dark} onChange={onDarkChange} t={t} dark={dark} F={F} />
          {/* P-5-Punkte-Fix Block 4: Setting unconditional gerendert.
              Klarere Sub-Texte je Status. */}
          <SettingsToggle
            label="Immer Landingpage anzeigen"
            sub={alwaysShowLanding
              ? "Aktiv — auch bei nur einem Bereich landest du auf der Übersicht"
              : "Bei genau einem Bereich öffnet die App ihn automatisch"}
            checked={alwaysShowLanding}
            onChange={(v) => onPreferencesChange({ always_show_landing: v })}
            t={t} dark={dark} F={F}
          />
        </SettingsCard>

        <SettingsSectionLabel t={t}>Daten</SettingsSectionLabel>
        <SettingsCard dark={dark} t={t}>
          <SettingsRow
            isFirst
            label="Vollständiger Daten-Export"
            sub={exporting ? "Exportiere…" : "ZIP mit allen Stores, Bestellungen, Items"}
            onClick={!exporting ? handleExport : undefined}
            t={t} dark={dark} F={F}
          />
        </SettingsCard>
        <div style={{ padding: "10px 32px", fontSize: 12, color: t.textSub, lineHeight: 1.5 }}>
          Daten werden automatisch in der Cloud gesichert.
        </div>

        <SettingsSectionLabel t={t}>Über die App</SettingsSectionLabel>
        <SettingsCard dark={dark} t={t}>
          <SettingsRow isFirst label="Version" value="0.9.0" t={t} dark={dark} F={F} />
          <SettingsRow label="Datenschutz & AGB" onClick={() => setPrivacyOpen(true)} t={t} dark={dark} F={F} />
          <SettingsRow label="Hilfe / FAQ" onClick={() => setHelpOpen(true)} t={t} dark={dark} F={F} />
          <SettingsRow label="Feedback senden" onClick={() => { window.location.href = "mailto:office@memorica.de?subject=Memorica%20Feedback"; }} t={t} dark={dark} F={F} />
        </SettingsCard>
      </BottomSheet>

      {pwOpen && (
        <PasswordChangeSheet dark={dark} t={t} onClose={() => setPwOpen(false)} showToast={showToast} />
      )}
      {confirmDeleteAccountOpen && (
        <AccountDeleteSheet dark={dark} t={t}
          authUser={authUser} authToken={authToken}
          onClose={() => setConfirmDeleteAccountOpen(false)}
          onDeleted={onLogout}
          showToast={showToast} />
      )}
      {privacyOpen && (
        <PrivacySheet dark={dark} t={t} authToken={authToken} onClose={() => setPrivacyOpen(false)} showToast={showToast} />
      )}
      {helpOpen && (
        <StaticTextSheet dark={dark} t={t} title="Hilfe / FAQ" body="Folgt bald. Bei Fragen schreib bitte an office@memorica.de — wir helfen schnell weiter." onClose={() => setHelpOpen(false)} />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// PasswordChangeSheet — Supabase Auth API
// ─────────────────────────────────────────────────────────────
function PasswordChangeSheet({ dark, t, onClose, showToast }) {
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { height: vh, kbOpen, offsetTop } = useModalViewport();

  const ready = pw1.length >= 8 && pw1 === pw2;

  const submit = async () => {
    if (!ready || submitting || !supabase) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pw1 });
      if (error) {
        showToast(error.message || "Passwort konnte nicht geändert werden", "error");
      } else {
        showToast("Passwort geändert");
        onClose();
      }
    } catch (e) {
      showToast(e.message || "Fehler", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 94,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 380, radius: 24, padding: "20px 22px 22px", zIndex: 95 })}>
        <ModalHeader t={t} dark={dark} F={F} title="Passwort ändern" onClose={onClose} marginBottom={14} />
        <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "0 14px", marginBottom: 8, border: `1px solid ${t.glassBorder}` }}>
          <input type="password" autoFocus value={pw1} onChange={e => setPw1(e.target.value)} placeholder="Neues Passwort (min. 8 Zeichen)"
            style={{ width: "100%", background: "transparent", border: "none", outline: "none", padding: "12px 0", fontSize: 14, color: t.text, fontFamily: F, minWidth: 0 }}/>
        </div>
        <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "0 14px", marginBottom: 14, border: `1px solid ${t.glassBorder}` }}>
          <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} placeholder="Wiederholen"
            style={{ width: "100%", background: "transparent", border: "none", outline: "none", padding: "12px 0", fontSize: 14, color: t.text, fontFamily: F, minWidth: 0 }}/>
        </div>
        {pw2 && pw1 !== pw2 && (
          <div style={{ fontSize: 12, color: t.accent.red.solid, marginBottom: 10, paddingLeft: 4 }}>Passwörter stimmen nicht überein.</div>
        )}
        <button onClick={submit} disabled={!ready || submitting} style={{
          width: "100%", padding: "13px",
          background: ready ? t.accent.blue.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
          color: ready ? "#fff" : t.textMuted,
          border: "none", borderRadius: 12,
          fontFamily: F, fontWeight: 700, fontSize: 14,
          cursor: ready && !submitting ? "pointer" : "not-allowed",
        }}>{submitting ? "Speichere…" : "Speichern"}</button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// AccountDeleteSheet — echte Konto-Löschung mit E-Mail-Bestätigung
// ─────────────────────────────────────────────────────────────
function AccountDeleteSheet({ dark, t, authUser, authToken, onClose, onDeleted, showToast }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [emailInput, setEmailInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { validate, errors, clearError, registerRef } = useValidation({
    emailInput: { required: true, type: "email" },
  });

  const submit = async () => {
    if (submitting) return;
    if (!validate({ emailInput })) return;
    const trimmed = emailInput.trim().toLowerCase();
    if (trimmed !== (authUser?.email || "").toLowerCase()) {
      showToast("E-Mail stimmt nicht überein", "error");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/me", {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${authToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showToast(data.reason || "Löschung fehlgeschlagen", "error");
        setSubmitting(false);
        return;
      }
      showToast("Konto gelöscht");
      onDeleted();
    } catch (e) {
      showToast(e.message || "Netzwerkfehler", "error");
      setSubmitting(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 94,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 380, radius: 24, padding: "22px 22px 18px", zIndex: 95 })}>
        <ModalHeader t={t} dark={dark} F={F} title="Konto löschen" onClose={onClose} marginBottom={12} />
        <div style={{ background: t.accent.red.weak, color: t.accent.red.solid, borderRadius: 10, padding: "10px 12px", marginBottom: 14, fontSize: 12, lineHeight: 1.5, fontWeight: 500 }}>
          Alle Daten werden permanent gelöscht. Diese Aktion ist unwiderruflich.
        </div>
        <div style={{ fontSize: 13, color: t.textSub, lineHeight: 1.5, marginBottom: 14 }}>
          Gib zur Bestätigung deine E-Mail-Adresse ein:
        </div>
        <div ref={registerRef("emailInput")} style={{
          background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
          borderRadius: 12, padding: "0 14px", marginBottom: errors.emailInput ? 4 : 14,
          border: `1px solid ${errors.emailInput ? "rgba(255,59,48,0.6)" : t.glassBorder}`,
        }}>
          <input
            type="email" inputMode="email" autoCapitalize="none" autoCorrect="off"
            value={emailInput} onChange={e => { setEmailInput(e.target.value); clearError("emailInput"); }}
            placeholder={authUser?.email || "E-Mail"}
            style={{ width: "100%", boxSizing: "border-box", background: "transparent", border: "none", outline: "none", padding: "13px 0", fontSize: 15, color: t.text, fontFamily: F }}
          />
        </div>
        {errors.emailInput && <div style={{ fontSize: 11, color: "rgba(255,59,48,0.9)", marginBottom: 10, paddingLeft: 4 }}>{errors.emailInput}</div>}
        <button onClick={submit} disabled={submitting} style={{
          width: "100%", padding: "13px",
          background: t.accent.red.solid, color: "#fff",
          border: "none", borderRadius: 12,
          fontFamily: F, fontWeight: 700, fontSize: 14,
          cursor: submitting ? "not-allowed" : "pointer",
          marginBottom: 8, opacity: submitting ? 0.7 : 1,
        }}>{submitting ? "Wird gelöscht…" : "Konto endgültig löschen"}</button>
        <button onClick={onClose} style={{
          width: "100%", padding: "10px",
          background: "transparent", border: "none",
          color: t.textMuted,
          fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer",
        }}>Abbrechen</button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// PrivacySheet — DSGVO-Dashboard (Datenschutz & AGB)
// ─────────────────────────────────────────────────────────────
function PrivacySheet({ dark, t, authToken, onClose, showToast }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [exporting, setExporting] = useState(false);
  const [dseOpen, setDseOpen] = useState(false);
  const [agbOpen, setAgbOpen] = useState(false);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await fetch("/api/me/export", {
        headers: { "Authorization": `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error("Export fehlgeschlagen");
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const fnMatch = cd.match(/filename="([^"]+)"/);
      const fileName = fnMatch ? fnMatch[1] : `memorica-export-${new Date().toISOString().slice(0, 10)}.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fileName; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      showToast("Daten exportiert");
    } catch (e) {
      showToast(e.message || "Export fehlgeschlagen", "error");
    } finally {
      setExporting(false);
    }
  };

  const infoRows = [
    { label: "Was wird gespeichert?", text: "Account-Daten (E-Mail), deine Bereiche, Artikel, Sitzungen und Verkaufshistorie." },
    { label: "Wo werden Daten gespeichert?", text: "Ausschließlich in EU-Rechenzentren (Supabase/AWS Frankfurt, Cloudflare EU)." },
    { label: "Wie lange?", text: "Bis zur Konto-Löschung. Nach Löschung werden alle personenbezogenen Daten sofort entfernt." },
    { label: "Weitergabe?", text: "Keine Weitergabe an Dritte, außer rein technisch notwendige Cloud-Infrastruktur." },
  ];

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 94,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 480, radius: 24, padding: 0, zIndex: 95 })}>
        <div style={{ padding: "20px 22px 12px" }}>
          <ModalHeader t={t} dark={dark} F={F} title="Datenschutz & AGB" onClose={onClose} marginBottom={8} />
        </div>
        <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "0 22px calc(env(safe-area-inset-bottom,14px) + 20px)", maxHeight: Math.max(280, vh - 140) }}>

          {/* Info-Kacheln */}
          <div style={{ marginBottom: 18 }}>
            {infoRows.map((row, i) => (
              <div key={i} style={{
                background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                borderRadius: 11, padding: "10px 13px",
                marginBottom: 6, border: `1px solid ${t.glassBorder}`,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 3 }}>{row.label}</div>
                <div style={{ fontSize: 12, color: t.textSub, lineHeight: 1.5 }}>{row.text}</div>
              </div>
            ))}
          </div>

          {/* Daten-Export */}
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Deine Daten</div>
          <div style={{ background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)", borderRadius: 13, overflow: "hidden", border: `1px solid ${t.glassBorder}`, marginBottom: 18 }}>
            <button onClick={handleExport} disabled={exporting} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              width: "100%", padding: "14px 16px",
              background: "transparent", border: "none",
              fontFamily: F, cursor: exporting ? "default" : "pointer",
              borderBottom: `0.5px solid ${t.divider}`,
            }}>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: t.text }}>{exporting ? "Exportiere…" : "Daten exportieren"}</div>
                <div style={{ fontSize: 11, color: t.textSub, marginTop: 2 }}>JSON-Datei mit allen deinen Daten</div>
              </div>
              <span style={{ fontSize: 18, color: t.textMuted }}>↓</span>
            </button>
            <button onClick={() => setDseOpen(true)} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              width: "100%", padding: "14px 16px",
              background: "transparent", border: "none",
              fontFamily: F, cursor: "pointer",
              borderBottom: `0.5px solid ${t.divider}`,
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.text, textAlign: "left" }}>Datenschutzerklärung</div>
              <span style={{ fontSize: 15, color: t.textMuted }}>›</span>
            </button>
            <button onClick={() => setAgbOpen(true)} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              width: "100%", padding: "14px 16px",
              background: "transparent", border: "none",
              fontFamily: F, cursor: "pointer",
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.text, textAlign: "left" }}>AGB</div>
              <span style={{ fontSize: 15, color: t.textMuted }}>›</span>
            </button>
          </div>

          {/* Kontakt */}
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Kontakt</div>
          <div style={{ background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)", borderRadius: 13, overflow: "hidden", border: `1px solid ${t.glassBorder}`, marginBottom: 8 }}>
            <button onClick={() => { window.location.href = "mailto:office@memorica.de?subject=Datenschutz-Anfrage"; }} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              width: "100%", padding: "14px 16px",
              background: "transparent", border: "none",
              fontFamily: F, cursor: "pointer",
            }}>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: t.text }}>Datenschutz-Anfrage</div>
                <div style={{ fontSize: 11, color: t.textSub, marginTop: 2 }}>office@memorica.de</div>
              </div>
              <span style={{ fontSize: 15, color: t.textMuted }}>✉</span>
            </button>
          </div>

          <div style={{ fontSize: 11, color: t.textSub, lineHeight: 1.5, paddingTop: 4 }}>
            ⚠ Datenschutzerklärung und AGB sind Platzhalter und werden vor dem offiziellen Launch durch juristisch geprüfte Texte ersetzt.
          </div>
        </div>
      </div>

      {dseOpen && <StaticTextSheet dark={dark} t={t} title="Datenschutzerklärung" body={PRIVACY_TEXT} onClose={() => setDseOpen(false)} />}
      {agbOpen && <StaticTextSheet dark={dark} t={t} title="AGB" body={AGB_TEXT} onClose={() => setAgbOpen(false)} />}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// StaticTextSheet — generisches Modal für AGB / Datenschutz / FAQ
// ─────────────────────────────────────────────────────────────
function StaticTextSheet({ dark, t, title, body, onClose }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 94,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 480, radius: 24, padding: 0, zIndex: 95 })}>
        <div style={{ padding: "20px 22px 12px" }}>
          <ModalHeader t={t} dark={dark} F={F} title={title} onClose={onClose} marginBottom={8} />
        </div>
        <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "0 22px 22px", maxHeight: Math.max(280, vh - 140), fontSize: 13, color: t.text, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
          {body}
        </div>
      </div>
    </>
  );
}

// ⚠ PLATZHALTER — VOR LAUNCH durch juristisch geprüfte Texte ersetzen!
const PRIVACY_TEXT = `⚠ PLATZHALTER — KEIN RECHTSGÜLTIGER TEXT ⚠
Dieser Text wird vor dem offiziellen Launch durch eine vollständige, juristisch geprüfte Datenschutzerklärung ersetzt.

1. Verantwortlicher
Memorica · office@memorica.de

2. Erhobene Daten
• Account-Daten (E-Mail, Passwort-Hash)
• Bereichs- und Artikeldaten (Stores, Items, Sessions, Verkäufe)
• Technische Logs (Fehler, Audit-Trail für Datensicherheit)

3. Zweck der Verarbeitung
Ausschließlich zur Bereitstellung der App-Funktionen.

4. Speicherort
EU-Rechenzentren (Supabase/AWS Frankfurt, Cloudflare EU).

5. Speicherdauer
Bis zur Konto-Löschung. Danach sofortige und vollständige Löschung.

6. Weitergabe
Keine Weitergabe an Dritte außer technisch notwendige Cloud-Dienstleister (Auftragsverarbeiter).

7. Deine Rechte (DSGVO Art. 15–21)
• Auskunft über gespeicherte Daten (Einstellungen → Datenschutz → Daten exportieren)
• Berichtigung, Löschung, Einschränkung der Verarbeitung
• Widerspruchsrecht, Recht auf Datenübertragbarkeit
• Beschwerde bei der zuständigen Aufsichtsbehörde

8. Kontakt
office@memorica.de

Stand: 2026 (Platzhalter)`;

const AGB_TEXT = `⚠ PLATZHALTER — KEIN RECHTSGÜLTIGER TEXT ⚠
Diese AGB werden vor dem offiziellen Launch durch vollständige, juristisch geprüfte Allgemeine Geschäftsbedingungen ersetzt.

§ 1 Geltungsbereich
Diese Nutzungsbedingungen gelten für die Nutzung der Memorica-App.

§ 2 Leistungsumfang
Memorica stellt eine App zur Verwaltung von Verkaufsständen, Flohmarkt-Artikeln und Markt-Sessions bereit.

§ 3 Nutzerkonto
Die Registrierung erfordert eine gültige E-Mail-Adresse. Der Nutzer ist für die Sicherheit seines Passworts verantwortlich.

§ 4 Haftung
Memorica übernimmt keine Haftung für Datenverluste. Regelmäßige Exports werden empfohlen (Einstellungen → Datenschutz).

§ 5 Kündigung
Der Nutzer kann sein Konto jederzeit selbst löschen (Einstellungen → Konto löschen). Alle Daten werden dabei sofort und unwiderruflich gelöscht.

§ 6 Kontakt
Memorica · office@memorica.de

Stand: 2026 (Platzhalter)`;


// ─────────────────────────────────────────────────────────────
// StaleSessionReminderSheet (P-Polish 1.1)
// Wird gezeigt wenn eine Trödel/Markt-Session > 12h alt ist und User die App
// erneut öffnet. Zwei Optionen: jetzt beenden ODER weiterlaufen lassen.
// ─────────────────────────────────────────────────────────────
function StaleSessionReminderSheet({ dark, t, store, session, onDismiss, onEndNow }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const startsIso = session?.starts_at;
  const hours = startsIso ? Math.floor((Date.now() - new Date(startsIso).getTime()) / 3600000) : 0;
  const dateStr = startsIso ? new Date(startsIso).toLocaleDateString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric"
  }) : "";
  const timeStr = startsIso ? new Date(startsIso).toLocaleTimeString("de-DE", {
    hour: "2-digit", minute: "2-digit"
  }) : "";
  const accent = store?.type === "flea" ? t.accent.purple : t.accent.green;
  return (
    <>
      <div onClick={onDismiss} style={{
        position: "fixed", inset: 0, zIndex: 96,
        background: dark ? "rgba(0,0,0,0.65)" : "rgba(0,0,0,0.4)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 400, radius: 24, padding: "22px 22px 18px", zIndex: 97 })}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <span style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
            background: accent.weak, color: accent.solid,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 20,
          }}>⏰</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: t.text, letterSpacing: "-0.3px" }}>
              Markt läuft seit {hours} Std
            </div>
            <div style={{ fontSize: 12, color: t.textSub, marginTop: 1 }}>
              Hast du vergessen ihn zu beenden?
            </div>
          </div>
        </div>
        <div style={{
          background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
          borderRadius: 12, padding: "10px 14px", marginBottom: 16,
          fontSize: 13, color: t.textSub, lineHeight: 1.5,
        }}>
          {session?.location && <div><b style={{ color: t.text }}>{session.location}</b></div>}
          <div>Gestartet am {dateStr} · {timeStr}</div>
          <div style={{ color: t.textMuted, fontSize: 11, marginTop: 3 }}>
            {store?.type === "flea" ? "Trödel" : "Standverkauf"} · {store?.name || ""}
          </div>
        </div>
        <button onClick={onEndNow} style={{
          width: "100%", padding: "13px",
          background: accent.solid, color: "#fff",
          border: "none", borderRadius: 12,
          fontFamily: F, fontWeight: 700, fontSize: 14,
          cursor: "pointer", marginBottom: 8,
          boxShadow: `0 4px 14px ${accent.weak}`,
        }}>Markt jetzt beenden</button>
        <button onClick={onDismiss} style={{
          width: "100%", padding: "11px",
          background: "transparent", border: "none",
          color: t.textMuted,
          fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer",
        }}>Weiterlaufen lassen</button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// LandingScreen (P-Landing-Redesign — Apple-Style fixed Layout)
// Reine Typografie-Übersicht statt Widget-Grid:
//   1. Header (Greeting + Settings-Icon)
//   2. Hero (Wochen-Umsatz monumental + Aufschlüsselung)
//   3. Aktiv-Karte (nur wenn Trödel/Markt-Session läuft)
//   4. Bereiche-Liste (alle Stores in einer Liste mit Trennlinien)
//   5. „+ Neuen Bereich erstellen"-Button
// ─────────────────────────────────────────────────────────────
function LandingScreen({
  authToken,
  authUser,
  userStores,
  activeSessions,
  landingData,
  loading,
  dark,
  showToast,
  onSelectStore,
  onCreateArea,
  onOpenSettings,
}) {
  const t = T[dark ? "dark" : "light"];
  // Scroll-to-top beim Einblenden der Landing (verhindert dass body-scrollTop
  // aus einem vorherigen Screen übrig bleibt).
  useEffect(() => { window.scrollTo(0, 0); }, []);
  // Skeleton mit 200ms Delay (P-Sheets-Skeleton): zeigen nur beim INITIAL-Load
  // (keine Daten gecacht). Bei Re-Fetch mit existierenden Daten kein Skeleton
  // (sonst flackert es jedes Mal beim Landing-Return).
  const isInitialLoad = !landingData?.byStore || Object.keys(landingData.byStore).length === 0;
  const showSkeleton = useDelayedSkeleton(!!loading && isInitialLoad, 200);
  const bg = dark ? "#0d1117" : "#dde8f8";

  // Greeting
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 5)  return "Gute Nacht";
    if (h < 11) return "Guten Morgen";
    if (h < 17) return "Guten Tag";
    if (h < 22) return "Guten Abend";
    return "Gute Nacht";
  })();

  // Wochen-Umsatz
  const wr = landingData?.weekRevenue || { online: 0, flea: 0, market: 0 };
  const weekTotal = (wr.online || 0) + (wr.flea || 0) + (wr.market || 0);

  // Aktive Session (erste die wir finden)
  const activeMatch = (() => {
    if (!activeSessions || !userStores) return null;
    for (const [storeId, session] of Object.entries(activeSessions)) {
      if (session && !session.ends_at) {
        const store = userStores.find(s => s.id === storeId);
        if (store) return { store, session };
      }
    }
    return null;
  })();

  // Bereiche-Liste: Online-Stores zuerst, dann Trödel, dann Markt
  const onlineStores = (userStores || []).filter(s => (s.type || "online") === "online")
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "de"));
  const fleaStore   = (userStores || []).find(s => s.type === "flea");
  const marketStore = (userStores || []).find(s => s.type === "market");
  const allAreas = [
    ...onlineStores.map(s => ({ store: s, mode: "online" })),
    ...(fleaStore   ? [{ store: fleaStore,   mode: "flea" }]   : []),
    ...(marketStore ? [{ store: marketStore, mode: "market" }] : []),
  ];

  // Format-Helpers
  const fmtCur = (n) => {
    if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
    return Number(n).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
  };
  const fmtSinceShort = (iso) => {
    if (!iso) return "";
    const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (min < 60) return `seit ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `seit ${h} Std`;
    return `seit ${Math.floor(h/24)} Tg`;
  };

  const accentForMode = (mode) => {
    if (mode === "flea")   return t.accent.purple;
    if (mode === "market") return t.accent.green;
    return t.accent.blue;
  };
  const labelForMode = (mode) => mode === "flea" ? "Trödel" : mode === "market" ? "Standverkauf" : "Online";
  const iconForMode = (mode, size = 20) => {
    if (mode === "flea")   return Icon.tag(size);
    if (mode === "market") return Icon.store ? Icon.store(size) : Icon.bars(size);
    return Icon.store ? Icon.store(size) : Icon.bars(size);
  };

  // Subtext pro Bereich.
  // P-Sheets-Skeleton: Online-Stores haben jetzt Priorität "Bereit > Offen >
  // Leer". Returns object { text, color } für reaktive Akzentfarbe (Bereit
  // = orange, prominent), oder { text, muted } für sonst.
  const subtextForArea = ({ store, mode }) => {
    const data = landingData?.byStore?.[store.id] || {};
    if (mode === "online") {
      const ready = data.ordersReady || 0;
      const open  = data.ordersOpen  || 0;
      if (ready > 0) {
        // Wichtigster Zustand: zu versenden — orange Akzent
        return {
          text: `${ready} zu versenden`,
          color: t.accent.orange.solid,
          weight: 600,
        };
      }
      if (open > 0) {
        return {
          text: `${open} ${open === 1 ? "offene Bestellung" : "offene Bestellungen"}`,
          color: t.textMuted,
        };
      }
      return { text: "Keine offenen Bestellungen", color: t.textMuted };
    }
    const sessionsCount = data.sessionsCount || 0;
    const total = data.totalRevenue || 0;
    if (sessionsCount === 0) return { text: "Noch kein Markt-Tag", color: t.textMuted };
    const sessLabel = sessionsCount === 1 ? "Markt-Tag" : "Markt-Tage";
    return {
      text: total > 0
        ? `${sessionsCount} ${sessLabel} · ${fmtCur(total)}`
        : `${sessionsCount} ${sessLabel}`,
      color: t.textMuted,
    };
  };

  // P-5-Punkte-Fix Block 3: Hero-Aufschlüsselung kompakt + alle existing
  // Bereiche zeigen (auch 0 €) damit User immer alle Modi überblickt.
  // Filter: nur Modi für die der User einen Bereich hat. Wenn nur Online da
  // → nur Online im Hero. Wenn alle 3 → alle 3.
  const hasOnline = onlineStores.length > 0;
  const hasFlea   = !!fleaStore;
  const hasMarket = !!marketStore;
  const breakdownItems = [];
  if (hasOnline) breakdownItems.push({ mode: "online", value: wr.online || 0, label: "online" });
  if (hasFlea)   breakdownItems.push({ mode: "flea",   value: wr.flea   || 0, label: "Trödel" });
  if (hasMarket) breakdownItems.push({ mode: "market", value: wr.market || 0, label: "Stand" });

  // Container-Style für Bereiche-Liste-Karte
  const cardBg = dark ? "rgba(28,28,30,0.85)" : "rgba(250,250,252,0.92)";

  // Section-Header (Caps-Schrift) — gleicher Padding-Left wie Container
  const SectionLabel = ({ children }) => (
    <div style={{
      fontSize: 11, fontWeight: 700, color: t.textMuted,
      letterSpacing: "0.10em", textTransform: "uppercase",
      paddingLeft: 18, marginBottom: 10,
    }}>{children}</div>
  );

  return (
    <div style={{
      minHeight: "100dvh",
      background: bg,
      fontFamily: F,
      color: t.text,
      paddingBottom: "calc(env(safe-area-inset-bottom, 24px) + 60px)",
    }}>
      <div style={{ maxWidth: 540, margin: "0 auto", padding: "0 14px" }}>

        {/* === SEKTION 1: Header === */}
        <div style={{
          paddingTop: "calc(env(safe-area-inset-top, 24px) + 18px)",
          paddingBottom: 8,
          paddingLeft: 4, paddingRight: 4,
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: t.text, letterSpacing: "-0.7px", lineHeight: 1.1 }}>
              Übersicht
            </div>
            <div style={{ fontSize: 14, color: t.textMuted, marginTop: 4 }}>
              {greeting}
            </div>
          </div>
          <button onClick={onOpenSettings} aria-label="Einstellungen" style={{
            width: 38, height: 38, borderRadius: 11,
            background: dark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.7)",
            border: `1px solid ${t.glassBorder}`,
            color: t.text, cursor: "pointer", padding: 0, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
        </div>

        {/* === SEKTION 2: Hero (kompakt — P-5-Punkte-Fix Block 3) === */}
        <div style={{
          paddingTop: 28, paddingBottom: 28,
          paddingLeft: 4, paddingRight: 4,
        }}>
          {showSkeleton ? (
            <div>
              <Skeleton width={90} height={11} radius={4} t={t} dark={dark} style={{ marginBottom: 10 }} />
              <Skeleton width="100%" height={20} radius={4} t={t} dark={dark} style={{ marginBottom: 8 }} />
              <Skeleton width={80} height={13} radius={4} t={t} dark={dark} />
            </div>
          ) : weekTotal <= 0 ? (
            <div style={{ fontSize: 15, color: t.textMuted, fontWeight: 400 }}>
              Diese Woche noch keine Verkäufe
            </div>
          ) : (
            <div>
              {/* Caps-Header */}
              <div style={{
                fontSize: 12, fontWeight: 600,
                color: t.textMuted,
                letterSpacing: "0.10em", textTransform: "uppercase",
                marginBottom: 8,
              }}>Diese Woche</div>

              {/* Inline-Aufschlüsselung */}
              {breakdownItems.length > 0 && (
                <div style={{
                  fontSize: 17, fontWeight: 500,
                  color: t.text,
                  fontVariantNumeric: "tabular-nums",
                  letterSpacing: "-0.2px",
                  lineHeight: 1.4,
                  display: "flex", flexWrap: "wrap", alignItems: "baseline",
                  gap: "4px 8px",
                }}>
                  {breakdownItems.map((b, i) => {
                    const accent = accentForMode(b.mode);
                    return (
                      <span key={b.mode} style={{ display: "inline-flex", alignItems: "baseline", gap: 4 }}>
                        {i > 0 && (
                          <span style={{ color: t.textMuted, fontSize: 14, marginRight: 4 }}>·</span>
                        )}
                        <span style={{ color: accent.solid, fontWeight: 600 }}>
                          {fmtCur(b.value).replace(",00 €", " €")}
                        </span>
                        <span style={{ color: t.textMuted, fontSize: 14, fontWeight: 400 }}>
                          {b.label}
                        </span>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Gesamt-Zeile */}
              <div style={{
                fontSize: 14, fontWeight: 400,
                color: t.textMuted,
                fontVariantNumeric: "tabular-nums",
                marginTop: 6,
              }}>
                gesamt <span style={{ color: t.text, fontWeight: 600 }}>
                  {fmtCur(weekTotal).replace(",00 €", " €")}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* === SEKTION 3: Aktiv (nur wenn Session läuft) === */}
        {activeMatch && (() => {
          const accent = accentForMode(activeMatch.store.type);
          const sessId = activeMatch.session.id;
          // Live-Stats sofern in landingData verfügbar (sessions wurden mit
          // include_stats geladen, embedded sales sind in lastSession)
          const storeData = landingData?.byStore?.[activeMatch.store.id] || {};
          const ls = storeData.lastSession || {};
          const liveSales = (ls.id === sessId ? (ls.sales || []) : [])
            .filter(x => !x.is_voided);
          const liveCount = liveSales.length;
          const liveTotal = liveSales.reduce((sum, x) => sum + Number(x.total || 0), 0);

          return (
            <div style={{ marginTop: 8, marginBottom: 8 }}>
              <SectionLabel>Aktiv</SectionLabel>
              <button onClick={() => onSelectStore(activeMatch.store)} style={{
                width: "100%",
                background: cardBg,
                border: `1px solid ${accent.solid}40`,
                borderRadius: 18,
                padding: "16px 18px",
                cursor: "pointer", fontFamily: F, textAlign: "left",
                display: "flex", alignItems: "center", gap: 14,
                boxShadow: `0 0 0 1px ${accent.solid}20, 0 6px 24px ${accent.weak}`,
                backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
              }}>
                <span style={{
                  width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                  background: accent.weak, color: accent.solid,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  position: "relative",
                }}>
                  {iconForMode(activeMatch.store.type, 22)}
                  <span style={{
                    position: "absolute", top: -2, right: -2,
                    width: 12, height: 12, borderRadius: "50%",
                    background: accent.solid,
                    border: `2px solid ${dark ? "#1c1c1e" : "#fff"}`,
                    animation: "pulse 1.6s ease-in-out infinite",
                  }}/>
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: t.text, letterSpacing: "-0.2px" }}>
                    {labelForMode(activeMatch.store.type)}-Markt · läuft
                  </div>
                  <div style={{ fontSize: 13, color: t.textSub, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {activeMatch.session.location || activeMatch.store.name}
                  </div>
                  <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
                    {fmtSinceShort(activeMatch.session.starts_at)}
                    {liveCount > 0 && ` · ${fmtCur(liveTotal)} · ${liveCount} ${liveCount === 1 ? "Verkauf" : "Verkäufe"}`}
                  </div>
                </div>
                <div style={{
                  flexShrink: 0,
                  display: "flex", alignItems: "center", gap: 4,
                  fontSize: 13, fontWeight: 700, color: accent.solid,
                }}>
                  Zum Markt
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                    <path d="M5 2L11 7L5 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </button>
            </div>
          );
        })()}

        {/* === SEKTION 4: Deine Bereiche === */}
        <div style={{ marginTop: 36 }}>
          {/* Skeleton-Liste während initialer Load */}
          {showSkeleton && (
            <>
              <SectionLabel>Deine Bereiche</SectionLabel>
              <div style={{
                background: cardBg,
                border: `1px solid ${t.glassBorder}`,
                borderRadius: 18,
                overflow: "hidden",
                backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
              }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{ borderTop: i === 0 ? "none" : `0.5px solid ${t.divider}` }}>
                    <SkeletonListItem t={t} dark={dark} />
                  </div>
                ))}
              </div>
            </>
          )}
          {!showSkeleton && allAreas.length > 0 && <SectionLabel>Deine Bereiche</SectionLabel>}
          {!showSkeleton && allAreas.length > 0 && (
            <div style={{
              background: cardBg,
              border: `1px solid ${t.glassBorder}`,
              borderRadius: 18,
              overflow: "hidden",
              backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
            }}>
              {allAreas.map((a, i) => {
                const accent = accentForMode(a.mode);
                const isFirst = i === 0;
                return (
                  <button key={a.store.id} onClick={() => onSelectStore(a.store)} style={{
                    width: "100%",
                    background: "transparent",
                    border: "none",
                    borderTop: isFirst ? "none" : `0.5px solid ${t.divider}`,
                    padding: "14px 18px",
                    cursor: "pointer", fontFamily: F, textAlign: "left",
                    display: "flex", alignItems: "center", gap: 14,
                    color: t.text,
                  }}>
                    <span style={{
                      width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                      background: accent.weak, color: accent.solid,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {iconForMode(a.mode, 18)}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 16, fontWeight: 600, color: t.text,
                        letterSpacing: "-0.2px",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>
                        {a.mode === "online" ? a.store.name : labelForMode(a.mode)}
                      </div>
                      {(() => {
                        const sub = subtextForArea(a);
                        return (
                          <div style={{
                            fontSize: 14,
                            color: sub.color || t.textMuted,
                            fontWeight: sub.weight || 400,
                            marginTop: 2,
                            fontVariantNumeric: "tabular-nums",
                          }}>
                            {sub.text}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ fontSize: 22, color: t.chevron, fontWeight: 200, flexShrink: 0 }}>›</div>
                  </button>
                );
              })}
            </div>
          )}

          {/* + Neuen Bereich erstellen — auch ohne Bereiche sichtbar */}
          <div style={{
            marginTop: allAreas.length > 0 ? 24 : 60,
            textAlign: "center",
            opacity: showSkeleton ? 0 : 1, // Während Skeleton verstecken
            transition: "opacity 0.3s ease",
          }}>
            {!showSkeleton && allAreas.length === 0 && (
              <div style={{ fontSize: 14, color: t.textMuted, marginBottom: 18, lineHeight: 1.5 }}>
                Du hast noch keinen Bereich.<br/>
                Lege einen an um zu starten.
              </div>
            )}
            <button onClick={onCreateArea} style={{
              padding: "12px 22px",
              background: "transparent",
              border: `1px solid ${t.glassBorder}`,
              borderRadius: 14,
              color: t.text,
              fontFamily: F, fontWeight: 600, fontSize: 14,
              cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M7 2v10M2 7h10"/>
              </svg>
              Neuen Bereich erstellen
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CreateAreaSheet (P-Landing-Redesign)
// Auswahl-Dialog "Welcher Bereich?" mit Online / Trödel / Standverkauf.
// Trödel/Standverkauf werden deaktiviert wenn schon angelegt.
// ─────────────────────────────────────────────────────────────
function CreateAreaSheet({ dark, t, userStores, authToken, onClose, onCreateOnline, onCreateMode, onSelectExisting, onJoinedViaCode, showToast }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const hasFlea   = !!(userStores || []).find(s => s.type === "flea");
  const hasMarket = !!(userStores || []).find(s => s.type === "market");
  const fleaStore   = (userStores || []).find(s => s.type === "flea");
  const marketStore = (userStores || []).find(s => s.type === "market");
  // P-5-Punkte-Fix Block 5: Step-State für 2-Schritt-Flow
  // step = "choose" → Hauptauswahl (Erstellen / Beitreten)
  //      = "create" → Mode-Auswahl (Online/Trödel/Markt)
  //      = "join"   → Code-Eingabe
  const [step, setStep] = useState("choose");
  const [joinCode, setJoinCode] = useState("");
  const [joinError, setJoinError] = useState(null);
  const [joining, setJoining] = useState(false);

  const handleJoin = async () => {
    const cleaned = joinCode.trim().toUpperCase().replace(/\s+/g, "");
    if (!cleaned) { setJoinError("Code eingeben"); return; }
    if (joining) return;
    setJoining(true); setJoinError(null);
    try {
      const res = await fetch(`/api/invitations/${encodeURIComponent(cleaned)}/redeem`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setJoinError(data.reason || "Code ungültig oder abgelaufen");
        setJoining(false);
        return;
      }
      // Success: data hat den joined-Store
      if (showToast) showToast(`${data.store?.name || "Bereich"} beigetreten`);
      onJoinedViaCode && onJoinedViaCode(data.store);
      setJoining(false);
    } catch (e) {
      setJoinError("Netzwerkfehler — versuche erneut");
      setJoining(false);
    }
  };

  const Option = ({ icon, accent, title, subtitle, disabled, disabledReason, onClick }) => (
    <button onClick={!disabled ? onClick : undefined} disabled={disabled} style={{
      width: "100%", padding: "14px 16px",
      background: "transparent", border: "none",
      borderTop: `0.5px solid ${t.divider}`,
      cursor: disabled ? "default" : "pointer",
      fontFamily: F, textAlign: "left",
      display: "flex", alignItems: "center", gap: 12,
      opacity: disabled ? 0.5 : 1,
    }}>
      <span style={{
        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
        background: accent.weak, color: accent.solid,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: t.text }}>{title}</div>
        <div style={{ fontSize: 12, color: t.textSub, marginTop: 1 }}>
          {disabled ? disabledReason : subtitle}
        </div>
      </div>
      {!disabled && <div style={{ fontSize: 18, color: t.chevron, fontWeight: 200, flexShrink: 0 }}>›</div>}
    </button>
  );

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 420, radius: 22, padding: 0, zIndex: 93 })}>
        {step === "choose" && (
          <>
            <div style={{ padding: "20px 22px 12px" }}>
              <ModalHeader t={t} dark={dark} F={F} title="Neuer Bereich" subtitle="Was möchtest du tun?" onClose={onClose} marginBottom={4} />
            </div>
            <div style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 14px) + 8px)" }}>
              <Option
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>}
                accent={t.accent.blue}
                title="Neuen Bereich erstellen"
                subtitle="Online-Store, Trödel, oder Standverkauf"
                onClick={() => setStep("create")}
              />
              <Option
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.07 0l3.54-3.54a5 5 0 0 0-7.07-7.07l-1.41 1.41"/><path d="M14 11a5 5 0 0 0-7.07 0l-3.54 3.54a5 5 0 0 0 7.07 7.07l1.41-1.41"/></svg>}
                accent={t.accent.purple}
                title="Bereich beitreten"
                subtitle="Mit Einladungscode"
                onClick={() => setStep("join")}
              />
            </div>
          </>
        )}

        {step === "create" && (
          <>
            <div style={{ padding: "20px 22px 12px" }}>
              <ModalHeader t={t} dark={dark} F={F}
                title="Bereich erstellen"
                subtitle="Wähle den Typ"
                onClose={onClose}
                marginBottom={4}
              />
              <button onClick={() => setStep("choose")} style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                background: "transparent", border: "none", cursor: "pointer",
                color: t.textMuted, fontFamily: F, fontSize: 13, padding: 0,
                marginTop: 4,
              }}>
                <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                  <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Zurück
              </button>
            </div>
            <div style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 14px) + 8px)" }}>
              <Option
                icon={Icon.store ? Icon.store(18) : Icon.bars(18)}
                accent={t.accent.blue}
                title="Online-Store"
                subtitle="eBay, Etsy, Website-Bestellungen"
                onClick={onCreateOnline}
              />
              <Option
                icon={Icon.tag(18)}
                accent={t.accent.purple}
                title="Trödel"
                subtitle="Flohmarkt mit Einzelstücken"
                disabled={hasFlea}
                disabledReason={fleaStore ? `Bereits angelegt — tippen um zu öffnen` : "Bereits angelegt"}
                onClick={hasFlea
                  ? () => onSelectExisting(fleaStore)
                  : () => onCreateMode("flea")
                }
              />
              <Option
                icon={Icon.store ? Icon.store(18) : Icon.bars(18)}
                accent={t.accent.green}
                title="Standverkauf"
                subtitle="Wochenmarkt mit Sortiment"
                disabled={hasMarket}
                disabledReason={marketStore ? `Bereits angelegt — tippen um zu öffnen` : "Bereits angelegt"}
                onClick={hasMarket
                  ? () => onSelectExisting(marketStore)
                  : () => onCreateMode("market")
                }
              />
            </div>
          </>
        )}

        {step === "join" && (
          <div style={{ padding: "20px 22px calc(env(safe-area-inset-bottom, 14px) + 18px)" }}>
            <ModalHeader t={t} dark={dark} F={F}
              title="Bereich beitreten"
              subtitle="Einladungscode eingeben"
              onClose={onClose}
              marginBottom={4}
            />
            <button onClick={() => { setStep("choose"); setJoinError(null); }} style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              background: "transparent", border: "none", cursor: "pointer",
              color: t.textMuted, fontFamily: F, fontSize: 13, padding: 0,
              marginTop: 4, marginBottom: 16,
            }}>
              <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Zurück
            </button>

            <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>
              Einladungscode
            </div>
            <div style={{
              background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
              borderRadius: 12, padding: "0 14px",
              marginBottom: 8,
              border: `1px solid ${joinError ? t.accent.red.solid + "60" : t.glassBorder}`,
            }}>
              <input
                autoFocus type="text"
                value={joinCode}
                onChange={e => { setJoinCode(e.target.value); setJoinError(null); }}
                placeholder="z.B. MEMOR-K7Q9-X3F2"
                style={{
                  width: "100%", background: "transparent", border: "none", outline: "none",
                  padding: "13px 0", fontSize: 16, color: t.text, fontFamily: F,
                  letterSpacing: "0.05em", textTransform: "uppercase",
                  fontVariantNumeric: "tabular-nums",
                }}
              />
            </div>
            {joinError ? (
              <div style={{ fontSize: 12, color: t.accent.red.solid, marginBottom: 12, paddingLeft: 4 }}>
                {joinError}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: t.textSub, marginBottom: 16, lineHeight: 1.5 }}>
                Du wirst Mitglied dieses Bereichs. Der Inhaber muss dich vorab einladen.
              </div>
            )}
            <button onClick={handleJoin} disabled={joining} style={{
              width: "100%", padding: "14px",
              background: t.accent.purple.solid, color: "#fff",
              border: "none", borderRadius: 14,
              fontFamily: F, fontWeight: 700, fontSize: 15,
              cursor: joining ? "default" : "pointer",
              opacity: joining ? 0.6 : 1,
            }}>{joining ? "Trete bei…" : "Beitreten"}</button>
          </div>
        )}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Main App
// ─────────────────────────────────────────────────────────────
function App() {
  const { vw, isMobile, isTablet, isDesktop, listW, containerMaxW } = useViewport();
  // ── Modal viewport tracking — kbOpen=true wenn iOS-Tastatur offen ist ──
  // Wird von ALLEN Inline-Modals (Add-Sheet, Detail, Delete-Confirm, Channels-Manager,
  // Status-Labels-Config) genutzt. Self-contained Modals wie GoalEditModal,
  // BatchSlotEditModal, NewChannelModal callen useModalViewport() selbst.
  const { height: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop } = useModalViewport();

  // ── Auth & multi-store state ──────────────────────────────
  // appScreen controls which screen is shown before the main app.
  // 'loading' → checking Supabase session
  // 'auth'    → login/register
  // 'store-selector' | 'create-store' | 'join-store' → onboarding
  // 'app'     → main app (a store has been selected)
  const [appScreen, setAppScreen]         = useState("loading");
  const [authToken, setAuthToken]         = useState(null);
  const [authUser, setAuthUser]           = useState(null);
  // Ref hält immer den neuesten Token für SyncManager-Retry ohne Re-Init
  const authTokenRef = useRef(null);
  useEffect(() => { authTokenRef.current = authToken; }, [authToken]);
  // SyncManager initialisieren wenn Token erscheint, Queue leeren bei Logout
  useEffect(() => {
    const sm = syncManager;
    if (!sm) return;
    if (authToken) {
      sm.setTokenGetter(() => authTokenRef.current);
      sm.init().catch(() => {});
    } else {
      sm.reset().catch(() => {});
    }
  // eslint-disable-next-line
  }, [!!authToken]);
  const [userStores, setUserStores]       = useState([]);
  const [currentStore, setCurrentStore]   = useState(null); // { id, name }
  const [currentRole, setCurrentRole]     = useState(null); // 'owner'|'admin'|...
  // User-Preferences (Migration 009). Aktuelle Keys:
  //   always_show_landing : bool — zwingt Landingpage statt Auto-Forward
  const [userPreferences, setUserPreferences] = useState({});
  // Globales Settings-Sheet auf der Landingpage (nicht store-spezifisch).
  const [globalSettingsOpen, setGlobalSettingsOpen] = useState(false);
  // Store-Delete-Bestätigung (Online + Mode-Stores, aus jeweiligen Settings)
  const [confirmDeleteStoreOpen, setConfirmDeleteStoreOpen] = useState(false);
  // Landing-Aggregat-Daten (P-Landing-Redesign): nur was die schlanke Landing
  // braucht — byStore-Counts + Wochen-Umsatz pro Modus.
  const [landingData, setLandingData] = useState({
    byStore: {}, weekRevenue: { online: 0, flea: 0, market: 0 },
  });
  // Sheet-State für "+ Neuen Bereich erstellen" (P-Landing-Redesign)
  const [createAreaOpen, setCreateAreaOpen] = useState(false);
  // Loading-State für Landing-Aggregat (P-Sheets-Skeleton)
  const [loadingLanding, setLoadingLanding] = useState(false);
  // 12h-Session-Reminder (P-Polish 1.1). Speichert per Session-ID welche
  // bereits dismissed wurden (Set, in-memory pro App-Run).
  const [staleSessionDismissed, setStaleSessionDismissed] = useState(() => new Set());
  // Wenn user "jetzt beenden" wählt, navigieren wir zum Tagesabschluss —
  // hier merken wir uns welche Session betroffen ist.
  const [staleEndTarget, setStaleEndTarget] = useState(null);

  // Per-store custom config (channels + status_labels). Loaded with store
  // selection. Defaults are sensible fallbacks if config is missing.
  const DEFAULT_CHANNELS = [
    { id: "ebay", name: "eBay", active: true, sort_order: 0, price: 27.99, fee_fixed: 3.81, fee_percent: 0, shipping: 3.80 },
    { id: "etsy", name: "Etsy", active: true, sort_order: 1, price: 27.99, fee_fixed: 3.54, fee_percent: 0, shipping: 3.80 },
  ];
  const DEFAULT_STATUS_LABELS = { Offen: "Offen", Bereit: "Bereit", Versendet: "Versendet", Retoure: "Retoure" };
  const [storeChannels, setStoreChannels]         = useState(DEFAULT_CHANNELS);
  const [storeStatusLabels, setStoreStatusLabels] = useState(DEFAULT_STATUS_LABELS);
  const [showChannelsConfig, setShowChannelsConfig]         = useState(false);
  const [showStatusLabelsConfig, setShowStatusLabelsConfig] = useState(false);
  const [expandedChannelId, setExpandedChannelId]           = useState(null);

  // Deep-Link aus dem BatchSlotEditModal: wenn beim Öffnen des Channels-Managers
  // bereits ein expandedChannelId gesetzt ist (vom Slot-"Kanal-Einstellungen"-Link),
  // nach dem Render zur Zeile scrollen.
  useEffect(() => {
    if (!showChannelsConfig || !expandedChannelId) return;
    const id = expandedChannelId;
    setTimeout(() => {
      const el = document.getElementById(`channel-row-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 360);
  }, [showChannelsConfig, expandedChannelId]);

  // Activity Log (Owner/Admin) — Modal-State, Filter, Cursor-Pagination
  // ── Dashboard / Bottom-Nav ────────────────────────────────
  // Top-level view switch on mobile/tablet. Desktop stays on existing layout
  // (currentView === "orders") but a Dashboard-button in the header toggles.
  // Default: dashboard on mobile (except helper-mode = workflow first), orders on desktop.
  const [currentView, setCurrentView]               = useState("orders"); // "dashboard" | "orders" | "more"
  // currentMode (Multi-Mode-Architektur, Migration 008):
  //   'online' → E-Commerce-Workflow (Default für bestehende Stores)
  //   'flea'   → Trödel-Hub (Stub)
  //   'market' → Standverkauf-Hub (Stub)
  // Wird in selectStore() aus storeWithRole.type gesetzt.
  const [currentMode, setCurrentMode] = useState("online");
  // Active sessions für flea/market-Stores des Users (für Landingpage-Anzeige).
  // Map<storeId, sessionObj | null>. Wird beim Landingpage-Mount gefüllt.
  const [activeSessions, setActiveSessions] = useState({});
  const [storeDashboardLayout, setStoreDashboardLayout] = useState(null);
  const [showCustomizeDashboard, setShowCustomizeDashboard] = useState(false);
  const [dashboardEditMode, setDashboardEditMode]   = useState(false); // long-press triggers wiggle/DnD

  const [showAuditLog, setShowAuditLog]             = useState(false);
  const [auditEntries, setAuditEntries]             = useState([]);
  const [auditFilter, setAuditFilter]               = useState({ entity_type: "all", days: 7 });
  const [auditLoading, setAuditLoading]             = useState(false);
  const [auditHasMore, setAuditHasMore]             = useState(false);

  // Scan-Modus (Versand-Workflow Phase 3b) — mobile-only barcode scanner
  const [showScanner, setShowScanner]               = useState(false);
  const [scannerState, setScannerState]             = useState("idle"); // idle | scanning | match | no_match | error
  const [scannerError, setScannerError]             = useState(null);
  const [lastMatchedOrder, setLastMatchedOrder]     = useState(null);
  const [lastUnmatchedCode, setLastUnmatchedCode]   = useState(null);
  const scannerControlsRef = useRef(null);
  const lastScanCodeRef    = useRef(null);   // dedup: same code within cooldown ignored
  const lastScanTimeRef    = useRef(0);
  const overlayTimerRef    = useRef(null);

  // Members & invitations management
  const [showMembers, setShowMembers]               = useState(false);
  const [membersList, setMembersList]               = useState([]);
  const [invitationsList, setInvitationsList]       = useState([]);
  const [memberSheetTab, setMemberSheetTab]         = useState("members"); // "members" | "invites"
  const [newInviteRole, setNewInviteRole]           = useState("fulfillment");
  const [createdInviteCode, setCreatedInviteCode]   = useState(null);
  const [confirmRemoveUserId, setConfirmRemoveUserId] = useState(null);
  const [memberLoading, setMemberLoading]           = useState(false);

  // Permission check helper — always safe to call even before role is loaded
  function can(perm) { return (ROLE_PERMS[currentRole] || []).includes(perm); }

  // ── Display helpers (channel/status name lookup, fallback-safe) ──
  function getChannelLabel(id) {
    if (!id) return "";
    const ch = storeChannels.find(c => c.id === id);
    if (ch) return ch.name;
    // Fallback: capitalize the raw id (orders that reference deleted/legacy channels)
    return id.charAt(0).toUpperCase() + id.slice(1);
  }
  function getStatusLabel(status) {
    return storeStatusLabels[status] || status;
  }
  // type-Filter: "sales" (default — Verkaufskanäle, akzeptieren Bestellungen)
  //              "reserved" (interne Pools wie Familie/Geschenk/Demo, keine Bestellungen)
  // Channels ohne explizites type werden als "sales" behandelt (Migrations-Default).
  function isSalesChannel(c)    { return (c?.type || "sales") === "sales"; }
  function isReservedChannel(c) { return c?.type === "reserved"; }
  function getActiveChannels() {
    // Sorted, only active sales-channels. Used in "create order" picker.
    return storeChannels
      .filter(c => c.active !== false && isSalesChannel(c))
      .slice()
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }
  function getAllChannels() {
    // Sorted, all sales-channels (including inactive) for filter dropdowns.
    // Reserved-pools sind hier nicht enthalten — sie tauchen nur im Lager auf.
    return storeChannels
      .filter(isSalesChannel)
      .slice()
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }
  function getReservedPools() {
    // Reserved-only pools (Familie, Geschenk, Demo, …) — nur fürs Lager.
    return storeChannels
      .filter(isReservedChannel)
      .slice()
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }

  // ── Per-channel finance helpers (Verkaufspreis, Gebühren, Versand) ──
  // Defaults preserve historical hardcoded values so old stores without
  // finance fields keep working identically.
  function getChannelFinance(kanalId) {
    const ch = storeChannels.find(c => c.id === kanalId);
    return {
      price:       Number.isFinite(ch?.price)       ? ch.price       : 27.99,
      fee_fixed:   Number.isFinite(ch?.fee_fixed)   ? ch.fee_fixed   : 0,
      fee_percent: Number.isFinite(ch?.fee_percent) ? ch.fee_percent : 0,
      shipping:    Number.isFinite(ch?.shipping)    ? ch.shipping    : 3.80,
    };
  }
  function getOrderRevenue(o) { return getChannelFinance(o.kanal).price; }
  function getOrderShipping(o) { return getChannelFinance(o.kanal).shipping; }
  function getOrderFee(o) {
    const c = getChannelFinance(o.kanal);
    return c.fee_fixed + c.price * (c.fee_percent || 0) / 100;
  }
  // Slugify channel name → channel id (lowercase, alphanumeric + dash)
  function slugifyChannelName(name) {
    return (name || "")
      .toLowerCase()
      .normalize("NFD").replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 32) || `channel-${Date.now()}`;
  }

  async function saveStoreConfig(patch) {
    if (!currentStore || !authToken) return false;
    const res = await fetch(`/api/stores/${currentStore.id}/config`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      showToast(d.reason || "Speichern fehlgeschlagen", "error");
      return false;
    }
    const updated = await res.json();
    if (updated.channels) {
      // Defensive Merge: wenn der deployed Worker das `type`-Feld (oder andere
      // neue Felder wie price/fee_*) noch nicht kennt, fehlen sie in der
      // Antwort und der Channel würde stillschweigend als "sales" behandelt.
      // Daher: Werte aus dem Request für jeden Channel mit gleicher ID
      // bevorzugen, falls die Antwort sie nicht enthält.
      const requestChannels = Array.isArray(patch.channels) ? patch.channels : null;
      const FIELDS = ["type", "price", "fee_fixed", "fee_percent", "shipping"];
      const merged = updated.channels.map(srvCh => {
        if (!requestChannels) return srvCh;
        const reqCh = requestChannels.find(r => r && r.id === srvCh.id);
        if (!reqCh) return srvCh;
        const out = { ...srvCh };
        for (const f of FIELDS) {
          if (out[f] === undefined && reqCh[f] !== undefined) out[f] = reqCh[f];
        }
        return out;
      });
      setStoreChannels(merged);
    }
    if (updated.status_labels) setStoreStatusLabels(updated.status_labels);
    return true;
  }

  // Channel löschen — wird vom Delete-Modal getriggert nach Bestätigung.
  // deleteChannelId: id != null → DeleteChannelConfirmModal öffnet sich.
  async function persistDeleteChannel(channelId) {
    const ch = storeChannels.find(c => c.id === channelId);
    if (!ch) return;
    // Sicherheit: keinen Sales-Channel löschen, der noch Bestellungen hat.
    const ordersUsing = orders.filter(o => o.kanal === channelId).length
                     + archive.filter(o => o.kanal === channelId).length;
    if (ordersUsing > 0) {
      showToast(`Kanal hat noch ${ordersUsing} Bestellungen — deaktivieren statt löschen`, "warning");
      return;
    }
    if (storeChannels.filter(isSalesChannel).length <= 1 && isSalesChannel(ch)) {
      showToast("Mindestens ein Verkaufskanal muss bestehen", "warning");
      return;
    }
    const next = storeChannels.filter(c => c.id !== channelId);
    setStoreChannels(next);
    const ok = await saveStoreConfig({ channels: next });
    if (ok) {
      showToast(`"${ch.name}" gelöscht`, "success");
      haptic(15);
    }
  }

  // ── Member-Management API helpers ─────────────────────────
  async function loadMembersAndInvitations() {
    if (!currentStore || !authToken) return;
    setMemberLoading(true);
    try {
      const headers = { "Authorization": `Bearer ${authToken}` };
      const [memRes, invRes] = await Promise.all([
        fetch(`/api/stores/${currentStore.id}/members`, { headers }),
        fetch(`/api/stores/${currentStore.id}/invitations`, { headers }),
      ]);
      if (memRes.ok) setMembersList(await memRes.json());
      if (invRes.ok) setInvitationsList(await invRes.json());
    } catch (e) { console.error("loadMembers failed:", e); }
    setMemberLoading(false);
  }

  async function createInvitation(role) {
    const res = await fetch(`/api/stores/${currentStore.id}/invitations`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify({ role }),
    });
    if (res.ok) {
      const inv = await res.json();
      setCreatedInviteCode(inv.code);
      loadMembersAndInvitations();
    } else {
      showToast("Einladung fehlgeschlagen", "error");
    }
  }

  async function revokeInvitation(invId) {
    const res = await fetch(`/api/stores/${currentStore.id}/invitations/${invId}`, {
      method: "DELETE",
      headers: { "Authorization": `Bearer ${authToken}` },
    });
    if (res.ok) loadMembersAndInvitations();
  }

  async function changeMemberRole(targetUserId, newRole) {
    const res = await fetch(`/api/stores/${currentStore.id}/members/${targetUserId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify({ role: newRole }),
    });
    if (res.ok) loadMembersAndInvitations();
    else { const d = await res.json(); showToast(d.reason || "Rollenwechsel fehlgeschlagen", "error"); }
  }

  async function removeMember(targetUserId) {
    const res = await fetch(`/api/stores/${currentStore.id}/members/${targetUserId}`, {
      method: "DELETE",
      headers: { "Authorization": `Bearer ${authToken}` },
    });
    if (res.ok) {
      setConfirmRemoveUserId(null);
      loadMembersAndInvitations();
    } else { const d = await res.json(); showToast(d.reason || "Entfernen fehlgeschlagen", "error"); }
  }

  const [orders, setOrders]               = useState([]);
  const [hasMoreOrders, setHasMoreOrders] = useState(false);
  const [ordersCursor, setOrdersCursor]   = useState(null);
  const [dark, setDark]                   = useState(() => {
    try {
      const stored = localStorage.getItem("memorica_dark");
      if (stored === "1") return true;
      if (stored === "0") return false;
    } catch {}
    // Fallback: Systempräferenz
    try { return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches || false; } catch { return false; }
  });
  const [showSheet, setShowSheet]         = useState(false);
  const [showDetail, setShowDetail]       = useState(false);
  const [detailOrder, setDetailOrder]     = useState(null);
  const [detailDeleteConfirm, setDetailDeleteConfirm] = useState(false);
  const [form, setForm]                   = useState(emptyForm());
  const [tabIndex, setTabIndex]           = useState(1); // Default: Bereit-Tab
  const [filterKanal, setFilterKanal]     = useState("Alle");
  const [showKanalFilter, setShowKanalFilter] = useState(false);
  const [search, setSearch]               = useState("");
  const [showSearch, setShowSearch]       = useState(false);
  // Toast-Stack: Array von { id, message, type, expiresAt }
  // type: "success" (default) | "error" | "info" | "warning"
  // Stack zeigt bis zu 3 gleichzeitig, FIFO. Auto-expire nach 2.4s.
  const [toasts, setToasts]               = useState([]);
  // Tracking-Confirm-Modal — gezeigt nach OCR-Extraktion damit User
  // den richtigen Kandidaten wählen oder manuell eingeben kann.
  // Shape: { orderId, candidates, ocrText, manualValue } | null
  const [trackingConfirm, setTrackingConfirm] = useState(null);
  // FulfillmentSweepModal entfernt — Versand läuft jetzt ausschliesslich über
  // den Scanner (Live-Camera + manueller Photo-Fallback). Single source of truth.
  const [showSettings, setShowSettings]   = useState(false);
  // Kanal-Filter-Picker (kleines Sheet aus Settings-Panel-Row)
  const [filterPickerOpen, setFilterPickerOpen] = useState(false);
  const [sortBy, setSortBy]               = useState(() => {
    try { return localStorage.getItem("memorica_sortBy") || "datum_desc"; } catch { return "datum_desc"; }
  });
  // Datum-Bereich-Filter: 30, 90 oder -1 (alles). Nur auf abgeschlossene (Versendet/Retoure) angewandt.
  const [dateRange, setDateRange]         = useState(() => {
    try { const v = parseInt(localStorage.getItem("memorica_dateRange") || "30", 10); return isNaN(v) ? 30 : v; } catch { return 30; }
  });
  const [sendungOrder, setSendungOrder]   = useState(null);
  const [showStats, setShowStats]         = useState(false);
  const [chartMode, setChartMode]         = useState("weeks");
  const [settingsDragY, setSettingsDragY] = useState(0);
  // addDragY/addStartY entfernt — Sheet ist jetzt top-anchored, Swipe-to-close
  // war an Bottom-Sheet-Layout gekoppelt und nicht mehr nötig.
  const [goal, setGoal]                   = useState(100);
  const [showGoalEdit, setShowGoalEdit]   = useState(false);
  // Legacy `batchInvestment` State + InvestEdit-Popup wurden entfernt.
  // Investment kommt jetzt ausschliesslich pro Batch (Lager-Modal). Die Summe
  // aller Batch-Investments fließt als `totalInvestment` in den Cashflow ein.
  const [showGuvDetails, setShowGuvDetails]   = useState(false);
  const [batches, setBatches]                 = useState([]);
  const [showLager, setShowLager]             = useState(false);
  const [lagerDragY, setLagerDragY]           = useState(0);
  const [editBatchId, setEditBatchId]         = useState(null);
  const [newBatchOpen, setNewBatchOpen]       = useState(false);
  const [nbName, setNbName]                   = useState("");
  // String-States damit User leer/mehrstellig tippen können (vorher coercion → Tastatur weg).
  const [nbStueck, setNbStueck]               = useState("100");
  const [nbInvest, setNbInvest]               = useState("1000");
  const [editPool, setEditPool]               = useState(null); // { batchId, kanal }
  // Set<batchId> der aktuell eingeklappten Batches im Lager-Tab.
  // Bewusst nicht persistiert — gilt nur für die aktuelle Session.
  const [collapsedBatches, setCollapsedBatches] = useState(() => new Set());
  // Channel-Verwaltung — nach Cleanup (Pass 1):
  //   • Anlegen + alle globalen Settings: Channels-Manager (Settings → Verkaufskanäle)
  //   • Pro-Batch Pool/Threshold: BatchSlotEditModal (Lager-Slot-Click)
  //   • Löschen: nur über Channels-Manager → Finanz-Sektion → "Löschen"-Button
  const [deleteChannelId, setDeleteChannelId]   = useState(null);
  // editChannelInBatch: { batchId, channelId } | null → BatchSlotEditModal
  const [editChannelInBatch, setEditChannelInBatch] = useState(null);
  // Add-Channel-Modal Status — replaces alte prompt+confirm-Sequenz.
  const [newChannelOpen, setNewChannelOpen]         = useState(false);
  const [confirmNoSendung, setConfirmNoSendung] = useState(false);
  const [confirmRetoure, setConfirmRetoure]     = useState(null); // order id pending
  // Bereit → Versendet Workflow:
  //   shipChoice   = ShipChoiceModal offen (Order-Obj). Erste Stufe: Scan vs. Manuell.
  //   manualShip   = ManualShipConfirmModal offen. Zweite Stufe: Reason + Checkbox + Confirm.
  // Default-Pfad: Scanner. Manueller Pfad nur als Ausnahme (Persönliche Übergabe etc.).
  const [shipChoice, setShipChoice]               = useState(null); // Order-Obj
  const [manualShip, setManualShip]               = useState(null); // Order-Obj
  const [helperSuccessBanner, setHelperSuccessBanner] = useState(null); // { id, bestellnummer } für Erfolgsbanner mit Undo
  const [confirmUnretoure, setConfirmUnretoure] = useState(null); // order id pending revert from Retoure
  const [collapsedMonths, setCollapsedMonths]   = useState({}); // { "2026-3": true } means April 2026 collapsed
  const [archive, setArchive]                   = useState([]);
  // Helfer-Modus: vereinfachte Ansicht für vertraute Personen (z.B. Vater im Urlaub)
  // helperModeManual ist der vom User toggelte Wert. Der effektive Modus
  // wird unten berechnet und kann durch die eingeloggte Identität erzwungen werden.
  const [helperModeManual, setHelperModeManual] = useState(false);
  // Hilfe-Block im Helfer-Modus auf Mobile/Tablet: Auf/Zu-Status pro Gerät persistieren.
  // Initial: false (= aufgeklappt) damit Vater es beim ersten Login sieht.
  const [helpCollapsed, setHelpCollapsed] = useState(() => {
    try { return localStorage.getItem("memorica_help_collapsed") === "1"; } catch { return false; }
  });
  const toggleHelp = () => {
    setHelpCollapsed(c => {
      const next = !c;
      try { localStorage.setItem("memorica_help_collapsed", next ? "1" : "0"); } catch {}
      return next;
    });
    haptic(8);
  };
  const [helperStartCount, setHelperStartCount] = useState(0);
  // Cloudflare Access Identity: wer ist eingeloggt?
  const [userEmail, setUserEmail]               = useState(null);
  const [identityLoaded, setIdentityLoaded]     = useState(false);
  const [showArchive, setShowArchive]           = useState(false);
  const [archiveDragY, setArchiveDragY]         = useState(0);
  const [statsDragY, setStatsDragY]             = useState(0);
  const [confirmEmptyArchive, setConfirmEmptyArchive] = useState(false);
  const [lastKanal, setLastKanal]         = useState("etsy");
  const [loaded, setLoaded]               = useState(false);
  const [listVisible, setListVisible]     = useState(true);
  const [slideDir, setSlideDir]           = useState(0);

  const searchRef    = useRef(null);
  const inputRef     = useRef(null);
  const listRef      = useRef(null);
  // Inline-Label-Upload (von Offen-Pille direkt, ohne Detail-Modal-Umweg).
  // Single hidden input + Ref auf die Ziel-Order — wird bei jedem Klick neu gesetzt.
  const labelUploadInputRef = useRef(null);
  const pendingLabelOrderIdRef = useRef(null);
  const triggerLabelUpload = (orderId) => {
    pendingLabelOrderIdRef.current = orderId;
    labelUploadInputRef.current?.click();
  };
  const tabIndexRef  = useRef(0);
  const touchStartX  = useRef(null);
  const switchingRef = useRef(false);
  const switchTabRef = useRef(null);
  const saveTimerRef = useRef(null);
  const loadStoresRequestRef = useRef(0); // Cancellation counter für loadUserStores
  const settingsStartY = useRef(null);
  // addStartY weggefallen mit Swipe-to-close (siehe addDragY-Notiz oben).
  const lagerStartY = useRef(null);
  const archiveStartY = useRef(null);
  const statsStartY = useRef(null);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error

  // ── State-Reset-Helpers ───────────────────────────────────
  // resetStoreState: leert alle store-spezifischen States.
  // Aufgerufen bei: Store-Wechsel, Store-Löschen, Logout.
  const resetStoreState = () => {
    setOrders([]);
    setBatches([]);
    setArchive([]);
    setMembersList([]);
    setInvitationsList([]);
    setCurrentView("orders");
    setShowSheet(false);
    setShowDetail(false);
    setDetailOrder(null);
    setForm(emptyForm());
    setFilterKanal("Alle");
    setSearch("");
    setLoaded(false);
    setShowArchive(false);
    setShowSettings(false);
    setShowLager(false);
    setShowStats(false);
    setAuditEntries([]);
    setShowAuditLog(false);
    setHelperModeManual(false);
    setStoreDashboardLayout(null);
    setCreatedInviteCode(null);
    setConfirmDeleteStoreOpen(false);
    setTabIndex(1);
    setGoal(100);
    setCollapsedBatches(new Set());
    setCollapsedMonths({});
    setScannerState("idle");
    setLastMatchedOrder(null);
    setStoreChannels(DEFAULT_CHANNELS);
    setStoreStatusLabels(DEFAULT_STATUS_LABELS);
  };

  // ── Supabase auth init ────────────────────────────────────
  // On mount: check for an existing session. If found, load user's stores.
  // Also subscribe to auth state changes (token refresh, sign-out).
  useEffect(() => {
    if (!supabase) {
      // Supabase not yet configured (REPLACE_ME placeholders) → skip to app directly
      // so existing Cloudflare Access users are not locked out during migration.
      setIdentityLoaded(true);
      setAppScreen("app");
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setAuthToken(session.access_token);
        setAuthUser(session.user);
        setUserEmail(session.user.email || null);
        setIdentityLoaded(true);
        loadUserStores(session.access_token);
      } else {
        setIdentityLoaded(true);
        setAppScreen("auth");
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT" || !session) {
        resetStoreState();
        setUserStores([]);
        setUserPreferences({});
        setCurrentStore(null);
        setCurrentRole(null);
        setCurrentMode("online");
        setAuthToken(null);
        setAuthUser(null);
        setAppScreen("auth");
      } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        setAuthToken(session.access_token);
        setAuthUser(session.user);
        setUserEmail(session.user.email || null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function loadUserStores(token) {
    const requestId = ++loadStoresRequestRef.current;
    try {
      // Stores + Preferences parallel laden — beides nötig für Auto-Forward-Decision.
      const [storesRes, prefsRes] = await Promise.all([
        fetch("/api/stores", { headers: { "Authorization": `Bearer ${token}` } }),
        fetch("/api/preferences", { headers: { "Authorization": `Bearer ${token}` } }),
      ]);
      if (requestId !== loadStoresRequestRef.current) return; // superseded by newer call
      if (!storesRes.ok) { setAppScreen("auth"); return; }
      const stores = await storesRes.json();
      const prefs = prefsRes.ok ? (await prefsRes.json()).preferences || {} : {};
      setUserStores(stores);
      setUserPreferences(prefs);

      // Auto-Forward-Logik (Block 1):
      //   genau 1 Store + always_show_landing nicht aktiv → direkt rein
      //   sonst → Landingpage
      // Active-Sessions für flea/market-Stores werden parallel weitergeladen
      // (auch beim Auto-Forward-Pfad, da der Store vielleicht eine aktive
      // Session hat die wir später anzeigen wollen).
      loadActiveSessions(stores, token);
      // Landing-Aggregat-Daten parallel laden (Counts, weekRevenue, recentActivity)
      loadLandingData(stores, token);

      const wantsLanding = prefs.always_show_landing === true;
      if (!wantsLanding && stores.length === 1) {
        // Direkt-Forward: Store auswählen, currentMode + appScreen werden in
        // selectStore() gesetzt.
        selectStore(stores[0], token);
      } else {
        setAppScreen("store-selector");
      }
    } catch (e) {
      console.warn("loadUserStores failed:", e);
      setAppScreen("store-selector");
    }
  }

  // Hard-Delete eines Stores (nur Owner). CASCADE räumt items/sessions/sales/
  // orders/batches/memberships auf. Nach Delete → zurück zur Landing.
  async function destroyCurrentStore() {
    if (!currentStore || !authToken) return;
    try {
      const res = await fetch(`/api/stores/${currentStore.id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        showToast(d.reason || "Löschen fehlgeschlagen", "error");
        return;
      }
      const remaining = userStores.filter(s => s.id !== currentStore.id);
      setUserStores(remaining);
      resetStoreState();
      setCurrentStore(null);
      setCurrentRole(null);
      setCurrentMode("online");
      setAppScreen("store-selector");
      showToast("Store gelöscht");
    } catch (e) {
      showToast("Netzwerkfehler", "error");
    }
  }

  // Speichert User-Preferences via Worker (PATCH-Merge).
  // Lokaler State wird optimistisch sofort aktualisiert.
  async function saveUserPreferences(patch) {
    const next = { ...userPreferences, ...patch };
    setUserPreferences(next); // optimistic
    try {
      const res = await fetch("/api/preferences", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authToken}`,
        },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        // Revert wenn fehlgeschlagen
        setUserPreferences(userPreferences);
        showToast("Einstellung konnte nicht gespeichert werden", "error");
      } else {
        const data = await res.json();
        if (data?.preferences) setUserPreferences(data.preferences);
      }
    } catch (e) {
      setUserPreferences(userPreferences);
      showToast("Netzwerkfehler beim Speichern", "error");
    }
  }

  // ─────────────────────────────────────────────────────────────
  // loadLandingData (P-Landing-Redesign — schlanker)
  // Lädt nur was die neue Landingpage braucht:
  //   • weekRevenue.{online, flea, market}  → Hero
  //   • byStore[id]: { ordersOpen, sessionsCount, totalRevenue, lastSession }
  //                                          → Bereiche-Liste-Subtexte
  // Kein recentActivity, kein topItem. Bei flea/market ein einziger
  // Sessions-Call mit ?include_stats=1 statt N+1 Detail-Calls.
  // ─────────────────────────────────────────────────────────────
  async function loadLandingData(stores, token) {
    const t = token || authToken;
    if (!t || !stores || stores.length === 0) {
      setLandingData({ byStore: {}, weekRevenue: { online: 0, flea: 0, market: 0 } });
      setLoadingLanding(false);
      return;
    }
    setLoadingLanding(true);
    // Wochenfenster Mo 00:00 → jetzt
    const now = new Date();
    const day = (now.getDay() + 6) % 7; // Mo=0 ... So=6
    const weekStart = new Date(now); weekStart.setDate(now.getDate() - day);
    weekStart.setHours(0, 0, 0, 0);
    const weekStartIso = weekStart.toISOString();

    const headers = { "Authorization": `Bearer ${t}` };
    const byStore = {};
    const weekRevenue = { online: 0, flea: 0, market: 0 };

    await Promise.all(stores.map(async (s) => {
      const apiBase = `/api/stores/${s.id}`;
      const data = { store: s };
      try {
        if ((s.type || "online") === "online") {
          const r = await fetch(`${apiBase}/orders`, { headers });
          if (r.ok) {
            const orders = await r.json();
            data.ordersOpen   = orders.filter(o => o.status === "Offen").length;
            data.ordersReady  = orders.filter(o => o.status === "Bereit").length;
            data.ordersTotal  = orders.length;
            // P-5-Punkte-Fix Block 3: Online-Wochen-Umsatz korrekt zählen.
            // Bug: vorher wurde o.datum genutzt (Bestelldatum) — Bestellungen
            // die diese Woche VERSENDET wurden aber älter bestellt waren,
            // wurden nicht gezählt. Neu: o.updated_at als Status-Änderungs-
            // Indikator (= effektiv das Versand-Datum, weil Versand setzt
            // den Status-Wechsel + updated_at).
            for (const o of orders) {
              if (o.status !== "Versendet" && o.status !== "Retoure") continue;
              // Bevorzugt updated_at, fallback datum, fallback created_at
              const d = o.updated_at || o.datum || o.created_at;
              if (!d || d < weekStartIso) continue;
              weekRevenue.online += Number(o.price || 0);
            }
          }
        } else if (s.type === "flea" || s.type === "market") {
          // Sessions mit embedded sales (Worker-Endpoint, P-Polish-Refactor):
          // 1 Anfrage statt N+1 Detail-Calls.
          const sr = await fetch(`${apiBase}/sessions?include_stats=1&limit=2000`, { headers });
          if (sr.ok) {
            const rawSr = await sr.json();
            const sessions = Array.isArray(rawSr) ? rawSr : (rawSr.sessions || []);
            data.sessionsCount = sessions.length;
            data.lastSession = sessions[0] || null;
            let storeTotalRevenue = 0;
            for (const sess of sessions) {
              const sessionRevenue = (sess.sales || [])
                .filter(x => !x.is_voided)
                .reduce((sum, x) => sum + Number(x.total || 0), 0);
              storeTotalRevenue += sessionRevenue;
              // Wochen-Umsatz: Sessions die diese Woche begannen
              // (Approximation — die Embedded-Sales haben kein sold_at,
              // aber ein Markt-Tag ist typisch ≤ 1 Tag.)
              if (sess.starts_at && sess.starts_at >= weekStartIso) {
                weekRevenue[s.type] += sessionRevenue;
              }
            }
            data.totalRevenue = storeTotalRevenue;
          }
        }
      } catch (e) {
        console.warn("loadLandingData store failed:", s.id, e);
      }
      byStore[s.id] = data;
    }));

    setLandingData({ byStore, weekRevenue });
    setLoadingLanding(false);
  }

  // Lädt parallel die aktive Session (ends_at IS NULL) für jeden flea/market-Store.
  // Resultat in activeSessions Map: { [storeId]: sessionObj | null }.
  // Online-Stores werden übersprungen — die haben keine Sessions.
  async function loadActiveSessions(stores, token) {
    const modeStores = stores.filter(s => s.type === "flea" || s.type === "market");
    if (modeStores.length === 0) { setActiveSessions({}); return; }
    const t = token || authToken;
    const results = await Promise.all(modeStores.map(async (s) => {
      try {
        const r = await fetch(`/api/stores/${s.id}/sessions/active`, {
          headers: { "Authorization": `Bearer ${t}` },
        });
        if (!r.ok) return [s.id, null];
        return [s.id, await r.json()];
      } catch {
        return [s.id, null];
      }
    }));
    setActiveSessions(Object.fromEntries(results));
  }

  // P-Sheets-Skeleton: Re-Fetch beim Landing-Return.
  // Wenn der User von Trödel/Markt-Hub zurück zur Landing kommt (z.B. nach
  // "Markt beenden"), müssen activeSessions + landingData reaktiv aktualisiert
  // werden. Sonst bleibt der "Markt LÄUFT"-Banner stehen bis App-Restart.
  // Triggered: jedes Mal wenn appScreen zu "store-selector" wechselt UND Stores
  // bereits geladen sind. Initialer Load wird durch loadUserStores abgedeckt
  // (das setzt appScreen zu store-selector und ruft loadActive/loadLanding
  // selbst), daher prüfen wir userStores.length > 0.
  useEffect(() => {
    if (appScreen !== "store-selector") return;
    if (!authToken || !userStores || userStores.length === 0) return;
    // Background-Refresh ohne Skeleton-Flicker
    loadActiveSessions(userStores, authToken);
    loadLandingData(userStores, authToken);
    // eslint-disable-next-line
  }, [appScreen]);

  // Mode-Store anlegen (Trödel oder Standverkauf).
  // Frontend-Pre-Check: User hat bereits einen Store dieses Typs → direkt
  // dorthin weiterleiten ohne API-Call.
  // Server-Side-Pre-Check (HTTP 409): Worker liefert den existierenden Store
  // zurück → Frontend nimmt ihn und ruft selectStore.
  async function handleCreateModeStore(type) {
    if (type !== "flea" && type !== "market") return;
    const existing = userStores.find(s => s.type === type);
    if (existing) {
      selectStore(existing);
      return;
    }
    try {
      const res = await fetch("/api/stores", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          name: type === "flea" ? "Trödel" : "Standverkauf",
          type,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409 && data?.store) {
        // Server kennt einen existierenden Store → übernehmen
        const list = userStores.find(s => s.id === data.store.id) ? userStores : [...userStores, data.store];
        setUserStores(list);
        selectStore(data.store);
        return;
      }
      if (!res.ok) {
        showToast(data?.reason || "Anlegen fehlgeschlagen", "error");
        return;
      }
      // Erfolgreich angelegt → Liste neu laden, dann zum neuen Store
      const created = data;
      setUserStores(prev => [...prev, created]);
      selectStore(created);
    } catch (e) {
      showToast("Netzwerkfehler beim Anlegen", "error");
    }
  }

  function selectStore(storeWithRole, token) {
    resetStoreState();
    setCurrentStore({ id: storeWithRole.id, name: storeWithRole.name, type: storeWithRole.type || "online" });
    setCurrentRole(storeWithRole.role);
    // currentMode aus dem Store-Type ableiten (Multi-Mode-Architektur).
    // 'online' → bestehende Online-Order-Logik (Dashboard, Bestellungen, Scanner)
    // 'flea'   → flea-hub Stub (Trödel)
    // 'market' → market-hub Stub (Standverkauf)
    const mode = storeWithRole.type === "flea" || storeWithRole.type === "market"
      ? storeWithRole.type
      : "online";
    setCurrentMode(mode);

    // Apply per-store config if present (fall back to defaults otherwise)
    if (Array.isArray(storeWithRole.channels) && storeWithRole.channels.length > 0) {
      setStoreChannels(storeWithRole.channels);
    } else {
      setStoreChannels(DEFAULT_CHANNELS);
    }
    if (storeWithRole.status_labels && typeof storeWithRole.status_labels === "object") {
      setStoreStatusLabels({ ...DEFAULT_STATUS_LABELS, ...storeWithRole.status_labels });
    } else {
      setStoreStatusLabels(DEFAULT_STATUS_LABELS);
    }
    // Helper-mode: fulfillment role maps to the existing helperMode UX (nur online)
    if (storeWithRole.role === "fulfillment" && mode === "online") {
      setHelperModeManual(true);
      setTabIndex(1);
    }
    // Mode-abhängiges Routing:
    if (mode === "flea") {
      setAppScreen("flea-hub");
      return;
    }
    if (mode === "market") {
      setAppScreen("market-hub");
      return;
    }
    // Online-Modus (Default) → bestehender Flow
    setCurrentView("orders");
    setAppScreen("app");
    // Kick off data load for this store
    loadStoreData(storeWithRole.id, token || authToken, storeWithRole.role);
    // Load own dashboard_layout (separate request — non-blocking)
    loadMyDashboardLayout(storeWithRole.id, token || authToken, storeWithRole.role);
  }

  // Load own membership row (incl. dashboard_layout). Falls back to role default.
  async function loadMyDashboardLayout(storeId, token, role) {
    try {
      const res = await fetch(`/api/stores/${storeId}/me`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (!res.ok) {
        setStoreDashboardLayout(defaultLayoutForRole(role));
        return;
      }
      const me = await res.json();
      if (me.dashboard_layout && Array.isArray(me.dashboard_layout.widgets)) {
        setStoreDashboardLayout(me.dashboard_layout);
      } else {
        setStoreDashboardLayout(defaultLayoutForRole(role));
      }
    } catch {
      setStoreDashboardLayout(defaultLayoutForRole(role));
    }
  }

  async function saveDashboardLayout(layout) {
    if (!currentStore || !authToken) return;
    setStoreDashboardLayout(layout);
    try {
      await fetch(`/api/stores/${currentStore.id}/me/dashboard_layout`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
        body: JSON.stringify(layout),
      });
    } catch (e) { console.warn("save layout failed:", e); }
  }

  // ── Orders: DB ↔ Frontend field conversion ────────────────
  function orderFromDb(o) {
    if (!o) return null;
    return {
      id: o.id,
      bestellnummer: o.bestellnummer || "",
      kanal: o.kanal || "",
      status: o.status || "Offen",
      datum: o.datum || "",
      notiz: o.notiz || "",
      trackingNumber: o.tracking_number || "",
      labelKey: o.label_key || "",
      labelMime: o.label_mime || "",
      retoureStatus: o.retoure_status || "",
      retoureErhalten: o.retoure_erhalten || false,
      batchId: o.batch_id || null,
      adresse: o.adresse || "",
      archived: o.archived || false,
      archivedAt: o.archived_at || null,
      createdAt: o.created_at || null,
    };
  }
  function orderToDb(o) {
    const out = {};
    const map = {
      bestellnummer: "bestellnummer", kanal: "kanal", status: "status",
      datum: "datum", notiz: "notiz", trackingNumber: "tracking_number",
      labelKey: "label_key", labelMime: "label_mime",
      retoureStatus: "retoure_status", retoureErhalten: "retoure_erhalten",
      batchId: "batch_id", adresse: "adresse",
    };
    for (const [front, db] of Object.entries(map)) {
      if (front in o) out[db] = o[front];
    }
    return out;
  }
  function batchFromDb(b) {
    return {
      id: b.id,
      name: b.name,
      stueckzahl: b.stueckzahl || 0,
      investment: parseFloat(b.investment) || 0,
      cogs: parseFloat(b.cogs) || 0,
      reservierungen: b.reservierungen || {},
      minThresholds: b.min_thresholds || {},
      createdAt: b.created_at || null,
    };
  }

  // ── Stock + Alarm helpers (consistent across Lager / Stats / Banner) ────
  // Total batch stock: stueckzahl - (verkauft, nicht Retoure, nicht archiviert)
  function getBatchStock(batch) {
    const used = orders.filter(o =>
      o.batchId === batch.id &&
      o.status !== "Retoure" &&
      !o.archived
    ).length;
    return Math.max(0, (batch.stueckzahl || 0) - used);
  }
  // Per-channel pool stock: reservierungen[channel] - sold[batch][channel]
  function getBatchChannelStock(batch, channelId) {
    const reserved = batch.reservierungen?.[channelId] || 0;
    const sold = orders.filter(o =>
      o.batchId === batch.id &&
      o.kanal === channelId &&
      o.status !== "Retoure" &&
      !o.archived
    ).length;
    return Math.max(0, reserved - sold);
  }
  // Alarm level for a (batch, channel) combo: null | "low" | "out"
  function channelAlarmLevel(batch, channelId) {
    const threshold = parseInt(batch.minThresholds?.[channelId], 10) || 0;
    if (threshold <= 0) return null;
    const stock = getBatchChannelStock(batch, channelId);
    if (stock <= 0) return "out";
    if (stock <= threshold) return "low";
    return null;
  }
  // Aggregate count of active alarms across all (batch, channel) combos
  function getActiveAlarms() {
    const out = [];
    for (const b of (batches || [])) {
      for (const channelId of Object.keys(b.reservierungen || {})) {
        const level = channelAlarmLevel(b, channelId);
        if (level) out.push({ batch: b, channelId, level, stock: getBatchChannelStock(b, channelId), threshold: b.minThresholds?.[channelId] || 0 });
      }
    }
    return out;
  }

  // ── Activity Log: API + describer ───────────────────────────
  async function loadAuditLog(reset = true) {
    if (!currentStore || !authToken) return;
    setAuditLoading(true);
    try {
      const params = new URLSearchParams();
      if (auditFilter.entity_type && auditFilter.entity_type !== "all") {
        params.set("entity_type", auditFilter.entity_type);
      }
      if (auditFilter.days && auditFilter.days > 0) {
        const from = new Date(Date.now() - auditFilter.days * 86400 * 1000);
        params.set("from", from.toISOString());
      }
      params.set("limit", "50");
      if (!reset && auditEntries.length > 0) {
        params.set("before", auditEntries[auditEntries.length - 1].created_at);
      }
      const res = await fetch(`/api/stores/${currentStore.id}/audit?${params}`, {
        headers: { "Authorization": `Bearer ${authToken}` },
      });
      if (!res.ok) { setAuditLoading(false); return; }
      const data = await res.json();
      setAuditEntries(reset ? (data.entries || []) : [...auditEntries, ...(data.entries || [])]);
      setAuditHasMore(!!data.has_more);
    } catch (e) {
      console.error("loadAuditLog failed:", e);
    }
    setAuditLoading(false);
  }

  // Human-readable summary for an audit log entry
  function describeAuditEntry(e) {
    const old = e.old_value || {};
    const nv  = e.new_value || {};
    switch (e.action) {
      case "order.created":
        return { icon: "📦", text: `Bestellung #${nv.bestellnummer || "—"} erstellt` };
      case "order.updated": {
        // Compute meaningful diff
        const changes = [];
        if (old.status !== nv.status && old.status && nv.status) changes.push(`Status ${getStatusLabel(old.status)} → ${getStatusLabel(nv.status)}`);
        if (old.tracking_number !== nv.tracking_number && nv.tracking_number) changes.push(`Trackingnr ${nv.tracking_number}`);
        if (old.notiz !== nv.notiz) changes.push("Notiz geändert");
        if (old.kanal !== nv.kanal && nv.kanal) changes.push(`Kanal: ${getChannelLabel(nv.kanal)}`);
        const txt = changes.length ? changes.join(" · ") : "bearbeitet";
        return { icon: "📝", text: `#${nv.bestellnummer || old.bestellnummer || "—"}: ${txt}` };
      }
      case "order.archived":  return { icon: "🗑", text: "Bestellung archiviert" };
      case "order.restored":  return { icon: "↩️", text: "Bestellung wiederhergestellt" };
      case "order.deleted":   return { icon: "❌", text: "Bestellung endgültig gelöscht" };
      case "archive.emptied": return { icon: "🗑", text: `Archiv geleert (${nv?.count ?? "?"})` };
      case "batch.created":   return { icon: "📋", text: `Batch "${nv.name || "—"}" angelegt` };
      case "batch.updated":   return { icon: "📋", text: `Batch "${nv.name || old.name || "—"}" bearbeitet` };
      case "batch.deleted":   return { icon: "📋", text: `Batch "${old.name || "—"}" gelöscht` };
      case "member.role_changed": return { icon: "👥", text: `Rolle geändert auf ${nv.role || "?"}` };
      case "member.removed":  return { icon: "👥", text: "Mitglied entfernt" };
      case "invitation.created": return { icon: "✉️", text: `Einladung als ${nv.role || "?"} erstellt (${nv.code || ""})` };
      case "settings.updated": return { icon: "⚙️", text: "Einstellungen geändert" };
      case "store.config_changed": return { icon: "⚙️", text: "Store-Konfiguration geändert" };
      default: return { icon: "•", text: e.action };
    }
  }

  // Format timestamp as "vor N Min/Std/Tagen" or absolute date
  function formatRelative(iso) {
    if (!iso) return "";
    const dt = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - dt);
    if (diff < 60_000) return "gerade eben";
    if (diff < 3600_000) return `vor ${Math.floor(diff / 60_000)} Min`;
    if (diff < 86400_000) return `vor ${Math.floor(diff / 3600_000)} Std`;
    if (diff < 7 * 86400_000) return `vor ${Math.floor(diff / 86400_000)} Tagen`;
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
  }

  // ── API mutation helpers (call after local optimistic update) ────
  async function apiOrderCreate(formObj, batchId) {
    if (!currentStore || !authToken) return null;
    const body = orderToDb({ ...formObj, batchId });
    const res = await fetch(`/api/stores/${currentStore.id}/orders`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) { showToast("Anlegen fehlgeschlagen", "error"); return null; }
    return orderFromDb(await res.json());
  }
  async function apiOrderUpdate(id, patch) {
    if (!currentStore || !authToken) return null;
    const res = await fetch(`/api/stores/${currentStore.id}/orders/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(orderToDb(patch)),
    });
    if (!res.ok) { showToast("Speichern fehlgeschlagen", "error"); return null; }
    return orderFromDb(await res.json());
  }
  async function apiOrderArchive(id) {
    if (!currentStore || !authToken) return false;
    const res = await fetch(`/api/stores/${currentStore.id}/orders/${id}/archive`, {
      method: "POST",
      headers: { "Authorization": `Bearer ${authToken}` },
    });
    if (!res.ok) { showToast("Archivieren fehlgeschlagen", "error"); return false; }
    return true;
  }
  async function apiOrderRestore(id) {
    if (!currentStore || !authToken) return false;
    const res = await fetch(`/api/stores/${currentStore.id}/orders/${id}/restore`, {
      method: "POST",
      headers: { "Authorization": `Bearer ${authToken}` },
    });
    if (!res.ok) { showToast("Wiederherstellen fehlgeschlagen", "error"); return false; }
    return true;
  }
  async function apiOrderEmptyArchive() {
    if (!currentStore || !authToken) return false;
    const res = await fetch(`/api/stores/${currentStore.id}/archive`, {
      method: "DELETE",
      headers: { "Authorization": `Bearer ${authToken}` },
    });
    return res.ok;
  }
  async function apiBatchCreate(batchData) {
    if (!currentStore || !authToken) return null;
    const res = await fetch(`/api/stores/${currentStore.id}/batches`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(batchData),
    });
    if (!res.ok) { showToast("Batch-Anlegen fehlgeschlagen", "error"); return null; }
    return batchFromDb(await res.json());
  }
  async function apiBatchUpdate(id, patch) {
    if (!currentStore || !authToken) return null;
    // Translate frontend camelCase → DB snake_case for known fields
    const dbPatch = { ...patch };
    if ("minThresholds" in dbPatch) {
      dbPatch.min_thresholds = dbPatch.minThresholds;
      delete dbPatch.minThresholds;
    }
    const res = await fetch(`/api/stores/${currentStore.id}/batches/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
      body: JSON.stringify(dbPatch),
    });
    if (!res.ok) { showToast("Batch-Speichern fehlgeschlagen", "error"); return null; }
    return batchFromDb(await res.json());
  }

  async function loadStoreData(storeId, token, role) {
    try {
      const headers = { "Authorization": `Bearer ${token}` };
      const [ordersRes, archivedRes, settingsRes, batchesRes] = await Promise.all([
        fetch(`/api/stores/${storeId}/orders`, { headers }),
        fetch(`/api/stores/${storeId}/orders?archived=true`, { headers }),
        fetch(`/api/stores/${storeId}/settings`, { headers }),
        fetch(`/api/stores/${storeId}/batches`, { headers }),
      ]);
      // 403 = User ist nicht mehr Mitglied dieses Stores → clean reset zur Landing
      if (ordersRes.status === 403 || settingsRes.status === 403) {
        resetStoreState();
        setCurrentStore(null);
        setCurrentRole(null);
        loadUserStores(authToken);
        showToast("Kein Zugriff auf diesen Bereich", "warning");
        return;
      }
      if (ordersRes.ok) {
        const data = await ordersRes.json();
        const arr = Array.isArray(data) ? data : (data.orders || []);
        setOrders(arr.map(orderFromDb));
        setHasMoreOrders(!!data.has_more);
        setOrdersCursor(data.next_cursor || null);
      }
      if (archivedRes.ok) {
        const data = await archivedRes.json();
        const arr = Array.isArray(data) ? data : (data.orders || []);
        setArchive(arr.map(orderFromDb));
      }
      if (settingsRes.ok) {
        const s = await settingsRes.json();
        if (s.filter_kanal) setFilterKanal(s.filter_kanal);
        if (s.last_kanal) setLastKanal(s.last_kanal);
        if (typeof s.goal === "number") setGoal(s.goal);
        // s.batch_investment ist legacy — wird ignoriert. Investment kommt nur per-Batch.
        if (typeof s.helper_mode === "boolean") setHelperModeManual(s.helper_mode);
        if (typeof s.helper_start_count === "number") setHelperStartCount(s.helper_start_count);
      }
      if (batchesRes.ok) {
        const batches = await batchesRes.json();
        if (Array.isArray(batches) && batches.length > 0) {
          setBatches(batches.map(batchFromDb));
        }
      }
      setLoaded(true);
      // Audit-Log im Hintergrund laden — damit das Aktivitäts-Widget auf dem
      // Dashboard sofort Daten hat (vorher musste der User erst das Audit-Modal
      // öffnen damit Daten kamen). Nur Owner/Admin (visibleFor des Widgets).
      if (role === "owner" || role === "admin") {
        // Audit-Filter auf "alle Entitäten, letzte 7 Tage" voreinstellen
        setAuditFilter({ entity_type: "all", days: 7 });
        setAuditEntries([]);
        // Defer mit setTimeout damit alle State-Updates oben durch sind
        setTimeout(() => loadAuditLog(true), 0);
      }
    } catch (e) {
      console.error("loadStoreData failed:", e);
      setLoaded(true);
    }
  }

  async function loadMoreOrders() {
    if (!currentStore || !authToken || !ordersCursor) return;
    try {
      const res = await fetch(
        `/api/stores/${currentStore.id}/orders?before=${encodeURIComponent(ordersCursor)}`,
        { headers: { "Authorization": `Bearer ${authToken}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      const arr = Array.isArray(data) ? data : (data.orders || []);
      setOrders(prev => [...prev, ...arr.map(orderFromDb)]);
      setHasMoreOrders(!!data.has_more);
      setOrdersCursor(data.next_cursor || null);
    } catch (e) {
      console.warn("loadMoreOrders failed:", e);
    }
  }

  // Debounced settings save — fires when settings-style state changes
  useEffect(() => {
    if (!loaded || !currentStore || !authToken) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveState("saving");
    saveTimerRef.current = setTimeout(async () => {
      try {
        const settingsRes = await fetch(`/api/stores/${currentStore.id}/settings`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
          body: JSON.stringify({
            last_kanal: lastKanal, filter_kanal: filterKanal,
            goal,
            helper_mode: helperModeManual, helper_start_count: helperStartCount,
          }),
        });
        if (settingsRes.ok) {
          setSaveState("saved");
          setTimeout(() => setSaveState("idle"), 1500);
        } else {
          setSaveState("error");
        }
      } catch { setSaveState("error"); }
    }, 500);
    // NOTE: orders/batches/archive are NOT in deps — they sync via per-mutation API calls.
    // Only settings-style values trigger this debounced save.
  }, [lastKanal, filterKanal, goal, helperModeManual, helperStartCount, loaded]);

  // Lokale Settings (dark + sortBy) bei Änderung sofort in localStorage persistieren
  useEffect(() => {
    try { localStorage.setItem("memorica_dark", dark ? "1" : "0"); } catch {}
  }, [dark]);
  useEffect(() => {
    try { localStorage.setItem("memorica_sortBy", sortBy); } catch {}
  }, [sortBy]);
  useEffect(() => {
    try { localStorage.setItem("memorica_dateRange", String(dateRange)); } catch {}
  }, [dateRange]);

  useEffect(() => {
    // Only lock scroll on mobile/tablet where sheets cover the viewport.
    // On desktop, panels are inline in the right column — body should stay scrollable.
    const locked = !isDesktop && (showSheet || showDetail);
    document.body.style.overflow = locked ? "hidden" : "";
    document.documentElement.style.overflow = locked ? "hidden" : "";
    return () => { document.body.style.overflow = ""; document.documentElement.style.overflow = ""; };
  }, [showSheet, showDetail, isDesktop]);

  useEffect(() => {
    const light = document.getElementById("theme-color");
    const darkTag = document.getElementById("theme-color-dark");
    const color = dark ? "#0d1117" : "#dde8f8";
    if (light) light.setAttribute("content", color);
    if (darkTag) darkTag.setAttribute("content", color);
    document.body.classList.toggle("light-mode", !dark);
    document.body.style.background = color;
    document.documentElement.style.background = color;
  }, [dark]);

  useEffect(() => { tabIndexRef.current = tabIndex; }, [tabIndex]);

  // Declared here (before their useEffect deps arrays at 4886/4899) to avoid TDZ crash.
  const isHelperUser = !!userEmail && HELPER_EMAILS.includes(userEmail);
  const helperMode = isHelperUser || helperModeManual || (!identityLoaded && helperModeManual);

  const helperModeRef = useRef(false);
  useEffect(() => { helperModeRef.current = helperMode; }, [helperMode]);

  // Helper-User: einmaligen Snapshot der offenen Bestellungen setzen, sobald
  // Daten + Identity geladen sind. Ohne diesen Snapshot ist der Fortschritts-
  // balken im Helfer-Modus immer auf 0%.
  const helperSnapshotted = useRef(false);
  useEffect(() => {
    if (isHelperUser && loaded && identityLoaded && !helperSnapshotted.current) {
      helperSnapshotted.current = true;
      const bereitNow = orders.filter(o => o.status === "Bereit").length;
      setHelperStartCount(bereitNow);
      setTabIndex(1);
    }
  }, [isHelperUser, loaded, identityLoaded, orders]);
  useEffect(() => { if (showSearch) searchRef.current?.focus(); else setSearch(""); }, [showSearch]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const onEnd   = (e) => {
      if (touchStartX.current === null) return;
      const delta = e.changedTouches[0].clientX - touchStartX.current;
      touchStartX.current = null;
      if (Math.abs(delta) < 48) return;
      if (helperModeRef.current) return; // Helfer-Modus: nur Offen-Tab, kein Swipe
      const cur = tabIndexRef.current;
      if (delta < 0 && cur < TABS.length - 1) switchTabRef.current(cur + 1);
      if (delta > 0 && cur > 0) switchTabRef.current(cur - 1);
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchend",   onEnd,   { passive: true });
    return () => { el.removeEventListener("touchstart", onStart); el.removeEventListener("touchend", onEnd); };
  }, []);

  const switchTab = (newIdx) => {
    if (newIdx === tabIndexRef.current || switchingRef.current) return;
    switchingRef.current = true;
    const dir = newIdx > tabIndexRef.current ? -1 : 1;
    setSlideDir(dir); setListVisible(false);
    setTimeout(() => {
      setTabIndex(newIdx); setSlideDir(-dir);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        setSlideDir(0); setListVisible(true); switchingRef.current = false;
      }));
    }, 150);
  };
  switchTabRef.current = switchTab;

  const t = dark ? T.dark : T.light;

  const glass = (extra = {}) => ({
    background: t.glass,
    backdropFilter: "blur(40px) saturate(200%)",
    WebkitBackdropFilter: "blur(40px) saturate(200%)",
    border: `1px solid ${t.glassBorder}`,
    boxShadow: t.glassShadow,
    ...extra,
  });

  const inp = (s = {}) => ({
    width: "100%", border: "none", background: "transparent",
    fontFamily: F, fontSize: 17, color: t.text, outline: "none",
    padding: "16px 18px", boxSizing: "border-box", lineHeight: 1.3, ...s
  });

  // showToast(message)                      → success default
  // showToast(message, "error" | "info" | "warning")
  // showToast(message, type, durationMs)
  const showToast = (msg = "Sendungsnummer kopiert", type = "success", duration = 2400) => {
    const id = Date.now() + Math.random();
    setToasts(arr => {
      const next = [...arr, { id, message: msg, type, expiresAt: Date.now() + duration }];
      // Limit auf 3 — älteste werden gestapelt verdrängt
      return next.length > 3 ? next.slice(-3) : next;
    });
    setTimeout(() => {
      setToasts(arr => arr.filter(t => t.id !== id));
    }, duration);
  };
  const openNew = () => {
    // CRITICAL für iOS-Tastatur: flushSync zwingt React den Sheet SYNCHRON zu rendern,
    // BEVOR wir aus dem Click-Handler raus sind. Der Input ist dann mounted und wir
    // sind noch im user-gesture-Context → focus() öffnet die Tastatur zuverlässig.
    // Vorher: setTimeout-Retries, die nach Gesture-Context kamen → iOS verweigerte Tastatur.
    flushSync(() => {
      setForm(emptyForm(lastKanal));
      setShowSheet(true);
    });
    inputRef.current?.focus({ preventScroll: true });
    // Defensiv: zusätzliche Refocus-Versuche falls die erste Iteration aus
    // irgendwelchen Gründen scheitert (Animation, Layout-Shift, etc.).
    const refocus = () => inputRef.current?.focus({ preventScroll: true });
    refocus();
    requestAnimationFrame(refocus);
    setTimeout(refocus, 80);
    setTimeout(refocus, 200);
  };
  const closeSheet  = () => { setShowSheet(false); setForm(emptyForm(lastKanal)); };
  const isDuplicate = form.bestellnummer && orders.some(o => o.bestellnummer.trim() === form.bestellnummer.trim());

  const save = async () => {
    if (!form.bestellnummer || isDuplicate) return;
    // FIFO-Stock-Check: kein freier Batch mehr → Bestellung blockieren
    if (!getActiveBatch()) {
      showToast("Kein Batch hat noch freie Stück — neuen Batch anlegen", "warning");
      return;
    }
    setLastKanal(form.kanal);
    const batch = getBatchForOrder({ kanal: form.kanal });
    haptic(12);
    // Snapshot der zu submittenden Daten BEVOR wir das Form zurücksetzen.
    const formSnapshot = { ...form, status: form.status };
    setForm(f => ({ ...emptyForm(f.kanal) }));
    // CRITICAL: synchroner Refocus VOR dem await — sonst bricht iOS den
    // user-gesture-Context auf und die Tastatur lässt sich nicht wieder öffnen.
    inputRef.current?.focus({ preventScroll: true });
    // Async API-Call läuft im Hintergrund — UI bleibt responsiv, Input behält Fokus.
    const created = await apiOrderCreate(formSnapshot, batch?.id);
    if (created) setOrders(o => [created, ...o]);
    // Neue Bestellungen sind "Offen" und nur im Alle-Tab sichtbar.
    if (filterStatus !== "Alle") setTabIndex(0);
    if (listRef.current) listRef.current.scrollTop = 0;
    // Defensive Mehrfach-Refocus nach dem await als Fallback
    const refocus = () => inputRef.current?.focus({ preventScroll: true });
    requestAnimationFrame(refocus);
    setTimeout(refocus, 50);
    setTimeout(refocus, 150);
  };
  const openDetail  = (order) => { setShowStats(false); setShowLager(false); setShowSettings(false); setShowArchive(false); setDetailOrder(order); setDetailDeleteConfirm(false); setShowDetail(true); };

  // Desktop: closes all other panels before opening the requested one (mutex)
  const openPanel = (which) => {
    setShowStats(which === "stats");
    setShowLager(which === "lager");
    setShowSettings(which === "settings");
    setShowArchive(which === "archive");
    if (which !== "detail") { setShowDetail(false); setDetailOrder(null); }
  };

  // Toggle Helfer-Modus: beim Aktivieren Snapshot der offenen Bestellungen,
  // damit "Erledigt: X von Y" einen sinnvollen Bezug hat.
  // (isHelperUser + helperMode are declared earlier to avoid TDZ in useEffect deps)
  const toggleHelperMode = () => {
    if (isHelperUser) return; // Helper-User darf den Modus nicht abschalten
    if (!helperModeManual) {
      const bereitNow = orders.filter(o => o.status === "Bereit").length;
      setHelperStartCount(bereitNow);
      setHelperModeManual(true);
      setTabIndex(1); // Bereit-Tab
      setCurrentView("orders"); // Helper sieht kein Dashboard → erzwinge Orders-View
      setShowSettings(false);
      setShowDetail(false);
      setShowStats(false);
      setShowLager(false);
      setShowArchive(false);
      haptic(20);
    } else {
      setHelperModeManual(false);
      setHelperStartCount(0);
      haptic(20);
    }
  };
  const closeDetail = () => { setShowDetail(false); setDetailOrder(null); setDetailDeleteConfirm(false); };
  const updateOrder = (id, field, val) => {
    // Optimistic local update
    let computedPatch = { [field]: val };
    setOrders(o => o.map(x => {
      if (x.id !== id) return x;
      const next = { ...x, [field]: val };
      if (field === "labelKey") {
        const hasLabel = typeof val === "string" && val.length > 0;
        if (next.status === "Offen" && hasLabel) { next.status = "Bereit"; computedPatch.status = "Bereit"; }
        else if (next.status === "Bereit" && !hasLabel) { next.status = "Offen"; computedPatch.status = "Offen"; }
      }
      return next;
    }));
    // Sync to API in background
    apiOrderUpdate(id, computedPatch);
  };
  // Label hochladen + Order updaten in einem Schritt
  const setLabelForOrder = async (id, file) => {
    try {
      const order = orders.find(x => x.id === id);
      // Altes Label aus R2 löschen falls vorhanden
      if (order?.labelKey) await deleteLabel(order.labelKey, currentStore?.id, authToken);
      const { key, mime } = await uploadLabel(file, id, currentStore?.id, authToken);
      const patch = { labelKey: key, labelMime: mime };
      // Auto-Transition Offen → Bereit (Workflow-Constraint: nur Bereit-Orders
      // werden für Tracking-Extraktion bearbeitet, der Auto-Transition macht
      // die Order direkt Bereit)
      if (order?.status === "Offen") patch.status = "Bereit";
      setOrders(o => o.map(x => x.id === id ? { ...x, ...patch } : x));
      apiOrderUpdate(id, patch);
      haptic(15);
      showToast("Label hochgeladen ✓");

      // Tracking-Nummer aus dem Label extrahieren (client-side, async).
      // Gilt nur für jetzt "Bereit"-Bestellungen. Wir starten die Extraktion
      // entkoppelt vom Upload — der UI-Flow bleibt unblockiert.
      const newStatus = patch.status || order?.status;
      if (newStatus === "Bereit") {
        // Skip if a tracking number is already set — don't overwrite manual input
        if (order?.trackingNumber && order.trackingNumber.trim()) {
          return;
        }
        showToast("Trackingnummer wird gelesen…", "info");
        try {
          const result = await extractTrackingNumber(file);
          if (!result) {
            showToast("Trackingnummer nicht erkannt — bitte manuell eingeben", "warning");
            return;
          }
          // Barcode-Quelle ist hochzuverlässig → auto-save
          if (result.source === "barcode" && result.primary) {
            setOrders(o => o.map(x => x.id === id ? { ...x, trackingNumber: result.primary } : x));
            await apiOrderUpdate(id, { trackingNumber: result.primary });
            haptic(15);
            showToast(`Trackingnummer: ${result.primary} (via Barcode) ✓`);
            return;
          }
          // OCR-Quelle: User soll den richtigen Kandidaten bestätigen
          // oder manuell eingeben (Labels haben oft mehrere Nummern:
          // Tracking, Rechnung, Bestellung, Kunden-Ref, etc.)
          setTrackingConfirm({
            orderId: id,
            candidates: result.candidates || [],
            ocrText: result.ocrText || "",
            manualValue: result.primary || "",
          });
          haptic(10);
        } catch (extractErr) {
          console.warn("Tracking-Extraktion fehlgeschlagen:", extractErr);
          showToast("Trackingnummer-Erkennung fehlgeschlagen", "error");
        }
      }
    } catch (e) {
      console.error("Label-Upload fehlgeschlagen:", e);
      showToast(`Label-Upload fehlgeschlagen: ${e.message}`, "error");
    }
  };
  // Label öffnen (signed URL holen, in neuem Tab öffnen)
  const openLabel = async (key) => {
    try {
      const url = await getLabelUrl(key, currentStore?.id, authToken);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error(e);
      showToast("Label kann nicht geöffnet werden: " + e.message, "error");
    }
  };
  const deleteFromDetail = async (id) => {
    const order = orders.find(x => x.id === id);
    if (order) {
      // Optimistic move to archive
      setArchive(a => [{ ...order, archived: true, archivedAt: new Date().toISOString() }, ...a]);
      setOrders(o => o.filter(x => x.id !== id));
      const ok = await apiOrderArchive(id);
      if (!ok) {
        // Rollback
        setArchive(a => a.filter(x => x.id !== id));
        setOrders(o => [order, ...o]);
      }
    }
    closeDetail();
  };
  const restoreFromArchive = async (id) => {
    const item = archive.find(x => x.id === id);
    if (!item) return;
    // Optimistic move back to active orders
    setOrders(o => [{ ...item, archived: false, archivedAt: null }, ...o]);
    setArchive(a => a.filter(x => x.id !== id));
    haptic(8);
    const ok = await apiOrderRestore(id);
    if (!ok) {
      setOrders(o => o.filter(x => x.id !== id));
      setArchive(a => [item, ...a]);
    }
  };
  const purgeFromArchive = async (id) => {
    const item = archive.find(x => x.id === id);
    setArchive(a => a.filter(x => x.id !== id));
    haptic(10);
    // Permanent delete via DELETE on the order itself
    if (currentStore && authToken) {
      try {
        await fetch(`/api/stores/${currentStore.id}/orders/${id}`, {
          method: "DELETE",
          headers: { "Authorization": `Bearer ${authToken}` },
        });
        // Cleanup the label too
        if (item?.labelKey) deleteLabel(item.labelKey, currentStore.id, authToken);
      } catch {}
    }
  };
  const emptyArchive = async () => {
    const oldArchive = archive;
    setArchive([]);
    setConfirmEmptyArchive(false);
    haptic(15);
    const ok = await apiOrderEmptyArchive();
    if (!ok) setArchive(oldArchive);
  };
  const confirmRetoureNow = () => {
    if (!confirmRetoure) return;
    haptic(8);
    setOrders(o => o.map(x => x.id !== confirmRetoure ? x : { ...x, status: "Retoure" }));
    apiOrderUpdate(confirmRetoure, { status: "Retoure" });
    setConfirmRetoure(null);
  };
  const confirmUnretoureNow = () => {
    if (!confirmUnretoure) return;
    haptic(8);
    setOrders(o => o.map(x => x.id !== confirmUnretoure ? x : { ...x, status: "Versendet", retoureErhalten: false }));
    apiOrderUpdate(confirmUnretoure, { status: "Versendet", retoureErhalten: false });
    setConfirmUnretoure(null);
  };
  const toggleRetoureErhalten = (id) => {
    haptic(10);
    const order = orders.find(o => o.id === id);
    if (!order) return;
    const newVal = !order.retoureErhalten;
    setOrders(o => o.map(x => x.id !== id ? x : { ...x, retoureErhalten: newVal }));
    apiOrderUpdate(id, { retoureErhalten: newVal });
  };
  const exportCSV = () => {
    const rows = [["Bestellnummer","Kanal","Status","Datum","Sendungsnummer","Notiz"],
      ...orders.map(o=>[o.bestellnummer,o.kanal,o.status,o.datum,o.trackingNumber||"",o.notiz||""])];
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([rows.map(r=>r.map(v=>`"${v}"`).join(",")).join("\n")],{type:"text/csv"})), download:`memorica-${today()}.csv` });
    a.click();
  };

  // Im Helfer-Modus immer "Offen" — egal welcher Tab gewählt ist
  // Im Helfer-Modus zeigt die Liste NUR "Bereit"-Bestellungen.
  // "Offen" (= Etikett noch nicht da) ist Noahs Aufgabe und für Vater nicht relevant.
  const filterStatus = helperMode ? "Bereit" : TABS[tabIndex];
  // Derived from batches (single source of truth if batches exist)
  const totalStueckzahl  = batches.reduce((s, b) => s + (b.stueckzahl || 0), 0);
  const totalInvestment  = batches.reduce((s, b) => s + (b.investment || 0), 0);
  const totalReserviert  = batches.reduce((s, b) => s + Object.values(b.reservierungen || {}).reduce((a, v) => a + (v || 0), 0), 0);
  const totalUnreserved  = batches.reduce((s, b) => {
    const res = Object.values(b.reservierungen || {}).reduce((a, v) => a + (v || 0), 0);
    return s + Math.max(0, (b.stueckzahl || 0) - res);
  }, 0);
  const effGoal          = totalStueckzahl > 0 ? totalStueckzahl : goal;
  // effInvestment ist jetzt einfach die Summe aller Batch-Investments.
  // Legacy-Fallback auf den alten Globals-Wert wurde entfernt.
  const effInvestment    = totalInvestment;

  // FIFO with kanal-pool awareness:
  // A sale uses the oldest batch where the kanal-pool has remaining capacity.
  // If no pool has capacity (all overdrawn), still assigns to oldest batch — system shows red warning.
  const getBatchForOrder = (order) => {
    if (order.batchId) return batches.find(b => b.id === order.batchId) || batches[0];
    const kanal = order.kanal;
    const sortedBatches = batches.slice().sort((x, y) => (x.createdAt || "").localeCompare(y.createdAt || ""));
    // Count sales per (batch, kanal)
    const soldByBatchKanal = {};
    orders.forEach(o => {
      if (o.status === "Retoure") return;
      if (!o.batchId) return;
      const key = `${o.batchId}_${o.kanal}`;
      soldByBatchKanal[key] = (soldByBatchKanal[key] || 0) + 1;
    });
    // Try to find oldest batch with kanal pool capacity
    for (const b of sortedBatches) {
      const pool = b.reservierungen?.[kanal] || 0;
      const sold = soldByBatchKanal[`${b.id}_${kanal}`] || 0;
      if (pool - sold > 0) return b;
    }
    // None has capacity — fall back to oldest batch (overdraw)
    return sortedBatches[0] || batches[0];
  };

  // Findet den ältesten Batch mit Restkapazität (kanal-übergreifend gezählt).
  // Liefert null wenn kein Batch mehr Stück frei hat. Genutzt für:
  //   • Order-Submit-Block (kein leerer Verkauf)
  //   • Aktiv-Batch-Banner im Finanz-Panel
  //   • Lager-leer-Pille im Order-List-Header
  const getActiveBatch = () => {
    if (!batches || batches.length === 0) return null;
    const sorted = batches.slice().sort((x, y) => (x.createdAt || "").localeCompare(y.createdAt || ""));
    // Verkäufe pro Batch zählen (excl. Retouren + archivierte)
    const soldByBatch = {};
    orders.forEach(o => {
      if (o.status === "Retoure" || o.archived) return;
      if (!o.batchId) return;
      soldByBatch[o.batchId] = (soldByBatch[o.batchId] || 0) + 1;
    });
    for (const b of sorted) {
      const sold = soldByBatch[b.id] || 0;
      if (sold < (b.stueckzahl || 0)) return b;
    }
    return null; // alles ausverkauft
  };

  // Datum-Bereich: nur auf abgeschlossene Bestellungen (Versendet/Retoure) anwenden,
  // damit Offen/Bereit niemals fälschlich versteckt werden.
  const dateRangeCutoff = (() => {
    if (dateRange < 0) return null; // alles
    const d = new Date();
    d.setDate(d.getDate() - dateRange);
    return d.toISOString().slice(0, 10);
  })();
  const passesDateRange = (o) => {
    if (!dateRangeCutoff) return true;
    if (o.status !== "Versendet" && o.status !== "Retoure") return true;
    return (o.datum || "") >= dateRangeCutoff;
  };
  const filtered = orders.filter(o =>
    (filterStatus === "Alle" || o.status === filterStatus) &&
    (filterKanal  === "Alle" || o.kanal  === filterKanal) &&
    passesDateRange(o) &&
    (!search || (() => {
      const q = search.toLowerCase().trim();
      // Datum auch durchsuchbar als deutsches Format ("11.04.2026", "April")
      const months = ["januar","februar","märz","april","mai","juni","juli","august","september","oktober","november","dezember"];
      const dateStr = (() => {
        const d = new Date(o.datum); if (isNaN(d)) return o.datum || "";
        return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}.${d.getFullYear()} ${months[d.getMonth()]}`;
      })();
      return [o.bestellnummer, o.trackingNumber, o.notiz, o.kanal, dateStr]
        .some(field => field?.toLowerCase?.().includes(q));
    })())
  ).sort((a, b) => {
    if (filterStatus === "Bereit") {
      // Bereit-Tab: älteste zuerst (= dringendste oben)
      const dateDiff = (a.datum || "").localeCompare(b.datum || "");
      return dateDiff !== 0 ? dateDiff : a.id - b.id;
    }
    if (filterStatus === "Alle") {
      // Alle-Tab: Offen ohne Label oben (Action needed), dann Bereit, dann Rest. Innerhalb: älteste zuerst.
      const order = (s) => s === "Offen" ? 0 : s === "Bereit" ? 1 : s === "Versendet" ? 2 : 3;
      const oa = order(a.status), ob = order(b.status);
      if (oa !== ob) return oa - ob;
      const dateDiff = (a.datum || "").localeCompare(b.datum || "");
      return dateDiff !== 0 ? dateDiff : a.id - b.id;
    }
    // All others: newest first by datum, then id as tiebreaker
    const dateDiff = (b.datum || "").localeCompare(a.datum || "");
    return dateDiff !== 0 ? dateDiff : (b.id || 0) - (a.id || 0);
  });
  // Group by month
  const MONTHS_DE = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
  const groupByMonth = (list) => {
    const groups = [];
    let currentKey = null;
    list.forEach(o => {
      const d = new Date(o.datum || 0);
      const key = isNaN(d.getTime()) ? "—" : `${d.getFullYear()}-${d.getMonth()}`;
      const label = isNaN(d.getTime()) ? "Ohne Datum" : `${MONTHS_DE[d.getMonth()]} ${d.getFullYear()}`;
      if (key !== currentKey) {
        groups.push({ key, label, items: [] });
        currentKey = key;
      }
      groups[groups.length - 1].items.push(o);
    });
    return groups;
  };
  const groupedFiltered = groupByMonth(filtered);
  const counts   = { Alle: orders.length, Bereit: orders.filter(o=>o.status==="Bereit").length, Versendet: orders.filter(o=>o.status==="Versendet").length, Retoure: orders.filter(o=>o.status==="Retoure").length };
  const revenue  = orders.filter(o => o.status !== "Retoure").length * 27.99;
  const tabColors = { Alle: t.text, Bereit: "#BF5AF2", Versendet: "#30D158", Retoure: "#FF453A" };
  const pillLeft  = `calc(${tabIndex * 25}% + 3px)`;
  const pillWidth = `calc(25% - 6px)`;
  const o = showDetail ? orders.find(x => x.id === detailOrder?.id) : null;

  // On desktop, the detail view becomes inline in the right panel (no modal)
  const detailInline = isDesktop;
  // On desktop, all sheets become centered popups instead of bottom sheets
  const sheetsAsPopup = isDesktop;

  // Desktop right-column panel positioning (used by sheets that should overlay the right column)
  const dRight = `max(0px, calc((100vw - ${containerMaxW}px) / 2))`;
  // Desktop-Sheet als schwebende Karte unter dem Top-Header (32px Margin),
  // mit Rundungen und Glas-Schatten — passend zum Hauptlayout.
  // Höhe: scrollbar, mit kleinem Bottom-Margin für Atmen.
  // Panel-Top liegt bündig mit dem Listen-Card-Top auf der linken Seite
  // (= Header-Höhe ~180 + Group-Header-Höhe ~23 + minimaler Spacer).
  const desktopRightPanelMarginTop = 221;
  const desktopRightPanel = isDesktop ? {
    position: "fixed",
    top: desktopRightPanelMarginTop,
    // Panel right: 32px innen vom Wrapper-Right (= Right-Column-Right-Edge)
    right: `calc(${dRight} + 32px)`,
    // Panel width: Right-Column-Width = wrapper - 2*32 (padding) - listW - 24 (gap)
    width: `min(calc(100vw - ${listW}px - 88px), ${containerMaxW - listW - 88}px)`,
    height: `calc(100vh - ${desktopRightPanelMarginTop + 24}px)`,
    maxHeight: `calc(100vh - ${desktopRightPanelMarginTop + 24}px)`,
    borderRadius: 24,
    transform: "none",
    paddingBottom: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  } : null;
  // Centered popup positioning (used for compact sheets like Add, Sendung on desktop)
  const desktopCenterPopup = isDesktop ? {
    position: "fixed",
    top: "50%",
    left: "50%",
    bottom: "auto",
    right: "auto",
    width: "min(480px, calc(100vw - 64px))",
    maxWidth: "480px",
    transform: "translate(-50%, -50%)",
    borderRadius: 24,
    paddingBottom: 24,
    maxHeight: "85vh",
  } : null;

  // ── Stale-Session-Reminder (P-Polish 1.1) ────────────────
  // Wenn eine Trödel/Markt-Session > 12h aktiv ist, zeige ein Reminder-Modal
  // beim nächsten App-Open. Dismissed-State pro session.id (in-memory).
  const staleSession = (() => {
    if (!activeSessions || !userStores) return null;
    for (const [storeId, session] of Object.entries(activeSessions)) {
      if (!session || session.ends_at) continue;
      if (staleSessionDismissed.has(session.id)) continue;
      const hours = (Date.now() - new Date(session.starts_at).getTime()) / 3600000;
      if (hours < 12) continue;
      const store = userStores.find(s => s.id === storeId);
      if (!store) continue;
      return { store, session };
    }
    return null;
  })();
  const staleReminderJsx = staleSession ? (
    <StaleSessionReminderSheet
      dark={dark} t={t}
      store={staleSession.store}
      session={staleSession.session}
      onDismiss={() => {
        setStaleSessionDismissed(prev => new Set(prev).add(staleSession.session.id));
      }}
      onEndNow={() => {
        // User in den Store navigieren — dort kann er „Markt beenden" tippen.
        // (Suggested ends_at = letzter Verkauf + 30 Min wäre nice-to-have, kommt
        // später als Optimierung im Sheet selbst.)
        setStaleSessionDismissed(prev => new Set(prev).add(staleSession.session.id));
        selectStore(staleSession.store);
        showToast(`Tippe „Markt beenden" um den Tagesabschluss zu öffnen`, "info", 3500);
      }}
    />
  ) : null;

  // ── Onboarding screens (rendered before the main app) ────
  if (appScreen === "loading") {
    return (
      <div style={{ minHeight: "100vh", background: dark ? "#0d1117" : "#dde8f8", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: F, color: dark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)", fontSize: 14 }}>
        Lade…
      </div>
    );
  }
  if (appScreen === "auth") {
    return <AuthScreen onAuth={(session) => {
      setAuthToken(session.access_token);
      setAuthUser(session.user);
      setUserEmail(session.user.email || null);
      loadUserStores(session.access_token);
    }} />;
  }
  if (appScreen === "store-selector") {
    // ── Landing-Page (P-Landing-Redesign) ──
    // Reines Apple-Style Typografie-Layout. Kein Widget-Grid mehr.
    return <>
      <LandingScreen
        authToken={authToken}
        authUser={authUser}
        userStores={userStores}
        activeSessions={activeSessions}
        landingData={landingData}
        loading={loadingLanding}
        dark={dark}
        showToast={showToast}
        onSelectStore={(s) => selectStore(s)}
        onCreateArea={() => setCreateAreaOpen(true)}
        onOpenSettings={() => setGlobalSettingsOpen(true)}
      />
      {createAreaOpen && (
        <CreateAreaSheet
          dark={dark} t={t}
          userStores={userStores}
          authToken={authToken}
          onClose={() => setCreateAreaOpen(false)}
          onCreateOnline={() => { setCreateAreaOpen(false); setAppScreen("create-store"); }}
          onCreateMode={(type) => { setCreateAreaOpen(false); handleCreateModeStore(type); }}
          onSelectExisting={(store) => { setCreateAreaOpen(false); selectStore(store); }}
          onJoinedViaCode={async (joinedStore) => {
            setCreateAreaOpen(false);
            // P-5-Punkte-Fix Block 5: Stores neu laden + zum beigetretenen Store
            if (authToken) await loadUserStores(authToken);
            if (joinedStore) selectStore(joinedStore);
          }}
          showToast={showToast}
        />
      )}
      <GlobalSettingsSheet
        isOpen={globalSettingsOpen}
        dark={dark}
        authUser={authUser}
        authToken={authToken}
        userStores={userStores}
        userPreferences={userPreferences}
        onPreferencesChange={saveUserPreferences}
        onDarkChange={setDark}
        onLogout={async () => {
          setGlobalSettingsOpen(false);
          if (supabase) await supabase.auth.signOut();
          setAppScreen("auth");
        }}
        onClose={() => setGlobalSettingsOpen(false)}
        showToast={showToast}
      />
      {staleReminderJsx}
    </>;
  }
  if (appScreen === "create-store") {
    return <CreateStoreScreen
      token={authToken}
      onCreated={(store) => {
        setUserStores(prev => [...prev, store]);
        selectStore(store);
      }}
      onBack={() => setAppScreen("store-selector")}
    />;
  }
  if (appScreen === "join-store") {
    return <JoinStoreScreen
      token={authToken}
      onJoined={(store) => {
        setUserStores(prev => prev.find(s => s.id === store.id) ? prev : [...prev, store]);
        selectStore(store);
      }}
      onBack={() => setAppScreen("store-selector")}
    />;
  }
  if (appScreen === "flea-hub") {
    if (FleaHub) {
      return <ErrorBoundary fallbackLabel="Trödel-Modus" onReset={() => setAppScreen("store-selector")}>
        <>
          <FleaHub
            store={currentStore}
            authToken={authToken}
            onBack={() => setAppScreen("store-selector")}
            onStoreDeleted={() => {
              const sid = currentStore?.id;
              setUserStores(prev => prev.filter(s => s.id !== sid));
              resetStoreState();
              setCurrentStore(null);
              setCurrentRole(null);
              setCurrentMode("online");
              setAppScreen("store-selector");
            }}
            dark={dark}
            showToast={showToast}
            userPreferences={userPreferences}
            saveUserPreferences={saveUserPreferences}
          />
          {staleReminderJsx}
        </>
      </ErrorBoundary>;
    }
    return <ModeHubStub
      title="Trödel"
      subtitle="Lade Trödel-Modul…"
      accentColor={{ weak: "rgba(191,90,242,0.12)", solid: "#BF5AF2" }}
      onBack={() => setAppScreen("store-selector")}
    />;
  }
  if (appScreen === "market-hub") {
    if (MarketHub) {
      return <ErrorBoundary fallbackLabel="Standverkauf-Modus" onReset={() => setAppScreen("store-selector")}>
        <>
          <MarketHub
            store={currentStore}
            authToken={authToken}
            onBack={() => setAppScreen("store-selector")}
            onStoreDeleted={() => {
              const sid = currentStore?.id;
              setUserStores(prev => prev.filter(s => s.id !== sid));
              resetStoreState();
              setCurrentStore(null);
              setCurrentRole(null);
              setCurrentMode("online");
              setAppScreen("store-selector");
            }}
            dark={dark}
            showToast={showToast}
          />
          {staleReminderJsx}
        </>
      </ErrorBoundary>;
    }
    return <ModeHubStub
      title="Standverkauf"
      subtitle="Lade Standverkauf-Modul…"
      accentColor={{ weak: "rgba(48,209,88,0.12)",  solid: "#30D158" }}
      onBack={() => setAppScreen("store-selector")}
    />;
  }

  // ── Dashboard / View navigation helpers ────────────────
  // Called by widgets (onNavigate) to jump to specific app sections
  function handleWidgetNavigate(target) {
    haptic(8);
    switch (target) {
      case "orders_bereit":
        setCurrentView("orders"); setTabIndex(1); break;
      case "orders":
        setCurrentView("orders"); break;
      case "lager":
        setCurrentView("orders"); setTimeout(() => { openPanel?.("lager"); setShowLager(true); }, 50);
        break;
      case "stats":
        setCurrentView("orders"); setTimeout(() => { openPanel?.("stats"); setShowStats(true); }, 50);
        break;
      case "goal_edit":
        // Direkt das Goal-Edit-Popup öffnen, ohne View-Switch
        setShowGoalEdit(true);
        break;
      case "audit":
        setShowAuditLog(true);
        setAuditFilter({ entity_type: "all", days: 7 });
        setAuditEntries([]);
        setTimeout(() => loadAuditLog(true), 0);
        break;
      default:
        break;
    }
  }

  // Build the visible widget list from layout + role + visibility
  function getVisibleWidgets() {
    const layout = storeDashboardLayout || defaultLayoutForRole(currentRole);
    // widgetProps werden weiter unten beim Rendern zusammengebaut — wir bauen
    // hier nur die für `shouldRender` nötigen Felder zusammen.
    const sharedProps = { alarms: getActiveAlarms(), orders };
    // Widgets, die im Code-Default-Layout sind, aber im gespeicherten Layout fehlen
    // (z.B. später hinzugefügt wie `stale_orders`), automatisch dazumergen — als
    // sichtbar. So bekommen Bestandsuser neue Features ohne aktive Aktion.
    const layoutIds = new Set((layout.widgets || []).map(w => w.id));
    const def = defaultLayoutForRole(currentRole);
    const merged = [
      ...(layout.widgets || []),
      ...def.widgets.filter(w => !layoutIds.has(w.id)),
    ];
    return merged.filter(w => {
      if (!w.visible) return false;
      const def = WIDGETS[w.id];
      if (!def) return false;
      if (!def.visibleFor.includes(currentRole)) return false;
      // Optionaler Render-Filter — z.B. Reorder-Alarme nur wenn welche da sind.
      if (typeof def.shouldRender === "function" && !def.shouldRender(sharedProps)) return false;
      return true;
    });
  }

  return (
    <div style={{ fontFamily: F, minHeight: "100dvh", background: t.bg, transition: "background 0.3s ease", position: "relative" }}>

      {/* ── DASHBOARD VIEW — works on all viewports (mobile=overlay, desktop=inline) ── */}
      {currentView === "dashboard" && (
        <DashboardArea
          isMobile={isMobile} isTablet={isTablet} isDesktop={isDesktop}
          dark={dark} t={t} F={F}
          glass={glass}
          loaded={loaded}
          onPullRefresh={async () => {
            if (currentStore?.id && authToken) {
              await loadStoreData(currentStore.id, authToken);
              haptic(15);
              showToast("Aktualisiert", "info", 1400);
            }
          }}
          currentStore={currentStore}
          getVisibleWidgets={getVisibleWidgets}
          dashboardEditMode={dashboardEditMode}
          setDashboardEditMode={setDashboardEditMode}
          setShowCustomizeDashboard={setShowCustomizeDashboard}
          setShowSettings={setShowSettings}
          storeDashboardLayout={storeDashboardLayout}
          saveDashboardLayout={saveDashboardLayout}
          defaultLayoutForRole={() => defaultLayoutForRole(currentRole)}
          handleWidgetNavigate={handleWidgetNavigate}
          haptic={haptic}
          setCurrentView={setCurrentView}
          // Widget data props (passed through to each Widget component)
          widgetProps={{
            orders, batches, currentStore, currentRole,
            t, dark, F, can,
            getStatusLabel, getChannelLabel, getOrderRevenue, fmt2,
            getAllChannels, alarms: getActiveAlarms(),
            auditEntries, describeAuditEntry, formatRelative,
            goal,
            // Helper für Lager- und Finanz-Summary-Widgets
            getActiveBatch, getReservedPools, getChannelFinance,
            getOrderShipping, getOrderFee, getBatchForOrder,
            // Quick action callbacks
            onOpenScanner: () => setShowScanner(true),
            onOpenLager:   () => { openPanel?.("lager"); setShowLager(true); },
            onOpenStats:   () => { openPanel?.("stats"); setShowStats(true); },
            onOpenAdd:     () => { setForm(emptyForm(lastKanal)); setShowSheet(true); },
            onSwitchToOrders: () => setCurrentView("orders"),
            // Stale-Banner-Click → "Alle"-Tab damit User die verspätete Order direkt sieht
            onShowAllOrders: () => { setCurrentView("orders"); setTabIndex(0); },
            onOpenAuditLog: () => { setShowAuditLog(true); setAuditFilter({ entity_type: "all", days: 7 }); setAuditEntries([]); setTimeout(() => loadAuditLog(true), 0); },
          }}
        />
      )}

      {/* ── BOTTOM NAV (mobile/tablet only) ── */}
      {!isDesktop && (() => {
        // Counts für Badges (nur aktive Orders, keine Archivierten)
        const offenInNav  = orders.filter(o => o.status === "Offen"  && !o.archived).length;
        const bereitInNav = orders.filter(o => o.status === "Bereit" && !o.archived).length;
        // Tabs: Dashboard (nur wenn KEIN Helper) | Bestellungen | Scanner
        // Im Helfer-Modus wird das Dashboard ausgeblendet — Workflow-only UX.
        const tabs = [
          ...(helperMode ? [] : [{
            id: "dashboard", label: "Dashboard",
            icon: (<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="2.5" y="2.5" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6"/><rect x="12.5" y="2.5" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6"/><rect x="2.5" y="12.5" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6"/><rect x="12.5" y="12.5" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6"/></svg>),
          }]),
          {
            id: "orders", label: "Bestellungen",
            icon: (<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M3 6.5L11 2L19 6.5L11 11L3 6.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><path d="M3 6.5V15.5L11 20L19 15.5V6.5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><path d="M11 11V20" stroke="currentColor" strokeWidth="1.6"/></svg>),
            // Subtiler blauer Badge mit offen-Anzahl
            badge: offenInNav > 0 ? { count: offenInNav, color: t.accent.blue.solid } : null,
          },
          {
            id: "scanner", label: "Scanner",
            icon: (<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="3" y="6" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M7 6V4.5C7 3.7 7.7 3 8.5 3H13.5C14.3 3 15 3.7 15 4.5V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><circle cx="11" cy="11.5" r="3" stroke="currentColor" strokeWidth="1.6"/></svg>),
            // Prominent lila Badge mit bereit-Anzahl — diese Pakete müssen schnell raus
            badge: bereitInNav > 0 ? { count: bereitInNav, color: t.accent.purple.solid, prominent: true } : null,
            // Wenn keine Order auf Bereit steht: Scanner ist nutzlos. Visuell als
            // disabled markieren (gedimmt) + Tap zeigt nur einen Hinweis-Toast statt
            // den leeren Scanner zu öffnen.
            disabled: bereitInNav === 0,
            disabledHint: "Keine Bestellung zum Scannen — erst Versandlabel hochladen",
          },
        ];
        return (
        <div style={{
          position: "fixed", left: 0, right: 0, bottom: 0,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          background: dark ? "rgba(13,17,23,0.85)" : "rgba(255,255,255,0.85)",
          backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
          borderTop: `0.5px solid ${t.glassBorder}`,
          zIndex: 35,
          fontFamily: F,
        }}>
          <div style={{ display: "flex", padding: "8px 0 6px" }}>
            {tabs.map(item => {
              const active = (item.id === "scanner" && showScanner) || currentView === item.id;
              const disabled = !!item.disabled;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (disabled) {
                      // Disabled-Tab → nur Hinweis-Toast, KEIN Öffnen.
                      haptic(8);
                      showToast(item.disabledHint || "Aktion gerade nicht verfügbar", "info");
                      return;
                    }
                    haptic(6);
                    if (item.id === "scanner") {
                      setShowScanner(true);
                      setScannerState?.("idle");
                    } else {
                      setCurrentView(item.id);
                    }
                  }}
                  style={{
                    flex: 1, background: "none", border: "none",
                    cursor: disabled ? "not-allowed" : "pointer",
                    padding: "6px 4px",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                    color: disabled ? t.textMuted : (active ? t.accent.blue.solid : t.textMuted),
                    opacity: disabled ? 0.4 : 1,
                    transition: "color 0.18s ease, opacity 0.18s ease",
                    fontFamily: F,
                    position: "relative",
                  }}
                >
                  {/* Icon-Container mit absolut-positioniertem Badge oben rechts.
                      Badge sitzt KOMPLETT ausserhalb des Icons (rechts versetzt
                      um die Hälfte der Badge-Breite + 2px Sicht-Abstand) damit
                      das Icon vollständig lesbar bleibt. */}
                  <span style={{ position: "relative", display: "inline-flex" }}>
                    {item.icon}
                    {item.badge && (
                      <span style={{
                        position: "absolute",
                        // Komplett außerhalb des Icons rechts oben:
                        // top: -50% der Badge-Höhe → halb über Icon-Oberkante
                        // left: 100% des Icons → Badge-Linke-Kante = Icon-Rechte-Kante
                        // marginLeft: kleines negatives Padding für sanften Überlap
                        top: -6, left: "100%",
                        marginLeft: -4,
                        minWidth: item.badge.prominent ? 16 : 14,
                        height: item.badge.prominent ? 16 : 14,
                        padding: "0 4px",
                        borderRadius: 8,
                        background: item.badge.color,
                        color: "#fff",
                        fontSize: item.badge.prominent ? 10 : 9,
                        fontWeight: 700,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        lineHeight: 1, letterSpacing: "-0.2px",
                        fontVariantNumeric: "tabular-nums",
                        // Border-Ring damit Badge optisch klar vom Nav-Hintergrund abhebt;
                        // Glow zusätzlich für prominente (Bereit-Versand-Hinweis).
                        boxShadow: item.badge.prominent
                          ? `0 0 0 2px ${dark ? "rgba(13,17,23,0.85)" : "rgba(255,255,255,0.85)"}, 0 2px 8px ${item.badge.color}66`
                          : `0 0 0 1.5px ${dark ? "rgba(13,17,23,0.85)" : "rgba(255,255,255,0.85)"}`,
                      }}>{item.badge.count}</span>
                    )}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: active ? 700 : 500, letterSpacing: "0.01em" }}>{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        );
      })()}

      <div style={{
        maxWidth: containerMaxW, margin: "0 auto",
        display: "flex", flexDirection: "column", alignItems: "stretch",
        // 100dvh statt 100vh — auf iOS berücksichtigt das die dynamische
        // Browser-Chrome (URL-Bar). Verhindert übermässiges Scroll-past-content.
        minHeight: !isDesktop ? "100dvh" : "100vh",
        position: "relative",
        // KEIN extra paddingBottom auf der Page — die Liste-innen hat schon
        // ihre eigene Bottom-Reserve (96px) für die fixed Bottom-Nav.
        // Vorher entstand doppelte Reserve = scrollbarer Leerraum unten.
        paddingBottom: 0,
        WebkitOverflowScrolling: "touch",
      }}>

      {/* ── DESKTOP HEADER (full width above columns) ── */}
      {isDesktop && currentView === "orders" && (
        <div style={{ padding: "32px 32px 16px", flexShrink: 0 }}>
          {/* View Toggle (Desktop) — Dashboard ↔ Bestellungen */}
          {!helperMode && (
            <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
              {[
                { id: "dashboard", label: "Dashboard" },
                { id: "orders",    label: "Bestellungen" },
              ].map(item => {
                const active = currentView === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => { haptic(6); setCurrentView(item.id); }}
                    style={{
                      padding: "8px 16px", border: "none", borderRadius: 12,
                      background: active ? t.pillActive : "transparent",
                      color: active ? t.text : t.textMuted,
                      fontWeight: active ? 600 : 500, fontSize: 13, letterSpacing: "-0.1px",
                      fontFamily: F, cursor: "pointer",
                      transition: "all 0.18s ease",
                      boxShadow: active ? `0 1px 8px rgba(0,0,0,${dark?0.3:0.06})` : "none",
                    }}
                  >{item.label}</button>
                );
              })}
            </div>
          )}
          {/* Title row */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.8px", color: t.text, lineHeight: 1.05 }}>Bestellungen</div>
                {saveState !== "idle" && (
                  <div style={{
                    width: 7, height: 7, borderRadius: "50%",
                    background: saveState === "saving" ? "#FF9F0A" : saveState === "saved" ? "#30D158" : "#FF453A",
                    animation: saveState === "saving" ? "pulse 1.4s infinite" : "none",
                    boxShadow: saveState === "saving" ? "0 0 8px rgba(255,159,10,0.6)" : saveState === "saved" ? "0 0 8px rgba(48,209,88,0.6)" : "0 0 8px rgba(255,69,58,0.6)",
                  }} />
                )}
              </div>
              {!helperMode ? (
                <div style={{ fontSize: 13, color: t.textSub, fontWeight: 500, marginTop: 6, letterSpacing: "0.04em" }}>
                  {(() => {
                    const d = new Date();
                    const days = ["Sonntag","Montag","Dienstag","Mittwoch","Donnerstag","Freitag","Samstag"];
                    const months = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
                    return `${days[d.getDay()]}, ${d.getDate()}. ${months[d.getMonth()]} ${d.getFullYear()}`;
                  })()}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: "#FF9F0A", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#FF9F0A", boxShadow: "0 0 6px rgba(255,159,10,0.7)" }} />
                  Helfer-Modus
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
              {!helperMode && (currentRole === null || can("finance")) && (
                <button onClick={() => openPanel("lager")} style={{ ...glass({ borderRadius: 50, border: `1px solid ${t.glassBorder}` }), width: 42, height: 42, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: t.text, flexShrink: 0, padding: 0, position: "relative" }}>
                  <svg width="19" height="19" viewBox="0 0 18 18" fill="none">
                    <path d="M2.5 6L9 2.5L15.5 6V13L9 16.5L2.5 13V6Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                    <path d="M2.5 6L9 9.5L15.5 6M9 9.5V16.5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                  </svg>
                  {totalUnreserved > 0 && (
                    <div style={{ position: "absolute", top: 7, right: 7, width: 8, height: 8, borderRadius: "50%", background: "#FF9F0A", boxShadow: "0 0 6px rgba(255,159,10,0.7)" }} />
                  )}
                </button>
              )}
              {!helperMode && (
                <button onClick={() => { setShowSearch(v => !v); }} style={{ ...glass({ borderRadius: 50, border: `1px solid ${t.glassBorder}` }), width: 42, height: 42, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: showSearch ? "#0A84FF" : t.text, flexShrink: 0, padding: 0 }}>
                  {showSearch ? (
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                      <path d="M2 2L13 13M13 2L2 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  ) : (
                    <svg width="19" height="19" viewBox="0 0 18 18" fill="none">
                      <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeWidth="2"/>
                      <path d="M11.5 11.5L16 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  )}
                </button>
              )}
              {/* Settings — Zahnrad-Icon, einheitlich mit Dashboard + Orders-Mobile */}
              <button onClick={() => openPanel("settings")} aria-label="Einstellungen" style={{ ...glass({ borderRadius: 50, border: `1px solid ${t.glassBorder}` }), width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: t.text, flexShrink: 0, padding: 0 }}>
                {Icon.gear(18)}
              </button>
            </div>
          </div>

          {/* Tab bar — im Helfer-Modus ausgeblendet (nur Bereit relevant) */}
          {!helperMode && (
            <div style={{ ...glass({ borderRadius: 14, padding: 4 }), display: "flex", gap: 4, marginBottom: 14, position: "relative" }}>
              <div style={{
                position: "absolute", top: 4, left: pillLeft, width: "calc(25% - 6px)", height: "calc(100% - 8px)",
                borderRadius: 11, background: t.pillActive, transition: "left 0.32s cubic-bezier(0.34,1.2,0.64,1)",
                boxShadow: `0 1px 6px rgba(0,0,0,${dark?0.3:0.1})`,
              }} />
              {TABS.map((tab, i) => {
                const isActive   = tabIndex === i;
                const isBereit   = tab === "Bereit"  && counts.Bereit > 0;
                const isRetoure  = tab === "Retoure" && orders.some(o => o.status === "Retoure" && !o.retoureErhalten);
                const hasAlert   = isBereit || isRetoure;
                const alertColor = isRetoure ? "#FF453A" : "#BF5AF2";
                return (
                  <button key={tab} onClick={() => switchTab(i)} style={{
                    flex: 1, padding: "10px 6px", border: "none", cursor: "pointer", fontFamily: F, position: "relative", zIndex: 1,
                    fontSize: 13, fontWeight: isActive ? 600 : 500,
                    background: hasAlert && !isActive ? `${alertColor}1F` : "transparent",
                    borderRadius: 11,
                    color: isActive ? tabColors[tab] : (hasAlert ? alertColor : t.tabInactive),
                    transition: "color 0.22s ease, background 0.22s ease",
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.1, letterSpacing: "0.04em" }}>{tab === "Alle" ? "Alle" : getStatusLabel(tab)}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.1, marginTop: 2, color: isActive ? tabColors[tab] : (hasAlert ? alertColor : t.tabCount) }}>{counts[tab]}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div style={{ display: isDesktop ? "flex" : "block", alignItems: "stretch", gap: isDesktop ? 24 : 0, padding: isDesktop ? "0 32px" : 0, minHeight: 0, flex: 1 }}>
      <div style={{ width: isDesktop ? listW : "auto", maxWidth: isDesktop ? listW : "100%", minWidth: isDesktop ? listW : 0, position: "relative" }}>

      <style>{`
        /* iOS-native Animationen — translate-only (kein scale) für GPU-friendly Compositing.
           Easing: cubic-bezier(0.32, 0.72, 0, 1) ist iOS Sheets/Modals.
           Backdrop nur opacity-Animation, blur ist konstant → wird einmal berechnet und gecached. */
        @keyframes pulse    { 0%,100%{transform:scale(1);opacity:.6}50%{transform:scale(1.5);opacity:0} }
        @keyframes breathe  { 0%,100%{transform:scale(0.9);opacity:0.5}50%{transform:scale(1.15);opacity:1} }
        @keyframes toastIn  { from{opacity:0;transform:translate3d(0,-12px,0)}to{opacity:1;transform:translate3d(0,0,0)} }
        @keyframes slideUp { from{transform:translateX(-50%) translateY(100%)}to{transform:translateX(-50%) translateY(0)} }
        @keyframes slideInRight { from{opacity:0;transform:translate3d(20px,0,0)}to{opacity:1;transform:translate3d(0,0,0)} }
        @keyframes fadeIn  { from{opacity:0}to{opacity:1} }
        /* Modal: translate-only, kein scale (scale forciert Re-Composite mit Backdrop-Filter).
           translate3d für Hardware-Acceleration. iOS-native Easing für „natürliches" Gleiten. */
        @keyframes modalIn { from{opacity:0;transform:translate3d(-50%,16px,0)}to{opacity:1;transform:translate3d(-50%,0,0)} }
        @keyframes skeletonPulse { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes ptrSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        input::placeholder { color: ${t.textMuted}; }
        input[type="date"]::-webkit-calendar-picker-indicator { filter: ${dark ? "invert(1)" : "none"}; opacity: 0.5; }
        /* Accessibility: User mit aktiviertem "Bewegung reduzieren"-OS-Setting bekommen
           alle Animationen quasi instant. Keine Modal-Slide-Ins, kein Spring, kein Bouncen. */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }
      `}</style>

      {!isDesktop && currentView === "orders" && (<>
      {/* ── HEADER (mobile/tablet — in left column) ── */}
      <div style={{
        position: isDesktop ? "relative" : "sticky",
        top: 0, zIndex: 30,
        background: isDesktop ? "transparent" : t.headerBg,
        backdropFilter: isDesktop ? "none" : "blur(40px) saturate(200%)",
        WebkitBackdropFilter: isDesktop ? "none" : "blur(40px) saturate(200%)",
        borderBottom: isDesktop ? "none" : t.headerBorder,
        boxShadow: isDesktop ? "none" : (dark ? "0 1px 20px rgba(0,0,0,0.3)" : "0 1px 32px rgba(0,0,0,0.06)"),
      }}>
        <div style={{ padding: isDesktop ? "32px 4px 16px" : "calc(env(safe-area-inset-top, 20px) + 20px) 16px 12px" }}>

          {/* Title row */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12, gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* P-5-Punkte-Fix Block 2: Zurück-Pfeil zur Landing-Page,
                    konsistent mit Trödel/Markt-Hubs (kleines Pill-Button links) */}
                <button onClick={() => { setAppScreen("store-selector"); haptic(8); }} aria-label="Zurück" style={{
                  width: 30, height: 30, borderRadius: 15, flexShrink: 0,
                  background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                  border: "none", color: t.textMuted, cursor: "pointer", padding: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: "-0.6px", color: t.text, lineHeight: 1.1 }}>Bestellungen</div>{/* Mobile: 26px (matches Dashboard mobile greeting) */}
                {saveState !== "idle" && (
                  <div style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: saveState === "saving" ? "#FF9F0A" : saveState === "saved" ? "#30D158" : "#FF453A",
                    boxShadow: `0 0 6px ${saveState === "saving" ? "#FF9F0A" : saveState === "saved" ? "#30D158" : "#FF453A"}`,
                    transition: "background 0.2s",
                  }} />
                )}
              </div>
              {/* Umsatz unter dem Titel — nur mit Finanz-Rechten */}
              {!helperMode && (currentRole === null || can("finance")) && (
                <button onClick={() => openPanel("stats")} style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: "4px 0 0", display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>Umsatz</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: t.textSub, letterSpacing: "-0.2px" }}>
                    {revenue.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  </span>
                </button>
              )}
              {helperMode && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0 0" }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#FF9F0A", boxShadow: "0 0 6px rgba(255,159,10,0.7)" }} />
                  <span style={{ fontSize: 11, color: "#FF9F0A", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>Helfer-Modus</span>
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
              {/* Lager — versteckt im Helfer-Modus oder ohne Finanz-Rechte */}
              {!helperMode && (currentRole === null || can("finance")) && (
              <button onClick={() => openPanel("lager")} style={{ ...glass({ borderRadius: 50, border: `1px solid ${t.glassBorder}` }), width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: t.text, flexShrink: 0, padding: 0, position: "relative" }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M2.5 6L9 2.5L15.5 6V13L9 16.5L2.5 13V6Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                  <path d="M2.5 6L9 9.5L15.5 6M9 9.5V16.5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                </svg>
                {totalUnreserved > 0 && (
                  <div style={{ position: "absolute", top: 6, right: 6, width: 8, height: 8, borderRadius: "50%", background: "#FF9F0A", boxShadow: "0 0 6px rgba(255,159,10,0.7)" }} />
                )}
              </button>
              )}
              {/* Search — versteckt im Helfer-Modus (Vater braucht keine Suche) */}
              {!helperMode && (
              <button onClick={() => { setShowSearch(v => !v); }} style={{ ...glass({ borderRadius: 50, border: `1px solid ${t.glassBorder}` }), width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: showSearch ? "#0A84FF" : t.text, flexShrink: 0, padding: 0 }}>
                {showSearch ? (
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                    <path d="M2 2L13 13M13 2L2 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeWidth="2"/>
                    <path d="M11.5 11.5L16 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                )}
              </button>
              )}
              {/* Help — nur im Helfer-Modus auf Mobile/Tablet, holt die Anleitung zurück */}
              {helperMode && !isDesktop && helpCollapsed && (
              <button onClick={toggleHelp} aria-label="Anleitung öffnen" style={{
                ...glass({ borderRadius: 50, border: `1px solid ${t.glassBorder}` }),
                width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: t.text, flexShrink: 0, padding: 0,
              }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <circle cx="9" cy="9" r="7.5" stroke="currentColor" strokeWidth="1.6"/>
                  <path d="M6.7 6.7C6.7 5.4 7.7 4.5 9 4.5C10.3 4.5 11.3 5.4 11.3 6.6C11.3 7.5 10.7 8 10 8.4C9.3 8.8 9 9.2 9 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <circle cx="9" cy="13" r="0.9" fill="currentColor"/>
                </svg>
              </button>
              )}
              {/* Settings — Zahnrad-Icon. Versteckt für Helfer-User. */}
              {!isHelperUser && (
              <button onClick={() => openPanel("settings")} aria-label="Einstellungen" style={{ ...glass({ borderRadius: 50, border: `1px solid ${t.glassBorder}` }), width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: t.text, flexShrink: 0, padding: 0 }}>
                {Icon.gear(18)}
              </button>
              )}
            </div>
          </div>

          {/* Search */}
          {showSearch && (
            <div style={{ background: t.searchBg, borderRadius: 14, display: "flex", alignItems: "center", padding: "10px 14px", gap: 8, marginBottom: 10 }}>
              <span style={{ color: t.textMuted, fontSize: 15 }}>⌕</span>
              <input ref={searchRef} type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Suche…"
                style={{ flex: 1, border: "none", background: "transparent", fontFamily: F, fontSize: 16, color: t.text, outline: "none" }} />
              {search && <button onClick={() => setSearch("")} style={{ background: "rgba(120,120,128,0.3)", border: "none", borderRadius: 50, width: 18, height: 18, cursor: "pointer", color: "#fff", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>}
            </div>
          )}

          {/* Lager-leer-Hinweis — sichtbar wenn FIFO keinen Batch mit Restkapazität hat */}
          {!helperMode && !getActiveBatch() && batches.length > 0 && (
            <button
              onClick={() => { openPanel?.("lager"); setShowLager(true); haptic(8); }}
              style={{
                width: "100%", marginBottom: 10, padding: "9px 14px",
                display: "flex", alignItems: "center", gap: 8,
                background: t.accent.red.weak, border: `1px solid ${t.accent.red.solid}55`,
                borderRadius: 12, cursor: "pointer", fontFamily: F, textAlign: "left",
              }}
            >
              <span style={{ flexShrink: 0, color: t.accent.red.solid }}>{Icon.warning(14)}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: t.accent.red.solid, flex: 1 }}>
                Lager leer — neuen Batch anlegen
              </span>
              <span style={{ color: t.accent.red.solid, opacity: 0.7 }}>{Icon.arrow(12)}</span>
            </button>
          )}

          {/* Tab bar — versteckt im Helfer-Modus, dort gibt's nur "Offen" */}
          {!helperMode && (
          <div style={{ position: "relative", ...glass({ borderRadius: 18, padding: "3px" }), display: "flex" }}>
            <div style={{
              position: "absolute", top: 3, left: pillLeft, width: pillWidth, height: "calc(100% - 6px)",
              background: t.pillBg, borderRadius: 14,
              boxShadow: `0 2px 10px rgba(0,0,0,${dark?0.3:0.1}), inset 0 0.5px 0 rgba(255,255,255,${dark?0.12:1})`,
              transition: "left 0.3s cubic-bezier(0.34,1.2,0.64,1)", pointerEvents: "none",
            }} />
            {TABS.map((tab, i) => {
              const isBereit   = tab === "Bereit"  && counts.Bereit > 0;
              const isRetoure  = tab === "Retoure" && orders.some(o => o.status === "Retoure" && !o.retoureErhalten);
              const hasAlert   = isBereit || isRetoure;
              const alertColor = isRetoure ? "#FF453A" : "#BF5AF2";
              const isActive   = tabIndex === i;
              return (
                <button key={tab} onClick={() => switchTab(i)} style={{
                  flex: 1, padding: "9px 2px", border: "none", cursor: "pointer", fontFamily: F,
                  fontSize: 12, fontWeight: isActive ? 700 : 500, borderRadius: 14,
                  background: hasAlert && !isActive ? `${alertColor}1F` : "transparent",
                  position: "relative", zIndex: 1,
                  color: isActive ? tabColors[tab] : (hasAlert ? alertColor : t.tabInactive),
                  transition: "color 0.25s ease, background 0.25s ease",
                }}>
                  <div style={{ display: "inline-block" }}>{tab === "Alle" ? "Alle" : getStatusLabel(tab)}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.1, marginTop: 1, color: isActive ? tabColors[tab] : (hasAlert ? alertColor : t.tabCount) }}>{counts[tab]}</div>
                </button>
              );
            })}
          </div>
          )}

          {/* ── GOAL PROGRESS / HELFER PROGRESS ── */}
          {(() => {
            if (helperMode) {
              // Helfer-Modus: zeigt "Erledigt: X von Y" basierend auf Snapshot.
              // Wenn keine "Bereit"-Bestellungen: Balken voll und grün als Erfolgsmeldung.
              const bereitNow = orders.filter(o => o.status === "Bereit").length;
              const baseCount = Math.max(helperStartCount, bereitNow);
              const done = baseCount - bereitNow;
              const allDone = bereitNow === 0;
              const pct = allDone ? 100 : (baseCount > 0 ? (done / baseCount) * 100 : 0);
              return (
                <div style={{
                  marginTop: 10, width: "100%",
                  padding: "10px 2px 2px", display: "flex", flexDirection: "column", gap: 6,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: allDone ? "#30D158" : t.textSub, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                      {allDone
                        ? "Alles erledigt ✓"
                        : bereitNow === 1
                          ? "1 Bestellung zu versenden"
                          : `${bereitNow} Bestellungen zu versenden`}
                    </span>
                    {allDone ? (
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#30D158", letterSpacing: "-0.2px" }}>
                        Geschafft 🎉
                      </span>
                    ) : baseCount > 0 ? (
                      <span style={{ fontSize: 12, fontWeight: 600, color: t.textSub, letterSpacing: "-0.2px" }}>
                        {done} <span style={{ color: t.textMuted, fontWeight: 500 }}>/ {baseCount}</span>
                      </span>
                    ) : null}
                  </div>
                  <div style={{
                    height: 6, borderRadius: 3, width: "100%", overflow: "hidden",
                    background: dark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.06)",
                  }}>
                    <div style={{
                      height: "100%", width: `${pct}%`, borderRadius: 3,
                      background: allDone
                        ? "linear-gradient(90deg, #30D158 0%, #5ee78a 100%)"
                        : "linear-gradient(90deg, #0A84FF 0%, #5AC8FA 100%)",
                      transition: "width 0.4s cubic-bezier(0.34,1.2,0.64,1)",
                    }} />
                  </div>
                </div>
              );
            }
            const soldCount = orders.filter(o => o.status !== "Retoure").length;
            const pct = Math.min(100, (soldCount / Math.max(goal, 1)) * 100);
            const done = soldCount >= goal;
            return (
              <button onClick={() => setShowGoalEdit(true)} style={{
                marginTop: 10, width: "100%", background: "none", border: "none", cursor: "pointer",
                padding: "6px 2px 2px", display: "flex", flexDirection: "column", gap: 5,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                    Verkaufsziel
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: done ? "#30D158" : t.textSub, letterSpacing: "-0.2px" }}>
                    {soldCount} <span style={{ color: t.textMuted, fontWeight: 500 }}>/ {goal}</span> {done && "✓"}
                  </span>
                </div>
                <div style={{
                  height: 5, borderRadius: 3, width: "100%", overflow: "hidden",
                  background: dark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.06)",
                }}>
                  <div style={{
                    height: "100%", width: `${pct}%`, borderRadius: 3,
                    background: done
                      ? "linear-gradient(90deg, #30D158 0%, #5ee78a 100%)"
                      : "linear-gradient(90deg, #0A84FF 0%, #5AC8FA 100%)",
                    transition: "width 0.5s cubic-bezier(0.34,1.2,0.64,1)",
                    boxShadow: done ? "0 0 8px rgba(48,209,88,0.4)" : "0 0 8px rgba(10,132,255,0.3)",
                  }} />
                </div>
              </button>
            );
          })()}
        </div>
      </div>
      </>)}

      {/* ── LIST ── */}
      {/* paddingBottom mobile: knapp die Bottom-Nav-Höhe (≈70px inkl. Safe-Area)
          + 10px Sicht-Reserve. Damit ist die letzte Order knapp über der Nav,
          ohne dass leerer Raum drunter scrollbar ist. */}
      <div ref={listRef} style={{ padding: isDesktop ? "0 0 60px" : "14px 16px 80px", overflow: "hidden" }}>
        {helperMode && !isDesktop && !helpCollapsed && (
          <div style={{
            ...glass({
              borderRadius: 22,
              border: "1px solid rgba(255,159,10,0.25)",
            }),
            marginBottom: 14,
            overflow: "hidden",
            animation: "fadeIn 0.28s ease",
          }}>
            {/* Header mit Titel links und X-Knopf rechts */}
            <div style={{
              padding: "14px 12px 12px 16px",
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>📋</span>
                <span style={{
                  fontSize: 13, fontWeight: 700, color: t.text,
                  letterSpacing: "-0.1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  So geht's
                </span>
              </div>
              <button onClick={toggleHelp} aria-label="Anleitung schließen" style={{
                ...glass({ borderRadius: 50 }),
                width: 28, height: 28, padding: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: t.textSub, flexShrink: 0,
              }}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M1 1L9 9M9 1L1 9" stroke={t.textSub} strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: "0 16px 18px" }}>
              {[
                { num: "1", title: "Bestellung antippen", text: "Bestellung in der Liste auswählen." },
                { num: "2", title: "Versandlabel öffnen & drucken", text: "Auf den grünen Versandlabel-Knopf tippen, drucken, aufs Paket kleben." },
                { num: "3", title: "Paket einwerfen & abhaken", text: "Auf den violetten Bereit-Knopf tippen — fertig." },
              ].map((step, i) => (
                <div key={step.num} style={{
                  display: "flex", gap: 12, alignItems: "flex-start",
                  marginTop: i === 0 ? 4 : 12,
                }}>
                  <div style={{
                    flexShrink: 0, width: 22, height: 22, borderRadius: 11,
                    background: "rgba(191,90,242,0.15)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 700, color: "#BF5AF2",
                  }}>{step.num}</div>
                  <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 1, letterSpacing: "-0.1px" }}>
                      {step.title}
                    </div>
                    <div style={{ fontSize: 12, color: t.textSub, fontWeight: 400, lineHeight: 1.4 }}>
                      {step.text}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Nachbestell-Alarm-Banner — Channel-Name direkt im Text, Click führt zum Batch */}
        {!helperMode && (() => {
          const alarms = getActiveAlarms();
          if (alarms.length === 0) return null;
          // Sortierung: out vor low, dann nach stock asc → kritischster zuerst
          const sorted = alarms.slice().sort((a, b) => {
            if (a.level === b.level) return (a.stock || 0) - (b.stock || 0);
            return a.level === "out" ? -1 : 1;
          });
          const top = sorted[0];
          const isOut = top.level === "out";
          const color = isOut ? "#FF453A" : "#FF9F0A";
          const bg    = isOut ? "rgba(255,69,58,0.1)" : "rgba(255,159,10,0.1)";
          const icon  = isOut ? "🔴" : "⚠️";
          const channelName = getChannelLabel(top.channelId);
          const otherCount = sorted.length - 1;
          // Wording mit Channel-Name direkt drin (nicht "Pool" — User-Wunsch)
          const text = otherCount === 0
            ? (isOut ? `${channelName}: ausverkauft` : `${channelName}: niedriger Bestand`)
            : (isOut
                ? `${channelName} ausverkauft · ${otherCount} weitere`
                : `${channelName} niedrig · ${otherCount} weitere`);
          // Click: Lager-Modal öffnen, betroffenen Batch ausklappen, hinscrollen
          const handleClick = () => {
            haptic(8);
            openPanel("lager");
            setShowLager(true);
            // Batch ausklappen falls eingeklappt
            setCollapsedBatches(prev => {
              if (!prev.has(top.batch.id)) return prev;
              const next = new Set(prev);
              next.delete(top.batch.id);
              return next;
            });
            // Nach Modal-Animation zum Batch scrollen (Modal braucht ~300ms zum Öffnen)
            setTimeout(() => {
              const el = document.getElementById(`batch-${top.batch.id}`);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 380);
          };
          return (
            <button
              onClick={handleClick}
              style={{
                width: "100%", marginBottom: 14, padding: "12px 14px",
                background: bg, border: `1px solid ${color}40`, borderRadius: 14,
                cursor: "pointer", fontFamily: F, textAlign: "left",
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color, letterSpacing: "-0.1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {text}
                </span>
              </span>
              <span style={{ fontSize: 18, color, flexShrink: 0 }}>›</span>
            </button>
          );
        })()}

        {/* Scanner-Trigger entfernt — Scanner ist jetzt in der Bottom-Nav-Leiste
            (mit lila Bereit-Badge) immer einen Tap entfernt. Doppelter Button
            würde den Bestellungen-Tab unnötig vollstopfen. */}

        <div style={{
          opacity: listVisible ? 1 : 0,
          transform: `translateX(${slideDir * 36}px)`,
          transition: listVisible ? "opacity 0.22s ease, transform 0.26s cubic-bezier(0.34,1.2,0.64,1)" : "opacity 0.14s ease, transform 0.14s ease",
        }}>
          {!loaded && orders.length === 0 ? (
            <div style={{ ...glass({ borderRadius: 18 }), overflow: "hidden" }}>
              {[0,1,2,3,4].map(i => <SkeletonOrderRow key={i} t={t} dark={dark} />)}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "72px 0", color: t.textMuted, fontSize: 16 }}>
              {orders.length === 0 ? "Noch keine Bestellungen" : "Keine Ergebnisse"}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {groupedFiltered.map((group, gIdx) => {
                const isCollapsed = !!collapsedMonths[group.key];
                return (
                <div key={group.key}>
                  <button
                    onClick={() => { setCollapsedMonths(m => ({ ...m, [group.key]: !m[group.key] })); haptic(6); }}
                    style={{ width: "100%", background: "none", border: "none", cursor: "pointer", padding: "0 6px 8px", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: F }}
                  >
                    <span style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                      {group.label}
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, color: t.textSub,
                        background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
                        borderRadius: 10, padding: "2px 8px", letterSpacing: "0.02em",
                        minWidth: 22, textAlign: "center",
                      }}>
                        {group.items.length}
                      </span>
                      <span style={{
                        fontSize: 13, color: t.textMuted, transform: isCollapsed ? "rotate(0deg)" : "rotate(90deg)",
                        transition: "transform 0.22s cubic-bezier(0.34,1.2,0.64,1)", display: "inline-block",
                      }}>›</span>
                    </span>
                  </button>
                  {!isCollapsed && (
                  <div style={{ ...glass({ borderRadius: 22, overflow: "hidden", padding: 0 }) }}>
                    {group.items.map((order, i) => {
                      const isPendingRetoure = order.status === "Retoure" && !order.retoureErhalten;
                      const isMissingLabel = order.status === "Offen" && !order.labelKey;
                      const tintColor = isPendingRetoure ? "#FF453A" : isMissingLabel ? "#0A84FF" : null;
                      return (
                      <div key={order.id} style={{
                        background: tintColor ? `${tintColor}14` : "transparent",
                        boxShadow: tintColor ? `inset 3px 0 0 ${tintColor}` : "none",
                        transition: "background 0.25s ease",
                      }}>
                        {/* Hidden file input entfernt — Label-Upload ausschliesslich
                            im Detail-Modal (single source of truth für Edits). */}
                        {i > 0 && <div style={{ height: "0.5px", background: t.divider, marginLeft: 46 }} />}
                  <button onClick={() => openDetail(order)}
                    onMouseEnter={e => e.currentTarget.style.background = dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    style={{ width: "100%", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", padding: "12px 14px", display: "flex", alignItems: "center", gap: 10, transition: "background 0.16s ease" }}>
                    <div style={{ position: "relative", flexShrink: 0 }}>
                      <div style={{ width: 9, height: 9, borderRadius: "50%", background: STATUS[order.status]?.color, boxShadow: `0 0 7px ${STATUS[order.status]?.color}90` }} />
                      {isPendingRetoure && (
                        <div style={{ position: "absolute", top: -3, left: -3, width: 15, height: 15, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,69,58,0.35) 0%, rgba(255,69,58,0) 70%)", animation: "breathe 2.4s ease-in-out infinite", pointerEvents: "none" }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 15, fontWeight: 500, color: t.text, letterSpacing: "-0.2px" }}>#{order.bestellnummer}</span>
                        {order.trackingNumber && (
                          <span
                            onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(order.trackingNumber); showToast(); haptic(8); }}
                            style={{ fontSize: 12, cursor: "pointer" }}
                            title={order.trackingNumber}
                          >📦</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: t.textSub, marginTop: 1 }}>
                        {helperMode ? fmt(order.datum) : `${order.kanal} · ${fmt(order.datum)}`}{order.notiz ? ` · ${order.notiz}` : ""}
                      </div>
                    </div>
                    {order.status === "Offen" && (() => {
                      const n = ageInDays(order.datum);
                      const urgent = n >= 1;
                      const label = n <= 0 ? "heute" : n === 1 ? "gestern" : `${n} Tage`;
                      if (urgent) {
                        return (
                          <div style={{
                            background: "rgba(255,69,58,0.18)",
                            color: "#FF453A",
                            border: "1px solid rgba(255,69,58,0.35)",
                            borderRadius: 10, padding: "4px 8px",
                            fontFamily: F, fontWeight: 700, fontSize: 11,
                            flexShrink: 0, letterSpacing: "-0.1px",
                            display: "flex", alignItems: "center", gap: 4,
                          }}>
                            <span>⚠</span>{label}
                          </div>
                        );
                      }
                      return (
                        <div style={{
                          color: t.textMuted,
                          fontFamily: F, fontWeight: 500, fontSize: 12,
                          flexShrink: 0, letterSpacing: "-0.1px",
                        }}>
                          {label}
                        </div>
                      );
                    })()}

                    <div onClick={e => {
                      e.stopPropagation();
                      haptic(10);
                      // Status-Pill als Speed-Action für jeden Workflow-Schritt.
                      if (order.status === "Offen") {
                        // Offen → Direkter Datei-Picker für Versandlabel (kein Detail-Modal-Umweg).
                        // setLabelForOrder triggert dann automatisch Offen→Bereit.
                        triggerLabelUpload(order.id);
                      } else if (order.status === "Bereit") {
                        // Schnelle "Versendet"-Bestätigung (auch für Helfer)
                        setShipChoice(order);
                      } else if (order.status === "Versendet") {
                        // Warn before flipping shipped order to Retoure
                        haptic(15);
                        setConfirmRetoure(order.id);
                      } else if (order.status === "Retoure") {
                        // Warn before reverting retoure
                        haptic(15);
                        setConfirmUnretoure(order.id);
                      } else {
                        const nextStatus = STATUS_ORDER[(STATUS_ORDER.indexOf(order.status) + 1) % STATUS_ORDER.length];
                        setOrders(o => o.map(x => x.id !== order.id ? x : { ...x, status: nextStatus }));
                        apiOrderUpdate(order.id, { status: nextStatus });
                      }
                    }} style={{
                      background: STATUS[order.status]?.bg, color: STATUS[order.status]?.color,
                      border: `1px solid ${STATUS[order.status]?.color}35`,
                      borderRadius: 10, padding: "4px 10px", fontFamily: F, fontWeight: 600, fontSize: 12, flexShrink: 0, cursor: "pointer",
                    }}>{getStatusLabel(order.status)}</div>
                    <div style={{ color: t.chevron, fontSize: 20, fontWeight: 200, flexShrink: 0 }}>›</div>
                  </button>
                </div>
                );
              })}
                  </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
          {hasMoreOrders && (
            <div style={{ textAlign: "center", padding: "14px 0 4px" }}>
              <button
                onClick={loadMoreOrders}
                style={{
                  background: "none", border: `1px solid ${t.glassBorder}`,
                  borderRadius: 10, padding: "8px 20px", cursor: "pointer",
                  color: t.textMuted, fontFamily: F, fontSize: 13, fontWeight: 600,
                }}
              >Mehr laden</button>
            </div>
          )}
        </div>
      </div>
      </div>{/* ── /left column ── */}

      {/* ── RIGHT COLUMN (desktop only) ── */}
      {isDesktop && (
        <div style={{ flex: 1, padding: "23px 0 60px" }}>
          {(showDetail && o) ? null /* detail rendered inline below */ :
           showStats         ? null /* stats rendered inline below */ :
           showLager         ? null /* lager rendered inline below */ :
           showSettings      ? null /* settings rendered inline below */ :
           helperMode        ? (
            // ── HELFER-MODUS DASHBOARD: nur Bereit-Bestellungen, klare Anleitung ──
            (() => {
              const bereitOrders = orders.filter(o => o.status === "Bereit");
              const bereitNow = bereitOrders.length;
              const allDone = bereitNow === 0;
              // Tagesziel: helperStartCount ist die Zahl beim Start der Helfer-Session.
              // Falls Vater zwischendrin erst angefangen hat (helperStartCount === 0), nehmen wir bereitNow als Start.
              const sessionStart = Math.max(helperStartCount || 0, bereitNow);
              const sessionDone = sessionStart - bereitNow;
              const sessionPct = sessionStart > 0 ? Math.min(100, (sessionDone / sessionStart) * 100) : 0;
              const openAllLabels = async () => {
                for (const o of bereitOrders) {
                  if (o.labelKey) {
                    // sequenziell, damit Browser die Pop-Ups nicht alle gleichzeitig blockt
                    // eslint-disable-next-line no-await-in-loop
                    await openLabel(o.labelKey);
                  }
                }
              };
              return (
                <div style={{ maxWidth: 520, margin: "0 auto" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#FF9F0A", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#FF9F0A", boxShadow: "0 0 6px rgba(255,159,10,0.7)" }} />
                    Helfer-Modus
                  </div>

                  {/* Status-Karte */}
                  <div style={{ ...glass({ borderRadius: 22, border: allDone ? "1px solid rgba(48,209,88,0.3)" : `1px solid ${t.glassBorder}` }), padding: "32px 28px", marginBottom: 16, textAlign: "center" }}>
                    {allDone ? (
                      <>
                        <div style={{ fontSize: 64, marginBottom: 8 }}>✅</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: "#30D158", letterSpacing: "-0.5px", marginBottom: 6 }}>Alle versendet!</div>
                        <div style={{ fontSize: 14, color: t.textSub, fontWeight: 500 }}>{sessionDone > 0 ? `${sessionDone} ${sessionDone === 1 ? "Paket" : "Pakete"} geschafft. Danke!` : "Sehr gut. Es ist nichts mehr zu tun."}</div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 56, fontWeight: 700, color: "#BF5AF2", letterSpacing: "-2px", lineHeight: 1, marginBottom: 6 }}>{bereitNow}</div>
                        <div style={{ fontSize: 16, color: t.text, fontWeight: 600, marginBottom: sessionDone > 0 ? 16 : 10 }}>
                          {bereitNow === 1 ? "Bestellung" : "Bestellungen"} zu versenden
                        </div>
                        {sessionDone > 0 && (
                          <div style={{ marginBottom: 14 }}>
                            <div style={{ fontSize: 13, color: t.textSub, fontWeight: 600, marginBottom: 6 }}>
                              {sessionDone} von {sessionStart} geschafft
                            </div>
                            <div style={{ height: 6, borderRadius: 3, background: dark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.06)", overflow: "hidden", margin: "0 auto", maxWidth: 280 }}>
                              <div style={{
                                height: "100%", width: `${sessionPct}%`, borderRadius: 3,
                                background: "linear-gradient(90deg, #30D158 0%, #5ee78a 100%)",
                                transition: "width 0.5s cubic-bezier(0.34,1.2,0.64,1)",
                                boxShadow: "0 0 8px rgba(48,209,88,0.4)",
                              }} />
                            </div>
                          </div>
                        )}
                        {/* Bulk-Print: alle Labels nacheinander öffnen */}
                        {bereitOrders.some(o => o.labelKey) && (
                          <button onClick={openAllLabels} style={{
                            background: "linear-gradient(180deg, #30D158 0%, #28b94c 100%)",
                            border: "1px solid rgba(48,209,88,0.5)",
                            borderRadius: 18, padding: "16px 22px",
                            display: "inline-flex", alignItems: "center", gap: 10,
                            cursor: "pointer", color: "#fff", fontFamily: F, fontWeight: 700, fontSize: 15,
                            boxShadow: "0 6px 20px rgba(48,209,88,0.35), inset 0 1px 0 rgba(255,255,255,0.25)",
                            transition: "transform 0.16s cubic-bezier(0.34,1.2,0.64,1)",
                            letterSpacing: "-0.2px", marginBottom: 10,
                          }}
                            onMouseDown={e => e.currentTarget.style.transform = "scale(0.97)"}
                            onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
                            onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                            onTouchStart={e => e.currentTarget.style.transform = "scale(0.97)"}
                            onTouchEnd={e => e.currentTarget.style.transform = "scale(1)"}
                          >
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                              <path d="M5 3H13V6H5V3Z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round"/>
                              <path d="M5 11H13V15H5V11Z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round"/>
                              <path d="M3 6H15V11H3V6Z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round"/>
                            </svg>
                            Alle {bereitOrders.filter(o => o.labelKey).length} Labels öffnen
                          </button>
                        )}
                        <div style={{ fontSize: 13, color: t.textSub, fontWeight: 500 }}>
                          {bereitOrders.some(o => o.labelKey) ? "oder einzeln links auswählen" : "Wähle links eine aus, um zu starten"}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Anleitung — nur 3 Schritte */}
                  {!allDone && (
                    <div style={{ ...glass({ borderRadius: 22 }), padding: "24px 26px" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: t.textSub, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>So geht's</div>
                      {[
                        { num: "1", title: "Bestellung antippen", text: "Eine Bestellung links auswählen." },
                        { num: "2", title: "Versandlabel öffnen & drucken", text: "Auf den grünen Versandlabel-Knopf tippen — PDF öffnet sich, dann drucken und aufs Paket kleben." },
                        { num: "3", title: "Paket verschicken & abhaken", text: "Paket einwerfen, dann auf den violetten Bereit-Knopf tippen — fertig." },
                      ].map(step => (
                        <div key={step.num} style={{ display: "flex", gap: 14, marginBottom: 14, alignItems: "flex-start" }}>
                          <div style={{
                            flexShrink: 0, width: 28, height: 28, borderRadius: 14,
                            background: "rgba(191,90,242,0.15)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 13, fontWeight: 700, color: "#BF5AF2",
                          }}>{step.num}</div>
                          <div style={{ flex: 1, paddingTop: 2 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: t.text, marginBottom: 2 }}>{step.title}</div>
                            <div style={{ fontSize: 13, color: t.textSub, fontWeight: 400, lineHeight: 1.45 }}>{step.text}</div>
                          </div>
                        </div>
                      ))}
                      <div style={{ height: 1, background: t.divider, margin: "16px 0 14px" }} />
                      <div style={{ fontSize: 12, color: t.textMuted, fontWeight: 500, lineHeight: 1.5 }}>
                        Bei Fragen oder Problemen Noah anrufen.<br/>Keine Sorge — du kannst nichts kaputtmachen.
                      </div>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            (() => {
              const soldCount = orders.filter(o => o.status !== "Retoure").length;
              const pct = Math.min(100, (soldCount / Math.max(goal, 1)) * 100);
              const done = soldCount >= goal;
              const tilePress = e => { e.currentTarget.style.transform = "scale(0.98)"; };
              const tileRelease = e => { e.currentTarget.style.transform = "scale(1)"; };
              return (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

                {/* Verkaufsziel — Card mit Progress-Bar */}
                <button onClick={() => setShowGoalEdit(true)}
                  onMouseDown={tilePress} onMouseUp={tileRelease} onMouseLeave={tileRelease}
                  onTouchStart={tilePress} onTouchEnd={tileRelease}
                  style={{
                    ...glass({ borderRadius: 20 }), padding: "18px 22px",
                    border: `1px solid ${t.glassBorder}`, cursor: "pointer", fontFamily: F, textAlign: "left",
                    display: "flex", flexDirection: "column", gap: 12,
                    transition: "transform 0.18s cubic-bezier(0.34,1.2,0.64,1)",
                  }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                      Verkaufsziel
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: done ? "#30D158" : t.text, letterSpacing: "-0.2px" }}>
                      {soldCount} <span style={{ color: t.textMuted, fontWeight: 500 }}>/ {goal}</span> {done && "✓"}
                    </span>
                  </div>
                  <div style={{
                    height: 6, borderRadius: 3, width: "100%", overflow: "hidden",
                    background: dark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.06)",
                  }}>
                    <div style={{
                      height: "100%", width: `${pct}%`, borderRadius: 3,
                      background: done
                        ? "linear-gradient(90deg, #30D158 0%, #5ee78a 100%)"
                        : "linear-gradient(90deg, #0A84FF 0%, #5AC8FA 100%)",
                      transition: "width 0.5s cubic-bezier(0.34,1.2,0.64,1)",
                      boxShadow: done ? "0 0 8px rgba(48,209,88,0.4)" : "0 0 8px rgba(10,132,255,0.3)",
                    }} />
                  </div>
                </button>

                {/* Umsatz gesamt — Hero */}
                <div style={{ ...glass({ borderRadius: 20 }), padding: "22px 24px" }}>
                  <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 8, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>Umsatz gesamt</div>
                  <div style={{ fontSize: 42, fontWeight: 700, letterSpacing: "-1.2px", color: t.text, lineHeight: 1 }}>{fmt2(revenue)} €</div>
                  <div style={{ fontSize: 13, color: t.textSub, marginTop: 8, fontWeight: 500 }}>{counts.Versendet + (orders.filter(x => x.status === "Offen").length)} Bestellungen · {counts.Retoure} Retouren</div>
                </div>

                {/* 2 Aktions-Cards (gleiche Höhe + Press-Animation) */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {[
                    { label: "Finanzen", text: "Umsatz, GuV, Cashflow", panel: "stats" },
                    { label: "Lager",    text: "Batches & Bestand",     panel: "lager" },
                  ].map(a => (
                    <button key={a.panel} onClick={() => openPanel(a.panel)}
                      onMouseDown={tilePress} onMouseUp={tileRelease} onMouseLeave={tileRelease}
                      onTouchStart={tilePress} onTouchEnd={tileRelease}
                      style={{
                        ...glass({ borderRadius: 18 }), padding: "18px 18px",
                        textAlign: "left", cursor: "pointer", border: `1px solid ${t.glassBorder}`, fontFamily: F,
                        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
                        minHeight: 76,
                        transition: "transform 0.18s cubic-bezier(0.34,1.2,0.64,1)",
                      }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>{a.label}</div>
                        <div style={{ fontSize: 15, color: t.text, fontWeight: 600 }}>{a.text}</div>
                      </div>
                      <div style={{ color: t.textMuted, fontSize: 22, fontWeight: 300, flexShrink: 0 }}>›</div>
                    </button>
                  ))}
                </div>
              </div>
              );
            })()
          )}
        </div>
      )}

      </div>{/* ── /columns row ── */}
      </div>{/* ── /outer column ── */}

      {/* ── FAB — versteckt im Helfer-Modus oder ohne Schreibrechte ── */}
      {!helperMode && (currentRole === null || can("write")) && (
      <div style={{
        position: "fixed",
        // Mobile/Tablet: über die Bottom-Nav heben (Nav ≈48px Inhalt + Safe-Area).
        // Desktop: keine Bottom-Nav vorhanden, alter Wert bleibt.
        bottom: isDesktop ? 38 : "calc(env(safe-area-inset-bottom, 0px) + 88px)",
        right: isDesktop
          ? `calc(${dRight} + 32px)`
          : isTablet
          ? `max(16px, calc(50% - ${listW/2 - 32}px))`
          : "max(16px, calc(50% - 215px + 32px))",
        zIndex: 36, // > Bottom-Nav (35), < Modale (50+)
      }}>
        {/* FAB — schlanker, brand-konsistenter (Akzent-Gradient) statt
            schwerem schwarzem Glass-Disc. Passt zum Schwung-Modus + iOS-26-Look. */}
        <button onClick={openNew} aria-label="Neue Bestellung" style={{
          width: 52, height: 52, borderRadius: 26,
          background: `linear-gradient(135deg, ${t.accent.blue.solid}, ${t.accent.purple.solid})`,
          border: "none",
          boxShadow: `0 6px 20px ${t.accent.blue.weak}, 0 2px 6px rgba(0,0,0,0.18)`,
          cursor: "pointer", padding: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "transform 0.16s cubic-bezier(0.32, 0.72, 0, 1)",
          willChange: "transform",
        }}
          onMouseDown={e => e.currentTarget.style.transform = "scale(0.92)"}
          onMouseUp={e   => e.currentTarget.style.transform = "scale(1)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
          onTouchStart={e => e.currentTarget.style.transform = "scale(0.92)"}
          onTouchEnd={e   => { e.currentTarget.style.transform = "scale(1)"; }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M9 3V15M3 9H15" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
      )}

      {/* ── DETAIL MODAL — iOS-native Smooth-Animation (translate3d, sheetIn, willChange) ── */}
      {showDetail && o && (
        <div onClick={isDesktop ? undefined : closeDetail} style={isDesktop ? { position: "fixed", inset: 0, pointerEvents: "none", zIndex: 50 } : {
          position: "fixed", inset: 0, zIndex: 50,
          background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
          backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
          animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
          willChange: "opacity",
        }}>
          <div onClick={e => e.stopPropagation()} style={isDesktop ? {
            ...desktopRightPanel,
            ...glass({ boxShadow: dark ? "0 0 48px rgba(0,0,0,0.4)" : "0 0 48px rgba(0,0,0,0.08)" }),
            zIndex: 51, animation: "slideInRight 0.24s cubic-bezier(0.32,0.72,0,1)",
            overflowY: "auto", overflowX: "hidden",
            pointerEvents: "auto",
          } : {
            // Mobile: visualViewport-aware → Tastatur-resistent (kein Whole-Popup-Scroll).
            ...modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 400, radius: 24, padding: 0, zIndex: 51 }),
            overflowX: "hidden",
          }}>
            <div style={{ padding: isDesktop ? "24px 32px 40px" : "20px 22px 22px" }}>

              {/* Header — einheitlich mit allen anderen Modals via ModalHeader */}
              {!isDesktop && (
                <ModalHeader t={t} dark={dark} F={F} title="Bestellung" onClose={closeDetail} marginBottom={18} />
              )}
              {isDesktop && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: helperMode ? 12 : 18 }}>
                  <ModalCloseButton onClose={closeDetail} dark={dark} />
                </div>
              )}

              {/* Helfer-Kontext-Badge */}
              {helperMode && (
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 20, marginBottom: 14,
                  background: o.status === "Bereit" ? "rgba(191,90,242,0.12)" :
                              o.status === "Offen" ? "rgba(255,159,10,0.14)" :
                              o.status === "Versendet" ? "rgba(48,209,88,0.14)" : "rgba(0,0,0,0.06)",
                  color: o.status === "Bereit" ? "#BF5AF2" :
                         o.status === "Offen" ? "#FF9F0A" :
                         o.status === "Versendet" ? "#30D158" : t.textSub,
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
                }}>
                  {o.status === "Offen"     ? "Etikett kaufen + versenden"
                  : o.status === "Bereit"    ? "Bereit · nur einwerfen"
                  : o.status === "Versendet" ? "Versendet ✓"
                  : "Bestellung"}
                </div>
              )}

              {/* HERO — Bestellnummer groß + Metadata-Zeile */}
              <div style={{ marginBottom: 22 }}>
                {!helperMode ? (
                  <input type="text" value={o.bestellnummer} placeholder="Bestellnummer"
                    onChange={e => updateOrder(o.id, "bestellnummer", e.target.value)}
                    style={{
                      width: "100%", border: "none", background: "transparent",
                      fontFamily: F, fontSize: 32, fontWeight: 700, letterSpacing: "-0.8px",
                      color: t.text, outline: "none", padding: 0, lineHeight: 1.1,
                    }} />
                ) : (
                  <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.8px", color: t.text, lineHeight: 1.1, wordBreak: "break-all" }}>
                    {o.bestellnummer || "—"}
                  </div>
                )}

                {/* Metadata-Zeile: Status-Chip · Kanal · Datum */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                  {!helperMode && o.status === "Bereit" ? (
                    <button onClick={() => setShipChoice(o)} style={{
                      background: STATUS[o.status]?.bg, color: STATUS[o.status]?.color,
                      border: `1px solid ${STATUS[o.status]?.color}40`, borderRadius: 20,
                      padding: "5px 12px", fontFamily: F, fontWeight: 600, fontSize: 12,
                      cursor: "pointer", flexShrink: 0,
                    }}>{o.status} → Versendet</button>
                  ) : (
                    <span style={{
                      background: STATUS[o.status]?.bg, color: STATUS[o.status]?.color,
                      border: `1px solid ${STATUS[o.status]?.color}40`, borderRadius: 20,
                      padding: "5px 12px", fontFamily: F, fontWeight: 600, fontSize: 12, flexShrink: 0,
                    }}>{getStatusLabel(o.status)}</span>
                  )}
                  <span style={{ fontSize: 13, color: t.textSub, fontWeight: 500 }}>{getChannelLabel(o.kanal)}</span>
                  <span style={{ fontSize: 12, color: t.textMuted }}>·</span>
                  {!helperMode ? (
                    <input type="date" value={o.datum || today()} onChange={e => updateOrder(o.id, "datum", e.target.value)}
                      style={{ border: "none", background: "transparent", fontFamily: F, fontSize: 13, color: t.textSub, outline: "none", padding: 0, fontWeight: 500 }} />
                  ) : (
                    <span style={{ fontSize: 13, color: t.textSub, fontWeight: 500 }}>
                      {(() => { const d = new Date(o.datum); return isNaN(d) ? o.datum : `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`; })()}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {!helperMode && orders.some(x => x.id !== o.id && x.bestellnummer.trim() === o.bestellnummer.trim()) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 14px", background: "rgba(255,159,10,0.12)", border: "1px solid rgba(255,159,10,0.3)", borderRadius: 14 }}>
                    <span style={{ fontSize: 14 }}>⚠️</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#FF9F0A", fontFamily: F }}>Bestellnummer bereits vorhanden</span>
                  </div>
                )}

                {!helperMode && (
                  <div style={{ ...glass({ borderRadius: 14, padding: 3 }), display: "flex" }}>
                    {getActiveChannels().map(ch => (
                      <button key={ch.id} onClick={() => updateOrder(o.id, "kanal", ch.id)} style={{
                        flex: 1, padding: "10px 4px", border: "none", cursor: "pointer", fontFamily: F,
                        fontSize: 14, fontWeight: o.kanal === ch.id ? 600 : 500, borderRadius: 11, transition: "all 0.2s ease",
                        background: o.kanal === ch.id ? t.pillActive : "transparent",
                        color: o.kanal === ch.id ? t.text : t.segText,
                        boxShadow: o.kanal === ch.id ? `0 1px 6px rgba(0,0,0,${dark?0.3:0.1})` : "none",
                      }}>{ch.name}</button>
                    ))}
                  </div>
                )}

                {/* Versand-Label — primärer Workflow-Anker */}
                {o.labelKey ? (
                  // Label vorhanden: prominenter Open-Button (für Vater UND Noah)
                  <div onClick={() => openLabel(o.labelKey)} style={{
                    ...glass({ borderRadius: 16, border: "1px solid rgba(48,209,88,0.35)" }),
                    padding: "14px 14px", display: "flex", alignItems: "center", gap: 10,
                    cursor: "pointer", width: "100%", textAlign: "left", fontFamily: F,
                    transition: "transform 0.12s ease",
                    boxSizing: "border-box", overflow: "hidden",
                  }}
                    onMouseDown={e => e.currentTarget.style.transform = "scale(0.98)"}
                    onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
                    onTouchStart={e => e.currentTarget.style.transform = "scale(0.98)"}
                    onTouchEnd={e => e.currentTarget.style.transform = "scale(1)"}
                  >
                    <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 11, background: "rgba(48,209,88,0.18)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>📄</div>
                    <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#30D158", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Versandlabel</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Öffnen & drucken ›</div>
                    </div>
                    {!helperMode && (
                      <button onClick={e => {
                        e.stopPropagation();
                        document.getElementById(`label-detail-input-${o.id}`)?.click();
                      }} style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "#0A84FF", fontFamily: F, fontSize: 13, fontWeight: 600, padding: "6px 0", flexShrink: 0,
                      }}>Ersetzen</button>
                    )}
                  </div>
                ) : (
                  // Kein Label: Upload-Button (nur Noah). Vater sieht stattdessen Hinweis.
                  helperMode ? (
                    <div style={{ ...glass({ borderRadius: 16, border: "1px solid rgba(255,69,58,0.3)" }), padding: "12px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 16 }}>⚠️</span>
                      <span style={{ fontSize: 13, color: "#FF453A", fontWeight: 600 }}>Kein Label hinterlegt — bei Noah nachfragen</span>
                    </div>
                  ) : (
                    <button onClick={() => document.getElementById(`label-detail-input-${o.id}`)?.click()} style={{
                      ...glass({ borderRadius: 16, border: `1px dashed ${t.glassBorder}` }),
                      padding: "16px 16px", display: "flex", alignItems: "center", gap: 12,
                      cursor: "pointer", width: "100%", textAlign: "left", fontFamily: F,
                    }}>
                      <div style={{ flexShrink: 0, width: 44, height: 44, borderRadius: 12, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>📤</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: t.textSub, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Versandlabel</div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: "#0A84FF" }}>PDF oder Bild hochladen</div>
                      </div>
                    </button>
                  )
                )}

                {/* Hidden input fürs Detail-Panel */}
                {!helperMode && (
                  <input
                    id={`label-detail-input-${o.id}`}
                    type="file"
                    accept="application/pdf,image/*"
                    style={{ display: "none" }}
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) setLabelForOrder(o.id, f);
                      e.target.value = "";
                    }}
                  />
                )}

                {/* Helfer-Bestätigung: Paket eingeworfen → Versendet */}
                {helperMode && o.status === "Bereit" && (
                  <button onClick={() => setShipChoice(o)} style={{
                    marginTop: 4,
                    background: "linear-gradient(180deg, #30D158 0%, #28b94c 100%)",
                    border: "1px solid rgba(48,209,88,0.5)",
                    borderRadius: 18, padding: "18px 18px",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                    cursor: "pointer", color: "#fff", fontFamily: F, fontWeight: 700, fontSize: 16,
                    width: "100%", boxShadow: "0 6px 20px rgba(48,209,88,0.35), inset 0 1px 0 rgba(255,255,255,0.25)",
                    transition: "transform 0.16s cubic-bezier(0.34,1.2,0.64,1)",
                    letterSpacing: "-0.2px",
                  }}
                    onMouseDown={e => e.currentTarget.style.transform = "scale(0.97)"}
                    onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
                    onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                    onTouchStart={e => e.currentTarget.style.transform = "scale(0.97)"}
                    onTouchEnd={e => e.currentTarget.style.transform = "scale(1)"}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M4 10.5L8 14.5L16 6" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Paket eingeworfen
                  </button>
                )}

                {/* Sendungsnummer — optional, nur für Noah, rein informativ.
                    Workflow basiert jetzt auf Label-PDF, nicht auf Sendungsnummer. */}

                {!helperMode && (
                <div style={{ ...glass({ borderRadius: 16 }), overflow: "hidden" }}>
                  <input type="text" value={o.notiz || ""} placeholder="Notiz…"
                    onChange={e => updateOrder(o.id, "notiz", e.target.value)}
                    style={inp({ padding: "14px 16px" })} />
                </div>
                )}

                {!helperMode && o.status === "Retoure" && (
                  <button onClick={() => toggleRetoureErhalten(o.id)} style={{ ...glass({ borderRadius: 16, border: `1px solid ${o.retoureErhalten ? "rgba(48,209,88,0.3)" : "rgba(255,69,58,0.25)"}` }), padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", width: "100%", transition: "all 0.2s ease" }}>
                    <span style={{ fontSize: 13, color: t.textSub, fontWeight: 500 }}>Retoure erhalten</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: o.retoureErhalten ? "#30D158" : "#FF453A" }}>{o.retoureErhalten ? "Ja" : "Ausstehend"}</span>
                      <div style={{ width: 22, height: 22, borderRadius: "50%", background: o.retoureErhalten ? "#30D158" : "transparent", border: o.retoureErhalten ? "none" : "1.5px solid rgba(255,69,58,0.4)", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}>
                        {o.retoureErhalten && <svg width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4.5L4 7.5L10 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>
                    </div>
                  </button>
                )}

                {!helperMode && (currentRole === null || can("delete")) && (!detailDeleteConfirm ? (
                  <button onClick={() => setDetailDeleteConfirm(true)} style={{
                    marginTop: 16, background: "transparent", border: "none",
                    cursor: "pointer", color: "#FF453A", fontFamily: F, fontWeight: 500, fontSize: 13,
                    width: "100%", padding: "10px", textAlign: "center",
                    transition: "opacity 0.16s ease", opacity: 0.7,
                  }}
                    onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                    onMouseLeave={e => e.currentTarget.style.opacity = "0.7"}
                  >Bestellung löschen</button>
                ) : (
                  <div style={{ marginTop: 16, ...glass({ borderRadius: 16, border: `1px solid ${t.deleteBdr}` }), padding: "14px 16px" }}>
                    <div style={{ fontSize: 13, color: t.textSub, textAlign: "center", marginBottom: 12 }}>Wirklich löschen?</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => setDetailDeleteConfirm(false)} style={{ flex: 1, background: t.btnGhost, border: "none", borderRadius: 12, padding: "11px", fontFamily: F, fontWeight: 600, fontSize: 14, cursor: "pointer", color: t.btnGhostTxt }}>Abbrechen</button>
                      <button onClick={() => deleteFromDetail(o.id)} style={{ flex: 1, background: "#FF453A", border: "none", borderRadius: 12, padding: "11px", fontFamily: F, fontWeight: 600, fontSize: 14, cursor: "pointer", color: "#fff" }}>Löschen</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD SHEET ── Mobile: top-anchored damit Eingabefeld klar über
          der iOS-Tastatur sichtbar bleibt (vorher bottom: 0 → Sheet wurde
          von Tastatur verdeckt). Desktop bleibt zentriert wie vorher. */}
      {showSheet && (
        <>
          <div onClick={closeSheet} style={{
            position: "fixed", inset: 0,
            background: dark ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0.32)",
            zIndex: 40,
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />
          <div
            style={isDesktop ? {
              ...desktopCenterPopup,
              ...glass({ borderRadius: 24, boxShadow: dark ? "0 24px 64px rgba(0,0,0,0.5)" : "0 24px 64px rgba(0,0,0,0.18)" }),
              zIndex: 50, animation: "modalIn 0.26s cubic-bezier(0.34,1.2,0.64,1)",
              overflowY: "auto",
            } : modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 400, radius: 24, padding: 0, zIndex: 50 })}
          >
            {/* Drag-Indicator weggefallen — bei top-anchored macht Swipe-to-close
                weniger Sinn. User schliesst per "Fertig" oder Backdrop-Tap. */}
            <div style={isDesktop ? { padding: "24px" } : { paddingTop: 20, paddingLeft: 22, paddingRight: 22, paddingBottom: "calc(22px + env(safe-area-inset-bottom, 0px))" }}>
              {/* Einheitlicher ModalHeader — Close + zentrierter Title */}
              <ModalHeader t={t} dark={dark} F={F} title="Neue Bestellung" onClose={closeSheet} marginBottom={18} />

              <div style={{ ...glass({ borderRadius: 18 }), marginBottom: 14, display: "flex", alignItems: "center", overflow: "hidden", minWidth: 0 }}>
                <input ref={inputRef} type="text" value={form.bestellnummer} placeholder="Bestellnummer" autoComplete="off"
                  onChange={e => setForm(v => ({ ...v, bestellnummer: e.target.value }))}
                  onKeyDown={e => e.key === "Enter" && save()}
                  style={inp({ flex: 1, width: "auto", minWidth: 0 })} />
                <button onClick={() => { inputRef.current?.focus(); document.execCommand('paste'); }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#0A84FF", fontFamily: F, fontSize: 14, fontWeight: 600, padding: "0 16px", flexShrink: 0, whiteSpace: "nowrap" }}>
                  Einfügen
                </button>
              </div>

              <div style={{ ...glass({ borderRadius: 14, padding: 3 }), display: "flex", marginBottom: 14 }}>
                {getActiveChannels().map(ch => (
                  <button key={ch.id} onMouseDown={e => e.preventDefault()} onClick={() => { setForm(v => ({ ...v, kanal: ch.id })); inputRef.current?.focus(); }} style={{
                    flex: 1, padding: "8px 4px", border: "none", cursor: "pointer", fontFamily: F,
                    fontSize: 14, fontWeight: form.kanal === ch.id ? 600 : 400, borderRadius: 11, transition: "all 0.18s",
                    background: form.kanal === ch.id ? t.pillActive : t.segInactive,
                    color: form.kanal === ch.id ? t.text : t.segText,
                    boxShadow: form.kanal === ch.id ? `0 1px 6px rgba(0,0,0,${dark?0.3:0.1})` : "none",
                  }}>{ch.name}</button>
                ))}
              </div>

              {isDuplicate && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: "rgba(255,159,10,0.12)", border: "1px solid rgba(255,159,10,0.3)", borderRadius: 12, marginBottom: 2 }}>
                  <span style={{ fontSize: 14 }}>⚠️</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#FF9F0A", fontFamily: F }}>Bestellnummer bereits vorhanden</span>
                </div>
              )}

              {/* FIFO-Stock-Hinweis: kein Batch hat noch freie Stück → Submit blockiert */}
              {!getActiveBatch() && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: t.accent.red.weak, border: `1px solid ${t.accent.red.solid}55`, borderRadius: 12, marginBottom: 2 }}>
                  <span style={{ fontSize: 14 }}>⚠️</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: t.accent.red.solid, fontFamily: F }}>Lager leer — neuen Batch anlegen</span>
                </div>
              )}

              {(() => {
                const stockOk = !!getActiveBatch();
                const enabled = form.bestellnummer && !isDuplicate && stockOk;
                return (
                  <button onClick={save} disabled={!enabled}
                    // preventDefault auf MouseDown/TouchStart → Klick blurt den
                    // Input NICHT, iOS-Tastatur bleibt offen für die nächste Eingabe.
                    onMouseDown={e => e.preventDefault()}
                    onTouchStart={e => e.preventDefault()}
                    style={{
                    width: "100%", border: "none", borderRadius: 18, padding: "17px", fontFamily: F, fontWeight: 600, fontSize: 17,
                    cursor: enabled ? "pointer" : "not-allowed",
                    background: enabled ? "rgba(10,132,255,0.85)" : t.btnGhost,
                    color: enabled ? "#fff" : t.textMuted,
                    boxShadow: enabled ? "0 4px 24px rgba(10,132,255,0.32)" : "none",
                    transition: "all 0.2s ease",
                  }}>Hinzufügen</button>
                );
              })()}
            </div>
          </div>
        </>
      )}
      {/* ── INLINE LABEL UPLOAD (für Offen-Pille → direkter Datei-Picker ohne Detail-Modal) ── */}
      <input
        ref={labelUploadInputRef}
        type="file"
        accept="image/*,application/pdf"
        style={{ display: "none" }}
        onChange={async e => {
          const f = e.target.files?.[0];
          const id = pendingLabelOrderIdRef.current;
          e.target.value = "";
          pendingLabelOrderIdRef.current = null;
          if (f && id) await setLabelForOrder(id, f);
        }}
      />

      {/* ── TOAST STACK (oben mittig, max 3, neueste oben) ── */}
      {toasts.length > 0 && (
        <div style={{
          position: "fixed", top: "calc(env(safe-area-inset-top, 0px) + 14px)",
          left: 0, right: 0, zIndex: 100,
          display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
          pointerEvents: "none",
        }}>
          {toasts.slice().reverse().map((tt, idx) => {
            const TYPE = {
              success: { color: t.accent.green.solid,  bg: dark ? "rgba(30,40,30,0.88)"  : "rgba(240,255,240,0.92)", border: "rgba(48,209,88,0.4)",  icon: StatusIcon.dotCheck },
              error:   { color: t.accent.red.solid,    bg: dark ? "rgba(40,30,30,0.88)"  : "rgba(255,240,240,0.92)", border: "rgba(255,69,58,0.4)",  icon: StatusIcon.dotCross },
              info:    { color: t.accent.blue.solid,   bg: dark ? "rgba(30,35,45,0.88)"  : "rgba(235,245,255,0.92)", border: "rgba(10,132,255,0.4)", icon: StatusIcon.dotInfo },
              warning: { color: t.accent.orange.solid, bg: dark ? "rgba(40,35,25,0.88)"  : "rgba(255,248,235,0.92)", border: "rgba(255,159,10,0.4)", icon: StatusIcon.dotWarn },
            }[tt.type] || null;
            if (!TYPE) return null;
            return (
              <div key={tt.id} style={{
                /* Solid bg + leichter Shadow statt heavy backdrop-filter →
                   Hardware-Composited Layer, smoothes Slide-In ohne Re-Blur. */
                background: TYPE.bg,
                border: `1px solid ${TYPE.border}`,
                borderRadius: 14,
                padding: "9px 16px", display: "flex", alignItems: "center", gap: 8,
                animation: "toastIn 0.32s cubic-bezier(0.32, 0.72, 0, 1) backwards",
                animationDelay: `${idx * 0.05}s`,
                boxShadow: dark
                  ? "0 6px 24px rgba(0,0,0,0.4), 0 0 0 0.5px rgba(255,255,255,0.04)"
                  : "0 6px 24px rgba(0,0,0,0.12), 0 0 0 0.5px rgba(0,0,0,0.04)",
                maxWidth: "min(calc(100vw - 64px), 420px)",
                pointerEvents: "auto",
                opacity: idx === 0 ? 1 : 0.85,
                willChange: "transform, opacity",
              }}>
                {TYPE.icon(15, TYPE.color)}
                <span style={{ fontSize: 13, fontWeight: 600, color: TYPE.color, fontFamily: F, lineHeight: 1.3 }}>{tt.message}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── SENDUNG MINI POPUP ── */}
      {sendungOrder && (() => {
        const sOrder = orders.find(x => x.id === sendungOrder.id) || sendungOrder;
        const tryClose = () => {
          if (!sOrder.trackingNumber || !sOrder.trackingNumber.trim()) {
            setConfirmNoSendung(true);
          } else {
            setSendungOrder(null);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        };
        return (
          <>
            <div onClick={tryClose} style={{
              position: "fixed", inset: 0, zIndex: 50,
              background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
              backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
              animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
              willChange: "opacity",
            }} />
            <div onClick={e => e.stopPropagation()} style={isDesktop ? {
              ...modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 460, radius: 24, padding: "20px 22px 22px", zIndex: 51 }),
            } : {
              ...bottomSheetShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, F, dark, width: 560, padding: "0 22px 22px", zIndex: 51 }),
            }}>
              <div style={{ padding: isDesktop ? 0 : "0 0 4px" }}>
                {!isDesktop ? (
                  <BottomSheetHeader t={t} dark={dark} F={F} title={`#${sOrder.bestellnummer}`} subtitle={sOrder.status} onClose={tryClose} marginBottom={16} />
                ) : (
                  <ModalHeader t={t} dark={dark} F={F} title={`#${sOrder.bestellnummer}`} subtitle={sOrder.status} onClose={tryClose} marginBottom={16} />
                )}

                <div style={{
                  ...glass({
                    borderRadius: 16,
                    border: sOrder.status === "Bereit"
                      ? "1px solid rgba(191,90,242,0.35)"
                      : "1px solid rgba(48,209,88,0.35)",
                  }),
                  padding: "4px 12px 4px 16px", display: "flex", alignItems: "center", gap: 8, overflow: "hidden",
                }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>📦</span>
                  <input
                    autoFocus
                    id="sendung-mini-input"
                    type="text"
                    value={sOrder.trackingNumber || ""}
                    placeholder="Sendungsnummer eingeben…"
                    onChange={e => updateOrder(sOrder.id, "trackingNumber", e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") tryClose(); }}
                    style={{ flex: 1, minWidth: 0, border: "none", background: "transparent", fontFamily: F, fontSize: 17, fontWeight: 500, color: sOrder.trackingNumber ? "#30D158" : t.text, outline: "none", padding: "14px 0" }}
                  />
                  <button onClick={() => { const el = document.getElementById("sendung-mini-input"); el?.focus(); document.execCommand("paste"); }}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#0A84FF", fontFamily: F, fontSize: 13, fontWeight: 600, padding: 0, flexShrink: 0 }}>
                    Einfügen
                  </button>
                </div>
                <div style={{ fontSize: 12, color: t.textMuted, marginTop: 10, paddingLeft: 4, lineHeight: 1.4 }}>
                  Bei Selbstabholung kannst du auch ohne Sendungsnummer fortfahren.
                </div>
              </div>
            </div>

            {/* Confirmation when no sendungsnummer */}
            {confirmNoSendung && (
              <>
                <div onClick={() => setConfirmNoSendung(false)} style={{
                  position: "fixed", inset: 0, zIndex: 90,
                  background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
                  backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
                  animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
                  willChange: "opacity",
                }} />
                <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 360, radius: 24, zIndex: 91 })}>
                  <ModalHeader
                    t={t} dark={dark} F={F}
                    title="Keine Sendungsnummer"
                    subtitle="Bestellung trotzdem als versendet markieren?"
                    accent={t.accent.orange}
                    accentIcon={
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 9v4"/><path d="M12 17h.01"/>
                        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      </svg>
                    }
                    onClose={() => setConfirmNoSendung(false)}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <button onClick={() => {
                      setConfirmNoSendung(false);
                      // Force keyboard reopen on iOS — focus immediately during user gesture
                      const el = document.getElementById("sendung-mini-input");
                      if (el) {
                        el.focus();
                        requestAnimationFrame(() => el.focus());
                        setTimeout(() => el.focus(), 50);
                        setTimeout(() => el.focus(), 200);
                      }
                    }} style={{
                      width: "100%", background: "rgba(10,132,255,0.85)", border: "none", borderRadius: 12,
                      padding: "13px", fontFamily: F, fontWeight: 600, fontSize: 15, cursor: "pointer", color: "#fff",
                      boxShadow: "0 4px 20px rgba(10,132,255,0.3)",
                    }}>Sendungsnummer eingeben</button>
                    {!helperMode && (
                    <button onClick={() => {
                      // Trotzdem als versendet: Status auf Versendet setzen (auch ohne Sendungsnummer)
                      const id = sendungOrder?.id;
                      if (id) {
                        setOrders(o => o.map(x => x.id !== id ? x : { ...x, status: "Versendet" }));
                        apiOrderUpdate(id, { status: "Versendet" });
                      }
                      setConfirmNoSendung(false);
                      setSendungOrder(null);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }} style={{
                      width: "100%", background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)", border: "none", borderRadius: 12,
                      padding: "11px", fontFamily: F, fontWeight: 500, fontSize: 14, cursor: "pointer", color: t.textSub,
                    }}>Trotzdem als versendet</button>
                    )}
                    <button onClick={() => {
                      // Cancel — revert status back to Offen
                      const id = sendungOrder?.id;
                      if (id) {
                        setOrders(o => o.map(x => x.id !== id ? x : { ...x, status: "Offen" }));
                        apiOrderUpdate(id, { status: "Offen" });
                      }
                      setConfirmNoSendung(false);
                      setSendungOrder(null);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }} style={{
                      width: "100%", background: "transparent", border: "none", borderRadius: 12,
                      padding: "9px", fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer", color: "#FF453A",
                    }}>Abbrechen (zurück zu Offen)</button>
                  </div>
                </div>
              </>
            )}
          </>
        );
      })()}

      {/* ── STATS SHEET ── */}
      {showStats && (() => {
        // Cogs fallback if no batch is assigned (rare). Per-channel finance
        // (Preis, Gebühr fix/%, Versand) is per-order via getChannelFinance().
        const COGS_FALLBACK = 12.16;

        // Reserved-Pool-IDs (Familie/Demo/Geschenk/…) — Orders auf diesen
        // Channels dürfen nie als Verkauf zählen.
        const _reservedIds = new Set(getReservedPools().map(c => c.id));
        const sold        = orders.filter(o => o.status !== "Retoure" && !_reservedIds.has(o.kanal));
        const retouren    = orders.filter(o => o.status === "Retoure");
        const verkauft    = sold.length;
        const lager       = Math.max(0, effGoal - verkauft);

        // Aggregate via per-order channel finance — supports per-channel pricing/fees.
        const totalRev    = sold.reduce((s, o) => s + getOrderRevenue(o), 0);
        const totalVers   = sold.reduce((s, o) => s + getOrderShipping(o), 0);
        const totalFees   = sold.reduce((s, o) => s + getOrderFee(o), 0);
        // Per-order COGS via FIFO batch assignment
        const totalCOGS   = sold.reduce((sum, o) => {
          const b = getBatchForOrder(o);
          return sum + (b?.cogs || COGS_FALLBACK);
        }, 0);
        const nettoErloes = totalRev - totalVers - totalFees; // ohne COGS — für Cashflow
        const cashStand   = nettoErloes - effInvestment;
        const realNetto   = totalRev - totalVers - totalFees - totalCOGS; // mit COGS pro Stück — für Stückgewinn
        const marge       = totalRev > 0 ? (realNetto / totalRev * 100) : 0;

        // Break-even — durchschnittlicher Netto-pro-Sale aus tatsächlichen Daten,
        // bzw. (falls noch nichts verkauft) aus dem aktiven Kanal-Mix.
        let nettoPerSale;
        if (verkauft > 0) {
          nettoPerSale = (totalRev - totalVers - totalFees) / verkauft;
        } else {
          const active = getActiveChannels();
          if (active.length > 0) {
            const sum = active.reduce((s, ch) => {
              const f = getChannelFinance(ch.id);
              return s + (f.price - f.shipping - (f.fee_fixed + f.price * (f.fee_percent || 0) / 100));
            }, 0);
            nettoPerSale = sum / active.length;
          } else {
            nettoPerSale = 27.99 - 3.80 - 0.56;
          }
        }
        const breakEven = nettoPerSale > 0 ? Math.ceil(effInvestment / nettoPerSale) : 0;
        const breakEvenPct = breakEven > 0 ? Math.min(100, (verkauft / breakEven) * 100) : 0;
        const isBreakEven = cashStand >= 0;

        // Per kanal — echter Netto-Gewinn (mit FIFO COGS + per-channel Preisen/Gebühren)
        const kanalStats = getAllChannels().map(ch => {
          const kSold = sold.filter(o => o.kanal === ch.id);
          const kRev  = kSold.reduce((s, o) => s + getOrderRevenue(o), 0);
          const kVers = kSold.reduce((s, o) => s + getOrderShipping(o), 0);
          const kFees = kSold.reduce((s, o) => s + getOrderFee(o), 0);
          const kCogs = kSold.reduce((s, o) => s + (getBatchForOrder(o)?.cogs || COGS_FALLBACK), 0);
          const kGew  = kRev - kVers - kFees - kCogs;
          return { k: ch.id, label: ch.name, count: kSold.length, rev: kRev, gewinn: kGew };
        });
        const maxKanal = Math.max(...kanalStats.map(k => k.count), 1);

        // Weekly/Monthly chart
        const periods = [];
        if (chartMode === "weeks") {
          for (let i = 5; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i * 7);
            const ws = new Date(d); ws.setDate(d.getDate() - d.getDay() + 1);
            const we = new Date(ws); we.setDate(ws.getDate() + 6);
            const wsStr = ws.toISOString().slice(0, 10);
            const weStr = we.toISOString().slice(0, 10);
            const wOrders = sold.filter(o => o.datum >= wsStr && o.datum <= weStr);
            periods.push({ label: ws.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }), count: wOrders.length });
          }
        } else {
          for (let i = 5; i >= 0; i--) {
            const d = new Date();
            d.setMonth(d.getMonth() - i, 1);
            const year  = d.getFullYear();
            const month = d.getMonth();
            const ms = new Date(year, month, 1).toISOString().slice(0, 10);
            const me = new Date(year, month + 1, 0).toISOString().slice(0, 10);
            const mOrders = sold.filter(o => o.datum >= ms && o.datum <= me);
            periods.push({ label: d.toLocaleDateString("de-DE", { month: "short" }), count: mOrders.length });
          }
        }
        const maxCount = Math.max(...periods.map(w => w.count), 1);

        const Row = ({ label, val, color, bold, border }) => (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: border ? `0.5px solid ${t.divider}` : "none" }}>
            <span style={{ fontSize: 14, color: t.textSub, fontWeight: bold ? 600 : 400 }}>{label}</span>
            <span style={{ fontSize: 14, fontWeight: bold ? 700 : 500, color: color || t.text, letterSpacing: "-0.2px" }}>{val}</span>
          </div>
        );

        return (
          <>
            {!isDesktop && <div onClick={() => setShowStats(false)} style={{
              position: "fixed", inset: 0, zIndex: 60,
              background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
              backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
              animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
              willChange: "opacity",
            }} />}
            <div onClick={e => e.stopPropagation()}
              onTouchStart={e => {
                if (e.currentTarget.scrollTop <= 0) statsStartY.current = e.touches[0].clientY;
              }}
              onTouchMove={e => {
                if (statsStartY.current !== null) {
                  const dy = e.touches[0].clientY - statsStartY.current;
                  if (dy > 0 && e.currentTarget.scrollTop <= 0) {
                    setStatsDragY(Math.min(dy, 400));
                  } else if (dy < 0) {
                    statsStartY.current = null;
                    setStatsDragY(0);
                  }
                }
              }}
              onTouchEnd={() => {
                if (statsDragY > 80) setShowStats(false);
                setStatsDragY(0);
                statsStartY.current = null;
              }}
              style={isDesktop ? {
                ...desktopRightPanel,
                background: dark ? "rgba(28,28,30,0.85)" : "rgba(250,250,252,0.88)",
                backdropFilter: "blur(24px)",
                WebkitBackdropFilter: "blur(24px)",
                border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"}`,
                borderRadius: 24,
                zIndex: 61, animation: "slideInRight 0.24s cubic-bezier(0.32,0.72,0,1)",
                overflowY: "auto", WebkitOverflowScrolling: "touch",
              } : {
                ...bottomSheetShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, F, dark, width: 560, padding: "0 22px 22px", zIndex: 61 }),
                transform: `translateX(-50%) translateY(${statsDragY}px)`,
                animation: statsDragY === 0 ? "slideUp 0.34s cubic-bezier(0.32,0.72,0,1) both" : "none",
                transition: statsDragY === 0 ? "transform 0.25s cubic-bezier(0.32,0.72,0,1)" : "none",
              }}>
              <div style={{ padding: isDesktop ? "32px 32px 36px" : "0 0 16px" }}>
                {!isDesktop ? (
                  <BottomSheetHeader t={t} dark={dark} F={F} title="Finanzen" onClose={() => setShowStats(false)} marginBottom={18} />
                ) : (
                  <div style={{ display: "flex", alignItems: "center", paddingBottom: 18, position: "relative", minHeight: 30 }}>
                    <ModalCloseButton onClose={() => setShowStats(false)} dark={dark} />
                    <span style={{ position: "absolute", left: 0, right: 0, textAlign: "center", fontSize: 17, fontWeight: 600, color: t.text, letterSpacing: "-0.3px", pointerEvents: "none", fontFamily: F, padding: "0 46px" }}>Finanzen</span>
                  </div>
                )}

                {/* Aktiver Batch — FIFO-Status oben im Panel */}
                {(() => {
                  const sortedBatches = batches.slice().sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""));
                  const active = getActiveBatch();
                  if (!active) {
                    // Alle ausverkauft → roter Block
                    return (
                      <div style={{
                        background: t.accent.red.weak,
                        border: `1px solid ${t.accent.red.solid}55`,
                        borderRadius: 14, padding: "11px 14px", marginBottom: 12,
                        display: "flex", alignItems: "center", gap: 10,
                      }}>
                        <span style={{ flexShrink: 0, color: t.accent.red.solid }}>{Icon.warning(16)}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: t.accent.red.solid, letterSpacing: "-0.1px" }}>
                            Alle Batches ausverkauft
                          </div>
                          <div style={{ fontSize: 11, color: t.textSub, marginTop: 1 }}>
                            Lege einen neuen Batch an, um wieder Bestellungen zu erfassen.
                          </div>
                        </div>
                      </div>
                    );
                  }
                  const soldA = orders.filter(o => o.batchId === active.id && o.status !== "Retoure" && !o.archived).length;
                  const remaining = (active.stueckzahl || 0) - soldA;
                  const activeIdx = sortedBatches.findIndex(b => b.id === active.id);
                  const nextBatch = activeIdx >= 0 && activeIdx < sortedBatches.length - 1 ? sortedBatches[activeIdx + 1] : null;
                  const showTransitionHint = remaining <= 5 && nextBatch;
                  return (
                    <div style={{
                      background: t.accent.blue.weak,
                      border: `1px solid ${t.accent.blue.solid}40`,
                      borderRadius: 14, padding: "11px 14px", marginBottom: 12,
                      display: "flex", flexDirection: "column", gap: 6,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ flexShrink: 0, color: t.accent.blue.solid }}>{Icon.box(15)}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: t.accent.blue.solid, letterSpacing: "0.06em", textTransform: "uppercase" }}>Aktiver Batch</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: t.text, letterSpacing: "-0.2px" }}>{active.name}</span>
                        <span style={{ fontSize: 12, color: t.textSub, fontWeight: 500 }}>
                          <b style={{ color: t.text }}>{remaining}</b> von {active.stueckzahl} Stück übrig · COGS {fmt2(active.cogs)} €
                        </span>
                      </div>
                      {showTransitionHint && (
                        <div style={{ fontSize: 11, color: t.textMuted, paddingTop: 4, borderTop: `0.5px solid ${t.divider}` }}>
                          Bald aktiv: <b style={{ color: t.textSub }}>{nextBatch.name}</b> · COGS {fmt2(nextBatch.cogs)} €
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Umsatz · Cash-Stand · Marge — top */}
                <div style={{ ...glass({ borderRadius: 14 }), padding: "11px 16px", marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", gap: 20 }}>
                    <div>
                      <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>Umsatz</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: t.textSub, letterSpacing: "-0.3px" }}>{fmt2(totalRev)} €</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>Cash-Stand</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: isBreakEven ? "#30D158" : "#FF9F0A", letterSpacing: "-0.3px" }}>{fmt2(cashStand)} €</div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>Marge</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: t.textSub, letterSpacing: "-0.3px" }}>{marge.toFixed(1)}%</div>
                  </div>
                </div>

                {/* Chart with toggle */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, paddingLeft: 4, paddingRight: 4 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, letterSpacing: "0.1em", textTransform: "uppercase" }}>Verkäufe</div>
                  <div style={{ display: "flex", gap: 2, background: t.searchBg, borderRadius: 8, padding: 2 }}>
                    {[{ k: "weeks", l: "Wochen" }, { k: "months", l: "Monate" }].map(m => (
                      <button key={m.k} onClick={() => setChartMode(m.k)} style={{
                        background: chartMode === m.k ? t.pillBg : "transparent",
                        border: "none", borderRadius: 6, padding: "3px 10px", cursor: "pointer",
                        fontFamily: F, fontSize: 11, fontWeight: chartMode === m.k ? 600 : 400,
                        color: chartMode === m.k ? t.text : t.textSub,
                      }}>{m.l}</button>
                    ))}
                  </div>
                </div>
                <div style={{ ...glass({ borderRadius: 18 }), padding: "16px 16px 12px", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 72 }}>
                    {periods.map((w, i) => (
                      <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: t.text }}>{w.count > 0 ? w.count : ""}</div>
                        <div style={{ width: "100%", borderRadius: 6, height: Math.max(4, (w.count / maxCount) * 48), background: w.count > 0 ? "#0A84FF" : (dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)") }} />
                        <div style={{ fontSize: 9, color: t.textMuted, textAlign: "center" }}>{w.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Kanal Performance */}
                <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>Kanal Performance</div>
                <div style={{ ...glass({ borderRadius: 18 }), overflow: "hidden", marginBottom: 12 }}>
                  {kanalStats.map((k, i) => (
                    <div key={k.k}>
                      {i > 0 && <div style={{ height: "0.5px", background: t.divider, marginLeft: 16 }} />}
                      <div style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <span style={{ fontSize: 14, fontWeight: 500, color: t.text }}>{k.label || k.k}</span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: t.textSub }}>{k.count} Stück · {fmt2(k.gewinn)} €</span>
                        </div>
                        <div style={{ height: 5, borderRadius: 3, background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${(k.count / maxKanal) * 100}%`, background: "#0A84FF", borderRadius: 3 }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Break-Even Fortschritt */}
                <div style={{ ...glass({ borderRadius: 18 }), padding: "14px 16px", marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: t.text }}>
                      {isBreakEven ? "Break-Even erreicht" : "Break-Even bei"}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: isBreakEven ? "#30D158" : t.textSub }}>
                      {verkauft} / {breakEven} Stück
                    </div>
                  </div>
                  <div style={{ height: 5, borderRadius: 3, background: dark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.06)", overflow: "hidden" }}>
                    <div style={{
                      height: "100%", width: `${breakEvenPct}%`, borderRadius: 3,
                      background: isBreakEven
                        ? "linear-gradient(90deg, #30D158 0%, #5ee78a 100%)"
                        : "linear-gradient(90deg, #FF9F0A 0%, #FFD60A 100%)",
                      transition: "width 0.5s cubic-bezier(0.34,1.2,0.64,1)",
                      boxShadow: isBreakEven ? "0 0 8px rgba(48,209,88,0.4)" : "0 0 8px rgba(255,159,10,0.3)",
                    }} />
                  </div>
                </div>

                {/* Operatives Geschäft */}
                <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>Operatives Geschäft</div>
                <div style={{ ...glass({ borderRadius: 18 }), overflow: "hidden", marginBottom: 12 }}>
                  <Row label="Umsatz" val={`+ ${fmt2(totalRev)} €`} color="#30D158" />
                  <div style={{ height: "0.5px", background: t.divider, marginLeft: 16 }} />
                  <button onClick={() => setShowGuvDetails(v => !v)} style={{
                    width: "100%", background: "none", border: "none", cursor: "pointer",
                    padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center",
                    fontFamily: F, textAlign: "left",
                  }}>
                    <span style={{ fontSize: 14, color: t.textSub, fontWeight: 500 }}>Kosten</span>
                    <span style={{ fontSize: 13, color: t.textMuted, fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}>
                      − {fmt2(totalCOGS + totalVers + totalFees)} €
                      <span style={{ fontSize: 16, color: t.textMuted, transform: showGuvDetails ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.25s ease", display: "inline-block" }}>›</span>
                    </span>
                  </button>
                  {showGuvDetails && (() => {
                    // Dynamische Durchschnitte aus den tatsächlich verkauften Stück-Daten,
                    // damit die Anzeige immer den aktuellen Mix aus FIFO-Batches reflektiert.
                    const avgCogs = verkauft > 0 ? totalCOGS / verkauft : 0;
                    const avgVers = verkauft > 0 ? totalVers / verkauft : 0;
                    return (
                    <div style={{ animation: "fadeIn 0.2s ease" }}>
                      <div style={{ height: "0.5px", background: t.divider, marginLeft: 16 }} />
                      <Row label={`COGS (${verkauft} Stück, Ø ${fmt2(avgCogs)} €)`} val={`− ${fmt2(totalCOGS)} €`} color="#FF453A" />
                      <Row label={`Versand (${verkauft} Stück, Ø ${fmt2(avgVers)} €)`} val={`− ${fmt2(totalVers)} €`} color="#FF453A" border />
                      <Row label="Kanalgebühren" val={`− ${fmt2(totalFees)} €`} color="#FF453A" border />
                    </div>
                    );
                  })()}
                  <div style={{ borderTop: `0.5px solid ${t.divider}`, margin: "0 16px" }} />
                  <Row label="Stückgewinn gesamt" val={`${realNetto >= 0 ? "+" : "−"} ${fmt2(Math.abs(realNetto))} €`} color={realNetto >= 0 ? "#30D158" : "#FF453A"} bold />
                </div>

                {/* Cashflow — pro Batch eine eigene Zeile (statt Legacy-Global) */}
                <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>Cashflow</div>
                <div style={{ ...glass({ borderRadius: 18 }), overflow: "hidden", marginBottom: 12 }}>
                  <Row label="Einnahmen netto" val={`+ ${fmt2(nettoErloes)} €`} color="#30D158" />
                  {(() => {
                    // Pro Batch: Name · X/Y Stück verkauft → Investment
                    const sortedBatches = batches.slice().sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""));
                    if (sortedBatches.length === 0) {
                      return <Row label="Investment" val={`− ${fmt2(0)} €`} color="#FF453A" border />;
                    }
                    return sortedBatches.map((b, i) => {
                      const soldB = orders.filter(o => o.batchId === b.id && o.status !== "Retoure" && !o.archived).length;
                      return (
                        <Row
                          key={b.id}
                          label={`${b.name} · ${soldB}/${b.stueckzahl} Stück`}
                          val={`− ${fmt2(b.investment || 0)} €`}
                          color="#FF453A"
                          border={i > 0}
                        />
                      );
                    });
                  })()}
                  <div style={{ borderTop: `0.5px solid ${t.divider}`, margin: "0 16px" }} />
                  <Row label="Cash-Stand" val={`${cashStand >= 0 ? "+" : "−"} ${fmt2(Math.abs(cashStand))} €`} color={cashStand >= 0 ? "#30D158" : "#FF9F0A"} bold />
                </div>

                {/* Netto pro Stück */}
                <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>Netto pro Stück</div>
                <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 8, paddingLeft: 4, lineHeight: 1.5 }}>
                  Gewinn nach COGS, Versand und Gebühren
                </div>
                <div style={{ ...glass({ borderRadius: 18 }), overflow: "hidden", marginBottom: 12 }}>
                  {getAllChannels().map((ch, i) => {
                    const f = getChannelFinance(ch.id);
                    const totalFee = f.fee_fixed + f.price * (f.fee_percent || 0) / 100;
                    const netto = f.price - COGS_FALLBACK - f.shipping - totalFee;
                    return <Row key={ch.id} label={ch.name} val={`${fmt2(netto)} €`} color="#30D158" border={i > 0} />;
                  })}
                </div>

                {/* Retouren */}
                {retouren.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>Retouren</div>
                    <div style={{ ...glass({ borderRadius: 18 }), overflow: "hidden" }}>
                      <Row label="Anzahl Retouren" val={retouren.length} />
                    </div>
                    <div style={{ fontSize: 11, color: t.textMuted, marginTop: 6, paddingLeft: 4, lineHeight: 1.5 }}>
                      Retoure-Waren gehen zurück ins Lager und können erneut verkauft werden.
                    </div>
                  </>
                )}

              </div>
            </div>
          </>
        );
      })()}

      {/* Legacy INVESTMENT EDIT POPUP entfernt — Investment wird jetzt
          ausschliesslich pro Batch im Lager-Modal gepflegt. */}

      {/* SCHWUNG-MODUS entfernt — Versand läuft komplett über den Scanner. */}

      {/* ── TRACKING-CONFIRM MODAL (OCR) ── */}
      {trackingConfirm && (() => {
        const tc = trackingConfirm;
        const cands = tc.candidates || [];
        const order = orders.find(x => x.id === tc.orderId);
        const friendlyName = (n) => ({
          keyword: "Sendungs-Keyword",
          dhl: "DHL-Format",
          dhl_intl: "DHL Int.",
          dpd: "DPD",
          gls: "GLS",
          hermes: "Hermes",
          deutschepost: "Dt. Post",
          ups: "UPS",
          fedex: "FedEx",
          usps: "USPS",
          numeric_long: "Lange Zahl",
          alpha: "Alphanumerisch",
        }[n] || n || "?");
        const confirm = async (value) => {
          const v = (value || "").trim();
          if (!v) { showToast("Bitte Trackingnummer eingeben oder wählen", "warning"); return; }
          setTrackingConfirm(null);
          setOrders(o => o.map(x => x.id === tc.orderId ? { ...x, trackingNumber: v } : x));
          try {
            await apiOrderUpdate(tc.orderId, { trackingNumber: v });
            haptic(15);
            showToast(`Trackingnummer: ${v} ✓`);
          } catch (e) {
            console.warn("Tracking-Update fehlgeschlagen:", e);
            showToast("Speichern fehlgeschlagen", "error");
          }
        };
        const skip = () => {
          setTrackingConfirm(null);
          showToast("Trackingnummer kann später manuell eingetragen werden", "info");
        };
        return (
          <>
            <div onClick={skip} style={{
              position: "fixed", inset: 0, zIndex: 80,
              background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
              backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
              animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
              willChange: "opacity",
            }} />
            <div onClick={e => e.stopPropagation()} style={bottomSheetShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, F, dark, width: 560, padding: "0 22px 22px", zIndex: 81 })}>
              <BottomSheetHeader t={t} dark={dark} F={F} title="Trackingnummer bestätigen" onClose={skip} marginBottom={14} />
              <div style={{ fontSize: 13, color: t.textSub, marginBottom: 14, lineHeight: 1.4 }}>
                {order?.name ? <>Für <b style={{ color: t.text }}>{order.name}</b>: w</> : "W"}
                ähle die korrekte Sendungsnummer aus oder gib sie manuell ein.
              </div>

              {/* Candidate-Liste */}
              {cands.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>
                    Erkannte Kandidaten
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {cands.map((c, i) => (
                      <button key={`${c.value}-${i}`}
                        onClick={() => confirm(c.value)}
                        style={{
                          ...glass({ borderRadius: 14 }),
                          border: i === 0 ? "1.5px solid rgba(10,132,255,0.5)" : `1px solid ${t.glassBorder}`,
                          background: i === 0 ? "rgba(10,132,255,0.08)" : (dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.5)"),
                          padding: "12px 14px",
                          textAlign: "left",
                          cursor: "pointer",
                          fontFamily: F,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                        }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 16, fontWeight: 600, color: t.text, fontFamily: "ui-monospace, SF Mono, Menlo, monospace", letterSpacing: "0.5px", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {c.value}
                          </div>
                          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
                            {friendlyName(c.name)}{i === 0 ? " · Beste Schätzung" : ""}
                          </div>
                        </div>
                        <div style={{ fontSize: 18, color: "#0A84FF", flexShrink: 0 }}>›</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Manuelle Eingabe */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>
                  Manuell eingeben
                </div>
                <div style={{ ...glass({ borderRadius: 14 }), padding: "0 14px", display: "flex", alignItems: "center" }}>
                  <input
                    type="text"
                    value={tc.manualValue}
                    onChange={e => setTrackingConfirm(s => s ? { ...s, manualValue: e.target.value } : s)}
                    placeholder="Trackingnummer eintippen…"
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    style={{
                      flex: 1, minWidth: 0, border: "none", background: "transparent",
                      fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                      fontSize: 16, fontWeight: 500, color: t.text, outline: "none",
                      padding: "14px 0", letterSpacing: "0.5px",
                    }}
                  />
                </div>
              </div>

              {/* OCR-Text Debug-Toggle */}
              {tc.ocrText && (
                <details style={{ marginBottom: 14 }}>
                  <summary style={{ fontSize: 11, color: t.textMuted, cursor: "pointer", padding: "6px 4px", userSelect: "none" }}>
                    OCR-Text anzeigen (Debug)
                  </summary>
                  <pre style={{
                    ...glass({ borderRadius: 10 }),
                    padding: "10px 12px",
                    marginTop: 6,
                    fontSize: 10,
                    color: t.textSub,
                    fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 140,
                    overflowY: "auto",
                  }}>{tc.ocrText}</pre>
                </details>
              )}

              {/* Action-Buttons */}
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={skip} style={{
                  flex: 1,
                  ...glass({ borderRadius: 14 }),
                  border: `1px solid ${t.glassBorder}`,
                  padding: "14px",
                  fontFamily: F, fontWeight: 500, fontSize: 14, cursor: "pointer", color: t.textSub,
                }}>Überspringen</button>
                <button onClick={() => confirm(tc.manualValue)} style={{
                  flex: 1.4,
                  background: "rgba(10,132,255,0.85)", border: "none", borderRadius: 14,
                  padding: "14px", fontFamily: F, fontWeight: 600, fontSize: 15, cursor: "pointer", color: "#fff",
                  boxShadow: "0 4px 20px rgba(10,132,255,0.3)",
                }}>Bestätigen</button>
              </div>
            </div>
          </>
        );
      })()}

      {/* ── GOAL EDIT POPUP — top-anchored, dvh, scroll-lock, smooth iOS-Animation ── */}
      {showGoalEdit && (
        <GoalEditModal
          t={t} dark={dark} F={F} glass={glass}
          goal={goal} setGoal={setGoal}
          onClose={() => setShowGoalEdit(false)}
        />
      )}

      {/* ── LAGER SHEET ── */}
      {showLager && (() => {
        // Color palette — assigned to channels by index. Sales-Channels und
        // Reserved-Pools werden gemeinsam aus storeChannels gelesen, kein
        // hardcoded Familie/Presse mehr (User-Wunsch: globale Skalierung).
        const KANAL_COLOR_PALETTE = ["#0A84FF","#FF9500","#30D158","#FF453A","#5E5CE6","#FF2D55","#64D2FF","#BF5AF2","#FF375F"];
        const salesChannels    = getAllChannels();
        const reservedPools    = getReservedPools();
        // Channel-List für Slot-Grid: Sales zuerst, dann Reservierungen
        const channelList      = [...salesChannels, ...reservedPools];
        const KANAL_COLORS = {};
        channelList.forEach((ch, i) => { KANAL_COLORS[ch.id] = KANAL_COLOR_PALETTE[i % KANAL_COLOR_PALETTE.length]; });
        // Legacy-Fallback: wenn ein alter Batch noch "Familie"/"Presse"-keys hat,
        // ohne dass ein entsprechender reserved-Channel existiert — nutze trotzdem
        // sinnvolle Farben damit die Daten lesbar bleiben.
        if (!KANAL_COLORS.Familie) KANAL_COLORS.Familie = "#BF5AF2";
        if (!KANAL_COLORS.Presse)  KANAL_COLORS.Presse  = "#FF375F";

        // Global stats across all batches
        const allActiveOrders = orders.filter(o => o.status !== "Retoure");
        const totalSold = allActiveOrders.length;
        const totalAmLager = totalStueckzahl - totalSold;

        // Sales by kanal across all batches
        const globalSalesByKanal = {};
        allActiveOrders.forEach(o => {
          globalSalesByKanal[o.kanal] = (globalSalesByKanal[o.kanal] || 0) + 1;
        });

        // Throughput: avg per WEEK over last 30 days (User-Wunsch — pro Woche statt pro Tag)
        const now = Date.now();
        const cutoff = now - 30 * 24 * 60 * 60 * 1000;
        const recent = allActiveOrders.filter(o => {
          const d = new Date(o.datum || 0).getTime();
          return d >= cutoff;
        }).length;
        // 30 Tage / 7 ≈ 4.3 Wochen → durchschnittliche Verkäufe pro Woche
        const throughputPerWeek = (recent / (30 / 7)).toFixed(1);

        return (
        <>
          {!isDesktop && <div onClick={() => setShowLager(false)} style={{
            position: "fixed", inset: 0, zIndex: 40,
            background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />}
          <div
            onTouchStart={e => {
              if (e.currentTarget.scrollTop <= 0) lagerStartY.current = e.touches[0].clientY;
            }}
            onTouchMove={e => {
              if (lagerStartY.current !== null) {
                const dy = e.touches[0].clientY - lagerStartY.current;
                if (dy > 0 && e.currentTarget.scrollTop <= 0) {
                  setLagerDragY(Math.min(dy, 400));
                } else if (dy < 0) {
                  lagerStartY.current = null;
                  setLagerDragY(0);
                }
              }
            }}
            onTouchEnd={() => {
              if (lagerDragY > 80) setShowLager(false);
              setLagerDragY(0);
              lagerStartY.current = null;
            }}
            style={isDesktop ? {
              ...desktopRightPanel,
              background: dark ? "rgba(28,28,30,0.85)" : "rgba(250,250,252,0.88)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"}`,
              borderRadius: 24,
              zIndex: 50, animation: "slideInRight 0.24s cubic-bezier(0.32,0.72,0,1)",
              overflowY: "auto", WebkitOverflowScrolling: "touch",
            } : {
              ...bottomSheetShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, F, dark, width: 560, padding: "0 22px 22px", zIndex: 50 }),
              transform: `translateX(-50%) translateY(${lagerDragY}px)`,
              animation: lagerDragY === 0 ? "slideUp 0.34s cubic-bezier(0.32,0.72,0,1) both" : "none",
              transition: lagerDragY === 0 ? "transform 0.25s cubic-bezier(0.32,0.72,0,1)" : "none",
            }}>
            <div style={{ padding: isDesktop ? "32px 32px 32px" : "0 0 4px" }}>
              {!isDesktop ? (
                <BottomSheetHeader t={t} dark={dark} F={F} title="Lager" subtitle="Übersicht & Verteilung deiner Bestände" onClose={() => setShowLager(false)} marginBottom={16} />
              ) : (
                <div style={{ display: "flex", alignItems: "center", paddingBottom: 18, position: "relative", minHeight: 30 }}>
                  <ModalCloseButton onClose={() => setShowLager(false)} dark={dark} />
                  <span style={{ position: "absolute", left: 0, right: 0, textAlign: "center", fontSize: 17, fontWeight: 600, color: t.text, letterSpacing: "-0.3px", pointerEvents: "none", fontFamily: F, padding: "0 46px" }}>Lager</span>
                </div>
              )}

              {/* HERO STATS */}
              <div style={{ ...glass({ borderRadius: 18 }), padding: "16px", marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>Im Lager</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: t.text, letterSpacing: "-0.7px", lineHeight: 1.1 }}>{totalAmLager}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>von {totalStueckzahl} Stück</div>
                </div>
                <div style={{ textAlign: "center", padding: "0 8px" }}>
                  <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>Verkauft</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#30D158", letterSpacing: "-0.4px", lineHeight: 1.1 }}>{totalSold}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>insgesamt</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>Durchsatz</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: t.text, letterSpacing: "-0.4px", lineHeight: 1.1 }}>{throughputPerWeek}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>pro Woche</div>
                </div>
              </div>

              {/* KANAL DISTRIBUTION (only if sales exist) */}
              {totalSold > 0 && (
                <div style={{ ...glass({ borderRadius: 18 }), padding: "14px 16px", marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>Verkäufe nach Kanal</div>
                  {/* Stacked bar */}
                  <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", marginBottom: 10, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" }}>
                    {channelList.map(ch => {
                      const v = globalSalesByKanal[ch.id] || 0;
                      const w = totalSold > 0 ? (v / totalSold) * 100 : 0;
                      if (w === 0) return null;
                      return <div key={ch.id} style={{ width: `${w}%`, background: KANAL_COLORS[ch.id], transition: "width 0.5s cubic-bezier(0.34,1.2,0.64,1)" }} />;
                    })}
                  </div>
                  {/* Legend — User-Wunsch: nur Anzahl, keine Prozent-Angabe */}
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    {channelList.map(ch => {
                      const v = globalSalesByKanal[ch.id] || 0;
                      return (
                        <div key={ch.id} style={{ flex: 1, textAlign: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, marginBottom: 2 }}>
                            <div style={{ width: 7, height: 7, borderRadius: "50%", background: KANAL_COLORS[ch.id] }} />
                            <span style={{ fontSize: 12, color: t.textSub, fontWeight: 500 }}>{ch.name}</span>
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{v}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* SECTION TITLE */}
              <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, letterSpacing: "0.1em", textTransform: "uppercase", margin: "20px 4px 10px" }}>Batches</div>

              {/* BATCH CARDS */}
              {(batches || []).slice().sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || "")).map((batch, batchIdx) => {
                const reservierungen = batch.reservierungen || { eBay: 0, Etsy: 0, Website: 0, Familie: 0, Presse: 0 };
                const stueckzahl = batch.stueckzahl || 0;
                const salesByKanal = {};
                orders.forEach(o => {
                  if (o.status === "Retoure") return;
                  if (o.batchId !== batch.id) return;
                  salesByKanal[o.kanal] = (salesByKanal[o.kanal] || 0) + 1;
                });
                const soldInBatch = Object.values(salesByKanal).reduce((a, v) => a + v, 0);
                const reservTotal = Object.values(reservierungen).reduce((a, v) => a + (v || 0), 0);
                const unreserved = Math.max(0, stueckzahl - reservTotal);
                const restbestand = Math.max(0, stueckzahl - soldInBatch);
                // Compute earliest sale date in this batch
                const batchOrders = orders.filter(o => o.status !== "Retoure" && o.batchId === batch.id);
                const oldestSale = batchOrders.length > 0
                  ? Math.min(...batchOrders.map(o => new Date(o.datum || 0).getTime()))
                  : null;
                const daysSinceFirstSale = oldestSale ? Math.floor((now - oldestSale) / (24 * 60 * 60 * 1000)) : null;

                // Status pill — corrected logic
                let statusLabel, statusColor, statusBg;
                if (stueckzahl > 0 && soldInBatch >= stueckzahl) {
                  statusLabel = "Ausverkauft"; statusColor = "#0A84FF"; statusBg = "rgba(10,132,255,0.15)";
                } else if (soldInBatch > 0) {
                  statusLabel = "Aktiv"; statusColor = "#30D158"; statusBg = "rgba(48,209,88,0.15)";
                } else if (reservTotal === 0) {
                  statusLabel = "Wartend"; statusColor = t.textMuted; statusBg = dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)";
                } else if (unreserved > 0) {
                  statusLabel = "In Verteilung"; statusColor = "#FF9F0A"; statusBg = "rgba(255,159,10,0.15)";
                } else {
                  statusLabel = "Bereit"; statusColor = "#0A84FF"; statusBg = "rgba(10,132,255,0.12)";
                }

                // Donut math: 3 segments — verkauft (green), reserviert frei (blue), unreserviert (gray/orange)
                const donutSize = 88;
                const donutStroke = 9;
                const donutRadius = (donutSize - donutStroke) / 2;
                const circumference = 2 * Math.PI * donutRadius;
                const reservFree = Math.max(0, reservTotal - soldInBatch);
                const sold_pct = stueckzahl > 0 ? soldInBatch / stueckzahl : 0;
                const reserv_pct = stueckzahl > 0 ? reservFree / stueckzahl : 0;
                const unres_pct = stueckzahl > 0 ? unreserved / stueckzahl : 0;
                const sold_dash = sold_pct * circumference;
                const reserv_dash = reserv_pct * circumference;
                const unres_dash = unres_pct * circumference;

                // Wert + Profit-Potential — komplett dynamisch aus storeChannels.
                // "Verschenkt" = alle Reservierungen in reserved-type Pools (Familie, Demo, …)
                // "Verkaufbar" = Stück minus Verschenkungen
                // Avg-Preis und Avg-Gebühren = gewichtet nach Reservierungs-Anteilen je Sales-Kanal.
                const reservedIds = new Set(getReservedPools().map(c => c.id));
                const verschenkt = Object.entries(reservierungen).reduce(
                  (s, [k, v]) => s + (reservedIds.has(k) ? (v || 0) : 0), 0);
                const verkaufbar = Math.max(0, stueckzahl - verschenkt);
                const restVerkaufbar = Math.max(0, verkaufbar - soldInBatch);
                // Gewichtete Mittelwerte: nur Sales-Kanäle, gewichtet nach Pool-Größe
                const salesEntries = getAllChannels().map(c => ({
                  id: c.id,
                  pool: reservierungen[c.id] || 0,
                  fin: getChannelFinance(c.id),
                }));
                const salesPoolSum = salesEntries.reduce((s, e) => s + e.pool, 0);
                const avgPrice = salesPoolSum > 0
                  ? salesEntries.reduce((s, e) => s + e.pool * e.fin.price, 0) / salesPoolSum
                  : (salesEntries[0]?.fin.price || 0);
                const avgFeePerSale = salesPoolSum > 0
                  ? salesEntries.reduce((s, e) => s + e.pool * (e.fin.fee_fixed + e.fin.price * (e.fin.fee_percent || 0) / 100), 0) / salesPoolSum
                  : (salesEntries[0] ? salesEntries[0].fin.fee_fixed + salesEntries[0].fin.price * (salesEntries[0].fin.fee_percent || 0) / 100 : 0);
                const avgShipping = salesPoolSum > 0
                  ? salesEntries.reduce((s, e) => s + e.pool * e.fin.shipping, 0) / salesPoolSum
                  : (salesEntries[0]?.fin.shipping || 0);
                const wertVerkauft = soldInBatch * avgPrice;
                const wertImLager  = restVerkaufbar * avgPrice;
                const profitPotential = (verkaufbar * avgPrice) - (batch.investment || 0) - (verkaufbar * avgShipping) - (verkaufbar * avgFeePerSale);

                const setReserv = (kanal, newVal) => {
                  const safe = Math.max(0, isNaN(newVal) ? 0 : newVal);
                  const newReservierungen = { ...(batch.reservierungen || {}), [kanal]: safe };
                  setBatches(bs => bs.map(b => b.id === batch.id ? { ...b, reservierungen: newReservierungen } : b));
                  apiBatchUpdate(batch.id, { reservierungen: newReservierungen });
                };

                const isCollapsed = collapsedBatches.has(batch.id);
                const toggleCollapse = (e) => {
                  e.stopPropagation();
                  setCollapsedBatches(prev => {
                    const next = new Set(prev);
                    if (next.has(batch.id)) next.delete(batch.id);
                    else next.add(batch.id);
                    return next;
                  });
                  haptic(6);
                };

                return (
                  <div key={batch.id} id={`batch-${batch.id}`} style={{ ...glass({ borderRadius: 20 }), padding: "16px", marginBottom: 12, transition: "padding 0.2s ease", scrollMarginTop: "16px" }}>
                    {/* Header — komplett klickbar zum Collapsen.
                        Innen: Name (eigener Click → öffnet Edit-Modal, stoppt Propagation),
                        Caret (zeigt Collapse-Status), datum/cogs, Status-Pill, Donut.
                        Click auf alles ausser Name → Collapse toggle. */}
                    <div
                      onClick={toggleCollapse}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        marginBottom: isCollapsed ? 0 : 14, gap: 12,
                        transition: "margin 0.2s ease", cursor: "pointer",
                        userSelect: "none",
                      }}
                      role="button"
                      aria-label={isCollapsed ? "Batch ausklappen" : "Batch einklappen"}
                    >
                      <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
                        {/* Collapse-Caret — visueller Indikator, kein eigener Handler nötig
                            (parent-div handelt den Click). aria-hidden weil rein dekorativ. */}
                        <span
                          aria-hidden
                          style={{
                            display: "flex", alignItems: "center", justifyContent: "center",
                            padding: 4, color: t.textMuted, flexShrink: 0,
                            transform: isCollapsed ? "rotate(0deg)" : "rotate(90deg)",
                            transition: "transform 0.2s cubic-bezier(0.34,1.2,0.64,1)",
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M4 2L8 6L4 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                            {/* Name-Klick → Edit-Modal (Finanzen, Stückzahl, Datum, COGS).
                                stopPropagation damit der Header-Toggle nicht mit-feuert. */}
                            <button
                              onClick={(e) => { e.stopPropagation(); setEditBatchId(batch.id); }}
                              style={{
                                background: "none", border: "none", cursor: "pointer", padding: 0,
                                textAlign: "left", color: t.text, fontFamily: F,
                                fontSize: 17, fontWeight: 700, letterSpacing: "-0.3px",
                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                minWidth: 0, maxWidth: "100%",
                              }}
                            >{batch.name || "Batch"}</button>
                            {/* Stift-Icon — visueller Hinweis "hier kannst du Finanzen ändern".
                                Eigene Click-Handler (gleiche Action) damit auch der Stift-Hit-Bereich
                                edit öffnet. */}
                            <button
                              onClick={(e) => { e.stopPropagation(); setEditBatchId(batch.id); }}
                              aria-label="Batch-Finanzen bearbeiten"
                              style={{
                                background: "none", border: "none", cursor: "pointer",
                                padding: 4, display: "flex", alignItems: "center", justifyContent: "center",
                                color: t.textMuted, flexShrink: 0,
                                transition: "color 0.15s ease",
                              }}
                              onPointerEnter={e => e.currentTarget.style.color = t.accent.blue.solid}
                              onPointerLeave={e => e.currentTarget.style.color = t.textMuted}
                            >
                              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                                <path d="M11.5 2L14 4.5L5.5 13L2 14L3 10.5L11.5 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                          {!isCollapsed && (
                            <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 6 }}>{batch.datum || "—"} · {fmt2(batch.cogs || 0)} €/Stück</div>
                          )}
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 6, background: statusBg }}>
                            <div style={{ width: 6, height: 6, borderRadius: "50%", background: statusColor }} />
                            <span style={{ fontSize: 11, fontWeight: 600, color: statusColor, letterSpacing: "-0.1px" }}>{statusLabel}</span>
                          </div>
                        </div>
                      </div>
                      {/* Donut */}
                      <div style={{ position: "relative", width: donutSize, height: donutSize, flexShrink: 0 }}>
                        <svg width={donutSize} height={donutSize} style={{ transform: "rotate(-90deg)" }}>
                          {/* Background ring */}
                          <circle cx={donutSize/2} cy={donutSize/2} r={donutRadius}
                            stroke={dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}
                            strokeWidth={donutStroke} fill="none" />
                          {/* Verkauft (green) */}
                          {sold_pct > 0 && (
                            <circle cx={donutSize/2} cy={donutSize/2} r={donutRadius}
                              stroke="#30D158" strokeWidth={donutStroke} fill="none"
                              strokeDasharray={`${sold_dash} ${circumference}`}
                              strokeLinecap="round"
                              style={{ transition: "stroke-dasharray 0.6s cubic-bezier(0.34,1.2,0.64,1)" }} />
                          )}
                          {/* Reserviert frei (blue) */}
                          {reserv_pct > 0 && (
                            <circle cx={donutSize/2} cy={donutSize/2} r={donutRadius}
                              stroke="#0A84FF" strokeWidth={donutStroke} fill="none"
                              strokeDasharray={`${reserv_dash} ${circumference}`}
                              strokeDashoffset={-sold_dash}
                              strokeLinecap="round"
                              style={{ transition: "all 0.6s cubic-bezier(0.34,1.2,0.64,1)" }} />
                          )}
                          {/* Unreserviert (orange) */}
                          {unres_pct > 0 && (
                            <circle cx={donutSize/2} cy={donutSize/2} r={donutRadius}
                              stroke="#FF9F0A" strokeWidth={donutStroke} fill="none"
                              strokeDasharray={`${unres_dash} ${circumference}`}
                              strokeDashoffset={-(sold_dash + reserv_dash)}
                              strokeLinecap="round"
                              style={{ transition: "all 0.6s cubic-bezier(0.34,1.2,0.64,1)" }} />
                          )}
                        </svg>
                        {/* Center text */}
                        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                          <div style={{ fontSize: 19, fontWeight: 700, color: t.text, letterSpacing: "-0.4px", lineHeight: 1 }}>{stueckzahl}</div>
                          <div style={{ fontSize: 9, color: t.textMuted, fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 2 }}>Stück</div>
                        </div>
                      </div>
                    </div>

                    {/* Body — wird bei eingeklapptem Batch ausgeblendet */}
                    {!isCollapsed && (<>

                    {/* Donut legend */}
                    <div style={{ display: "flex", gap: 12, marginBottom: 14, fontSize: 11 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#30D158" }} />
                        <span style={{ color: t.textSub }}>Verkauft <span style={{ color: t.text, fontWeight: 600 }}>{soldInBatch}</span></span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#0A84FF" }} />
                        <span style={{ color: t.textSub }}>Reserviert <span style={{ color: t.text, fontWeight: 600 }}>{reservFree}</span></span>
                      </div>
                      {unreserved > 0 && (
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#FF9F0A" }} />
                          <span style={{ color: t.textSub }}>Frei <span style={{ color: t.text, fontWeight: 600 }}>{unreserved}</span></span>
                        </div>
                      )}
                    </div>

                    {/* Action hints */}
                    {unreserved > 0 && (
                      <div style={{ fontSize: 12, color: "#FF9F0A", marginBottom: 12, padding: "8px 12px", borderRadius: 10, background: "rgba(255,159,10,0.1)", border: "1px solid rgba(255,159,10,0.25)", display: "flex", alignItems: "center", gap: 6 }}>
                        <span>⚠</span>
                        <span><strong>{unreserved}</strong> Stück noch nicht zugeordnet</span>
                      </div>
                    )}

                    {/* Slot Grid: alle Channels (Sales + Reserved-Pools) + "+"-Tile zum Anlegen */}
                    <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>Verteilung</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                      {(() => {
                        // Alle aktiven Channels (sales + reserved) + Legacy-Keys, die noch
                        // Daten haben aber keinen entsprechenden Channel mehr (orphan).
                        const liveIds = new Set(channelList.map(c => c.id));
                        const orphanKeys = Object.keys(reservierungen).filter(k => !liveIds.has(k) && (reservierungen[k] || 0) > 0);
                        return [...channelList.map(c => c.id), ...orphanKeys];
                      })().map(k => {
                        const pool = reservierungen[k] || 0;
                        const sales = salesByKanal[k] || 0;
                        const ch = channelList.find(c => c.id === k);
                        const isSalesKanal = ch ? isSalesChannel(ch) : false;
                        const displayName = ch?.name || k;
                        const rest = isSalesKanal ? pool - sales : pool;
                        const isOverdrawn = rest < 0;
                        const isEmpty = pool === 0;
                        const editKey = `${batch.id}_${k}`;
                        const isEditing = editPool === editKey;
                        const fillPct = pool > 0 ? Math.min(100, (sales / pool) * 100) : 0;
                        // Configured Mindestbestand for this channel (sales channels only)
                        const configuredThreshold = isSalesKanal ? (parseInt(batch.minThresholds?.[k], 10) || 0) : 0;
                        // Alarm state (priority): out > low (configured) > low (heuristic 20%)
                        const isOut = isSalesKanal && pool > 0 && rest <= 0;
                        const isLowConfigured = configuredThreshold > 0 && rest > 0 && rest <= configuredThreshold;
                        const isLowHeuristic = isSalesKanal && pool > 0 && rest > 0 && rest <= Math.max(2, Math.floor(pool * 0.2));
                        const isLow = isLowConfigured || (configuredThreshold === 0 && isLowHeuristic);
                        const kColor = KANAL_COLORS[k];
                        // Slot color states
                        const slotBg = isEmpty
                          ? (dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.025)")
                          : `${kColor}10`; // 6% tint
                        const slotBorder = isOverdrawn
                          ? "#FF453A"
                          : isEmpty
                            ? (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)")
                            : `${kColor}33`;

                        return (
                          <div
                            key={k}
                            // Click auf den Slot → öffnet BatchSlotEditModal
                            // (nur per-Batch: Pool + Mindestbestand). Globale
                            // Channel-Settings ausschliesslich im Channels-Manager.
                            onClick={() => {
                              if (!ch) return;
                              setEditChannelInBatch({ batchId: batch.id, channelId: ch.id });
                              haptic(6);
                            }}
                            style={{
                              background: slotBg,
                              border: `1px solid ${slotBorder}`,
                              borderRadius: 14,
                              padding: "10px 12px",
                              opacity: isEmpty ? 0.55 : 1,
                              transition: "background 0.3s ease, border 0.3s ease, opacity 0.3s ease, transform 0.15s ease",
                              display: "flex", flexDirection: "column",
                              cursor: "pointer",
                              minHeight: 78,
                              position: "relative",
                              ...(isEditing ? { background: `${kColor}22`, border: `1px solid ${kColor}80` } : {}),
                            }}
                          >
                            {/* × wurde entfernt — Channel-Löschen ausschliesslich
                                über Settings → Verkaufskanäle (single source of truth). */}

                            {/* Header: dot + name — kompakter */}
                            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 5 }}>
                              <div style={{ width: 6, height: 6, borderRadius: "50%", background: kColor, flexShrink: 0 }} />
                              <span style={{ fontSize: 11, color: t.textSub, fontWeight: 600, letterSpacing: "-0.1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{displayName}</span>
                            </div>

                            {/* Big number = restbestand — kompakter (24 statt 28) */}
                            <div style={{ fontSize: 24, fontWeight: 700, color: isOverdrawn ? "#FF453A" : (isEmpty ? t.textMuted : t.text), letterSpacing: "-0.6px", lineHeight: 1, marginBottom: 5 }}>
                              <span>{rest}</span>
                            </div>

                            {/* Mini progress bar */}
                            {isSalesKanal ? (
                              <div style={{ height: 3, borderRadius: 2, background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", overflow: "hidden", marginBottom: 4 }}>
                                <div style={{ height: "100%", width: `${fillPct}%`, background: isOverdrawn ? "#FF453A" : kColor, transition: "width 0.5s cubic-bezier(0.34,1.2,0.64,1)" }} />
                              </div>
                            ) : (
                              <div style={{ height: 3, borderRadius: 2, background: pool > 0 ? kColor : (dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"), opacity: pool > 0 ? 0.7 : 1, marginBottom: 4 }} />
                            )}

                            {/* Sub-info — kompakter Status-Hinweis */}
                            <div style={{ fontSize: 9.5, color: isOverdrawn ? "#FF453A" : (isLow ? "#FF9F0A" : t.textMuted), fontWeight: 500, lineHeight: 1.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {isSalesKanal
                                ? (pool === 0 ? "tippen zum bearbeiten" : `${sales} / ${pool} verkauft${isOverdrawn ? " · überzogen" : isLowConfigured ? " · Min unter." : isLowHeuristic ? " · niedrig" : ""}`)
                                : (pool === 0 ? "tippen zum bearbeiten" : `${pool} reserviert`)
                              }
                            </div>

                            {/* Min-Schwelle-Indikator (read-only) — Bearbeitung jetzt im Modal */}
                            {isSalesKanal && configuredThreshold > 0 && (
                              <div style={{
                                marginTop: 4, fontSize: 10, fontWeight: 500,
                                color: (isLowConfigured || isOut) ? "#FF9F0A" : t.textSub,
                              }}>
                                Min: {configuredThreshold}{isLowConfigured ? " ⚠" : isOut ? " 🔴" : ""}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* "+"-Tile: führt zum Channels-Manager (single source of truth).
                          Anlegen und alle Settings dort, nicht mehr inline im Lager. */}
                      {can("finance") && (
                        <button
                          onClick={() => { setShowLager(false); setShowChannelsConfig(true); haptic(8); }}
                          style={{
                            background: "transparent",
                            border: `1.5px dashed ${t.glassBorder}`,
                            borderRadius: 14, padding: "10px 12px",
                            cursor: "pointer", fontFamily: F,
                            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
                            minHeight: 78,
                            color: t.accent.blue.solid,
                            transition: "all 0.18s ease",
                          }}
                          onPointerDown={e => e.currentTarget.style.transform = "scale(0.97)"}
                          onPointerUp={e => e.currentTarget.style.transform = ""}
                          onPointerLeave={e => e.currentTarget.style.transform = ""}
                        >
                          <span style={{
                            display: "flex", alignItems: "center", justifyContent: "center",
                            width: 26, height: 26, borderRadius: 9,
                            background: t.accent.blue.weak, color: t.accent.blue.solid,
                            fontSize: 18, fontWeight: 600, lineHeight: 1,
                          }}>+</span>
                          <span style={{ fontSize: 10, fontWeight: 600, color: t.textSub, letterSpacing: "-0.1px", textAlign: "center" }}>Verwalten</span>
                        </button>
                      )}
                    </div>

                    {/* Wert-Übersicht — prominent footer */}
                    <div style={{ paddingTop: 12, borderTop: `0.5px solid ${t.divider}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
                        <span style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Wert im Lager</span>
                        <span style={{ fontSize: 16, fontWeight: 700, color: t.text, letterSpacing: "-0.3px" }}>{fmt2(wertImLager)} €</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
                        <span style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Wert verkauft</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: "#30D158", letterSpacing: "-0.2px" }}>{fmt2(wertVerkauft)} €</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
                        <span style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Profit-Potential</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: t.textSub, letterSpacing: "-0.2px" }}>~{fmt2(profitPotential)} €</span>
                      </div>
                      {daysSinceFirstSale !== null && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
                          <span style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Erste Verkäufe</span>
                          <span style={{ fontSize: 12, fontWeight: 500, color: t.textSub }}>vor {daysSinceFirstSale} {daysSinceFirstSale === 1 ? "Tag" : "Tagen"}</span>
                        </div>
                      )}
                    </div>
                    </>)}{/* /Body — !isCollapsed */}
                  </div>
                );
              })}

              {(!batches || batches.length === 0) && (
                <div style={{ ...glass({ borderRadius: 18 }), padding: "32px 16px", marginBottom: 12, textAlign: "center" }}>
                  <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.5 }}>📦</div>
                  <div style={{ fontSize: 14, color: t.textSub, marginBottom: 4 }}>Noch keine Batches</div>
                  <div style={{ fontSize: 12, color: t.textMuted }}>Erstelle deinen ersten Batch unten</div>
                </div>
              )}

              {/* Add new batch button */}
              <button onClick={() => setNewBatchOpen(true)} style={{ width: "100%", ...glass({ borderRadius: 16 }), padding: "16px", border: `1px dashed ${t.glassBorder}`, cursor: "pointer", color: "#0A84FF", fontFamily: F, fontSize: 15, fontWeight: 600, marginTop: 4 }}>
                + Neuer Batch
              </button>
            </div>
          </div>
        </>
        );
      })()}

      {/* ── NEW CHANNEL MODAL — saubere UI statt prompt() + confirm() ── */}
      {newChannelOpen && (
        <NewChannelModal
          t={t} dark={dark} F={F} glass={glass}
          onClose={() => setNewChannelOpen(false)}
          onCreate={async (name, type) => {
            const trimmed = (name || "").trim();
            if (!trimmed) return;
            const baseId = slugifyChannelName(trimmed);
            let id = baseId, n = 2;
            while (storeChannels.some(c => c.id === id)) { id = `${baseId}-${n++}`; }
            const next = [...storeChannels, {
              id, name: trimmed, active: true,
              sort_order: storeChannels.length,
              type: type === "reserved" ? "reserved" : "sales",
              price: 27.99, fee_fixed: 0, fee_percent: 0, shipping: 3.80,
            }];
            setStoreChannels(next);
            const ok = await saveStoreConfig({ channels: next });
            if (ok) {
              showToast(`${type === "sales" ? "Verkaufskanal" : "Pool"} "${trimmed}" angelegt`, "success");
              haptic(15);
            }
            setNewChannelOpen(false);
          }}
        />
      )}

      {/* ── DELETE CHANNEL CONFIRM MODAL ── */}
      {deleteChannelId && (() => {
        const ch = storeChannels.find(c => c.id === deleteChannelId);
        if (!ch) { setDeleteChannelId(null); return null; }
        const ordersUsing = orders.filter(o => o.kanal === ch.id).length
                         + archive.filter(o => o.kanal === ch.id).length;
        const isLastSales = isSalesChannel(ch) && storeChannels.filter(isSalesChannel).length <= 1;
        const blocked = ordersUsing > 0 || isLastSales;
        return (
          <>
            <div onClick={() => setDeleteChannelId(null)} style={{
              position: "fixed", inset: 0, zIndex: 90,
              background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
              backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
              animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
              willChange: "opacity",
            }} />
            <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 360, radius: 22, padding: "22px 22px 18px", zIndex: 91 })}>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text, letterSpacing: "-0.3px", marginBottom: 6 }}>
                {ch.name} löschen?
              </div>
              {blocked ? (
                <div style={{
                  fontSize: 13, color: t.accent.orange.solid,
                  background: t.accent.orange.weak,
                  border: `1px solid ${t.accent.orange.solid}33`,
                  padding: "10px 12px", borderRadius: 10,
                  marginTop: 12, lineHeight: 1.4,
                }}>
                  {ordersUsing > 0
                    ? `Es gibt noch ${ordersUsing} ${ordersUsing === 1 ? "Bestellung" : "Bestellungen"} mit diesem Kanal. Erst archivieren oder Kanal stattdessen deaktivieren.`
                    : "Mindestens ein Verkaufskanal muss bestehen."}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: t.textSub, marginTop: 6, marginBottom: 16, lineHeight: 1.4 }}>
                  Reservierungen in diesem {isSalesChannel(ch) ? "Kanal" : "Pool"} bleiben in den Batches als Daten erhalten,
                  werden aber nicht mehr angezeigt.
                </div>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                <button onClick={() => setDeleteChannelId(null)} style={{
                  flex: 1, ...glass({ borderRadius: 12 }),
                  border: `1px solid ${t.glassBorder}`,
                  padding: "12px", color: t.text,
                  fontFamily: F, fontWeight: 500, fontSize: 14, cursor: "pointer",
                }}>Abbrechen</button>
                <button
                  onClick={async () => {
                    const id = deleteChannelId;
                    setDeleteChannelId(null);
                    await persistDeleteChannel(id);
                  }}
                  disabled={blocked}
                  style={{
                    flex: 1.2,
                    background: blocked ? t.btnGhost : t.accent.red.solid,
                    color: blocked ? t.textMuted : "#fff",
                    border: "none", borderRadius: 12, padding: "12px",
                    fontFamily: F, fontWeight: 700, fontSize: 14,
                    cursor: blocked ? "not-allowed" : "pointer",
                    boxShadow: blocked ? "none" : `0 3px 14px ${t.accent.red.weak}`,
                  }}>Löschen</button>
              </div>
            </div>
          </>
        );
      })()}

      {/* ── BATCH-SLOT EDIT MODAL (aus Lager-Slot) ──
          Nur per-batch Werte (Pool, Threshold). Alles globale → Channels-Manager. */}
      {editChannelInBatch && (() => {
        const { batchId, channelId } = editChannelInBatch;
        const batchObj = batches.find(b => b.id === batchId);
        const channelObj = storeChannels.find(c => c.id === channelId);
        if (!batchObj || !channelObj) { setEditChannelInBatch(null); return null; }
        const sold = orders.filter(o => o.batchId === batchId && o.kanal === channelId && o.status !== "Retoure").length;
        return (
          <BatchSlotEditModal
            t={t} dark={dark} F={F} glass={glass}
            batch={batchObj}
            channel={channelObj}
            isSalesChannel={isSalesChannel(channelObj)}
            currentSales={sold}
            haptic={haptic}
            onClose={() => setEditChannelInBatch(null)}
            onSavePool={(bId, cId, newCount) => {
              const newRes = { ...(batchObj.reservierungen || {}), [cId]: newCount };
              setBatches(bs => bs.map(b => b.id === bId ? { ...b, reservierungen: newRes } : b));
              return apiBatchUpdate(bId, { reservierungen: newRes });
            }}
            onSaveThreshold={(bId, cId, newThreshold) => {
              const newThresholds = { ...(batchObj.minThresholds || {}) };
              if (newThreshold === 0) delete newThresholds[cId];
              else newThresholds[cId] = newThreshold;
              setBatches(bs => bs.map(b => b.id === bId ? { ...b, minThresholds: newThresholds } : b));
              return apiBatchUpdate(bId, { minThresholds: newThresholds });
            }}
            onOpenChannelsManager={(cId) => {
              // Schliesst Slot-Modal, schliesst Lager (damit der User nicht "verschachtelt"
              // ist), öffnet Channels-Manager und expandiert den gewählten Kanal direkt.
              setEditChannelInBatch(null);
              setShowLager(false);
              setExpandedChannelId(cId);
              setShowChannelsConfig(true);
              haptic(8);
            }}
          />
        );
      })()}

      {/* ── BATCH EDIT POPUP ── */}
      {editBatchId !== null && (() => {
        const batch = batches.find(b => b.id === editBatchId);
        if (!batch) return null;
        return (
          <BatchEditPopup
            t={t} dark={dark} F={F} glass={glass} inp={inp}
            batch={batch}
            canDelete={batches.length > 1}
            fmt2={fmt2}
            onClose={() => setEditBatchId(null)}
            onSaveName={(name) => {
              setBatches(bs => bs.map(b => b.id === batch.id ? { ...b, name } : b));
              apiBatchUpdate(batch.id, { name });
            }}
            onSaveNumbers={(stueckzahl, investment) => {
              const cogs = investment / Math.max(1, stueckzahl);
              setBatches(bs => bs.map(b => b.id === batch.id ? { ...b, stueckzahl, investment, cogs } : b));
              apiBatchUpdate(batch.id, { stueckzahl, investment, cogs });
            }}
            onDelete={async () => {
              if (confirm(`Batch "${batch.name}" wirklich löschen?`)) {
                setBatches(bs => bs.filter(b => b.id !== batch.id));
                setEditBatchId(null);
                if (currentStore && authToken) {
                  try {
                    await fetch(`/api/stores/${currentStore.id}/batches/${batch.id}`, {
                      method: "DELETE",
                      headers: { "Authorization": `Bearer ${authToken}` },
                    });
                  } catch {}
                }
              }
            }}
          />
        );
      })()}

      {/* ── NEW BATCH POPUP ── */}
      {newBatchOpen && (
        <>
          <div onClick={() => setNewBatchOpen(false)} style={{
            position: "fixed", inset: 0, zIndex: 80,
            background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />
          <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 360, radius: 24, zIndex: 81 })}>
            <ModalHeader t={t} dark={dark} F={F} title="Neuer Batch" onClose={() => setNewBatchOpen(false)} />

            <div style={{ ...glass({ borderRadius: 14 }), overflow: "hidden", marginBottom: 10 }}>
              <input value={nbName} onChange={e => setNbName(e.target.value)} placeholder={`Runde ${batches.length + 1}`} style={inp({ fontSize: 15, padding: "14px 16px" })} />
            </div>

            {/* Inputs sind jetzt String-State → leerer/mehrstelliger Input möglich,
                Coercion erst beim Erstellen unten. */}
            <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1, ...glass({ borderRadius: 14 }), overflow: "hidden", padding: "10px 14px" }}>
                <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Stück</div>
                <input type="number" inputMode="numeric" value={nbStueck}
                  onChange={e => setNbStueck(e.target.value)}
                  style={{ width: "100%", border: "none", background: "transparent", fontFamily: F, fontSize: 17, fontWeight: 600, color: t.text, outline: "none", padding: 0 }} />
              </div>
              <div style={{ flex: 1.2, ...glass({ borderRadius: 14 }), overflow: "hidden", padding: "10px 14px" }}>
                <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Investment €</div>
                <input type="number" step="0.01" inputMode="decimal" value={nbInvest}
                  onChange={e => setNbInvest(e.target.value)}
                  style={{ width: "100%", border: "none", background: "transparent", fontFamily: F, fontSize: 17, fontWeight: 600, color: t.text, outline: "none", padding: 0 }} />
              </div>
            </div>

            {(() => {
              const sPreview = Math.max(1, parseInt(nbStueck, 10) || 1);
              const iPreview = Math.max(0, parseFloat(String(nbInvest).replace(",", ".")) || 0);
              return (
                <div style={{ ...glass({ borderRadius: 14 }), padding: "10px 14px", marginBottom: 14 }}>
                  <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>COGS pro Stück</div>
                  <div style={{ fontSize: 17, fontWeight: 600, color: t.text, letterSpacing: "-0.3px" }}>{(iPreview / sPreview).toFixed(2)} €</div>
                </div>
              );
            })()}

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setNewBatchOpen(false)} style={{ flex: 1, background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)", border: "none", borderRadius: 12, padding: "13px", fontFamily: F, fontWeight: 600, fontSize: 14, cursor: "pointer", color: t.text }}>Abbrechen</button>
              <button onClick={async () => {
                // Coercion erst beim Submit
                const safeStueck = Math.max(1, parseInt(nbStueck, 10) || 1);
                const safeInvest = Math.max(0, parseFloat(String(nbInvest).replace(",", ".")) || 0);
                const cogs = safeInvest / safeStueck;
                const newBatchData = {
                  name: nbName || `Runde ${batches.length + 1}`,
                  stueckzahl: safeStueck,
                  investment: safeInvest,
                  cogs,
                  reservierungen: {},
                };
                setNbName(""); setNbStueck("100"); setNbInvest("1000");
                setNewBatchOpen(false);
                const created = await apiBatchCreate(newBatchData);
                if (created) setBatches(bs => [...bs, created]);
              }} style={{ flex: 1.3, background: "rgba(10,132,255,0.85)", border: "none", borderRadius: 12, padding: "13px", fontFamily: F, fontWeight: 600, fontSize: 14, cursor: "pointer", color: "#fff", boxShadow: "0 4px 20px rgba(10,132,255,0.3)" }}>Erstellen</button>
            </div>
          </div>
        </>
      )}

      {/* ── RETOURE CONFIRM ── */}
      {confirmRetoure !== null && (() => {
        const order = orders.find(o => o.id === confirmRetoure);
        if (!order) return null;
        return (
          <>
            <div onClick={() => setConfirmRetoure(null)} style={{
              position: "fixed", inset: 0, zIndex: 90,
              background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
              backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
              animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
              willChange: "opacity",
            }} />
            <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 360, radius: 22, zIndex: 91 })}>
              <ModalHeader
                t={t} dark={dark} F={F}
                title="Retoure markieren?"
                subtitle={<>#{order.bestellnummer}</>}
                accent={t.accent.orange}
                accentIcon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 9v4"/><path d="M12 17h.01"/>
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  </svg>
                }
                onClose={() => setConfirmRetoure(null)}
              />
              <div style={{ fontSize: 13, color: t.textSub, lineHeight: 1.5, marginBottom: 16, padding: "10px 12px", background: t.accent.orange.weak, borderRadius: 10, border: `1px solid ${t.accent.orange.solid}33` }}>
                Diese Bestellung ist als <strong style={{ color: t.text }}>versendet</strong> markiert. Eine Retoure setzt sie zurück und entfernt sie aus dem Umsatz.
              </div>
              <button onClick={() => setConfirmRetoure(null)} style={{
                width: "100%", padding: "13px", marginBottom: 8,
                background: "rgba(10,132,255,0.92)", border: "none", borderRadius: 12,
                fontFamily: F, fontWeight: 700, fontSize: 15, cursor: "pointer", color: "#fff",
                letterSpacing: "-0.1px", boxShadow: "0 4px 20px rgba(10,132,255,0.3)",
              }}>Abbrechen</button>
              <button onClick={confirmRetoureNow} style={{
                width: "100%", padding: "11px",
                background: "transparent", border: "none",
                fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer", color: t.accent.red.solid,
              }}>Trotzdem als Retoure setzen</button>
            </div>
          </>
        );
      })()}

      {/* ─────────────────────────────────────────────────────────
          BEREIT → VERSENDET — Stufe 1: ShipChoiceModal
          User soll standardmässig den Scanner nutzen. Manuell nur als
          Ausnahme (Persönliche Übergabe etc.). Beide Pfade führen zu
          status: "Versendet", aber der manuelle Pfad braucht eine
          doppelte Bestätigung in einem zweiten Modal.
          ───────────────────────────────────────────────────────── */}
      {shipChoice && (
        <>
          <div onClick={() => setShipChoice(null)} style={{
            position: "fixed", inset: 0, zIndex: 90,
            background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />
          <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 380, radius: 22, zIndex: 91 })}>
            <ModalHeader
              t={t} dark={dark} F={F}
              title="Versenden"
              subtitle={<><strong style={{ color: t.text, fontWeight: 700 }}>#{shipChoice.bestellnummer}</strong>{shipChoice.trackingNumber ? <><br/><span style={{ fontSize: 12, color: t.textSub, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{shipChoice.trackingNumber}</span></> : null}</>}
              onClose={() => setShipChoice(null)}
            />

            {/* Primary CTA — Scanner */}
            <button onClick={() => { setShipChoice(null); setShowScanner(true); }} style={{
              width: "100%", padding: "16px 18px", marginBottom: 10,
              background: "linear-gradient(180deg, #0A84FF 0%, #0066D6 100%)",
              border: "none", borderRadius: 14,
              color: "#fff", fontFamily: F, fontWeight: 700, fontSize: 16, letterSpacing: "-0.2px",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              boxShadow: "0 6px 22px rgba(10,132,255,0.4)",
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/>
                <path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
                <path d="M7 12h10"/>
              </svg>
              Versandlabel scannen
            </button>
            <div style={{ fontSize: 11, color: t.textMuted, textAlign: "center", marginBottom: 18, lineHeight: 1.4 }}>
              Scan-Pfad — Standard. Trackingnummer wird automatisch verifiziert.
            </div>

            {/* Divider */}
            <div style={{ height: 0.5, background: t.divider, marginBottom: 14 }} />

            {/* Manual fallback — secondary, intentionally less prominent. Abbrechen entfällt
                weil der Header schon einen ✕-Close-Button hat (einheitlich mit anderen Modals). */}
            <button onClick={() => { const o = shipChoice; setShipChoice(null); setManualShip(o); }} style={{
              width: "100%", padding: "12px",
              background: "transparent", border: `1px solid ${t.glassBorder}`, borderRadius: 12,
              color: t.textSub, fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <span>Manuell bestätigen</span>
              <span style={{ fontSize: 10, color: t.textMuted }}>(Persönliche Übergabe etc.)</span>
            </button>
          </div>
        </>
      )}

      {/* ─────────────────────────────────────────────────────────
          BEREIT → VERSENDET — Stufe 2: ManualShipConfirmModal
          Doppelte Bestätigung: Grund-Auswahl + Checkbox + Confirm.
          Erst wenn beide gesetzt sind ist der Confirm-Button aktiv.
          Grund landet in der Notiz für Audit-Trail.
          ───────────────────────────────────────────────────────── */}
      {manualShip && (() => {
        const REASONS = [
          { id: "handover", label: "Persönliche Übergabe" },
          { id: "pickup",   label: "Lokale Abholung" },
          { id: "scan_fail",label: "Scanner funktioniert nicht" },
          { id: "other",    label: "Anderer Grund" },
        ];
        return (
          <ManualShipConfirmModal
            order={manualShip}
            reasons={REASONS}
            t={t} dark={dark} F={F} glass={glass}
            vh={appVh} kbOpen={appKbOpen} offsetTop={appOffsetTop}
            onClose={() => setManualShip(null)}
            onConfirm={(reasonLabel, reasonNote) => {
              const id = manualShip.id;
              const ordnumber = manualShip.bestellnummer;
              const stamp = new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
              const tag = `[Manuell als versendet markiert · ${reasonLabel}${reasonNote ? `: ${reasonNote}` : ""} · ${stamp}]`;
              const order = orders.find(x => x.id === id);
              const newNotiz = order?.notiz ? `${order.notiz}\n${tag}` : tag;
              setOrders(o => o.map(x => x.id !== id ? x : { ...x, status: "Versendet", notiz: newNotiz }));
              apiOrderUpdate(id, { status: "Versendet", notiz: newNotiz });
              setManualShip(null);
              setHelperSuccessBanner({ id, bestellnummer: ordnumber });
              haptic(20);
              setTimeout(() => setHelperSuccessBanner(curr => curr?.id === id ? null : curr), 5000);
            }}
          />
        );
      })()}

      {/* ── HELFER: ERFOLGS-BANNER mit Undo ── */}
      {helperSuccessBanner && (
        <div style={{
          position: "fixed",
          top: "calc(env(safe-area-inset-top, 16px) + 16px)",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 100,
          ...glass({ borderRadius: 50, border: "1px solid rgba(48,209,88,0.4)" }),
          padding: "10px 8px 10px 18px",
          display: "flex", alignItems: "center", gap: 12,
          animation: "toastIn 0.32s cubic-bezier(0.34,1.4,0.64,1)",
          boxShadow: "0 8px 32px rgba(48,209,88,0.25)",
          background: dark ? "rgba(20,40,28,0.92)" : "rgba(232,250,238,0.96)",
          maxWidth: "calc(100vw - 64px)",
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%", background: "#30D158",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <svg width="16" height="14" viewBox="0 0 16 14" fill="none">
              <path d="M2 7.5L6 11.5L14 2.5" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#30D158", fontFamily: F, letterSpacing: "-0.2px" }}>Verschickt!</span>
            <span style={{ fontSize: 12, color: t.textSub, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              #{helperSuccessBanner.bestellnummer}
            </span>
          </div>
          {/* Undo-Knopf — setzt Status zurück auf Bereit */}
          <button onClick={() => {
            const id = helperSuccessBanner.id;
            setOrders(o => o.map(x => x.id !== id ? x : { ...x, status: "Bereit" }));
            apiOrderUpdate(id, { status: "Bereit" });
            setHelperSuccessBanner(null);
            haptic(15);
          }} style={{
            background: dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)",
            border: "none",
            borderRadius: 50,
            padding: "8px 14px",
            fontFamily: F,
            fontWeight: 600,
            fontSize: 13,
            color: "#0A84FF",
            cursor: "pointer",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ marginTop: -1 }}>
              <path d="M2 4.5h6.5a3 3 0 0 1 0 6H6.5" stroke="#0A84FF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M4.5 2L2 4.5L4.5 7" stroke="#0A84FF" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Rückgängig
          </button>
        </div>
      )}

      {/* ── UNRETOURE CONFIRM ── */}
      {confirmUnretoure !== null && (() => {
        const order = orders.find(o => o.id === confirmUnretoure);
        if (!order) return null;
        return (
          <>
            <div onClick={() => setConfirmUnretoure(null)} style={{
              position: "fixed", inset: 0, zIndex: 90,
              background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
              backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
              animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
              willChange: "opacity",
            }} />
            <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 360, radius: 22, zIndex: 91 })}>
              <ModalHeader
                t={t} dark={dark} F={F}
                title="Retoure zurücksetzen?"
                subtitle={<>#{order.bestellnummer}</>}
                accent={t.accent.blue}
                accentIcon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 1 0 9-9"/><path d="M3 4v5h5"/>
                  </svg>
                }
                onClose={() => setConfirmUnretoure(null)}
              />
              <div style={{ fontSize: 13, color: t.textSub, lineHeight: 1.5, marginBottom: 16, padding: "10px 12px", background: t.accent.orange.weak, borderRadius: 10, border: `1px solid ${t.accent.orange.solid}33` }}>
                Diese Bestellung ist als <strong style={{ color: t.text }}>Retoure</strong> markiert. Zurück auf Versendet setzen macht normalerweise keinen Sinn — nur bei Fehler-Klicks.
              </div>
              <button onClick={() => setConfirmUnretoure(null)} style={{
                width: "100%", padding: "13px", marginBottom: 8,
                background: "rgba(10,132,255,0.92)", border: "none", borderRadius: 12,
                fontFamily: F, fontWeight: 700, fontSize: 15, cursor: "pointer", color: "#fff",
                letterSpacing: "-0.1px", boxShadow: "0 4px 20px rgba(10,132,255,0.3)",
              }}>Abbrechen</button>
              <button onClick={confirmUnretoureNow} style={{
                width: "100%", padding: "11px",
                background: "transparent", border: "none",
                fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer", color: t.accent.red.solid,
              }}>Ja, war ein Fehler</button>
            </div>
          </>
        );
      })()}

      {/* ── ARCHIVE SHEET ── */}
      {showArchive && (
        <>
          {!isDesktop && <div onClick={() => setShowArchive(false)} style={{
            position: "fixed", inset: 0, zIndex: 40,
            background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />}
          <div
            onTouchStart={e => {
              if (e.currentTarget.scrollTop <= 0) archiveStartY.current = e.touches[0].clientY;
            }}
            onTouchMove={e => {
              if (archiveStartY.current !== null) {
                const dy = e.touches[0].clientY - archiveStartY.current;
                if (dy > 0 && e.currentTarget.scrollTop <= 0) {
                  setArchiveDragY(Math.min(dy, 400));
                } else if (dy < 0) {
                  archiveStartY.current = null;
                  setArchiveDragY(0);
                }
              }
            }}
            onTouchEnd={() => {
              if (archiveDragY > 80) setShowArchive(false);
              setArchiveDragY(0);
              archiveStartY.current = null;
            }}
            style={isDesktop ? {
              ...desktopRightPanel,
              background: dark ? "rgba(28,28,30,0.85)" : "rgba(250,250,252,0.88)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"}`,
              borderRadius: 24,
              zIndex: 50, animation: "slideInRight 0.24s cubic-bezier(0.32,0.72,0,1)",
              overflowY: "auto", WebkitOverflowScrolling: "touch",
            } : {
              ...bottomSheetShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, F, dark, width: 560, padding: "0 22px 22px", zIndex: 50 }),
              transform: `translateX(-50%) translateY(${archiveDragY}px)`,
              animation: archiveDragY === 0 ? "slideUp 0.34s cubic-bezier(0.32,0.72,0,1) both" : "none",
              transition: archiveDragY === 0 ? "transform 0.25s cubic-bezier(0.32,0.72,0,1)" : "none",
            }}>
            <div style={{ padding: isDesktop ? "32px 32px 32px" : "0 0 4px" }}>
              {!isDesktop ? (
                <BottomSheetHeader
                  t={t} dark={dark} F={F}
                  title="Archiv"
                  subtitle={archive.length === 0 ? "Keine archivierten Bestellungen" : `${archive.length} archivierte Bestellung${archive.length === 1 ? "" : "en"}`}
                  onClose={() => setShowArchive(false)}
                  marginBottom={16}
                />
              ) : (
                <div style={{ display: "flex", alignItems: "center", paddingBottom: 18, position: "relative", minHeight: 30 }}>
                  <ModalCloseButton onClose={() => setShowArchive(false)} dark={dark} />
                  <span style={{ position: "absolute", left: 0, right: 0, textAlign: "center", fontSize: 17, fontWeight: 600, color: t.text, letterSpacing: "-0.3px", pointerEvents: "none", fontFamily: F, padding: "0 46px" }}>Archiv</span>
                </div>
              )}
              {archive.length > 0 && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14, marginTop: -8 }}>
                  <button onClick={() => setConfirmEmptyArchive(true)} style={{ background: "none", border: "none", color: "#FF453A", fontFamily: F, fontSize: 13, fontWeight: 500, cursor: "pointer", padding: "4px 8px" }}>Archiv leeren</button>
                </div>
              )}

              {archive.length === 0 ? (
                <div style={{ ...glass({ borderRadius: 18 }), padding: "32px 16px", textAlign: "center" }}>
                  <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.5 }}>📦</div>
                  <div style={{ fontSize: 14, color: t.textSub, marginBottom: 4 }}>Archiv ist leer</div>
                  <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.4 }}>Gelöschte Bestellungen erscheinen hier und können wiederhergestellt werden.</div>
                </div>
              ) : (
                <div style={{ ...glass({ borderRadius: 18 }), overflow: "hidden" }}>
                  {archive.slice().sort((a, b) => (b.archivedAt || "").localeCompare(a.archivedAt || "")).map((item, idx) => (
                    <div key={item.id}>
                      {idx > 0 && <div style={{ height: "0.5px", background: t.divider, marginLeft: 16 }} />}
                      <div style={{ padding: "14px 16px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 15, fontWeight: 600, color: t.text, letterSpacing: "-0.2px" }}>#{item.bestellnummer}</div>
                            <div style={{ fontSize: 12, color: t.textSub, marginTop: 2 }}>{getChannelLabel(item.kanal)} · {fmt(item.datum)} · {getStatusLabel(item.status)}</div>
                            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 3 }}>Gelöscht {item.archivedAt ? new Date(item.archivedAt).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—"}</div>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => restoreFromArchive(item.id)} style={{ flex: 1, background: "rgba(10,132,255,0.15)", border: "none", borderRadius: 10, padding: "10px", fontFamily: F, fontWeight: 600, fontSize: 13, cursor: "pointer", color: "#0A84FF" }}>↺ Wiederherstellen</button>
                          <button onClick={() => purgeFromArchive(item.id)} style={{ flex: 1, background: "rgba(255,69,58,0.12)", border: "none", borderRadius: 10, padding: "10px", fontFamily: F, fontWeight: 600, fontSize: 13, cursor: "pointer", color: "#FF453A" }}>Endgültig löschen</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── EMPTY ARCHIVE CONFIRM ── */}
      {confirmEmptyArchive && (
        <>
          <div onClick={() => setConfirmEmptyArchive(false)} style={{
            position: "fixed", inset: 0, zIndex: 90,
            background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />
          <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 360, radius: 24, zIndex: 91 })}>
            <ModalHeader
              t={t} dark={dark} F={F}
              title="Archiv leeren?"
              subtitle={`Alle ${archive.length} archivierten Bestellungen werden endgültig gelöscht. Nicht rückgängig zu machen.`}
              accent={t.accent.red}
              accentIcon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                </svg>
              }
              onClose={() => setConfirmEmptyArchive(false)}
            />
            <button onClick={() => setConfirmEmptyArchive(false)} style={{
              width: "100%", background: "rgba(10,132,255,0.92)", border: "none", borderRadius: 12,
              padding: "13px", marginBottom: 8,
              fontFamily: F, fontWeight: 700, fontSize: 15, cursor: "pointer", color: "#fff",
              letterSpacing: "-0.1px", boxShadow: "0 4px 20px rgba(10,132,255,0.3)",
            }}>Abbrechen</button>
            <button onClick={emptyArchive} style={{
              width: "100%", background: "transparent", border: "none",
              padding: "11px", fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer", color: t.accent.red.solid,
            }}>Archiv endgültig leeren</button>
          </div>
        </>
      )}

      {/* ── DASHBOARD CUSTOMIZE MODAL ── */}
      {showCustomizeDashboard && (
        <DashboardCustomizeModal
          t={t} dark={dark} F={F} glass={glass}
          vh={appVh} kbOpen={appKbOpen} offsetTop={appOffsetTop}
          currentRole={currentRole}
          layout={storeDashboardLayout || defaultLayoutForRole(currentRole)}
          saveDashboardLayout={saveDashboardLayout}
          haptic={haptic}
          onClose={() => setShowCustomizeDashboard(false)}
        />
      )}

      {/* ── ACTIVITY LOG MODAL — Owner/Admin only ── */}
      {showAuditLog && (() => {
        const ENTITY_OPTIONS = [
          { v: "all",         l: "Alle Aktionen" },
          { v: "order",       l: "Bestellungen" },
          { v: "batch",       l: "Batches" },
          { v: "member",      l: "Mitglieder" },
          { v: "invitation",  l: "Einladungen" },
          { v: "settings",    l: "Einstellungen" },
          { v: "store",       l: "Store-Konfig" },
        ];
        const DAYS_OPTIONS = [
          { v: 1,    l: "Heute" },
          { v: 7,    l: "7 Tage" },
          { v: 30,   l: "30 Tage" },
          { v: -1,   l: "Alles" },
        ];
        const setFilter = (patch) => {
          const next = { ...auditFilter, ...patch };
          setAuditFilter(next);
          setAuditEntries([]);
          // Apply with the new filter immediately
          setTimeout(() => {
            // We piggyback through state — but loadAuditLog reads from auditFilter ref.
            // To make sure it uses the new value, we rebuild the request inline:
            (async () => {
              if (!currentStore || !authToken) return;
              setAuditLoading(true);
              const params = new URLSearchParams();
              if (next.entity_type && next.entity_type !== "all") params.set("entity_type", next.entity_type);
              if (next.days && next.days > 0) params.set("from", new Date(Date.now() - next.days * 86400 * 1000).toISOString());
              params.set("limit", "50");
              const res = await fetch(`/api/stores/${currentStore.id}/audit?${params}`, {
                headers: { "Authorization": `Bearer ${authToken}` },
              });
              if (res.ok) {
                const data = await res.json();
                setAuditEntries(data.entries || []);
                setAuditHasMore(!!data.has_more);
              }
              setAuditLoading(false);
            })();
          }, 0);
        };
        return (
        <>
          <div onClick={() => setShowAuditLog(false)} style={{
            position: "fixed", inset: 0, zIndex: 80,
            background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />
          <div onClick={e => e.stopPropagation()} style={{
            ...modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 560, radius: 24, padding: "20px 22px 22px", zIndex: 81 }),
            display: "flex", flexDirection: "column",
          }}>
            <ModalHeader
              t={t} dark={dark} F={F}
              title="Aktivitätsverlauf"
              subtitle={`${currentStore?.name || ""} — Alle Aktionen mit Zeitstempel`}
              onClose={() => setShowAuditLog(false)}
              marginBottom={14}
            />

            {/* Filter Dropdowns */}
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <select value={auditFilter.entity_type} onChange={e => setFilter({ entity_type: e.target.value })} style={{
                flex: 1, padding: "9px 12px", fontSize: 13, fontFamily: F,
                background: t.pillActive, color: t.text, border: `1px solid ${t.glassBorder}`,
                borderRadius: 10, cursor: "pointer",
              }}>
                {ENTITY_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
              <select value={auditFilter.days} onChange={e => setFilter({ days: parseInt(e.target.value, 10) })} style={{
                flex: 1, padding: "9px 12px", fontSize: 13, fontFamily: F,
                background: t.pillActive, color: t.text, border: `1px solid ${t.glassBorder}`,
                borderRadius: 10, cursor: "pointer",
              }}>
                {DAYS_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>

            {/* Entries list */}
            <div style={{ flex: 1, overflowY: "auto", marginRight: -8, paddingRight: 8 }}>
              {auditLoading && auditEntries.length === 0 && (
                <div style={{ textAlign: "center", color: t.textMuted, fontSize: 13, padding: 20 }}>Lade…</div>
              )}
              {!auditLoading && auditEntries.length === 0 && (
                <div style={{ textAlign: "center", color: t.textMuted, fontSize: 13, padding: 20 }}>Keine Einträge</div>
              )}
              {auditEntries.length > 0 && (
                <div style={{ ...glass({ borderRadius: 14 }), overflow: "hidden" }}>
                  {auditEntries.map((e, i) => {
                    const desc = describeAuditEntry(e);
                    const who = e.user_email || (e.user_id ? e.user_id.slice(0, 8) : "System");
                    const isMe = e.user_id === authUser?.id;
                    return (
                      <React.Fragment key={e.id}>
                        {i > 0 && <div style={{ height: "0.5px", background: t.divider, marginLeft: 16 }} />}
                        <div style={{ padding: "11px 14px", display: "flex", gap: 10, alignItems: "flex-start" }}>
                          <span style={{ fontSize: 17, lineHeight: 1.2, flexShrink: 0, marginTop: 1 }}>{desc.icon}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, color: t.text, fontWeight: 500, lineHeight: 1.35 }}>
                              {desc.text}
                            </div>
                            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2, display: "flex", gap: 6, flexWrap: "wrap" }}>
                              <span>{isMe ? "Du" : who}</span>
                              <span>·</span>
                              <span>{formatRelative(e.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
              {auditHasMore && (
                <button onClick={() => loadAuditLog(false)} disabled={auditLoading} style={{
                  width: "100%", marginTop: 12, padding: "10px",
                  background: t.pillActive, border: `1px solid ${t.glassBorder}`, borderRadius: 10,
                  color: "#0A84FF", fontFamily: F, fontSize: 13, fontWeight: 600, cursor: "pointer",
                  opacity: auditLoading ? 0.5 : 1,
                }}>{auditLoading ? "Lade…" : "Mehr laden"}</button>
              )}
            </div>
          </div>
        </>
        );
      })()}

      {/* ── SCANNER MODAL — mobile barcode scanner für Versand-Workflow ── */}
      {showScanner && (() => {
        const bereitOrders = orders.filter(o => o.status === "Bereit");
        const closeScanner = () => {
          // Stop ZXing controls cleanly so the camera light goes off
          try { scannerControlsRef.current?.stop?.(); } catch {}
          scannerControlsRef.current = null;
          if (overlayTimerRef.current) { clearTimeout(overlayTimerRef.current); overlayTimerRef.current = null; }
          setShowScanner(false);
          setScannerState("idle");
          setScannerError(null);
          setLastMatchedOrder(null);
          setLastUnmatchedCode(null);
          lastScanCodeRef.current = null;
          lastScanTimeRef.current = 0;
        };

        const handleScanResult = async (decoded) => {
          if (!decoded) return;
          const sanitized = sanitizeTrackingNumber(decoded);
          if (!sanitized) return;

          // Dedup: ignore the same code within 1.5s cooldown
          const now = Date.now();
          if (lastScanCodeRef.current === sanitized && (now - lastScanTimeRef.current) < 1500) return;
          lastScanCodeRef.current = sanitized;
          lastScanTimeRef.current = now;

          // Validierung: Wenn der dekodierte Code KEIN Tracking-Format hat
          // (z.B. Kunden-Referenz, URL, Bestellnummer), silent skip — Scanner
          // läuft weiter, kein Error-Beep, kein Modal-Wechsel. So kann der
          // User die Kamera weiter über das Label schwenken bis das echte
          // Tracking-Barcode in den Sucher kommt.
          if (!looksLikeValidTrackingNumber(sanitized)) {
            console.info("[scanner] Code skipped (no tracking format):", sanitized);
            return;
          }

          // Match against current Bereit orders
          const matches = findOrdersForCode(sanitized, orders);

          if (overlayTimerRef.current) { clearTimeout(overlayTimerRef.current); overlayTimerRef.current = null; }

          if (matches.length >= 1) {
            // Match: erste Bereit-Order übernehmen. Bei mehreren (selten — selbe
            // Trackingnummer auf mehreren Bestellungen) flaggen wir _multipleHit
            // im Banner-Overlay. Optimistic Update + API-Call.
            const matched = matches[0];
            const ackOrder = matches.length > 1
              ? { ...matched, status: "Versendet", _multipleHit: matches.length }
              : { ...matched, status: "Versendet" };
            setOrders(o => o.map(x => x.id === matched.id ? { ...x, status: "Versendet" } : x));
            apiOrderUpdate(matched.id, { status: "Versendet" });
            playBeep("success");
            haptic(20);
            setLastMatchedOrder(ackOrder);
            setScannerState("match");
            overlayTimerRef.current = setTimeout(() => {
              setLastMatchedOrder(null);
              setScannerState("scanning");
            }, 1500);
          } else {
            // No match
            playBeep("error");
            haptic(15);
            setLastUnmatchedCode(sanitized);
            setScannerState("no_match");
            overlayTimerRef.current = setTimeout(() => {
              setLastUnmatchedCode(null);
              setScannerState("scanning");
            }, 2500);
          }
        };

        return (
          <ScannerModal
            t={t} dark={dark} F={F}
            bereitOrders={bereitOrders}
            scannerState={scannerState}
            scannerError={scannerError}
            lastMatchedOrder={lastMatchedOrder}
            lastUnmatchedCode={lastUnmatchedCode}
            onClose={closeScanner}
            onScanResult={handleScanResult}
            onSetState={setScannerState}
            onError={setScannerError}
            scannerControlsRef={scannerControlsRef}
            getStatusLabel={getStatusLabel}
            getChannelLabel={getChannelLabel}
            extractTrackingNumber={extractTrackingNumber}
          />
        );
      })()}

      {/* ── CHANNELS CONFIG MODAL ── */}
      {showChannelsConfig && (() => {
        // Working copy — flush to API on changes via saveStoreConfig
        const sortedChannels = storeChannels.slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        const persist = (next) => {
          // Renumber sort_order so it's always 0..n-1 dense
          const normalized = next.map((c, i) => ({ ...c, sort_order: i }));
          setStoreChannels(normalized);
          saveStoreConfig({ channels: normalized });
        };
        const moveChannel = (idx, dir) => {
          const j = idx + dir;
          if (j < 0 || j >= sortedChannels.length) return;
          const arr = sortedChannels.slice();
          [arr[idx], arr[j]] = [arr[j], arr[idx]];
          persist(arr);
        };
        const renameChannel = (id, newName) => {
          const arr = sortedChannels.map(c => c.id === id ? { ...c, name: newName } : c);
          persist(arr);
        };
        const toggleActive = (id) => {
          const arr = sortedChannels.map(c => c.id === id ? { ...c, active: !c.active } : c);
          persist(arr);
        };
        // Trigger das schöne DeleteChannelConfirmModal (Z. ~6465) statt native confirm().
        // Das Modal prüft selbst Orders-using und Mindest-Kanal-Constraints,
        // und ruft persistDeleteChannel(id) für den eigentlichen Delete auf.
        const deleteChannel = (id) => setDeleteChannelId(id);
        const updateFinance = (id, field, raw) => {
          const num = parseFloat(String(raw).replace(",", "."));
          if (!Number.isFinite(num) || num < 0) return;
          if (field === "fee_percent" && num > 100) return;
          const arr = sortedChannels.map(c => c.id === id ? { ...c, [field]: num } : c);
          persist(arr);
        };
        return (
        <>
          <div onClick={() => setShowChannelsConfig(false)} style={{
            position: "fixed", inset: 0, zIndex: 80,
            background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />
          <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 540, radius: 24, padding: "20px 22px 22px", zIndex: 81 })}>
            <ModalHeader
              t={t} dark={dark} F={F}
              title="Kanäle & Pools"
              subtitle="Verkauf-Kanäle akzeptieren Bestellungen. Reservierungs-Pools sind interne Verteilungen."
              onClose={() => setShowChannelsConfig(false)}
              marginBottom={16}
            />

            <div style={{ ...glass({ borderRadius: 14 }), overflow: "hidden", marginBottom: 14 }}>
              {sortedChannels.map((ch, i) => {
                const fin = getChannelFinance(ch.id);
                const totalFee = fin.fee_fixed + fin.price * (fin.fee_percent || 0) / 100;
                const netto = fin.price - fin.shipping - totalFee;
                const isExpanded = expandedChannelId === ch.id;
                return (
                <React.Fragment key={ch.id}>
                  {i > 0 && <div style={{ height: "0.5px", background: t.divider, marginLeft: 16 }} />}
                  <div
                    id={`channel-row-${ch.id}`}
                    style={{
                      padding: "12px 14px",
                      display: "flex", gap: 10, alignItems: "center",
                      // Highlight wenn vom BatchSlotModal hierhin navigiert
                      // (expandedChannelId wird beim Deep-Link gesetzt).
                      background: isExpanded ? t.accent.blue.weak : "transparent",
                      transition: "background 0.4s ease",
                      scrollMarginTop: 16,
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
                      <button onClick={() => moveChannel(i, -1)} disabled={i === 0} style={{ background: "none", border: "none", cursor: i === 0 ? "default" : "pointer", color: i === 0 ? t.textMuted : t.text, fontSize: 11, padding: "1px 4px", opacity: i === 0 ? 0.3 : 1 }}>▲</button>
                      <button onClick={() => moveChannel(i, 1)} disabled={i === sortedChannels.length - 1} style={{ background: "none", border: "none", cursor: i === sortedChannels.length - 1 ? "default" : "pointer", color: i === sortedChannels.length - 1 ? t.textMuted : t.text, fontSize: 11, padding: "1px 4px", opacity: i === sortedChannels.length - 1 ? 0.3 : 1 }}>▼</button>
                    </div>
                    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                      <input
                        defaultValue={ch.name}
                        onBlur={e => { if (e.target.value.trim() && e.target.value.trim() !== ch.name) renameChannel(ch.id, e.target.value.trim()); }}
                        onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                        style={{ width: "100%", background: "transparent", border: "none", color: t.text, fontFamily: F, fontSize: 15, fontWeight: 500, outline: "none", padding: "6px 4px", borderBottom: `1px dashed ${t.glassBorder}` }}
                      />
                      {/* Type-Pille: zeigt sofort ob Sales oder Reservierung */}
                      <span style={{
                        alignSelf: "flex-start",
                        fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                        padding: "1px 7px", borderRadius: 5, marginLeft: 4,
                        background: isSalesChannel(ch) ? t.accent.green.weak : t.accent.purple.weak,
                        color: isSalesChannel(ch) ? t.accent.green.solid : t.accent.purple.solid,
                      }}>{isSalesChannel(ch) ? "Verkauf" : "Reservierung"}</span>
                    </div>
                    {/* Expand-Button für ALLE Channels — Sales: Finanzen+Löschen, Reserved: nur Löschen */}
                    <button onClick={() => setExpandedChannelId(isExpanded ? null : ch.id)} title={isSalesChannel(ch) ? "Finanzen & Löschen" : "Pool löschen"} style={{
                      background: isExpanded ? t.accent.blue.weak : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
                      border: `1px solid ${isExpanded ? t.accent.blue.solid : t.glassBorder}`,
                      borderRadius: 10, padding: "8px 12px", color: isExpanded ? t.accent.blue.solid : t.text,
                      fontSize: 14, fontWeight: 700, cursor: "pointer", flexShrink: 0,
                      display: "flex", alignItems: "center", gap: 5,
                      transition: "all 0.18s ease",
                      minWidth: 56,
                    }}>
                      <span style={{ fontSize: isSalesChannel(ch) ? 16 : 14, lineHeight: 1 }}>{isSalesChannel(ch) ? "€" : "···"}</span>
                      <span style={{ fontSize: 11 }}>{isExpanded ? "▴" : "▾"}</span>
                    </button>
                    {/* iOS-26 Toggle — größer, glatter Spring-Slide. Mülleimer-Button daneben entfernt (User-Wunsch). */}
                    <button onClick={() => toggleActive(ch.id)} title={ch.active !== false ? "Aktiv — klick zum deaktivieren" : "Inaktiv — klick zum aktivieren"} style={{
                      width: 46, height: 28, borderRadius: 14, border: "none", cursor: "pointer", padding: 0, position: "relative",
                      background: ch.active !== false ? t.accent.green.solid : (dark ? "rgba(120,120,128,0.32)" : "rgba(120,120,128,0.18)"),
                      boxShadow: ch.active !== false ? "inset 0 1px 2px rgba(0,0,0,0.18)" : "inset 0 1px 2px rgba(0,0,0,0.08)",
                      transition: "background 0.22s ease",
                      flexShrink: 0,
                    }}>
                      <span style={{
                        position: "absolute", top: 2,
                        left: ch.active !== false ? 20 : 2,
                        width: 24, height: 24, borderRadius: "50%",
                        background: "#fff",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.25), 0 0 0 0.5px rgba(0,0,0,0.04)",
                        transition: "left 0.24s cubic-bezier(0.34,1.2,0.64,1)",
                      }} />
                    </button>
                  </div>
                  {isExpanded && (
                    <div style={{ padding: "0 14px 14px 36px", background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)" }}>
                      {isSalesChannel(ch) ? (
                      <>
                      <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 6, marginBottom: 8 }}>Finanzen für {ch.name}</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                        {[
                          { key: "price",       label: "Verkaufspreis", suffix: "€" },
                          { key: "fee_fixed",   label: "Gebühr fix",    suffix: "€" },
                          { key: "fee_percent", label: "Gebühr",        suffix: "%" },
                          { key: "shipping",    label: "Versand",       suffix: "€" },
                        ].map(field => (
                          <div key={field.key} style={{ ...glass({ borderRadius: 10 }), padding: "8px 12px" }}>
                            <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, marginBottom: 2 }}>{field.label} {field.suffix}</div>
                            <input
                              type="number" step="0.01" min="0"
                              defaultValue={fin[field.key]}
                              onBlur={e => updateFinance(ch.id, field.key, e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                              style={{ width: "100%", border: "none", background: "transparent", fontFamily: F, fontSize: 15, fontWeight: 600, color: t.text, outline: "none", padding: 0 }}
                            />
                          </div>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: t.textSub, lineHeight: 1.5, padding: "8px 12px", background: dark ? "rgba(48,209,88,0.08)" : "rgba(48,209,88,0.06)", borderRadius: 8, border: "1px solid rgba(48,209,88,0.2)" }}>
                        <span style={{ color: "#30D158", fontWeight: 600 }}>Netto pro Verkauf: {fmt2(netto)} €</span>
                        <span style={{ color: t.textMuted, marginLeft: 8 }}>(Preis − Versand − Gebühr {fmt2(totalFee)} €)</span>
                      </div>
                      </>
                      ) : (
                      <div style={{ fontSize: 11, color: t.textSub, lineHeight: 1.5, padding: "10px 12px", background: dark ? "rgba(191,90,242,0.08)" : "rgba(191,90,242,0.06)", borderRadius: 8, border: "1px solid rgba(191,90,242,0.2)", marginTop: 8 }}>
                        Reservierungs-Pool — keine Finanzen, nur interne Verteilung von Lagerbestand (z.B. Familie, Demo, Geschenk).
                      </div>
                      )}
                      {/* Lösch-Aktion — für ALLE Channel-Typen verfügbar */}
                      <button onClick={() => deleteChannel(ch.id)} style={{
                        marginTop: 10, width: "100%",
                        background: t.accent.red.weak, border: `1px solid ${t.accent.red.solid}33`,
                        borderRadius: 10, padding: "8px 12px",
                        color: t.accent.red.solid, fontSize: 12, fontWeight: 600, fontFamily: F,
                        cursor: "pointer",
                      }}>{isSalesChannel(ch) ? "Kanal löschen" : "Pool löschen"}</button>
                    </div>
                  )}
                </React.Fragment>
                );
              })}
            </div>

            {/* Trigger jetzt das neue Modal statt prompt+confirm */}
            <button onClick={() => setNewChannelOpen(true)} style={{
              width: "100%", padding: "12px", background: t.pillActive, border: `1px solid ${t.glassBorder}`,
              borderRadius: 12, color: "#0A84FF", fontSize: 14, fontWeight: 600, fontFamily: F, cursor: "pointer",
            }}>+ Neuen Kanal hinzufügen</button>
          </div>
        </>
        );
      })()}

      {/* ── STATUS LABELS CONFIG MODAL ── */}
      {showStatusLabelsConfig && (() => {
        const STATUS_KEYS = ["Offen", "Bereit", "Versendet", "Retoure"];
        const updateLabel = (key, val) => {
          const next = { ...storeStatusLabels, [key]: (val || "").trim() || key };
          setStoreStatusLabels(next);
          saveStoreConfig({ status_labels: next });
        };
        const resetDefaults = () => {
          setStoreStatusLabels(DEFAULT_STATUS_LABELS);
          saveStoreConfig({ status_labels: DEFAULT_STATUS_LABELS });
        };
        return (
        <>
          <div onClick={() => setShowStatusLabelsConfig(false)} style={{
            position: "fixed", inset: 0, zIndex: 80,
            background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />
          <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 440, radius: 24, padding: "20px 22px 22px", zIndex: 81 })}>
            <ModalHeader
              t={t} dark={dark} F={F}
              title="Status-Bezeichnungen"
              subtitle="Anzeigenamen für die vier Status. Interne Logik bleibt unverändert."
              onClose={() => setShowStatusLabelsConfig(false)}
              marginBottom={16}
            />

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
              {STATUS_KEYS.map(k => (
                <div key={k} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ minWidth: 80, fontSize: 12, color: t.textMuted, fontFamily: "monospace" }}>{k}</span>
                  <input
                    defaultValue={storeStatusLabels[k] || k}
                    onBlur={e => { if (e.target.value !== (storeStatusLabels[k] || k)) updateLabel(k, e.target.value); }}
                    onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                    style={{ flex: 1, padding: "10px 12px", background: t.inpBg || (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"), border: `1px solid ${t.glassBorder}`, borderRadius: 10, color: t.text, fontFamily: F, fontSize: 14, outline: "none" }}
                  />
                </div>
              ))}
            </div>

            <button onClick={resetDefaults} style={{
              width: "100%", padding: "11px", background: "transparent", border: `1px solid ${t.glassBorder}`,
              borderRadius: 12, color: t.textSub, fontSize: 13, fontWeight: 500, fontFamily: F, cursor: "pointer",
            }}>Auf Standard zurücksetzen</button>
          </div>
        </>
        );
      })()}

      {/* ── MEMBERS MODAL ── */}
      {showMembers && (() => {
        const ROLE_LABELS = { owner: "Inhaber", admin: "Admin", fulfillment: "Fulfillment", finance: "Finanzen", viewer: "Betrachter" };
        const ROLE_OPTIONS_INVITE = [
          { v: "admin",       l: "Admin"       },
          { v: "fulfillment", l: "Fulfillment" },
          { v: "finance",     l: "Finanzen"    },
          { v: "viewer",      l: "Betrachter"  },
        ];
        return (
        <>
          <div onClick={() => { setShowMembers(false); setCreatedInviteCode(null); }} style={{
            position: "fixed", inset: 0, zIndex: 80,
            background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />
          <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 560, radius: 24, padding: "20px 22px 22px", zIndex: 81 })}>
            <ModalHeader
              t={t} dark={dark} F={F}
              title="Mitglieder"
              subtitle={currentStore?.name || ""}
              onClose={() => { setShowMembers(false); setCreatedInviteCode(null); }}
              marginBottom={16}
            />

            {/* Tabs */}
            <div style={{ ...glass({ borderRadius: 12, padding: 3 }), display: "flex", marginBottom: 16 }}>
              {[
                { v: "members", l: `Mitglieder · ${membersList.length}` },
                { v: "invites", l: `Einladungen · ${invitationsList.length}` },
              ].map(tab => (
                <button key={tab.v} onClick={() => setMemberSheetTab(tab.v)} style={{
                  flex: 1, padding: "9px 4px", border: "none", cursor: "pointer", fontFamily: F,
                  fontSize: 13, fontWeight: memberSheetTab === tab.v ? 600 : 500, borderRadius: 9,
                  background: memberSheetTab === tab.v ? t.pillActive : "transparent",
                  color: memberSheetTab === tab.v ? t.text : t.textSub,
                  transition: "all 0.2s ease",
                }}>{tab.l}</button>
              ))}
            </div>

            {/* MEMBERS TAB */}
            {memberSheetTab === "members" && (
              <div>
                {memberLoading && <div style={{ fontSize: 13, color: t.textMuted, padding: 16, textAlign: "center" }}>Lade…</div>}
                {!memberLoading && membersList.length === 0 && <div style={{ fontSize: 13, color: t.textMuted, padding: 16, textAlign: "center" }}>Keine Mitglieder gefunden</div>}
                <div style={{ ...glass({ borderRadius: 14 }), overflow: "hidden" }}>
                  {membersList.map((m, i) => (
                    <React.Fragment key={m.id}>
                      {i > 0 && <div style={{ height: "0.5px", background: t.divider, marginLeft: 16 }} />}
                      <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 14, color: t.text, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {m.email || m.user_id.slice(0, 8)}
                            {m.user_id === authUser?.id && <span style={{ marginLeft: 6, fontSize: 11, color: t.textMuted, fontWeight: 400 }}>(du)</span>}
                          </div>
                        </div>
                        {currentRole === "owner" && m.user_id !== authUser?.id ? (
                          <select value={m.role} onChange={e => changeMemberRole(m.user_id, e.target.value)} style={{ padding: "5px 8px", fontSize: 12, fontFamily: F, background: t.pillActive, color: t.text, border: `1px solid ${t.glassBorder}`, borderRadius: 8, cursor: "pointer" }}>
                            <option value="owner">Inhaber</option>
                            <option value="admin">Admin</option>
                            <option value="fulfillment">Fulfillment</option>
                            <option value="finance">Finanzen</option>
                            <option value="viewer">Betrachter</option>
                          </select>
                        ) : (
                          <span style={{ fontSize: 12, padding: "4px 10px", background: t.pillActive, borderRadius: 9, color: t.textSub, fontWeight: 500 }}>{ROLE_LABELS[m.role] || m.role}</span>
                        )}
                        {currentRole === "owner" && m.user_id !== authUser?.id && (
                          <button onClick={() => setConfirmRemoveUserId(m.user_id)} style={{ background: "none", border: "none", color: "#FF453A", fontFamily: F, fontSize: 12, cursor: "pointer", padding: "4px 8px" }}>Entfernen</button>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* INVITES TAB */}
            {memberSheetTab === "invites" && (
              <div>
                {/* Create new invite */}
                <div style={{ ...glass({ borderRadius: 14 }), padding: 14, marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Neuen Mitglied einladen</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <select value={newInviteRole} onChange={e => setNewInviteRole(e.target.value)} style={{ flex: 1, padding: "9px 12px", fontSize: 14, fontFamily: F, background: t.pillActive, color: t.text, border: `1px solid ${t.glassBorder}`, borderRadius: 10, cursor: "pointer" }}>
                      {ROLE_OPTIONS_INVITE.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
                    </select>
                    <button onClick={() => createInvitation(newInviteRole)} style={{ padding: "9px 16px", background: "#0A84FF", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 600, fontFamily: F, cursor: "pointer", boxShadow: "0 2px 10px rgba(10,132,255,0.3)" }}>+ Code erstellen</button>
                  </div>
                  {createdInviteCode && (
                    <div style={{ marginTop: 12, padding: "12px 14px", background: "rgba(48,209,88,0.1)", borderRadius: 10, border: "1px solid rgba(48,209,88,0.25)" }}>
                      <div style={{ fontSize: 12, color: "#30D158", fontWeight: 600, marginBottom: 4 }}>Einladungs-Code (7 Tage gültig)</div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                        <code style={{ fontSize: 17, fontFamily: "monospace", color: t.text, fontWeight: 700, letterSpacing: "0.1em" }}>{createdInviteCode}</code>
                        <button onClick={() => { navigator.clipboard?.writeText(createdInviteCode); showToast("Code kopiert"); }} style={{ padding: "6px 12px", background: t.pillActive, border: `1px solid ${t.glassBorder}`, borderRadius: 8, color: t.text, fontSize: 12, fontWeight: 500, fontFamily: F, cursor: "pointer" }}>Kopieren</button>
                      </div>
                      <div style={{ fontSize: 12, color: t.textMuted, marginTop: 6, lineHeight: 1.4 }}>
                        Sende diesen Code an die Person. Sie kann sich registrieren und unter "Einladungscode eingeben" beitreten.
                      </div>
                    </div>
                  )}
                </div>

                {/* List existing invitations */}
                {invitationsList.length > 0 ? (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>Offene Einladungen</div>
                    <div style={{ ...glass({ borderRadius: 14 }), overflow: "hidden" }}>
                      {invitationsList.map((inv, i) => {
                        const expSoon = (new Date(inv.expires_at).getTime() - Date.now()) < 24 * 3600 * 1000;
                        return (
                          <React.Fragment key={inv.id}>
                            {i > 0 && <div style={{ height: "0.5px", background: t.divider, marginLeft: 16 }} />}
                            <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <code style={{ fontSize: 14, fontFamily: "monospace", color: t.text, fontWeight: 700, letterSpacing: "0.06em" }}>{inv.code}</code>
                                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
                                  {ROLE_LABELS[inv.role] || inv.role} · läuft ab {new Date(inv.expires_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
                                  {expSoon && <span style={{ color: "#FF9F0A", marginLeft: 6 }}>(bald)</span>}
                                </div>
                              </div>
                              <button onClick={() => { navigator.clipboard?.writeText(inv.code); showToast("Code kopiert"); }} style={{ background: "none", border: "none", color: "#0A84FF", fontFamily: F, fontSize: 12, cursor: "pointer", padding: "4px 8px" }}>Kopieren</button>
                              <button onClick={() => revokeInvitation(inv.id)} style={{ background: "none", border: "none", color: "#FF453A", fontFamily: F, fontSize: 12, cursor: "pointer", padding: "4px 8px" }}>Widerrufen</button>
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: t.textMuted, textAlign: "center", padding: 16 }}>Keine offenen Einladungen</div>
                )}
              </div>
            )}
          </div>

          {/* Confirm remove member */}
          {confirmRemoveUserId && (() => {
            const target = membersList.find(m => m.user_id === confirmRemoveUserId);
            return (
              <>
                <div onClick={() => setConfirmRemoveUserId(null)} style={{
                  position: "fixed", inset: 0, zIndex: 90,
                  background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
                  backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
                  animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
                  willChange: "opacity",
                }} />
                <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 360, radius: 24, zIndex: 91 })}>
                  <ModalHeader
                    t={t} dark={dark} F={F}
                    title="Mitglied entfernen?"
                    subtitle={target?.email || "Dieses Mitglied"}
                    accent={t.accent.red}
                    accentIcon={
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <line x1="22" y1="11" x2="16" y2="11"/>
                      </svg>
                    }
                    onClose={() => setConfirmRemoveUserId(null)}
                  />
                  <button onClick={() => setConfirmRemoveUserId(null)} style={{
                    width: "100%", background: "rgba(10,132,255,0.92)", border: "none", borderRadius: 12,
                    padding: "13px", marginBottom: 8,
                    fontFamily: F, fontWeight: 700, fontSize: 15, cursor: "pointer", color: "#fff",
                    letterSpacing: "-0.1px", boxShadow: "0 4px 20px rgba(10,132,255,0.3)",
                  }}>Abbrechen</button>
                  <button onClick={() => removeMember(confirmRemoveUserId)} style={{
                    width: "100%", background: "transparent", border: "none",
                    padding: "11px", fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer", color: t.accent.red.solid,
                  }}>Entfernen</button>
                </div>
              </>
            );
          })()}
        </>
        );
      })()}

      {/* ── SETTINGS SHEET ── */}
      <BottomSheet
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        title="Einstellungen"
        subtitle={currentStore?.name}
        dark={dark} t={t} F={F}
      >
        <div style={{ padding: 0 }}>
          {/* Inhalt nutzt die existing SettingsSection / SettingsToggleRow / SettingsLinkRow Helper.
              Padding-Bug (Switches angeschnitten) durch BottomSheet-eigenes Layout behoben. */}

              {/* ───────────────────────────────────────────────
                  iOS-Settings-Style List-Helpers für saubere Sektionen.
                  Nutzung: <Section title="…">…</Section>
                          <Row icon={Icon.x} accent="#..." label="…" right={…} onClick={…} />
                  Trennlinien werden automatisch gesetzt zwischen Rows.
                  ─────────────────────────────────────────────── */}
              {(() => null)()}

              {/* === SEKTION 1 — ANZEIGE & WORKFLOW === */}
              <SettingsSection title="Anzeige" t={t}>
                <SettingsToggleRow
                  t={t} dark={dark} F={F}
                  icon={Icon.moon} accent={t.accent.blue.solid}
                  label="Dark Mode"
                  value={dark}
                  onChange={() => setDark(v => !v)}
                  first
                />
                {!isHelperUser && (
                  <SettingsToggleRow
                    t={t} dark={dark} F={F}
                    icon={Icon.bolt} accent={t.accent.orange.solid}
                    label="Helfer-Modus"
                    sub={helperMode ? "Aktiv — vereinfachte Ansicht" : "Vereinfachte Vertretungs-Ansicht"}
                    value={helperMode}
                    onChange={toggleHelperMode}
                    activeColor={t.accent.orange.solid}
                  />
                )}
                <SettingsLinkRow
                  t={t} dark={dark} F={F}
                  icon={Icon.bars} accent={t.accent.blue.solid}
                  label="Dashboard anpassen"
                  onClick={() => { setShowSettings(false); setShowCustomizeDashboard(true); }}
                />
              </SettingsSection>

              {/* === SEKTION 2 — BESTELL-FILTER === */}
              <SettingsSection title="Bestell-Filter" t={t}>
                <SettingsLinkRow
                  t={t} dark={dark} F={F}
                  icon={Icon.filter} accent={t.accent.purple.solid}
                  label="Kanal"
                  rightText={filterKanal === "Alle" ? "Alle" : (getAllChannels().find(c => c.id === filterKanal)?.name || filterKanal)}
                  onClick={() => setFilterPickerOpen(true)}
                  first
                />
                <SettingsSegmentedRow
                  t={t} dark={dark} F={F}
                  icon={Icon.clock} accent={t.accent.blue.solid}
                  label="Zeitraum"
                  sub="Versendet & Retoure"
                  options={[{ v: 30, l: "30T" }, { v: 90, l: "90T" }, { v: -1, l: "Alles" }]}
                  value={dateRange}
                  onChange={setDateRange}
                />
              </SettingsSection>

              {/* === SEKTION 3 — DATEN === */}
              <SettingsSection title="Daten" t={t}>
                <SettingsLinkRow
                  t={t} dark={dark} F={F}
                  icon={Icon.archive} accent={t.accent.orange.solid}
                  label="Archiv"
                  rightText={String(archive.length)}
                  onClick={() => { setShowSettings(false); setTimeout(() => setShowArchive(true), 200); }}
                  first
                />
                {can("read") && (
                  <SettingsLinkRow
                    t={t} dark={dark} F={F}
                    icon={Icon.download} accent={t.accent.green.solid}
                    label="Als CSV exportieren"
                    onClick={() => { exportCSV(); setShowSettings(false); }}
                    accentLabel
                  />
                )}
              </SettingsSection>

              {/* === SEKTION 4 — STORE (Admin/Owner) === */}
              {currentStore && can("manage_members") && (
                <SettingsSection title="Store" t={t}>
                  {/* Store-Header (read-only Info) */}
                  <div style={{
                    padding: "14px 16px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    background: dark ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.015)",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                      <span style={{ color: t.accent.blue.solid, display: "inline-flex", flexShrink: 0 }}>{Icon.store(16)}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, color: t.text, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{currentStore.name}</div>
                        <div style={{ fontSize: 11, color: t.textMuted, marginTop: 1 }}>
                          {{owner:"Inhaber",admin:"Admin",fulfillment:"Fulfillment",finance:"Finanzen",viewer:"Betrachter"}[currentRole] || currentRole}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ height: "0.5px", background: t.divider, marginLeft: 42 }} />

                  {userStores.length > 1 && (
                    <>
                      <SettingsLinkRow
                        t={t} dark={dark} F={F}
                        icon={Icon.arrow} accent={t.accent.blue.solid}
                        label="Store wechseln"
                        onClick={() => { setShowSettings(false); setAppScreen("store-selector"); }}
                      />
                    </>
                  )}
                  <SettingsLinkRow
                    t={t} dark={dark} F={F}
                    icon={Icon.users} accent={t.accent.purple.solid}
                    label="Mitglieder verwalten"
                    onClick={() => { setShowSettings(false); setShowMembers(true); setMemberSheetTab("members"); loadMembersAndInvitations(); }}
                  />
                  <SettingsLinkRow
                    t={t} dark={dark} F={F}
                    icon={Icon.bars} accent={t.accent.blue.solid}
                    label="Verkaufskanäle"
                    onClick={() => { setShowSettings(false); setShowChannelsConfig(true); }}
                  />
                  <SettingsLinkRow
                    t={t} dark={dark} F={F}
                    icon={Icon.tag} accent={t.accent.orange.solid}
                    label="Status-Bezeichnungen"
                    onClick={() => { setShowSettings(false); setShowStatusLabelsConfig(true); }}
                  />
                  <SettingsLinkRow
                    t={t} dark={dark} F={F}
                    icon={Icon.clock} accent={t.accent.blue.solid}
                    label="Aktivitätsverlauf"
                    onClick={() => { setShowSettings(false); setShowAuditLog(true); setAuditFilter({ entity_type: "all", days: 7 }); setAuditEntries([]); setTimeout(() => loadAuditLog(true), 0); }}
                  />
                  {currentRole === "owner" && (
                    <SettingsLinkRow
                      t={t} dark={dark} F={F}
                      icon={Icon.trash || Icon.x} accent={t.accent.red.solid}
                      label="Store löschen"
                      accentLabel
                      labelColor={t.accent.red.solid}
                      onClick={() => { setShowSettings(false); setConfirmDeleteStoreOpen(true); }}
                    />
                  )}
                </SettingsSection>
              )}

              {/* === SEKTION 5 — KONTO === */}
              {authUser?.email && (
                <SettingsSection title="Konto" t={t}>
                  <div style={{
                    padding: "14px 16px",
                    display: "flex", alignItems: "center", gap: 10,
                  }}>
                    <span style={{ color: t.textMuted, display: "inline-flex", flexShrink: 0 }}>{Icon.user(16)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 500 }}>Angemeldet als</div>
                      <div style={{ fontSize: 13, color: t.text, marginTop: 1, wordBreak: "break-all" }}>{authUser.email}</div>
                    </div>
                  </div>
                  {supabase && (
                    <>
                      <div style={{ height: "0.5px", background: t.divider, marginLeft: 42 }} />
                      <SettingsLinkRow
                        t={t} dark={dark} F={F}
                        icon={Icon.logout} accent={t.accent.red.solid}
                        label="Abmelden"
                        accentLabel
                        labelColor={t.accent.red.solid}
                        onClick={async () => {
                          await supabase.auth.signOut();
                          setShowSettings(false);
                        }}
                      />
                    </>
                  )}
                </SettingsSection>
              )}
        </div>
      </BottomSheet>

      {/* ── STORE-DELETE-BESTÄTIGUNG (aus Online-/Flea-/Market-Settings) ── */}
      {confirmDeleteStoreOpen && currentStore && (
        <ConfirmDeleteStoreSheet
          dark={dark} t={t}
          storeName={currentStore.name}
          storeType={currentStore.type || "online"}
          onCancel={() => setConfirmDeleteStoreOpen(false)}
          onConfirm={async () => { await destroyCurrentStore(); setConfirmDeleteStoreOpen(false); }}
        />
      )}

      {/* ── KANAL-PICKER (aus Settings → Bestell-Filter) ── */}
      {filterPickerOpen && (
        <KanalPickerModal
          t={t} dark={dark} F={F} glass={glass}
          channels={[{ id: "Alle", name: "Alle" }, ...getAllChannels().map(c => ({ id: c.id, name: `${c.name} · ${orders.filter(o => o.kanal === c.id).length}` }))]}
          value={filterKanal}
          onPick={setFilterKanal}
          onClose={() => setFilterPickerOpen(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
export default App;
